const {
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
  useCallback
} = React;
const SIGN_FA = "شان د شیپ";
const SIGN_EN = "Shaun the Sheep";

// ═══════════════════════════════════════════════════════════════════════════
// i18n — the site speaks four languages: English (en), Arabic (ar), Farsi (fa),
// Turkish (tr). The choice lives in the `sf-lang` cookie (shared with the SSR pages) and is
// mirrored to localStorage. The paper-ledger design keeps its two-script look:
// the CHOSEN language is the primary script everywhere, and the small accent
// line shows the complementary language (en→fa accents — today's look —
// fa→en, ar→en; tr→fa: Turkish is Latin like English, so it wears English's
// typography and the same Persian accent). Direction: en/tr=ltr, ar/fa=rtl.
// ═══════════════════════════════════════════════════════════════════════════
const LANG_KEY = 'sf-lang';
const KNOWN_LANGS = ['en', 'ar', 'fa', 'tr'];
const ACCENT = {
  en: 'fa',
  fa: 'en',
  ar: 'en',
  tr: 'fa'
};
// Latin-script, left-to-right languages share English's typography (fonts,
// small-caps, italics, uppercase labels, dir=ltr). Use these for any
// script / direction / font intent; keep `=== 'en'` only where the TEXT itself
// must be English.
const isLatin = L => L === 'en' || L === 'tr';
const isRtl = L => !isLatin(L);
// Script class for a language — pairs with the slot class so fonts/direction
// follow the CONTENT script (see the i18n layer at the end of paper.css).
const SCR = l => isLatin(l) ? 'scr-latin' : 'scr-arab';
function detectLang() {
  // Ad-declared intent: shaunfood.com/?lang=ar lands the visitor in Arabic
  // (the ad *said* the language — this is not browser auto-detect, which the
  // owner rejected). SSR already sets the cookie for the param; this covers
  // cached/SPA entries and persists the choice.
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (KNOWN_LANGS.indexOf(q) !== -1) {
      persistLang(q);
      return q;
    }
  } catch (e) {}
  try {
    const m = document.cookie.match(/(?:^|;\s*)sf-lang=(en|ar|fa|tr)\b/);
    if (m) return m[1];
  } catch (e) {}
  try {
    const s = localStorage.getItem(LANG_KEY);
    if (KNOWN_LANGS.indexOf(s) !== -1) return s;
  } catch (e) {}
  // Default to English for first-time visitors (owner preference). We do NOT
  // auto-detect navigator.language — Dubai browsers commonly report Arabic,
  // which was defaulting new visitors to Arabic. The switcher (sf-lang cookie
  // + localStorage) always overrides and persists a real choice. Kept in sync
  // with resolveLang() in src/lib/i18n.ts.
  return 'en';
}
function persistLang(l) {
  try {
    localStorage.setItem(LANG_KEY, l);
  } catch (e) {}
  try {
    document.cookie = LANG_KEY + '=' + l + '; path=/; max-age=31536000; SameSite=Lax';
  } catch (e) {}
}
function applyLangToDocument(l) {
  try {
    const root = document.documentElement;
    root.lang = l;
    root.dir = isLatin(l) ? 'ltr' : 'rtl';
    root.setAttribute('data-lang', l);
  } catch (e) {}
}
const LangCtx = React.createContext('en');
const useLang = () => useContext(LangCtx);

// ── UI string dictionary — every key carries all three languages ────────────
const T = {
  // Nav / menus
  'nav.menu': {
    en: 'Menu',
    fa: 'منو',
    ar: 'القائمة',
    tr: 'Menü'
  },
  'nav.platters': {
    en: 'Platters',
    fa: 'دیس‌ها',
    ar: 'الصواني',
    tr: 'Tabaklar'
  },
  'nav.howmade': {
    en: "How it's made",
    fa: 'طرز پخت',
    ar: 'طريقة التحضير',
    tr: 'Nasıl yapılır'
  },
  'nav.findus': {
    en: 'Find us',
    fa: 'آدرس',
    ar: 'موقعنا',
    tr: 'Bizi bulun'
  },
  'nav.reserve': {
    en: 'Reserve',
    fa: 'رزرو',
    ar: 'احجز',
    tr: 'Rezervasyon'
  },
  'nav.reserveTable': {
    en: 'Reserve a table',
    fa: 'رزرو میز',
    ar: 'احجز طاولة',
    tr: 'Masa ayırtın'
  },
  'nav.orders': {
    en: 'My orders',
    fa: 'سفارش‌های من',
    ar: 'طلباتي',
    tr: 'Siparişlerim'
  },
  'nav.account': {
    en: 'Account',
    fa: 'حساب',
    ar: 'حسابي',
    tr: 'Hesap'
  },
  'nav.blog': {
    en: 'Journal',
    fa: 'مجله',
    ar: 'المدوّنة',
    tr: 'Blog'
  },
  'nav.myAccount': {
    en: 'My account',
    fa: 'حساب من',
    ar: 'حسابي',
    tr: 'Hesabım'
  },
  'nav.call': {
    en: 'Call',
    fa: 'تماس',
    ar: 'اتصل بنا',
    tr: 'Arayın'
  },
  'nav.order': {
    en: 'Order',
    fa: 'سفارش',
    ar: 'اطلب',
    tr: 'Sipariş'
  },
  // Sangak Club — the monthly bread plan (/bread). Burger menu + footer + one QuickCard.
  'nav.bread': {
    en: 'Sangak Club',
    fa: 'باشگاه سنگک',
    ar: 'نادي السنكك',
    tr: 'Sangak Kulübü'
  },
  // Hero
  'hero.tagline': {
    en: 'Authentic Iranian kalle pache · in Dubai',
    fa: 'کله‌پاچه‌ی اصیل ایرانی · در دبی',
    ar: 'باجة إيرانية على أصولها · كله باتشه في دبي',
    tr: "Otantik İran usulü kelle paça (kalle pache) · Dubai'de"
  },
  'hero.welcome': {
    en: 'Welcome',
    fa: 'خوش آمدید',
    ar: 'حيّاكم الله',
    tr: 'Hoş geldiniz'
  },
  'hero.meat': {
    en: 'Meat',
    fa: 'گوشت',
    ar: 'لحومنا',
    tr: 'Et'
  },
  'hero.halal': {
    en: '100% Halal',
    fa: '۱۰۰٪ حلال',
    ar: 'حلال 100%',
    tr: '%100 helal'
  },
  // Plain-words line under the tagline — what the dish IS, for the visitor who
  // has never heard "kalle pache". {p} = cheapest platter price (Western digits).
  'hero.plain': {
    en: 'Sheep head & trotters, slow-cooked — delivered hot · platters from {p} AED',
    fa: 'کله و پاچه‌ی گوسفند، آرام‌پز — داغ به دستتان می‌رسد · دیس‌ها از {p} درهم',
    ar: 'رأس وكوارع غنم مطبوخة على مهل — تصلكم ساخنة · الصواني من {p} درهم',
    tr: "Koyun kellesi ve paça, kısık ateşte pişirilir — kapınıza sıcak gelir · tabaklar {p} AED'den başlıyor"
  },
  'menu.halalNote': {
    en: 'All our meat is halal',
    fa: 'همه‌ی گوشت‌های ما حلال است',
    ar: 'جميع لحومنا حلال',
    tr: 'Tüm etlerimiz helaldir'
  },
  'hero.h1.l1': {
    en: 'The whole head,',
    fa: 'کلّه‌ی کامل،',
    ar: 'الرأس كاملاً،',
    tr: 'Bütün kelle,'
  },
  'hero.h1.l2': {
    en: 'the four trotters,',
    fa: 'چهار پاچه،',
    ar: 'والكوارع الأربع،',
    tr: 'dört paça,'
  },
  'hero.h1.l3': {
    en: 'the slow fire.',
    fa: 'آتشِ آرام.',
    ar: 'على نارٍ هادئة.',
    tr: 'kısık ateş.'
  },
  'hero.sub': {
    en: 'Head, trotters, tongue & brain — simmered 14 hours',
    fa: 'سر، پاچه، زبان و مغز — پخت ۱۴ ساعته',
    ar: 'رأس وكوارع ولسان ومخ — مطهوّة على مهل 14 ساعة',
    tr: 'Kelle, paça, dil ve beyin — 14 saat kısık ateşte'
  },
  'hero.hours': {
    en: 'Hours',
    fa: 'ساعات کار',
    ar: 'ساعات العمل',
    tr: 'Çalışma saatleri'
  },
  'hero.dayNight': {
    en: 'Day & Night',
    fa: 'شبانه‌روزی · همیشه باز',
    ar: 'ليلاً ونهاراً · أبوابنا مفتوحة',
    tr: 'Gece gündüz'
  },
  'hero.always': {
    en: 'always',
    fa: 'همیشه',
    ar: 'دائماً',
    tr: 'her zaman'
  },
  'hero.delivery': {
    en: 'Delivery',
    fa: 'پیک',
    ar: 'التوصيل',
    tr: 'Teslimat'
  },
  'hero.orderNow': {
    en: 'Order Now',
    fa: 'سفارش آنلاین',
    ar: 'اطلب الآن',
    tr: 'Hemen sipariş ver'
  },
  // Status strip
  'status.always': {
    en: 'Always',
    fa: 'همیشه',
    ar: 'دائماً',
    tr: 'Her zaman'
  },
  'status.morning': {
    en: 'The morning bowl is waiting.',
    fa: 'کله‌پاچه‌ی صبح آماده است.',
    ar: 'صباح الخير — الباجة جاهزة بانتظاركم.',
    tr: 'Sabah kasesi sizi bekliyor.'
  },
  'status.midday': {
    en: 'The midday pot is simmering.',
    fa: 'دیگِ ظهر روی آتش است.',
    ar: 'قِدر الظهيرة على النار.',
    tr: 'Öğle kazanı ocakta.'
  },
  'status.evening': {
    en: 'The evening broth runs deep.',
    fa: 'آبگوشتِ شام پخته و خوش‌رنگ است.',
    ar: 'مرق المساء غنيّ ودسم.',
    tr: 'Akşamın et suyu demini aldı.'
  },
  'status.night': {
    en: 'The pot keeps watch through the night.',
    fa: 'دیگ شبانه روی آتش بیدار است.',
    ar: 'القِدر ساهرة طوال الليل.',
    tr: 'Gece boyunca bakır kazan nöbette.'
  },
  'status.simmering': {
    en: 'simmering',
    fa: 'روی آتش',
    ar: 'على النار',
    tr: 'kısık ateşte'
  },
  'status.city': {
    en: 'Dubai',
    fa: 'دبی',
    ar: 'دبي',
    tr: 'Dubai'
  },
  'status.amber': {
    en: 'Deep amber',
    fa: 'کهربایی سیر',
    ar: 'كهرماني غامق',
    tr: 'Koyu kehribar'
  },
  'status.brothColor': {
    en: 'broth color',
    fa: 'رنگ آبگوشت',
    ar: 'لون المرق',
    tr: 'et suyu rengi'
  },
  // Menu board
  'menu.vatNote': {
    en: 'Prices exclude 5% VAT · added at checkout',
    fa: 'قیمت‌ها بدون احتساب مالیات ۵٪ — هنگام پرداخت اضافه می‌شود',
    ar: 'الأسعار لا تشمل ضريبة القيمة المضافة 5% — تُضاف عند الدفع',
    tr: 'Fiyatlara %5 KDV dahil değildir · ödeme sırasında eklenir'
  },
  'menu.searchPh': {
    en: 'Search the menu — e.g. brain',
    fa: 'جست‌وجوی منو — مثلاً مغز',
    ar: 'ابحث في القائمة — مثلاً مخ',
    tr: 'Menüde ara — örn. beyin'
  },
  'menu.matches': {
    en: 'matches for',
    fa: 'نتیجه برای',
    ar: 'نتائج عن',
    tr: '“{q}” için {n} sonuç'
  },
  'menu.match': {
    en: 'match for',
    fa: 'نتیجه برای',
    ar: 'نتيجة عن',
    tr: '“{q}” için {n} sonuç'
  },
  'menu.noMatch': {
    en: 'No matches in the ledger.',
    fa: 'چیزی پیدا نشد.',
    ar: 'لم نجد ما تبحث عنه — جرّب كلمة أخرى.',
    tr: 'Sonuç bulunamadı.'
  },
  'menu.outToday': {
    en: 'Out today',
    fa: 'تمام شد',
    ar: 'نفد اليوم',
    tr: 'Bugün tükendi'
  },
  'menu.dineinOnly': {
    en: 'Dine-in only',
    fa: 'فقط سالن',
    ar: 'داخل المطعم فقط',
    tr: 'Yalnızca restoranda'
  },
  'menu.add': {
    en: '+ Add',
    fa: '+ اضافه',
    ar: '+ أضف',
    tr: '+ Ekle'
  },
  'menu.addToOrder': {
    en: '+ Add to order',
    fa: '+ به سفارش اضافه کن',
    ar: '+ أضف إلى الطلب',
    tr: '+ Sepete ekle'
  },
  'menu.includes': {
    en: 'Includes',
    fa: 'شامل',
    ar: 'يشمل',
    tr: 'İçindekiler'
  },
  'menu.pairsWith': {
    en: 'Pairs well with',
    fa: 'معمولاً با این می‌خورند',
    ar: 'يحلو معه',
    tr: 'Yanında iyi gider'
  },
  'menu.roundOut': {
    en: 'Add to round out the meal',
    fa: 'برای کامل‌کردن سفارش',
    ar: 'أكمل وجبتك',
    tr: 'Sofranızı tamamlamak için ekleyin'
  },
  // Order rail / cart
  'rail.yourOrder': {
    en: 'Your Order',
    fa: 'سفارش شما',
    ar: 'طلبك',
    tr: 'Sepetiniz'
  },
  'rail.delivery': {
    en: 'Delivery',
    fa: 'پیک',
    ar: 'توصيل',
    tr: 'Teslimat'
  },
  'rail.dinein': {
    en: 'Dine in',
    fa: 'سالن',
    ar: 'في المطعم',
    tr: 'Restoranda'
  },
  'rail.emptyT1': {
    en: 'Your bowl awaits.',
    fa: 'سفره آماده است؛ فقط سفارش‌تان مانده.',
    ar: 'سفرتك بانتظارك.',
    tr: 'Sofranız sizi bekliyor.'
  },
  'rail.emptyHint': {
    en: 'Tap + Add on any item below to start',
    fa: 'برای شروع، روی «اضافه» در منو بزنید',
    ar: 'اضغط «أضف» على أي صنف للبدء',
    tr: 'Başlamak için menüden bir ürünün yanındaki “+ Ekle” düğmesine dokunun'
  },
  'rail.browse': {
    en: 'Browse the menu',
    fa: 'مرور منو',
    ar: 'تصفح القائمة',
    tr: 'Menüye göz at'
  },
  'rail.subtotal': {
    en: 'Subtotal',
    fa: 'جمع',
    ar: 'المجموع الفرعي',
    tr: 'Ara toplam'
  },
  'rail.vat': {
    en: 'VAT',
    fa: 'مالیات بر ارزش افزوده',
    ar: 'ضريبة القيمة المضافة',
    tr: 'KDV'
  },
  'rail.atCheckout': {
    en: 'at checkout',
    fa: 'هنگام پرداخت',
    ar: 'حسب المنطقة',
    tr: 'ödeme sırasında'
  },
  'rail.total': {
    en: 'Total',
    fa: 'جمع کل',
    ar: 'الإجمالي',
    tr: 'Toplam'
  },
  'rail.checkout': {
    en: 'Checkout',
    fa: 'ادامه و پرداخت',
    ar: 'إتمام الطلب',
    tr: 'Siparişi tamamla'
  },
  'cart.items': {
    en: 'items',
    fa: 'قلم',
    ar: 'أصناف',
    tr: 'ürün'
  },
  'cart.item': {
    en: 'item',
    fa: 'قلم',
    ar: 'صنف',
    tr: 'ürün'
  },
  'cart.ready': {
    en: 'ready',
    fa: 'آماده',
    ar: 'في السلة',
    tr: 'sepette'
  },
  'cart.empty': {
    en: 'empty',
    fa: 'خالی',
    ar: 'السلة فارغة',
    tr: 'boş'
  },
  'cart.view': {
    en: 'View Order',
    fa: 'دیدن سفارش',
    ar: 'عرض الطلب',
    tr: 'Sepeti gör'
  },
  // How it's made
  'how.kicker': {
    en: 'HOW WE MAKE IT',
    fa: 'طرز پخت',
    ar: 'طريقة التحضير',
    tr: 'NASIL YAPIYORUZ'
  },
  'how.h2': {
    en: 'Four steps. Fourteen hours.',
    fa: 'چهار مرحله، چهارده ساعت.',
    ar: 'أربع مراحل، وأربع عشرة ساعة على النار.',
    tr: 'Dört adım. On dört saat.'
  },
  'how.s1': {
    en: 'Cleaned by hand',
    fa: 'تمیز کردن با دست',
    ar: 'تنظيف يدوي',
    tr: 'Elde temizlenir'
  },
  'how.s1.text': {
    en: 'Heads split, trotters scrubbed, tongues rinsed in salted water before dawn.',
    fa: 'سرها شکافته، پاچه‌ها شسته و زبان‌ها پیش از سپیده در آب‌نمک آبکشی می‌شوند.',
    ar: 'تُشقّ الرؤوس وتُفرك الكوارع وتُغسل الألسنة بماء مملّح قبل الفجر.',
    tr: 'Kelleler ikiye ayrılır, paçalar ovularak yıkanır, diller şafaktan önce tuzlu suda durulanır.'
  },
  'how.s2': {
    en: 'Slow fire',
    fa: 'آتش ملایم',
    ar: 'نار هادئة',
    tr: 'Kısık ateş'
  },
  'how.s2.text': {
    en: 'Set in a copper pot with onion, turmeric and a knot of garlic at 4 a.m.',
    fa: 'ساعت ۴ صبح، با پیاز و زردچوبه و یک سرِ سیر در دیگ مسی بار گذاشته می‌شود.',
    ar: 'توضع في قِدرٍ نحاسية مع البصل والكركم ورأس ثوم عند الرابعة فجراً.',
    tr: "Sabah 4'te soğan, zerdeçal ve bir baş sarımsakla bakır kazana konur."
  },
  'how.s3': {
    en: 'Fourteen hours',
    fa: '۱۴ ساعت پخت',
    ar: 'أربع عشرة ساعة',
    tr: '14 saatlik pişirme'
  },
  'how.s3.text': {
    en: 'Skimmed every hour. The broth turns the colour of strong tea.',
    fa: 'هر ساعت کف‌گیری می‌شود؛ آبگوشت به رنگ چای دم‌کشیده درمی‌آید.',
    ar: 'تُزال رغوة المرق كل ساعة، حتى يصير بلون الشاي الثقيل.',
    tr: 'Saat başı köpüğü alınır; et suyu demli çay rengine döner.'
  },
  'how.s4': {
    en: 'Served at the table',
    fa: 'سرو در سفره',
    ar: 'يُقدَّم على السفرة',
    tr: 'Sofrada servis'
  },
  'how.s4.text': {
    en: 'Brass bowl, warm sangak, a bulb of seven-year garlic torshi on the side.',
    fa: 'کاسه‌ی برنجی، سنگک گرم و سیرترشیِ هفت‌ساله در کنارش.',
    ar: 'كاسة نحاسية، وخبز سنكك إيراني دافئ، وإلى جانبها طرشي ثوم معتّق سبع سنين.',
    tr: 'Pirinçten yapılmış kase, sıcak sangak ve yanında 7 yıllık sarımsak turşusu.'
  },
  // Find us
  'find.h2': {
    en: 'Find us',
    fa: 'آدرس و تماس',
    ar: 'زورونا',
    tr: 'Bizi bulun'
  },
  'find.address': {
    en: 'Address',
    fa: 'نشانی',
    ar: 'العنوان',
    tr: 'Adres'
  },
  'find.phone': {
    en: 'Telephone',
    fa: 'تلفن',
    ar: 'الهاتف',
    tr: 'Telefon'
  },
  'find.hours': {
    en: 'Hours',
    fa: 'ساعات کار',
    ar: 'ساعات العمل',
    tr: 'Çalışma saatleri'
  },
  'find.hoursVal': {
    en: 'Open 24 hours · daily',
    fa: 'شبانه‌روزی · همه‌ی روزها',
    ar: 'نستقبلكم 24 ساعة · طوال أيام الأسبوع',
    tr: '7/24 açık · her gün'
  },
  'find.order': {
    en: 'Order',
    fa: 'سفارش',
    ar: 'الطلب',
    tr: 'Sipariş'
  },
  'find.orderVal': {
    en: 'Online here, by phone, or walk in',
    fa: 'آنلاین، تلفنی، یا حضوری',
    ar: 'من الموقع هنا، أو بالاتصال، أو بزيارتنا في المطعم',
    tr: 'Buradan online, telefonla ya da restorana gelerek'
  },
  'find.whatsapp': {
    en: 'WhatsApp',
    fa: 'واتس‌اپ',
    ar: 'واتساب',
    tr: 'WhatsApp'
  },
  'find.whatsappMsg': {
    en: 'Message on WhatsApp',
    fa: 'پیام در واتس‌اپ',
    ar: 'راسلنا على واتساب',
    tr: "WhatsApp'tan yazın"
  },
  'find.openMaps': {
    en: '↗ Open in Google Maps',
    fa: 'باز کردن در نقشه',
    ar: 'افتح في خرائط جوجل',
    tr: "↗ Google Haritalar'da aç"
  },
  'find.zones': {
    en: 'Delivery across Dubai',
    fa: 'ارسال به سراسر دبی',
    ar: 'التوصيل إلى جميع أنحاء دبي',
    tr: 'Dubai genelinde teslimat'
  },
  'find.zonesNote': {
    en: 'We deliver to every area of Dubai — all {n} of them, 24 hours a day. Your delivery fee is shown by area at checkout, and delivery is free on orders over AED {f}.',
    fa: 'ما به همهٔ مناطق دبی ارسال داریم — هر {n} منطقه، ۲۴ ساعته. هزینهٔ پیک بر پایهٔ منطقه هنگام پرداخت نمایش داده می‌شود و برای سفارش‌های بالای {f} درهم رایگان است.',
    ar: 'نوصّل طلبك إلى كل مناطق دبي — {n} منطقة، على مدار 24 ساعة. تظهر رسوم التوصيل حسب منطقتك عند الدفع، والتوصيل مجاني للطلبات بقيمة {f} درهم فأكثر.',
    tr: "Dubai'nin her bölgesine teslimat yapıyoruz — {n} bölgenin tamamına, 7/24. Teslimat ücreti ödeme sırasında bölgenize göre gösterilir; {f} AED üzeri siparişlerde teslimat ücretsizdir."
  },
  'find.here': {
    en: 'we are here',
    fa: 'اینجا',
    ar: 'نحن هنا',
    tr: 'buradayız'
  },
  // Map / area / geo
  'map.fallback': {
    en: "Map couldn't load — tap “Use my GPS”, or just leave your address and we'll call to confirm.",
    fa: 'نقشه بارگذاری نشد — از دکمه‌ی موقعیت استفاده کنید یا آدرس را بنویسید.',
    ar: 'تعذّر تحميل الخريطة — اضغط «حدّد موقعي» أو اكتب عنوانك وسنتصل بك للتأكيد.',
    tr: 'Harita yüklenemedi — “Konumumu kullan” düğmesine dokunun ya da adresinizi yazın, onay için sizi arayacağız.'
  },
  'map.toRestaurant': {
    en: 'to the restaurant',
    fa: 'تا رستوران',
    ar: 'إلى المطعم',
    tr: 'restorana kadar'
  },
  'map.hint': {
    en: 'Drag the map so the pin is on your door',
    fa: 'پین را روی درِ خانه‌تان بگذارید',
    ar: 'حرّك الخريطة حتى يقع الدبوس على بابك',
    tr: 'İşareti kapınızın önüne getirmek için haritayı kaydırın'
  },
  'area.ph': {
    en: 'Type your area — e.g. Marina',
    fa: 'منطقه را تایپ کنید — مثلاً مارینا',
    ar: 'اكتب منطقتك — مثلاً مارينا',
    tr: 'Bölgenizi yazın — örn. Marina'
  },
  'area.label': {
    en: 'Delivery area',
    fa: 'منطقه‌ی ارسال',
    ar: 'منطقة التوصيل',
    tr: 'Teslimat bölgesi'
  },
  'area.change': {
    en: 'Change area',
    fa: 'تغییر منطقه',
    ar: 'تغيير المنطقة',
    tr: 'Bölgeyi değiştir'
  },
  'area.noMatch': {
    en: "No area matches. Try another spelling — or drop the map pin below and we'll sort it out.",
    fa: 'منطقه‌ای پیدا نشد — املای دیگری را امتحان کنید یا پین نقشه را بگذارید.',
    ar: 'لم نجد هذه المنطقة — جرّب كتابة الاسم بطريقة أخرى، أو ضع الدبوس على الخريطة ونحن نتكفّل بالباقي.',
    tr: 'Bölge bulunamadı. Başka bir yazım deneyin — ya da aşağıdan haritada konumu işaretleyin, gerisini biz hallederiz.'
  },
  'area.groupHead': {
    en: 'Delivery',
    fa: 'پیک',
    ar: 'التوصيل',
    tr: 'Teslimat'
  },
  'geo.useGps': {
    en: 'Use my GPS',
    fa: 'موقعیت من',
    ar: 'حدّد موقعي',
    tr: 'Konumumu kullan'
  },
  'geo.locating': {
    en: 'Locating…',
    fa: 'در حال مکان‌یابی…',
    ar: 'جارٍ تحديد الموقع…',
    tr: 'Konum alınıyor…'
  },
  'geo.pinned': {
    en: 'Pinned ✓',
    fa: 'پین شد ✓',
    ar: 'تم تحديد الموقع ✓',
    tr: 'İşaretlendi ✓'
  },
  // Clarity 2026-10-16: the hint is a button now (tap = the same GPS lookup as «Use my GPS»).
  'geo.hint': {
    en: 'Required for delivery. Drag the map onto your door — or tap here to use GPS.',
    fa: 'برای ارسال الزامی است. نقشه را جابه‌جا کنید تا پین درست روی محل شما قرار بگیرد — یا روی همین‌جا بزنید تا موقعیت‌تان با GPS پیدا شود.',
    ar: 'مطلوب للتوصيل. حرّكوا الخريطة حتى يقع الدبوس على بابكم — أو اضغطوا هنا لتحديد موقعكم تلقائياً.',
    tr: 'Teslimat için gerekli. Haritayı kaydırarak işareti kapınızın önüne getirin — ya da GPS için buraya dokunun.'
  },
  // Shown under the chosen area while there is no pin yet; tap = scroll to the map.
  'co.pinNext': {
    en: 'Next: set your pin on the map ↓',
    fa: 'مرحله‌ی بعد: موقعیت‌تان را روی نقشه پین کنید ↓',
    ar: 'الخطوة التالية: ثبّتوا موقعكم على الخريطة ↓',
    tr: 'Sıradaki adım: haritada konumunuzu işaretleyin ↓'
  },
  'geo.noSupport': {
    en: 'Your browser does not support location.',
    fa: 'مرورگر شما از مکان‌یابی پشتیبانی نمی‌کند.',
    ar: 'متصفحك لا يدعم تحديد الموقع.',
    tr: 'Tarayıcınız konumu desteklemiyor.'
  },
  'geo.denied': {
    en: 'Permission denied — enable location in your browser and try again.',
    fa: 'اجازه داده نشد — مکان‌یابی مرورگر را روشن کنید و دوباره امتحان کنید.',
    ar: 'تم رفض الإذن — فعّل الموقع في المتصفح وحاول مجدداً.',
    tr: 'İzin verilmedi — tarayıcınızdan konumu açıp tekrar deneyin.'
  },
  'geo.unavailable': {
    en: "Couldn't get your position. Make sure device location is on.",
    fa: 'موقعیت پیدا نشد. مطمئن شوید مکان‌یابی دستگاه روشن است.',
    ar: 'تعذّر تحديد موقعك. تأكد أن خدمة الموقع مفعّلة.',
    tr: 'Konumunuz alınamadı. Cihazınızın konum özelliğinin açık olduğundan emin olun.'
  },
  'geo.timeout': {
    en: 'Took too long. Try again.',
    fa: 'خیلی طول کشید. دوباره امتحان کنید.',
    ar: 'استغرق وقتاً طويلاً. حاول مجدداً.',
    tr: 'Çok uzun sürdü. Tekrar deneyin.'
  },
  'geo.failed': {
    en: "Couldn't get location.",
    fa: 'موقعیت پیدا نشد.',
    ar: 'تعذّر تحديد الموقع.',
    tr: 'Konum alınamadı.'
  },
  'geo.slow': {
    en: 'Location is taking too long. Try again — or open shaunfood.com on your phone for faster GPS.',
    fa: 'مکان‌یابی طول کشید. دوباره امتحان کنید — یا سایت را روی گوشی باز کنید.',
    ar: 'تحديد الموقع يتأخر. حاول مجدداً — أو افتح shaunfood.com على هاتفك.',
    tr: 'Konum alınması uzun sürüyor. Tekrar deneyin — ya da daha hızlı GPS için shaunfood.com adresini telefonunuzda açın.'
  },
  // Checkout
  'co.title': {
    en: 'Checkout',
    fa: 'پرداخت سفارش',
    ar: 'إتمام الطلب',
    tr: 'Siparişi tamamla'
  },
  'co.subtotal': {
    en: 'Subtotal',
    fa: 'جمع',
    ar: 'المجموع الفرعي',
    tr: 'Ara toplam'
  },
  'co.discount': {
    en: 'Discount',
    fa: 'تخفیف',
    ar: 'الخصم',
    tr: 'İndirim'
  },
  'co.promo': {
    en: 'World Cup 20% off',
    fa: 'تخفیف ۲۰٪ جام جهانی',
    ar: 'خصم كأس العالم 20٪',
    tr: 'Dünya Kupası %20 indirim'
  },
  'co.vat': {
    en: 'VAT',
    fa: 'مالیات',
    ar: 'ضريبة القيمة المضافة',
    tr: 'KDV'
  },
  'co.delivery': {
    en: 'Delivery',
    fa: 'پیک',
    ar: 'التوصيل',
    tr: 'Teslimat ücreti'
  },
  'co.selectArea': {
    en: 'Select area',
    fa: 'انتخاب منطقه',
    ar: 'اختر المنطقة',
    tr: 'Bölge seçin'
  },
  'co.freeDelivery': {
    en: 'Free',
    fa: 'رایگان',
    ar: 'مجاني',
    tr: 'Ücretsiz'
  },
  'rail.freeNudge': {
    en: 'Add AED {n} more for free delivery',
    fa: '{n} درهم دیگر تا ارسال رایگان',
    ar: 'أضف {n} درهم فقط والتوصيل علينا',
    tr: 'Ücretsiz teslimat için {n} AED daha ekleyin'
  },
  'promo.delivery50': {
    en: '🎉 All delivery fees include a {p}% discount — limited time',
    fa: '🎉 {p}٪ تخفیف روی همه‌ی هزینه‌های ارسال — فقط برای مدت محدود',
    ar: '🎉 خصم {p}% على جميع رسوم التوصيل — عرض لفترة محدودة',
    tr: '🎉 Tüm teslimat ücretlerinde %{p} indirim — sınırlı süreyle'
  },
  'co.grand': {
    en: 'Grand Total',
    fa: 'جمع کل',
    ar: 'الإجمالي الكلي',
    tr: 'Genel toplam'
  },
  'co.discPh': {
    en: 'Discount code',
    fa: 'کد تخفیف',
    ar: 'رمز الخصم',
    tr: 'İndirim kodu'
  },
  'co.apply': {
    en: 'Apply',
    fa: 'اعمال',
    ar: 'تطبيق',
    tr: 'Uygula'
  },
  'co.applied': {
    en: 'applied',
    fa: 'اعمال شد',
    ar: 'تم التطبيق',
    tr: 'uygulandı'
  },
  'co.remove': {
    en: 'Remove',
    fa: 'حذف',
    ar: 'إزالة',
    tr: 'Kaldır'
  },
  'co.discEnter': {
    en: 'Enter a code',
    fa: 'یک کد وارد کنید',
    ar: 'أدخل الرمز',
    tr: 'Bir kod girin'
  },
  'co.discInvalid': {
    en: "This code isn't valid",
    fa: 'این کد معتبر نیست',
    ar: 'هذا الرمز غير صالح',
    tr: 'Bu kod geçerli değil'
  },
  'co.discFailed': {
    en: "Couldn't check that code — please try again",
    fa: 'بررسی کد ممکن نشد — دوباره امتحان کنید',
    ar: 'تعذّر التحقق من الرمز — حاول مجدداً',
    tr: 'Kod kontrol edilemedi — lütfen tekrar deneyin'
  },
  'co.mode': {
    en: 'Mode',
    fa: 'نوع سفارش',
    ar: 'نوع الطلب',
    tr: 'Sipariş türü'
  },
  'co.deliver': {
    en: 'Deliver',
    fa: 'پیک',
    ar: 'توصيل',
    tr: 'Adrese teslim'
  },
  'co.dinein': {
    en: 'Dine in',
    fa: 'سالن',
    ar: 'في المطعم',
    tr: 'Restoranda'
  },
  // WhatsApp-ordering page (/wa/<token>): "Dine in" becomes "Pickup".
  'co.pickup': {
    en: 'Pickup',
    fa: 'تحویل حضوری',
    ar: 'استلام',
    tr: 'Gel Al'
  },
  'co.optIn': {
    en: 'Send me offers and news on WhatsApp',
    fa: 'پیشنهادها و خبرها را در واتس‌اپ برایم بفرست',
    ar: 'أرسلوا لي العروض والأخبار على واتساب',
    tr: "Kampanya ve haberleri WhatsApp'tan almak istiyorum"
  },
  'co.optInNote': {
    en: 'Optional. Order updates are sent either way.',
    fa: 'اختیاری. اطلاع‌رسانی سفارش در هر صورت ارسال می‌شود.',
    ar: 'اختياري. تحديثات الطلب تصلك في كل الأحوال.',
    tr: 'İsteğe bağlı. Sipariş bildirimleri her durumda gönderilir.'
  },
  'co.pickupNote': {
    en: 'Collect from Shaun the Sheep · Jumeirah 1. No delivery fee — we will tell you the moment it is ready.',
    fa: 'از شان د شیپ · جمیرا ۱ تحویل بگیرید. بدون هزینه پیک — به‌محض آماده شدن خبرتان می‌کنیم.',
    ar: 'استلم طلبك من شون ذا شيب · جميرا 1. بدون رسوم توصيل — وسنخبرك فور تجهيز طلبك.',
    tr: "Shaun the Sheep · Jumeirah 1'den teslim alın. Teslimat ücreti yok — hazır olunca hemen haber vereceğiz."
  },
  'co.collect': {
    en: 'When will you collect?',
    fa: 'کی می‌آیید بگیرید؟',
    ar: 'متى ستستلم؟',
    tr: 'Ne zaman almaya geleceksiniz?'
  },
  'co.waBanner': {
    en: '📱 Ordering via WhatsApp — your payment link will also arrive in your chat',
    fa: 'سفارش از طریق واتس‌اپ — لینک پرداخت در چت شما هم ارسال می‌شود',
    ar: 'الطلب عبر واتساب — سيصلك رابط الدفع في محادثتك أيضاً',
    tr: '📱 WhatsApp ile sipariş — ödeme bağlantınız sohbetinize de gelecek'
  },
  'co.waVerified': {
    en: '✓ WhatsApp verified',
    fa: 'تأیید شده در واتس‌اپ ✓',
    ar: 'موثّق عبر واتساب ✓',
    tr: '✓ WhatsApp doğrulandı'
  },
  'co.savedAddr': {
    en: 'Saved addresses',
    fa: 'آدرس‌های ذخیره‌شده',
    ar: 'العناوين المحفوظة',
    tr: 'Kayıtlı adresler'
  },
  'co.when': {
    en: 'When',
    fa: 'زمان دریافت',
    ar: 'متى تريد طلبك؟',
    tr: 'Ne zaman'
  },
  'co.asap': {
    en: 'ASAP',
    fa: 'فوری',
    ar: 'في أقرب وقت',
    tr: 'En kısa sürede'
  },
  'co.schedule': {
    en: 'Schedule',
    fa: 'زمان‌بندی',
    ar: 'حدد موعداً',
    tr: 'Zamanla'
  },
  'co.schedNote1': {
    en: 'Dubai time — at least',
    fa: 'به وقت دبی — حداقل',
    ar: 'بتوقيت دبي — بعد',
    tr: 'Dubai saati — en erken'
  },
  'co.schedNote2': {
    en: 'min ahead, up to 2 days.',
    fa: 'دقیقه بعد، تا ۲ روز آینده.',
    ar: 'دقيقة على الأقل من الآن، وحتى يومين.',
    tr: 'dk sonrasına, en geç 2 gün sonrasına kadar.'
  },
  'co.name': {
    en: 'Name',
    fa: 'نام',
    ar: 'الاسم',
    tr: 'Ad'
  },
  'co.namePh': {
    en: 'Your name',
    fa: 'نام شما',
    ar: 'اسمك',
    tr: 'Adınız'
  },
  'co.phone': {
    en: 'Phone',
    fa: 'تلفن',
    ar: 'الهاتف',
    tr: 'Telefon'
  },
  'co.ccode': {
    en: 'Country code',
    fa: 'کد کشور',
    ar: 'رمز الدولة',
    tr: 'Ülke kodu'
  },
  'co.area': {
    en: 'Area',
    fa: 'منطقه',
    ar: 'المنطقة',
    tr: 'Bölge'
  },
  'co.deliveryTo': {
    en: 'Delivery to',
    fa: 'ارسال به',
    ar: 'التوصيل إلى',
    tr: 'Teslimat bölgesi'
  },
  'co.areaHint': {
    en: 'Choose your area to see the delivery fee',
    fa: 'برای دیدن هزینه‌ی پیک، منطقه‌ی خود را انتخاب کنید',
    ar: 'اختر منطقتك لعرض رسوم التوصيل',
    tr: 'Teslimat ücretini görmek için bölgenizi seçin'
  },
  'co.address': {
    en: 'Address',
    fa: 'آدرس',
    ar: 'العنوان',
    tr: 'Adres'
  },
  'co.addressPh': {
    en: 'Apartment, street, neighbourhood',
    fa: 'آپارتمان، خیابان، محله',
    ar: 'الفيلا أو الشقة، المبنى، الشارع',
    tr: 'Daire, sokak, community'
  },
  'co.pin': {
    en: 'Pin on map',
    fa: 'پین روی نقشه',
    ar: 'حدد على الخريطة',
    tr: 'Haritada işaretle'
  },
  'co.notes': {
    en: 'Notes',
    fa: 'یادداشت',
    ar: 'ملاحظات',
    tr: 'Sipariş notu'
  },
  'co.notesPh': {
    en: 'extra brain, no tongue, …',
    fa: 'مثلاً مغز اضافه، بدون زبان…',
    ar: 'مثلاً مخ إضافي، بدون لسان…',
    tr: 'örn. fazladan beyin, dil olmasın…'
  },
  'co.minWarn1': {
    en: 'Add AED',
    fa: 'برای ارسال پیک،',
    ar: 'أضف',
    tr: 'Teslimat için'
  },
  'co.minWarn2': {
    en: 'more for delivery (min AED',
    fa: 'درهم دیگر اضافه کنید (حداقل',
    ar: 'درهم لطلب التوصيل (الحد الأدنى',
    tr: 'AED daha ekleyin (minimum'
  },
  'co.minWarn3': {
    en: ')',
    fa: ' درهم)',
    ar: ' درهم)',
    tr: ' AED)'
  },
  'co.payConnect': {
    en: 'Connecting to secure payment…',
    fa: 'در حال اتصال به درگاه پرداخت…',
    ar: 'جارٍ الاتصال ببوابة الدفع الآمن…',
    tr: 'Güvenli ödeme sayfasına bağlanılıyor…'
  },
  'co.dontClose': {
    en: "Don't close this page",
    fa: 'این صفحه را نبندید',
    ar: 'لا تغلق هذه الصفحة',
    tr: 'Bu sayfayı kapatmayın'
  },
  'co.tooLong': {
    en: 'Taking too long? Cancel',
    fa: 'طول کشید؟ لغو',
    ar: 'تأخر كثيراً؟ إلغاء',
    tr: 'Çok mu uzun sürüyor? Vazgeç'
  },
  'co.errName': {
    en: 'Please tell us your name',
    fa: 'لطفاً نام‌تان را بنویسید',
    ar: 'من فضلك اكتب اسمك',
    tr: 'Lütfen adınızı yazın'
  },
  'co.errPhone': {
    en: 'A reachable phone number, please',
    fa: 'یک شماره تلفن در دسترس وارد کنید',
    ar: 'من فضلك أدخل رقم هاتف نتواصل معك عليه',
    tr: 'Ulaşabileceğimiz bir telefon numarası girin'
  },
  'co.errUaePhone': {
    en: 'Please enter a valid UAE mobile number (05X XXX XXXX)',
    fa: 'لطفاً یک شمارهٔ موبایل معتبر امارات وارد کنید (05X XXX XXXX)',
    ar: 'الرجاء إدخال رقم موبايل إماراتي صحيح (05X XXX XXXX)',
    tr: 'Lütfen geçerli bir BAE cep numarası girin (05X XXX XXXX)'
  },
  'co.errAddress': {
    en: 'We need a delivery address',
    fa: 'آدرس تحویل لازم است',
    ar: 'من فضلك أدخل عنوان التوصيل',
    tr: 'Teslimat adresi gerekli'
  },
  'co.errArea': {
    en: 'Please choose your delivery area',
    fa: 'لطفاً منطقه‌ی ارسال را انتخاب کنید',
    ar: 'من فضلك اختر منطقة التوصيل',
    tr: 'Lütfen teslimat bölgenizi seçin'
  },
  'co.errPin': {
    en: 'Please pin your location on the map — required for delivery',
    fa: 'لطفاً موقعیت‌تان را روی نقشه پین کنید',
    ar: 'من فضلك ثبّت موقعك على الخريطة — مطلوب للتوصيل',
    tr: 'Lütfen konumunuzu haritada işaretleyin — teslimat için gerekli'
  },
  'co.riderCall': {
    en: 'UAE mobile — the rider will call this number when your order is on the way',
    fa: 'شماره موبایل امارات — هنگام ارسال، پیک با همین شماره تماس می‌گیرد',
    ar: 'رقم موبايل إماراتي — سيتصل بك مندوب التوصيل عليه عند خروج طلبك',
    tr: 'BAE cep numarası — siparişiniz yola çıktığında kurye bu numarayı arayacak'
  },
  // Phase 2 (K1-K4) — structured delivery details, drop-off, free extras,
  // device-saved addresses, phone read-back.
  'co.addrType': {
    en: 'Delivering to',
    fa: 'نوع محل',
    ar: 'نوع العنوان',
    tr: 'Adres türü'
  },
  'co.aptT': {
    en: 'Apartment',
    fa: 'آپارتمان',
    ar: 'شقة',
    tr: 'Daire'
  },
  'co.villaT': {
    en: 'Villa',
    fa: 'ویلا',
    ar: 'فيلا',
    tr: 'Villa'
  },
  'co.officeT': {
    en: 'Office',
    fa: 'دفتر کار',
    ar: 'مكتب',
    tr: 'Ofis'
  },
  'co.building': {
    en: 'Building name',
    fa: 'نام ساختمان',
    ar: 'اسم المبنى',
    tr: 'Bina adı'
  },
  'co.floor': {
    en: 'Floor',
    fa: 'طبقه',
    ar: 'الطابق',
    tr: 'Kat'
  },
  'co.flat': {
    en: 'Flat no.',
    fa: 'شماره‌ی واحد',
    ar: 'رقم الشقة',
    tr: 'Daire no.'
  },
  'co.villaNo': {
    en: 'Villa no.',
    fa: 'شماره‌ی ویلا',
    ar: 'رقم الفيلا',
    tr: 'Villa no.'
  },
  'co.street': {
    en: 'Street / community',
    fa: 'خیابان / شهرک',
    ar: 'الشارع / المجمّع السكني',
    tr: 'Sokak / community'
  },
  'co.company': {
    en: 'Company or building',
    fa: 'نام شرکت یا ساختمان',
    ar: 'اسم الشركة أو المبنى',
    tr: 'Şirket veya bina adı'
  },
  'co.landmark': {
    en: 'Landmark / directions',
    fa: 'نشانی کمکی / راهنمایی',
    ar: 'معلَم قريب / إرشادات',
    tr: 'Adres tarifi'
  },
  'co.landmarkPh': {
    en: 'e.g. opposite Spinneys, blue gate',
    fa: 'مثلاً روبه‌روی اسپینیز، درِ آبی',
    ar: 'مثلاً مقابل سبينيس، البوابة الزرقاء',
    tr: "örn. Spinneys'in karşısı, mavi kapı"
  },
  'co.makani': {
    en: 'Makani number',
    fa: 'شماره‌ی مکانی',
    ar: 'رقم مكاني',
    tr: 'Makani numarası'
  },
  'co.makaniPh': {
    en: '10 digits, from the Makani plate',
    fa: '۱۰ رقم، روی پلاک مکانی',
    ar: '10 أرقام من لوحة مكاني',
    tr: '10 haneli; Makani tabelasında bulabilirsiniz'
  },
  'co.optional': {
    en: 'optional',
    fa: 'اختیاری',
    ar: 'اختياري',
    tr: 'isteğe bağlı'
  },
  'co.errBuilding': {
    en: 'Please enter the building name',
    fa: 'لطفاً نام ساختمان را بنویسید',
    ar: 'من فضلكم أدخلوا اسم المبنى',
    tr: 'Lütfen bina adını yazın'
  },
  'co.errFlat': {
    en: 'Please enter the flat number',
    fa: 'لطفاً شماره‌ی واحد را بنویسید',
    ar: 'من فضلكم أدخلوا رقم الشقة',
    tr: 'Lütfen daire numarasını yazın'
  },
  'co.errVilla': {
    en: 'Please enter the villa number',
    fa: 'لطفاً شماره‌ی ویلا را بنویسید',
    ar: 'من فضلكم أدخلوا رقم الفيلا',
    tr: 'Lütfen villa numarasını yazın'
  },
  'co.errCompany': {
    en: 'Please enter the company or building name',
    fa: 'لطفاً نام شرکت یا ساختمان را بنویسید',
    ar: 'من فضلكم أدخلوا اسم الشركة أو المبنى',
    tr: 'Lütfen şirket veya bina adını yazın'
  },
  'co.errMakani': {
    en: 'A Makani number has exactly 10 digits',
    fa: 'شماره‌ی مکانی دقیقاً ۱۰ رقم دارد',
    ar: 'رقم مكاني يتكوّن من 10 أرقام بالضبط',
    tr: 'Makani numarası tam 10 haneden oluşur'
  },
  'co.dropoff': {
    en: 'Drop-off',
    fa: 'نحوه‌ی تحویل',
    ar: 'طريقة التسليم',
    tr: 'Teslim şekli'
  },
  'co.dropHand': {
    en: 'Hand it to me',
    fa: 'به دست خودم بدهید',
    ar: 'التسليم باليد',
    tr: 'Elden teslim'
  },
  'co.dropCall': {
    en: 'Call me when downstairs',
    fa: 'پایین که رسیدید تماس بگیرید',
    ar: 'اتصلوا بي عند الوصول أسفل المبنى',
    tr: 'Aşağıya gelince beni arayın'
  },
  'co.dropDoor': {
    en: 'Leave at the door',
    fa: 'پشت در بگذارید',
    ar: 'اتركوه عند الباب',
    tr: 'Kapıya bırakın'
  },
  'co.dropNoBell': {
    en: "Don't ring the bell",
    fa: 'زنگ نزنید',
    ar: 'بدون رنّ الجرس',
    tr: 'Zili çalmayın'
  },
  'co.dropEarly': {
    en: 'Set for the early hours — change it if you like',
    fa: 'برای ساعات اول صبح انتخاب شد — در صورت تمایل تغییرش دهید',
    ar: 'اخترناه لساعات الصباح الباكر — يمكنكم تغييره',
    tr: 'Sabahın erken saatleri için seçtik — isterseniz değiştirin'
  },
  'co.extras': {
    en: 'Free extras',
    fa: 'افزودنی رایگان',
    ar: 'إضافات مجانية',
    tr: 'Ücretsiz ekstralar'
  },
  'co.lemon': {
    en: 'Lemon',
    fa: 'لیمو',
    ar: 'ليمون',
    tr: 'Limon'
  },
  'co.spoons': {
    en: 'Spoons & napkins',
    fa: 'قاشق و دستمال',
    ar: 'ملاعق ومناديل',
    tr: 'Kaşık ve peçete'
  },
  'co.myAddr': {
    en: 'Your addresses',
    fa: 'آدرس‌های شما',
    ar: 'عناوينكم',
    tr: 'Adresleriniz'
  },
  'co.newAddr': {
    en: 'New address',
    fa: 'آدرس جدید',
    ar: 'عنوان جديد',
    tr: 'Yeni adres'
  },
  'co.saveAs': {
    en: 'Save this address as',
    fa: 'ذخیره‌ی این آدرس با نام',
    ar: 'احفظوا هذا العنوان باسم',
    tr: 'Bu adresi şu adla kaydet'
  },
  'co.saveNote': {
    en: 'Saved on this device only',
    fa: 'فقط روی همین دستگاه ذخیره می‌شود',
    ar: 'يُحفظ على هذا الجهاز فقط',
    tr: 'Yalnızca bu cihazda saklanır'
  },
  'co.lblHome': {
    en: 'Home',
    fa: 'خانه',
    ar: 'المنزل',
    tr: 'Ev'
  },
  'co.lblWork': {
    en: 'Work',
    fa: 'محل کار',
    ar: 'العمل',
    tr: 'İş'
  },
  'co.lblFamily': {
    en: 'Family',
    fa: 'خانواده',
    ar: 'العائلة',
    tr: 'Aile'
  },
  'co.lblOther': {
    en: 'Other',
    fa: 'دیگر',
    ar: 'أخرى',
    tr: 'Diğer'
  },
  'co.forget': {
    en: 'Forget this address',
    fa: 'حذف این آدرس',
    ar: 'حذف هذا العنوان',
    tr: 'Bu adresi sil'
  },
  'co.pinCheck': {
    en: 'Is the pin still on your door?',
    fa: 'پین هنوز روی درِ شماست؟',
    ar: 'هل ما زال الدبوس على بابكم؟',
    tr: 'İşaret hâlâ kapınızın önünde mi?'
  },
  'co.pinConfirm': {
    en: 'This is the right spot',
    fa: 'بله، همین‌جاست',
    ar: 'نعم، هذا هو المكان الصحيح',
    tr: 'Doğru yer burası'
  },
  'co.errPinConfirm': {
    en: 'Please confirm the pin on the map',
    fa: 'لطفاً پین روی نقشه را تأیید کنید',
    ar: 'من فضلكم أكّدوا موقع الدبوس على الخريطة',
    tr: 'Lütfen haritadaki işareti onaylayın'
  },
  'co.callBack': {
    en: "We'll call you on {p} — correct?",
    fa: 'با شماره‌ی {p} با شما تماس می‌گیریم — درست است؟',
    ar: 'سنتصل بكم على {p} — هل الرقم صحيح؟',
    tr: 'Sizi şu numaradan arayacağız: {p} — doğru mu?'
  },
  'co.edit': {
    en: 'Edit',
    fa: 'ویرایش',
    ar: 'تعديل',
    tr: 'Düzenle'
  },
  // Phase 2 (K5-K7): delivery time bands, Now | Schedule, day tabs + slot
  // chips, "Eating for" platter helper. {r} = a range built by bandRange().
  'band.usually': {
    en: 'usually {r}',
    fa: 'معمولاً {r}',
    ar: 'عادةً {r}',
    tr: 'genelde {r}'
  },
  'band.approx': {
    en: '~{r}',
    fa: 'حدود {r}',
    ar: '{r}',
    tr: 'yaklaşık {r}'
  },
  'band.to': {
    en: 'Delivery to {a} · usually {r}',
    fa: 'ارسال به {a} · معمولاً {r}',
    ar: 'التوصيل إلى {a} · عادةً {r}',
    tr: 'Teslimat bölgesi: {a} · genelde {r}'
  },
  'hero.minsTo': {
    en: '~{r} to {a}',
    fa: '{a}: حدود {r}',
    ar: '{a}: {r}',
    tr: '{a}: yaklaşık {r}'
  },
  'mode.chooseArea': {
    en: 'Choose your area',
    fa: 'منطقه را انتخاب کنید',
    ar: 'اختاروا منطقتكم',
    tr: 'Bölgenizi seçin'
  },
  'mode.pickupSub': {
    en: 'Free · Jumeirah 1',
    fa: 'رایگان · جمیرا ۱',
    ar: 'مجاناً · جميرا 1',
    tr: 'Ücretsiz · Jumeirah 1'
  },
  'mode.dineinSub': {
    en: 'Table on arrival',
    fa: 'میز، هر وقت رسیدید',
    ar: 'طاولة عند وصولكم',
    tr: 'Geldiğinizde masa'
  },
  'cart.now': {
    en: 'Now',
    fa: 'همین حالا',
    ar: 'الآن',
    tr: 'Hemen'
  },
  'cart.whenLbl': {
    en: 'When',
    fa: 'زمان',
    ar: 'الموعد',
    tr: 'Ne zaman'
  },
  'sched.day': {
    en: 'Choose a day',
    fa: 'انتخاب روز',
    ar: 'اختيار اليوم',
    tr: 'Gün seçin'
  },
  'sched.weekend': {
    en: 'Weekend breakfast',
    fa: 'صبحانهٔ آخر هفته',
    ar: 'فطور نهاية الأسبوع',
    tr: 'Hafta sonu kahvaltısı'
  },
  'sched.breakfast': {
    en: 'Breakfast',
    fa: 'صبحانه',
    ar: 'الفطور',
    tr: 'Kahvaltı'
  },
  'sched.later': {
    en: 'Later',
    fa: 'ساعت‌های دیگر',
    ar: 'أوقات أخرى',
    tr: 'Daha sonra'
  },
  'sched.list': {
    en: 'Choose from a list instead',
    fa: 'انتخاب از فهرست',
    ar: 'الاختيار من قائمة',
    tr: 'Bunun yerine listeden seçin'
  },
  'sched.suhoor': {
    en: 'Suhoor',
    fa: 'سحری',
    ar: 'السحور',
    tr: 'Sahur'
  },
  'eat.for': {
    en: 'Eating for:',
    fa: 'چند نفر هستید؟',
    ar: 'عدد الأشخاص:',
    tr: 'Kaç kişi için?'
  },
  'eat.fit': {
    en: 'Best fit: {x}',
    fa: 'مناسب شما: {x}',
    ar: 'الأنسب لكم: {x}',
    tr: 'Size en uygun: {x}'
  },
  'eat.combo': {
    en: 'For {n}: {list}',
    fa: 'برای {n}: {list}',
    ar: 'لعدد {n}: {list}',
    tr: '{n} için: {list}'
  },
  'eat.addAll': {
    en: 'Add all',
    fa: 'افزودن همه',
    ar: 'إضافة الكل',
    tr: 'Hepsini ekle'
  },
  'eat.more': {
    en: 'One more person',
    fa: 'یک نفر بیشتر',
    ar: 'شخص إضافي',
    tr: 'Bir kişi daha'
  },
  'eat.fewer': {
    en: 'One person fewer',
    fa: 'یک نفر کمتر',
    ar: 'شخص أقل',
    tr: 'Bir kişi daha az'
  },
  'eat.new': {
    en: 'New to kalle pache?',
    fa: 'اولین بار کله‌پاچه می‌خورید؟',
    ar: 'أول تجربة لكم مع الباجة؟',
    tr: 'Kelle paçaya yeni misiniz?'
  },
  'eat.gather': {
    en: 'Planning for 10+ guests? See gatherings →',
    fa: 'مهمانی ۱۰ نفر به بالا دارید؟ سفارش مهمانی ←',
    ar: 'تستقبلون 10 ضيوف أو أكثر؟ اطّلعوا على طلبات المناسبات ←',
    tr: '10 veya daha fazla misafiriniz mi var? Grup siparişlerine göz atın →'
  },
  'eat.newNote': {
    en: 'A little of everything — the best first order',
    fa: 'از همه‌چیز کمی — بهترین سفارش برای بار اول',
    ar: 'قليل من كل شيء — أفضل طلب لأول مرة',
    tr: 'Her şeyden biraz — ilk sipariş için en iyisi'
  },
  'co.introDiscount': {
    en: 'Welcome reward ({n}% off)',
    fa: 'هدیهٔ خوش‌آمد ({n}٪ تخفیف)',
    ar: 'هدية الترحيب (خصم {n}%)',
    tr: 'Hoş geldiniz hediyesi (%{n} indirim)'
  },
  'loy.welcomeTitle': {
    en: 'First time at our table?',
    fa: 'اولین بار است که مهمان ما هستید؟',
    ar: 'أهلاً وسهلاً! أول مرة على سفرتنا؟',
    tr: 'Soframıza ilk kez mi geldiniz?'
  },
  'loy.offWord': {
    en: 'off',
    fa: 'تخفیف',
    ar: 'خصم',
    tr: 'indirim'
  },
  'loy.firstOrderCap': {
    en: 'on your first order',
    fa: 'روی اولین سفارش شما',
    ar: 'على طلبك الأول',
    tr: 'ilk siparişinize özel'
  },
  'loy.growsTo': {
    en: 'Order again and it grows — {b}% on your 2nd, {c}% on your 3rd.',
    fa: 'سفارش بعدی، تخفیف بیشتر — {b}٪ برای سفارش دوم و {c}٪ برای سفارش سوم.',
    ar: 'اطلب مجدداً ويكبر خصمك — {b}% على طلبك الثاني و{c}% على الثالث.',
    tr: 'Tekrar sipariş verin, indiriminiz büyüsün — 2. siparişte %{b}, 3. siparişte %{c}.'
  },
  'loy.welcomeCta': {
    en: 'Claim my {a}%',
    fa: 'دریافت تخفیف {a}٪',
    ar: 'احصل على خصم {a}%',
    tr: '%{a} indirimimi al'
  },
  'co.errTime': {
    en: 'Please pick a time',
    fa: 'لطفاً زمان را انتخاب کنید',
    ar: 'من فضلك اختر الوقت',
    tr: 'Lütfen bir saat seçin'
  },
  'co.errTimeBad': {
    en: 'That time looks invalid',
    fa: 'زمان نامعتبر است',
    ar: 'الوقت غير صالح',
    tr: 'Bu saat geçersiz görünüyor'
  },
  'co.errTimeMin': {
    en: 'Pick a time at least',
    fa: 'زمانی حداقل',
    ar: 'اختر وقتاً بعد',
    tr: 'Şimdiden en az'
  },
  'co.errTimeMin2': {
    en: 'min ahead',
    fa: 'دقیقه بعد انتخاب کنید',
    ar: 'دقيقة على الأقل',
    tr: 'dk sonrasına bir saat seçin'
  },
  'co.errTimeMax': {
    en: 'You can schedule up to 2 days ahead',
    fa: 'حداکثر تا ۲ روز آینده',
    ar: 'يمكن تحديد موعد الطلب حتى يومين مقدماً',
    tr: 'En fazla 2 gün sonrasına zamanlayabilirsiniz'
  },
  // KHAN (owner 2026-10-04): a cart holding the Khan's Platter books up to 7 days ahead.
  'co.errTimeMax7': {
    en: 'You can schedule up to 7 days ahead',
    fa: 'حداکثر تا ۷ روز آینده',
    ar: 'يمكن تحديد موعد الطلب حتى 7 أيام مقدماً',
    tr: 'En fazla 7 gün sonrasına zamanlayabilirsiniz'
  },
  'co.khanNote': {
    en: "Khan's Platter: you can book up to 7 days ahead",
    fa: 'دیس خان: تا ۷ روز جلوتر می‌توانید رزرو کنید',
    ar: 'صينية الخان: يمكنكم الحجز حتى 7 أيام مقدماً',
    tr: 'Han Tabağı: 7 güne kadar önceden sipariş verebilirsiniz'
  },
  // Phase 3 (L2) — gathering orders (came through /gatherings, cart feeds 10+).
  'co.gatherNote': {
    en: 'Gathering order: order by 8 pm the day before · up to 7 days ahead',
    fa: 'سفارش مهمانی: تا ساعت ۸ شبِ روز قبل سفارش دهید · حداکثر تا ۷ روز آینده',
    ar: 'طلب مناسبة: اطلبوا قبل الساعة 8 مساءً من اليوم السابق · حتى 7 أيام مقدماً',
    tr: "Grup siparişi: en geç bir önceki gün saat 20:00'ye kadar sipariş verin · 7 güne kadar önceden"
  },
  'co.errGatherMin': {
    en: 'Gathering orders need platters for 10 or more people',
    fa: 'سفارش مهمانی باید برای ۱۰ نفر یا بیشتر باشد',
    ar: 'طلبات المناسبات تحتاج إلى أطباق تكفي 10 أشخاص أو أكثر',
    tr: 'Grup siparişleri için en az 10 kişilik tabak gerekir'
  },
  'co.errGatherCutoff': {
    en: 'Gathering orders must be placed by 8 pm the day before — please pick a later day above.',
    fa: 'سفارش مهمانی باید تا ساعت ۸ شبِ روز قبل ثبت شود — لطفاً روز دیرتری را انتخاب کنید.',
    ar: 'يجب تقديم طلب المناسبة قبل الساعة 8 مساءً من اليوم السابق — يُرجى اختيار يوم لاحق من الأعلى.',
    tr: "Grup siparişlerini en geç bir önceki gün saat 20:00'ye kadar vermeniz gerekir — lütfen yukarıdan daha sonraki bir gün seçin."
  },
  'co.errGatherMax': {
    en: 'Gathering orders can be scheduled up to 7 days ahead',
    fa: 'سفارش مهمانی را حداکثر تا ۷ روز آینده می‌توانید زمان‌بندی کنید',
    ar: 'يمكن تحديد موعد طلب المناسبة حتى 7 أيام مقدماً',
    tr: 'Grup siparişleri en fazla 7 gün sonrasına zamanlanabilir'
  },
  'co.errOrder': {
    en: 'Could not place order — try again',
    fa: 'ثبت سفارش ممکن نشد — دوباره امتحان کنید',
    ar: 'تعذّر إرسال الطلب — حاول مجدداً',
    tr: 'Sipariş verilemedi — lütfen tekrar deneyin'
  },
  'co.errNetwork': {
    en: 'Network problem — try again',
    fa: 'مشکل شبکه — دوباره امتحان کنید',
    ar: 'مشكلة في الشبكة — حاول مجدداً',
    tr: 'Bağlantı sorunu — lütfen tekrar deneyin'
  },
  // Server-side rejections, translated for Arabic checkout (mapped from create.ts messages).
  'co.errNoZone': {
    en: 'We do not deliver to your area',
    fa: 'متأسفانه پیک به این محدوده ارسال نمی‌کنیم',
    ar: 'عذراً، التوصيل لا يشمل منطقتك حالياً',
    tr: 'Maalesef bölgenize teslimat yapmıyoruz'
  },
  'co.errMinSrv': {
    en: 'Your order is below the delivery minimum — please add another item',
    fa: 'سفارش شما کمتر از حداقل ارسال است — لطفاً یک قلم دیگر اضافه کنید',
    ar: 'طلبك أقل من الحد الأدنى للتوصيل — أضف صنفاً آخر من فضلك',
    tr: 'Sepetiniz teslimat için minimum tutarın altında — lütfen bir ürün daha ekleyin'
  },
  'co.errDineinOnly': {
    en: 'Some items are dine-in only and cannot be delivered',
    fa: 'برخی اقلام فقط در سالن سرو می‌شوند',
    ar: 'بعض الأصناف للتناول داخل المطعم فقط ولا يمكن توصيلها',
    tr: 'Bazı ürünler yalnızca restoranda servis edilir, teslim edilemez'
  },
  'co.errPrevPay': {
    en: 'We could not confirm your previous payment yet. Please wait a moment and check your order before paying again.',
    fa: 'هنوز نتوانستیم پرداخت قبلی شما را تأیید کنیم. لطفاً کمی صبر کنید و پیش از پرداخت دوباره، سفارش خود را بررسی کنید.',
    ar: 'لم نتمكن بعد من تأكيد دفعتك السابقة — انتظر قليلاً وراجع طلبك قبل الدفع مرة أخرى.',
    tr: 'Önceki ödemenizi henüz doğrulayamadık. Lütfen biraz bekleyin ve tekrar ödeme yapmadan önce siparişinizi kontrol edin.'
  },
  'co.stamp': {
    en: 'Stamp & Send',
    fa: 'ثبت و پرداخت',
    ar: 'أكّد طلبك وادفع',
    tr: 'Onayla ve öde'
  },
  'co.errCount': {
    en: '{n} details missing — marked in red above',
    fa: '{n} مورد ناقص است — بالاتر با رنگ قرمز مشخص شده',
    ar: 'من فضلك أكمل الحقول المحددة بالأحمر أعلاه ({n})',
    tr: '{n} bilgi eksik — yukarıda kırmızıyla işaretlendi'
  },
  'co.btnShort': {
    en: 'Add {n} AED to reach the minimum',
    fa: 'برای حداقل سفارش {n} درهم اضافه کنید',
    ar: 'أضف {n} درهم لإكمال الحد الأدنى',
    tr: 'Minimum tutar için {n} AED daha ekleyin'
  },
  'co.haveCode': {
    en: 'Have a discount code?',
    fa: 'کد تخفیف دارید؟',
    ar: 'لديك رمز خصم؟',
    tr: 'İndirim kodunuz var mı?'
  },
  'co.payRow': {
    en: 'Payment: Apple Pay · Google Pay · card — Visa / Mastercard, secured by Network International',
    fa: 'پرداخت: Apple Pay، Google Pay یا کارت بانکی — ویزا/مسترکارت، درگاه امن Network International',
    ar: 'الدفع: Apple Pay أو Google Pay أو بالبطاقة — فيزا/ماستركارد عبر بوابة Network International الآمنة',
    tr: 'Ödeme: Apple Pay · Google Pay · kart (Visa / Mastercard) — Network International güvencesiyle'
  },
  'inapp.bar': {
    en: 'Ordering & Apple Pay / Google Pay work best in your browser',
    fa: 'سفارش و Apple Pay / Google Pay در مرورگر خودتان بهتر کار می‌کند',
    ar: 'الطلب وApple Pay / Google Pay يعملان بشكل أفضل في متصفحك',
    tr: 'Sipariş ve Apple Pay / Google Pay tarayıcınızda daha iyi çalışır'
  },
  'inapp.open': {
    en: 'Open in browser',
    fa: 'باز کردن در مرورگر',
    ar: 'افتح في المتصفح',
    tr: 'Tarayıcıda aç'
  },
  'inapp.tip': {
    en: 'Tip: for Apple Pay / Google Pay, open this page in Safari or Chrome',
    fa: 'نکته: برای Apple Pay / Google Pay این صفحه را در Safari یا Chrome باز کنید',
    ar: 'نصيحة: لاستخدام Apple Pay / Google Pay افتح هذه الصفحة في Safari أو Chrome',
    tr: "İpucu: Apple Pay / Google Pay için bu sayfayı Safari veya Chrome'da açın"
  },
  'inapp.hint': {
    en: 'Tap ··· at the top, then "Open in browser"',
    fa: 'روی ··· بالای صفحه بزنید، سپس «باز کردن در مرورگر»',
    ar: 'اضغط ··· في الأعلى ثم «فتح في المتصفح»',
    tr: 'Üstteki ··· simgesine dokunun, ardından “Tarayıcıda aç” seçeneğini seçin'
  },
  'hero.google': {
    en: 'Google',
    fa: 'گوگل',
    ar: 'جوجل',
    tr: 'Google'
  },
  'hero.reviews': {
    en: '{n} reviews',
    fa: '{n} نظر',
    ar: '{n} تقييم',
    tr: '{n} yorum'
  },
  'quick.reorderT': {
    en: 'Order your last order again',
    fa: 'سفارش قبلی‌تان را تکرار کنید',
    ar: 'كرّر طلبك السابق',
    tr: 'Son siparişinizi tekrarlayın'
  },
  'quick.reorderS': {
    en: '{items} · one tap',
    fa: '{items} · با یک لمس',
    ar: '{items} · بلمسة واحدة',
    tr: '{items} · tek dokunuş'
  },
  'quick.reorderB': {
    en: 'Order again',
    fa: 'سفارش دوباره',
    ar: 'كرّر الطلب',
    tr: 'Tekrar sipariş ver'
  },
  'quick.preT': {
    en: 'Order for tomorrow morning',
    fa: 'برای صبح فردا سفارش دهید',
    ar: 'اطلب فطور الباجة لصباح الغد',
    tr: 'Yarın sabah için sipariş verin'
  },
  'quick.preS': {
    en: 'Kalle pache is a morning meal — pick a time and we cook it to arrive right then. Friday family sofreh? Reserve the platter now.',
    fa: 'کله‌پاچه غذای صبح است — زمان را انتخاب کنید تا همان موقع برسد. سفره‌ی جمعه؟ دیس را از حالا رزرو کنید.',
    ar: 'الباجة وجبة الصباح — اختر الوقت ونطبخها لتصلك في موعدها. فطور الجمعة مع الأهل؟ احجز الصينية من الآن.',
    tr: 'Kelle paça bir sabah yemeğidir — saati seçin, tam o saatte ulaşacak şekilde pişiririz. Cuma günü aile sofrası mı? Tabağı şimdiden sipariş edin.'
  },
  'quick.preB': {
    en: 'Schedule an order',
    fa: 'زمان‌بندی سفارش',
    ar: 'حدّد موعد الطلب',
    tr: 'Siparişi zamanla'
  },
  'quick.breadT': {
    en: 'Fresh sangak, every week',
    fa: 'سنگک تازه، هر هفته',
    ar: 'خبز سنكك طازج كل أسبوع',
    tr: 'Her hafta taze sangak'
  },
  'quick.breadS': {
    en: 'Sangak Club: choose your days and breads, one payment every 4 weeks, skip any day until 8 PM the night before.',
    fa: 'باشگاه سنگک: روزها و نان‌ها را خودتان انتخاب کنید، هر ۴ هفته یک پرداخت، و تا ساعت ۸ شبِ قبل هر روز را می‌توانید رد کنید.',
    ar: 'نادي السنكك: اختر أيامك وخبزك، دفعة واحدة كل 4 أسابيع، ويمكنك تخطي أي يوم حتى 8 مساءً في الليلة السابقة.',
    tr: "Sangak Kulübü: gün ve ekmeklerinizi siz seçin, 4 haftada bir tek ödeme; her günü bir önceki akşam 20:00'ye kadar atlayabilirsiniz."
  },
  'quick.breadB': {
    en: 'See the plan',
    fa: 'دیدن اشتراک',
    ar: 'شاهد الاشتراك',
    tr: 'Planı gör'
  },
  // Phase 3 (L5) — one slim occasion card in the QuickCards area (never a pop-up).
  // {from}/{to}/{range} are filled with LTR-isolated numbers.
  'occ.ramadanT': {
    en: 'Suhoor delivered {from}–{to} — pre-order tonight',
    fa: 'سحری را از {from} تا {to} درِ خانه تحویل می‌دهیم — برای امشب پیش‌سفارش دهید',
    ar: 'نوصل السحور من {from} إلى {to} — اطلبوا مسبقاً الليلة',
    tr: 'Sahur {from}–{to} arası kapınızda — bu gece ön sipariş verin'
  },
  'occ.ramadanB': {
    en: 'Pre-order suhoor',
    fa: 'پیش‌سفارش سحری',
    ar: 'اطلبوا السحور مسبقاً',
    tr: 'Sahur için ön sipariş ver'
  },
  'occ.thuT': {
    en: 'Friday breakfast at home? Pre-order tonight',
    fa: 'صبحانهٔ جمعه در خانه؟ همین امشب پیش‌سفارش دهید',
    ar: 'فطور الجمعة في البيت؟ اطلبوا مسبقاً الليلة',
    tr: 'Cuma kahvaltısı evde mi? Bu gece ön sipariş verin'
  },
  'occ.thuB': {
    en: 'Schedule for Friday',
    fa: 'زمان‌بندی برای جمعه',
    ar: 'حدّدوا موعداً للجمعة',
    tr: 'Cuma için zamanla'
  },
  'occ.nowruzT': {
    en: 'Hosting for Nowruz? Order for {range} guests',
    fa: 'مهمانی نوروز دارید؟ سفارش برای {range} نفر',
    ar: 'تستقبلون ضيوفاً في النوروز؟ اطلبوا لعدد {range} ضيفاً',
    tr: "Nevruz'da misafiriniz mi var? {range} kişilik sipariş verin"
  },
  'occ.eidT': {
    en: 'Hosting for Eid? Order for {range} guests',
    fa: 'مهمانی عید دارید؟ سفارش برای {range} نفر',
    ar: 'تستقبلون ضيوفاً في العيد؟ اطلبوا لعدد {range} ضيفاً',
    tr: 'Bayramda misafiriniz mi var? {range} kişilik sipariş verin'
  },
  'occ.gatherB': {
    en: 'See gatherings →',
    fa: 'سفارش مهمانی ←',
    ar: 'طلبات المناسبات ←',
    tr: 'Grup siparişlerine göz at →'
  },
  'toast.schedArmed': {
    en: 'Pick your dishes — you choose the time at checkout',
    fa: 'غذاهایتان را انتخاب کنید — زمان را هنگام پرداخت تعیین می‌کنید',
    ar: 'اختر أطباقك — وتحدد الوقت عند الدفع',
    tr: 'Yemeklerinizi seçin — saati ödeme sırasında belirlersiniz'
  },
  'menu.feeds': {
    en: 'Feeds {n}',
    fa: 'برای {n} نفر',
    ar: 'يكفي {n} أشخاص',
    tr: '{n} kişilik'
  },
  'menu.each': {
    en: '~{p} AED each',
    fa: 'نفری حدود {p} درهم',
    ar: 'حوالي {p} درهم للشخص',
    tr: 'kişi başı ~{p} AED'
  },
  // Item sheet of a multi-person platter: tapping 'menu.each' reveals this one line.
  // {name} = the Single Dish's menu name, {p} = its live menu price (isolated LTR number).
  'menu.single': {
    en: 'Ordering for one? {name} – {p} AED →',
    fa: 'سفارش برای یک نفر؟ {name} – {p} درهم ←',
    ar: 'تطلبون لفرد واحد؟ {name} – {p} درهم ←',
    tr: 'Tek başınıza mı yiyeceksiniz? {name} – {p} AED →'
  },
  'menu.popular': {
    en: 'Most ordered',
    fa: 'پرسفارش‌ترین',
    ar: 'الأكثر طلباً',
    tr: 'En çok sipariş edilen'
  },
  // KHAN (owner 2026-10-04): the honest value line + badge + book line on the dish-6
  // card and sheet. {p} = '<n> AED' (Western digits), rendered in code inside <bdi className="ltr-num">.
  // 2026-10-24: the Khan's Platter now includes 6 soups (wording from src/lib/special.ts).
  'khan.value': {
    en: 'Everything in 2 Full Dishes + an extra tongue & sangak, and 6 soups — only {p} more',
    fa: 'هرچه در ۲ دیس کامل هست + یک زبان و یک سنگک اضافه، و ۶ آبگوشت — فقط {p} بیشتر',
    ar: 'كل ما في صينيتين كاملتين + لسان وخبز سنكك إضافيان، و6 شوربات — بزيادة {p} فقط',
    tr: "2 Tam Tabak'taki her şey + fazladan bir dil ve bir sangak, ayrıca 6 çorba — yalnızca {p} daha"
  },
  'khan.valueSame': {
    en: 'Everything in 2 Full Dishes + an extra tongue & sangak, and 6 soups — same price',
    fa: 'هرچه در ۲ دیس کامل هست + یک زبان و یک سنگک اضافه، و ۶ آبگوشت — با همان قیمت',
    ar: 'كل ما في صينيتين كاملتين + لسان وخبز سنكك إضافيان، و6 شوربات — بنفس السعر',
    tr: "2 Tam Tabak'taki her şey + fazladan bir dil ve bir sangak, ayrıca 6 çorba — aynı fiyata"
  },
  'khan.badge': {
    en: 'Best value for 6',
    fa: 'به‌صرفه‌ترین برای ۶ نفر',
    ar: 'الخيار الأوفر لستة أشخاص',
    tr: '6 kişi için en avantajlı'
  },
  'khan.book': {
    en: 'Book it up to 7 days ahead',
    fa: 'تا ۷ روز جلوتر رزرو کنید',
    ar: 'احجزوها حتى 7 أيام مقدماً',
    tr: '7 güne kadar önceden sipariş verin'
  },
  'menu.lamma': {
    en: 'For the family table',
    fa: 'برای سفره‌ی خانواده',
    ar: 'لمّة العائلة',
    tr: 'Aile sofrası için'
  },
  'co.refundNote': {
    en: "Paid now. If the kitchen can't accept your order, the full amount is refunded automatically — your bank shows it within 5 to 10 working days.",
    fa: 'مبلغ همین حالا پرداخت می‌شود. اگر آشپزخانه نتواند سفارش را بپذیرد، کل مبلغ خودکار برمی‌گردد — بانک ظرف ۵ تا ۱۰ روز کاری آن را نشان می‌دهد.',
    ar: 'يُخصم المبلغ الآن. وإذا تعذّر على المطبخ قبول طلبكم، يُعاد المبلغ كاملاً تلقائياً — ويظهر في حسابكم خلال 5 إلى 10 أيام عمل.',
    tr: 'Ödeme şimdi alınır. Mutfak siparişinizi kabul edemezse tutarın tamamı otomatik olarak iade edilir — bankanızda 5–10 iş günü içinde görünür.'
  },
  // Directly under the pay button: what happens after the tap (the bank's 3DS page).
  'co.nextStep': {
    en: "Next: the bank's secure card page. Your bank may text you a code — enter it there and you'll come straight back.",
    fa: 'مرحله‌ی بعد: صفحه‌ی امن پرداخت بانک. ممکن است بانک برایتان کد پیامکی بفرستد — همان‌جا واردش کنید تا خودکار به همین صفحه برگردید.',
    ar: 'الخطوة التالية: صفحة الدفع الآمنة لدى البنك. قد يرسل لكم البنك رمزاً في رسالة نصية — أدخلوه هناك وستعودون إلينا مباشرة.',
    tr: 'Sonraki adım: bankanızın güvenli kart sayfası. Bankanız size doğrulama kodu içeren bir SMS gönderebilir — kodu orada girin, doğrudan buraya döneceksiniz.'
  },
  // Trust line under it. {n} is rendered as an isolated LTR number.
  'co.trustReviews': {
    en: '{n} Google reviews',
    fa: '{n} نظر در گوگل',
    ar: 'تقييمات جوجل: {n}',
    tr: '{n} Google yorumu'
  },
  'co.payGuide': {
    en: 'Payment guide',
    fa: 'راهنمای پرداخت',
    ar: 'دليل الدفع',
    tr: 'Ödeme rehberi'
  },
  // Live availability (ERP pause + "86" list). {t} = Dubai clock time, already localised.
  'avail.paused': {
    en: 'Our kitchen has paused new orders until {t}. You can still schedule your order for later.',
    fa: 'آشپزخانه تا ساعت {t} سفارش جدید نمی‌گیرد. می‌توانید سفارش‌تان را برای بعد زمان‌بندی کنید.',
    ar: 'مطبخنا أوقف استقبال الطلبات الجديدة مؤقتاً حتى {t}. يمكنكم جدولة طلبكم لوقت لاحق.',
    tr: 'Mutfağımız {t} saatine kadar yeni sipariş almıyor. Siparişinizi yine de daha sonraki bir saate zamanlayabilirsiniz.'
  },
  'co.errPaused': {
    en: 'Our kitchen has just paused new orders — please pick a later time above.',
    fa: 'آشپزخانه همین حالا پذیرش سفارش جدید را متوقف کرد — لطفاً زمان دیرتری انتخاب کنید.',
    ar: 'أوقف مطبخنا استقبال الطلبات الجديدة للتو — يُرجى اختيار وقت لاحق من الأعلى.',
    tr: 'Mutfağımız az önce yeni sipariş almayı durdurdu — lütfen yukarıdan daha sonraki bir saat seçin.'
  },
  'co.errUnavailable': {
    en: 'Some items just finished for today — we took them out. Please check your order and tap pay to continue.',
    fa: 'برخی اقلام برای امروز تمام شد و از سفارش حذفشان کردیم. لطفاً سفارش را بررسی کنید و برای پرداخت ادامه دهید.',
    ar: 'نفدت بعض الأصناف لهذا اليوم وأزلناها من طلبكم. يُرجى مراجعة الطلب ثم المتابعة إلى الدفع.',
    tr: 'Bazı ürünler bugün için tükendi, sepetinizden çıkardık. Lütfen sepetinizi kontrol edip ödemeye devam edin.'
  },
  // {items} = the dish names, joined with the language's list comma.
  'toast.removedOut': {
    en: '{items} — finished for today and taken out of your order',
    fa: '{items} — برای امروز تمام شد و از سفارش‌تان برداشتیم',
    ar: 'أزلنا من طلبكم ما نفد لهذا اليوم: {items}',
    tr: '{items} — bugün için tükendi, sepetinizden çıkarıldı'
  },
  // Welcome-reward stamp card on the "order again" card (intro ladder, C11).
  'stamp.next': {
    en: 'Your next order: {n}% off',
    fa: 'سفارش بعدی‌تان: {n}٪ تخفیف',
    ar: 'طلبكم القادم: خصم {n}٪',
    tr: 'Bir sonraki siparişiniz: %{n} indirim'
  },
  'stamp.done': {
    en: 'All 3 welcome rewards used — thank you',
    fa: 'از هر ۳ هدیه‌ی خوش‌آمد استفاده کرده‌اید — ممنون از شما.',
    ar: 'استخدمتم مكافآت الترحيب الثلاث — شكراً لكم',
    tr: '3 hoş geldiniz hediyesinin tamamını kullandınız — teşekkürler'
  },
  'stamp.aria': {
    en: 'Welcome rewards: {k} of 3 used',
    fa: 'هدیه‌های خوش‌آمد: {k} از ۳ استفاده شده',
    ar: 'مكافآت الترحيب: {k} من 3 مستخدمة',
    tr: 'Hoş geldiniz hediyeleri: 3 üzerinden {k} kullanıldı'
  },
  'co.betterApplied': {
    en: 'A bigger discount is already applied — codes do not stack',
    fa: 'تخفیف بزرگ‌تری از قبل اعمال شده — کدها با هم جمع نمی‌شوند',
    ar: 'طُبّق لك خصم أكبر تلقائياً — لا يمكن الجمع بين الخصومات',
    tr: 'Daha yüksek bir indirim zaten uygulandı — kodlar birlikte kullanılamaz'
  },
  'co.noStack': {
    en: 'Your best discount is applied automatically — offers do not stack.',
    fa: 'بهترین تخفیف به‌طور خودکار اعمال می‌شود — تخفیف‌ها با هم جمع نمی‌شوند.',
    ar: 'يُطبَّق لك أفضل خصم تلقائياً — لا يمكن الجمع بين العروض.',
    tr: 'En iyi indiriminiz otomatik olarak uygulanır — kampanyalar birlikte kullanılamaz.'
  },
  'co.today': {
    en: 'Today',
    fa: 'امروز',
    ar: 'اليوم',
    tr: 'Bugün'
  },
  'co.tomorrow': {
    en: 'Tomorrow',
    fa: 'فردا',
    ar: 'غداً',
    tr: 'Yarın'
  },
  'bar.plusDelivery': {
    en: '+ delivery',
    fa: '+ ارسال',
    ar: '+ رسوم التوصيل',
    tr: '+ teslimat'
  },
  'co.arrive': {
    en: 'When will you arrive?',
    fa: 'کی می‌رسید؟',
    ar: 'متى ستصل؟',
    tr: 'Ne zaman geleceksiniz?'
  },
  'co.arriveNow': {
    en: 'Now',
    fa: 'الان',
    ar: 'الآن',
    tr: 'Hemen'
  },
  'co.min': {
    en: 'min',
    fa: 'دقیقه',
    ar: 'دقيقة',
    tr: 'dk'
  },
  'menu.firstTimer': {
    en: "First time here? Start with a Platter below — the heart of the table. We'll help you set the rest.",
    fa: 'اولین بار است؟ از دیس‌ها شروع کنید — دیس دلِ سفره است؛ بقیه‌ی سفره را با هم می‌چینیم.',
    ar: 'أول مرة عندنا؟ ابدؤوا بإحدى الصواني أدناه — هي قلب السفرة، ونساعدكم في تجهيز الباقي.',
    tr: 'İlk kez mi geliyorsunuz? Aşağıdaki Tabaklardan biriyle başlayın — sofranın kalbi. Gerisini birlikte tamamlarız.'
  },
  'menu.firstTimer0': {
    en: 'First time here? Start with a Platter below — the heart of the table.',
    fa: 'اولین بار است؟ از دیس‌ها شروع کنید — دیس دلِ سفره است.',
    ar: 'أول مرة عندنا؟ ابدؤوا بإحدى الصواني أدناه — هي قلب السفرة.',
    tr: 'İlk kez mi geliyorsunuz? Aşağıdaki Tabaklardan biriyle başlayın — sofranın kalbi.'
  },
  'menu.mostOrdered': {
    en: 'Most ordered — straight from the pot',
    fa: 'پرسفارش‌ترین‌ها — مستقیم از دیگ',
    ar: 'الأكثر طلباً — مباشرة من القِدر',
    tr: 'En çok sipariş edilenler — doğrudan kazandan'
  },
  'map.setPin': {
    en: 'Set pin here',
    fa: 'ثبت پین همین‌جا',
    ar: 'ثبّت موقعي هنا',
    tr: 'Konumu buraya işaretle'
  },
  'map.twoFinger': {
    en: 'Use two fingers to move the map',
    fa: 'برای حرکت نقشه از دو انگشت استفاده کنید',
    ar: 'استخدم إصبعين لتحريك الخريطة',
    tr: 'Haritayı hareket ettirmek için iki parmak kullanın'
  },
  'co.sending': {
    en: 'Sending…',
    fa: 'در حال ارسال…',
    ar: 'جارٍ الإرسال…',
    tr: 'Gönderiliyor…'
  },
  // Confirm + toasts
  'ok.onFire': {
    en: 'Your order is on the fire.',
    fa: 'سفارش شما روی آتش است.',
    ar: 'طلبك على النار.',
    tr: 'Siparişiniz ocakta.'
  },
  'ok.orderNo': {
    en: 'Order №',
    fa: 'سفارش شماره',
    ar: 'طلب رقم',
    tr: 'Sipariş no'
  },
  'ok.msg': {
    en: 'Your order is with our kitchen — we will confirm your exact time in a minute. Or walk in, the pot is always warm.',
    fa: 'سفارش شما به آشپزخانه رسید — زمان دقیق را تا چند لحظه دیگر تأیید می‌کنیم؛ یا حضوری بیایید، دیگ همیشه گرم است.',
    ar: 'وصل طلبك إلى المطبخ — سنؤكد وقتك الدقيق خلال دقيقة، أو تفضّل بزيارتنا؛ القِدر دائماً حارة.',
    tr: 'Siparişiniz mutfağımıza ulaştı — kesin saati birazdan onaylayacağız. İsterseniz uğrayın, kazan hep sıcak.'
  },
  'ok.close': {
    en: 'Close',
    fa: 'بستن',
    ar: 'إغلاق',
    tr: 'Kapat'
  },
  'toast.added': {
    en: 'Added',
    fa: 'اضافه شد',
    ar: 'تمت الإضافة',
    tr: 'Eklendi'
  },
  'toast.dineinOnly': {
    en: 'is dine-in only',
    fa: 'فقط برای سالن است',
    ar: 'للطلب داخل المطعم فقط',
    tr: 'yalnızca restoranda servis edilir'
  },
  'toast.removedDinein': {
    en: 'dine-in only — removed',
    fa: 'فقط برای سالن — حذف شد',
    ar: 'داخل المطعم فقط — تمّت الإزالة من طلب التوصيل',
    tr: 'yalnızca restoranda — çıkarıldı'
  },
  'toast.reorder': {
    en: 'Your last order is in the cart — review & checkout',
    fa: 'سفارش قبلی شما در سبد است — بررسی و پرداخت',
    ar: 'طلبك السابق في السلة — راجعه وأكمل الدفع',
    tr: 'Son siparişiniz sepette — kontrol edip siparişi tamamlayın'
  },
  // Upsell (website upsell system 2026-07-18) — {n} is replaced with a count.
  'up.together': {
    en: 'Ordered together {n}×',
    fa: '{n} بار با هم سفارش شده',
    ar: 'طُلبا معاً {n} مرة',
    tr: '{n} kez birlikte sipariş edildi'
  },
  'up.lastChance': {
    en: 'A little something to finish?',
    fa: 'چیزی برای حسنِ ختام؟',
    ar: 'لمسة أخيرة لطلبك؟',
    tr: 'Sofraya son bir dokunuş?'
  },
  'up.reachMin': {
    en: 'This gets you there:',
    fa: 'با این به حداقل سفارش می‌رسید:',
    ar: 'هذا يُكمل لك الحد الأدنى:',
    tr: 'Bununla minimum tutara ulaşırsınız:'
  },
  // Host at the dish (meal companion, 2026-10) — the per-person table step. Product names always come
  // from nm(L, item); {for} = host.forN; prices are rendered in code (<bdi className="ltr-num">), never here.
  // menu.firstTimer0 (above) replaces menu.firstTimer when the step is switched off (no promise of help).
  'host.preNote': {
    en: "Bread comes with the dish. Soup and drinks are ordered separately — we'll help you add them next.",
    fa: 'نان همراه دیس است؛ آبگوشت و نوشیدنی جداگانه سفارش داده می‌شوند — در قدم بعد کمک‌تان می‌کنیم اضافه‌شان کنید.',
    ar: 'الخبز مع الصينية، أما المرق والمشروبات فتُطلب على حدة — نساعدكم في إضافتها في الخطوة التالية.',
    tr: 'Ekmek tabakla birlikte gelir. Çorba ve içecekler ayrı sipariş edilir — bir sonraki adımda eklemenize yardımcı olacağız.'
  },
  'host.onOrder': {
    en: '{dish} is on your order',
    fa: '{dish} توی سفارش‌تونه',
    ar: '{dish} في طلبكم',
    tr: '{dish} sepetinizde'
  },
  'host.title': {
    en: 'Your table {for}',
    fa: 'سفره‌تون {for}',
    ar: 'سفرتكم {for}',
    tr: 'Sofranız: {for}'
  },
  'host.for1': {
    en: 'for one',
    fa: 'برای یک نفر',
    ar: 'لشخص واحد',
    tr: 'tek kişilik'
  },
  'host.for2': {
    en: 'for two',
    fa: 'برای دو نفر',
    ar: 'لشخصين',
    tr: 'iki kişilik'
  },
  'host.for3': {
    en: 'for three',
    fa: 'برای سه نفر',
    ar: 'لثلاثة أشخاص',
    tr: 'üç kişilik'
  },
  'host.for4': {
    en: 'for four',
    fa: 'برای چهار نفر',
    ar: 'لأربعة أشخاص',
    tr: 'dört kişilik'
  },
  'host.for5': {
    en: 'for five',
    fa: 'برای پنج نفر',
    ar: 'لخمسة أشخاص',
    tr: 'beş kişilik'
  },
  'host.for6': {
    en: 'for six',
    fa: 'برای شش نفر',
    ar: 'لستة أشخاص',
    tr: 'altı kişilik'
  },
  'host.for7': {
    en: 'for seven',
    fa: 'برای هفت نفر',
    ar: 'لسبعة أشخاص',
    tr: 'yedi kişilik'
  },
  'host.for8': {
    en: 'for eight',
    fa: 'برای هشت نفر',
    ar: 'لثمانية أشخاص',
    tr: 'sekiz kişilik'
  },
  'host.for9': {
    en: 'for nine',
    fa: 'برای نه نفر',
    ar: 'لتسعة أشخاص',
    tr: 'dokuz kişilik'
  },
  'host.sub': {
    en: 'Most of our tables start with soup.',
    fa: 'بیشتر میزهای ما با آبگوشت شروع می‌کنن.',
    ar: 'أغلب طاولاتنا تبدأ بالمرق.',
    tr: 'Masalarımızın çoğu çorbayla başlar.'
  },
  'host.bundle1': {
    en: 'Soup and a cold drink',
    fa: 'آبگوشت و یه نوشیدنی خنک',
    ar: 'مرق ومشروب بارد',
    tr: 'Çorba ve soğuk bir içecek'
  },
  'host.bundleN': {
    en: 'Soup and a cold drink for each of you',
    fa: 'برای هر نفر آبگوشت و یه نوشیدنی خنک',
    ar: 'مرق ومشروب بارد لكلّ واحد منكم',
    tr: 'Her biriniz için çorba ve soğuk bir içecek'
  },
  'host.bundleSub1': {
    en: 'a bowl of {soup} and a {drink}',
    fa: 'یه کاسه {soup} و یه {drink}',
    ar: 'صحن {soup} و{drink}',
    tr: 'bir kase {soup} ve bir {drink}'
  },
  'host.bundleSubN': {
    en: 'a bowl of {soup} and a {drink} each',
    fa: 'برای هر نفر یه کاسه {soup} و یه {drink}',
    ar: 'صحن {soup} و{drink} لكلّ شخص',
    tr: 'kişi başı bir kase {soup} ve bir {drink}'
  },
  'host.bundleDone': {
    en: '✓ Soup and drinks are on your order',
    fa: '✓ آبگوشت و نوشیدنی توی سفارش‌تونه',
    ar: '✓ المرق والمشروبات في طلبكم',
    tr: '✓ Çorba ve içecekler sepetinizde'
  },
  'host.why.broth': {
    en: 'From the same pot as your dish — tear the bread into it first.',
    fa: 'از همون دیگ کله‌پاچه؛ اول نون رو توش تلیت کنید.',
    ar: 'من قِدر الباجة نفسها — يُثرَد فيه الخبز أولاً.',
    tr: 'Tabağınızla aynı kazandan — önce ekmeği koparıp çorbaya doğrayın.'
  },
  'host.why.doogh': {
    en: 'Salted yogurt drink with mint — cool against the rich broth.',
    fa: 'دوغ خنک نعناعی، کنار کله‌پاچه‌ی داغ می‌چسبه.',
    ar: 'لبن مملّح بالنعناع، بارد يوازن دسامة الطبق.',
    tr: 'Naneli tuzlu yoğurt içeceği — zengin et suyunun yanında ferahlatır.'
  },
  'host.why.lemonade': {
    en: 'Cold and tart beside a hot dish.',
    fa: 'خنک و ترش، کنار یه غذای داغ.',
    ar: 'بارد ومنعش بجانب طبق ساخن.',
    tr: 'Sıcak yemeğin yanında soğuk ve mayhoş.'
  },
  'host.why.eye2': {
    en: 'Your dish has one eye — add a pair so you each get your own.',
    fa: 'توی دیس‌تون یه چشم هست؛ یه جفت اضافه کنید که به هر کدومتون یکی برسه.',
    ar: 'في صينيتكم عين واحدة؛ أضيفوا زوجاً ليكون لكلٍّ منكما عينه.',
    tr: 'Tabağınızda bir göz var; bir çift ekleyin, her birinize birer tane düşsün.'
  },
  'host.why.eye3': {
    en: 'Two eyes for three of you — a pair so nobody misses out.',
    fa: 'دو تا چشم برای سه نفر؛ یه جفت دیگه که به همه برسه.',
    ar: 'في الصينية عينان لثلاثة أشخاص؛ زوج إضافي حتى يكون لكلّ واحد نصيبه.',
    tr: 'Üç kişi için iki göz var; bir çift daha, kimse eksik kalmasın.'
  },
  'host.why.eye6': {
    en: 'Four eyes for six of you — one more pair and everyone gets one.',
    fa: 'توی دیس چهار تا چشم هست؛ با یه جفت دیگه به هر شش نفر یکی می‌رسه.',
    ar: 'في الصينية أربع عيون لستة أشخاص؛ بزوج إضافي يصير لكلّ واحد عين.',
    tr: 'Altı kişi için dört göz var; bir çift daha ekleyin, herkese bir tane düşsün.'
  },
  'host.why.bread1': {
    en: 'Your dish has half a bread — soaking up the soup takes more.',
    fa: 'توی دیس‌تون نصف نونه؛ تلیت نون بیشتری می‌خواد.',
    ar: 'في صينيتكم نصف رغيف، والثريد يحتاج أكثر.',
    tr: 'Tabağınızda yarım ekmek var; çorbaya doğramak için daha fazlası gerekir.'
  },
  'host.why.bread1n': {
    en: 'Each Single Dish has half a bread — soaking up the soup takes more.',
    fa: 'هر دیس یک‌نفره نصف نون داره؛ تلیت نون بیشتری می‌خواد.',
    ar: 'في كلّ صينية فردية نصف رغيف، والثريد يحتاج أكثر.',
    tr: 'Her tek kişilik tabakta yarım ekmek var; çorbaya doğramak için daha fazlası gerekir.'
  },
  'host.why.breadN': {
    en: 'Bread soaks up the soup — one more so the table never runs short.',
    fa: 'تلیت نون می‌خواد؛ یه سنگک دیگه که سفره کم نیاره.',
    ar: 'الثريد يحتاج خبزاً؛ رغيف إضافي حتى لا تنقص السفرة.',
    tr: 'Ekmek çorbayı çeker — sofra eksik kalmasın diye bir tane daha.'
  },
  'host.why.omelette': {
    en: 'Someone not into kalle pache? An omelette keeps everyone at the table.',
    fa: 'کسی سر سفره اهل کله‌پاچه نیست؟ یه املت که همه دور هم باشن.',
    ar: 'أحدٌ على السفرة لا يأكل الباجة؟ أومليت حتى يجتمع الجميع.',
    tr: 'Sofrada kelle paça sevmeyen biri mi var? Bir omlet herkesi sofrada buluşturur.'
  },
  'host.btn.bowl': {
    en: 'Add a bowl',
    fa: 'یه کاسه',
    ar: 'صحن واحد',
    tr: 'Bir kase ekle'
  },
  'host.btn.bowlEach': {
    en: 'A bowl each',
    fa: 'هر نفر یه کاسه',
    ar: 'صحن لكلّ شخص',
    tr: 'Kişi başı bir kase'
  },
  'host.btn.one': {
    en: 'Add one',
    fa: 'یکی',
    ar: 'واحد',
    tr: 'Bir tane ekle'
  },
  'host.btn.oneEach': {
    en: 'One each',
    fa: 'هر نفر یکی',
    ar: 'واحد لكلّ شخص',
    tr: 'Kişi başı bir tane'
  },
  'host.btn.pair': {
    en: 'Add a pair',
    fa: 'یه جفت',
    ar: 'زوج',
    tr: 'Bir çift ekle'
  },
  'host.btn.add': {
    en: 'Add',
    fa: 'اضافه',
    ar: 'إضافة',
    tr: 'Ekle'
  },
  'host.trust': {
    en: '🍋 Lemon and a little mixed torshi come in every delivery bag.',
    fa: '🍋 لیمو و یه کم ترشی مخلوط همیشه توی کیسه‌ی سفارش هست.',
    ar: '🍋 الليمون وقليل من المخلل المشكّل يأتيان مع كلّ طلب توصيل.',
    tr: '🍋 Her teslimat paketinde limon ve biraz karışık turşu gelir.'
  },
  'host.none': {
    en: "That's all, thanks",
    fa: 'نه ممنون، همین کافیه',
    ar: 'هذا يكفي، شكراً',
    tr: 'Bu kadar, teşekkürler'
  },
  'host.done': {
    en: 'Done',
    fa: 'تمومه',
    ar: 'تمّ',
    tr: 'Tamam'
  },
  'host.set': {
    en: '✓ Your table is set — nush-e jan!',
    fa: '✓ سفره‌تون چیده شد — نوش جان!',
    ar: '✓ سفرتكم جاهزة — بالعافية!',
    tr: '✓ Sofranız kuruldu — afiyet olsun!'
  },
  'host.change': {
    en: 'Change',
    fa: 'تغییر',
    ar: 'تعديل',
    tr: 'Değiştir'
  },
  'host.a11y': {
    en: 'Set your table',
    fa: 'سفره‌تون رو بچینید',
    ar: 'جهّزوا سفرتكم',
    tr: 'Sofranızı kurun'
  },
  'host.a11yPill': {
    en: '{btn} — {name}, {p}',
    fa: '{btn} — {name}، {p}',
    ar: '{btn} — {name}، {p}',
    tr: '{btn} — {name}, {p}'
  },
  'host.each': {
    en: '{p} each',
    fa: 'هر کدوم {p}',
    ar: '{p} للواحد',
    tr: 'tanesi {p}'
  },
  'host.pp': {
    en: '{p} per person',
    fa: 'نفری {p}',
    ar: '{p} للشخص',
    tr: 'kişi başı {p}'
  },
  'host.orChoose': {
    en: 'or choose yourself',
    fa: 'یا خودتون انتخاب کنید',
    ar: 'أو اختاروا بأنفسكم',
    tr: 'ya da kendiniz seçin'
  },
  'host.footSet1': {
    en: 'Add soup & a drink',
    fa: 'آبگوشت و نوشیدنی اضافه بشه',
    ar: 'أضيفوا مرقاً ومشروباً',
    tr: 'Çorba ve içecek ekle'
  },
  'host.footSetN': {
    en: 'Add soup & a drink each',
    fa: 'برای هر نفر آبگوشت و نوشیدنی',
    ar: 'مرق ومشروب لكلّ شخص',
    tr: 'Kişi başı çorba ve içecek ekle'
  },
  'host.noThanks': {
    en: 'No thanks',
    fa: 'نه، ممنون',
    ar: 'لا، شكراً',
    tr: 'Hayır, teşekkürler'
  },
  'host.drinkRowN': {
    en: 'A cold drink each',
    fa: 'برای هر نفر یه نوشیدنی خنک',
    ar: 'مشروب بارد لكلّ شخص',
    tr: 'Kişi başı soğuk içecek'
  },
  'host.drinkRow1': {
    en: 'A cold drink',
    fa: 'یه نوشیدنی خنک',
    ar: 'مشروب بارد',
    tr: 'Soğuk bir içecek'
  },
  // v3 (2026-10-04): the bread row (base, two choices) + the Extra-cuts scroller
  'host.breadRowN': {
    en: 'Extra bread each',
    fa: 'برای هر نفر نون اضافه',
    ar: 'خبز إضافي لكلّ شخص',
    tr: 'Kişi başı ekstra ekmek'
  },
  'host.breadRow1': {
    en: 'Extra bread',
    fa: 'نون اضافه',
    ar: 'خبز إضافي',
    tr: 'Ekstra ekmek'
  },
  'host.cutsTitle': {
    en: 'Extra cuts',
    fa: 'تکه‌های اضافه',
    ar: 'قطع إضافية',
    tr: 'Ek porsiyonlar'
  },
  'host.often': {
    en: 'Often added',
    fa: 'پرطرفدار',
    ar: 'يُطلب كثيراً',
    tr: 'Sık eklenenler'
  },
  'host.addItem': {
    en: 'Add {name}, {p}',
    fa: '{name} اضافه بشه، {p}',
    ar: 'أضيفوا {name}، {p}',
    tr: '{name} ekle, {p}'
  },
  // Footer
  'foot.privacy': {
    en: 'Privacy',
    fa: 'حریم خصوصی',
    ar: 'الخصوصية',
    tr: 'Gizlilik'
  },
  'foot.terms': {
    en: 'Terms',
    fa: 'شرایط',
    ar: 'الشروط',
    tr: 'Şartlar'
  },
  'foot.gather': {
    en: 'Gatherings',
    fa: 'سفارش مهمانی',
    ar: 'طلبات المناسبات',
    tr: 'Grup siparişi'
  },
  'foot.bread': {
    en: 'Sangak Club',
    fa: 'باشگاه سنگک',
    ar: 'نادي السنكك',
    tr: 'Sangak Kulübü'
  },
  // Phase 3 (L3) — "Sending to someone else?" (gift delivery). The payer's
  // name/phone stay on the order; the recipient rides in the kitchen notes.
  'co.giftToggle': {
    en: 'Sending to someone else?',
    fa: 'برای کس دیگری می‌فرستید؟',
    ar: 'هل ترسلون الطلب إلى شخص آخر؟',
    tr: 'Başkasına mı gönderiyorsunuz?'
  },
  'co.giftNote': {
    en: 'We deliver to them — your receipt and order updates still come to you.',
    fa: 'سفارش را به دست گیرنده می‌رسانیم — رسید و اطلاع‌رسانی سفارش برای خودتان می‌آید.',
    ar: 'نوصل الطلب إليه — ويبقى الإيصال وتحديثات الطلب لكم.',
    tr: 'Siparişi onlara teslim ederiz — fişiniz ve sipariş bildirimleri size gelmeye devam eder.'
  },
  'co.giftName': {
    en: "Recipient's name",
    fa: 'نام گیرنده',
    ar: 'اسم المستلم',
    tr: 'Alıcının adı'
  },
  'co.giftPhone': {
    en: "Recipient's mobile",
    fa: 'موبایل گیرنده',
    ar: 'رقم موبايل المستلم',
    tr: 'Alıcının cep numarası'
  },
  'co.giftPhoneNote': {
    en: 'The rider calls this number on arrival',
    fa: 'پیک هنگام رسیدن با همین شماره تماس می‌گیرد',
    ar: 'سيتصل المندوب بهذا الرقم عند الوصول',
    tr: 'Kurye vardığında bu numarayı arar'
  },
  'co.giftMsg': {
    en: 'Card message',
    fa: 'متن کارت',
    ar: 'رسالة البطاقة',
    tr: 'Kart mesajı'
  },
  'co.giftMsgPh': {
    en: 'e.g. Happy birthday!',
    fa: 'مثلاً تولدت مبارک!',
    ar: 'مثلاً كل عام وأنتم بخير!',
    tr: 'örn. İyi ki doğdun!'
  },
  'co.errGiftName': {
    en: "Please enter the recipient's name",
    fa: 'لطفاً نام گیرنده را بنویسید',
    ar: 'من فضلكم أدخلوا اسم المستلم',
    tr: 'Lütfen alıcının adını yazın'
  },
  'co.errGiftPhone': {
    en: "Please enter the recipient's UAE mobile (05X XXX XXXX)",
    fa: 'لطفاً موبایل امارات گیرنده را وارد کنید (05X XXX XXXX)',
    ar: 'من فضلكم أدخلوا رقم موبايل إماراتي صحيح للمستلم (05X XXX XXXX)',
    tr: 'Lütfen alıcının BAE cep numarasını girin (05X XXX XXXX)'
  },
  'co.giftNoSave': {
    en: 'A gift address is only saved on this device if you pick a label',
    fa: 'آدرس هدیه فقط وقتی روی این دستگاه ذخیره می‌شود که برایش نامی انتخاب کنید',
    ar: 'لا يُحفظ عنوان الهدية على هذا الجهاز إلا إذا اخترتم له اسماً',
    tr: 'Hediye adresi yalnızca bir etiket seçerseniz bu cihaza kaydedilir'
  },
  // Phase 3 (L4) — company tax invoice, collapsed by default.
  'co.invToggle': {
    en: 'Need a tax invoice for your company?',
    fa: 'برای شرکت‌تان صورتحساب مالیاتی لازم دارید؟',
    ar: 'هل تحتاجون فاتورة ضريبية باسم شركتكم؟',
    tr: 'Şirket adına fatura mı gerekiyor?'
  },
  'co.invCompany': {
    en: 'Company name',
    fa: 'نام شرکت',
    ar: 'اسم الشركة',
    tr: 'Şirket adı'
  },
  'co.invTrn': {
    en: 'Company TRN',
    fa: 'شماره‌ی ثبت مالیاتی (TRN) شرکت',
    ar: 'الرقم الضريبي للشركة (TRN)',
    tr: "Şirket TRN'si (vergi kayıt numarası)"
  },
  'co.invTrnPh': {
    en: '15 digits',
    fa: '۱۵ رقم',
    ar: '15 رقماً',
    tr: '15 hane'
  },
  'co.invAddr': {
    en: 'Company address',
    fa: 'نشانی شرکت',
    ar: 'عنوان الشركة',
    tr: 'Şirket adresi'
  },
  'co.invNote': {
    en: 'After payment, open the invoice from your order page on this phone.',
    fa: 'پس از پرداخت، صورتحساب را از صفحه‌ی سفارش روی همین گوشی باز کنید.',
    ar: 'بعد الدفع، افتحوا الفاتورة من صفحة الطلب على هذا الهاتف.',
    tr: 'Ödemeden sonra faturayı bu telefondaki sipariş sayfanızdan açın.'
  },
  'co.errInvCompany': {
    en: 'Please enter the company name for the invoice',
    fa: 'لطفاً نام شرکت را برای صورتحساب بنویسید',
    ar: 'من فضلكم أدخلوا اسم الشركة للفاتورة',
    tr: 'Lütfen fatura için şirket adını yazın'
  },
  'co.errTrn': {
    en: 'A TRN has exactly 15 digits',
    fa: 'شماره‌ی TRN دقیقاً ۱۵ رقم دارد',
    ar: 'الرقم الضريبي يتكوّن من 15 رقماً بالضبط',
    tr: 'TRN tam 15 haneden oluşur'
  },
  // Phase 3 (L7) — screen-reader labels (were English-only).
  'a11y.language': {
    en: 'Language',
    fa: 'زبان',
    ar: 'اللغة',
    tr: 'Dil seçimi'
  },
  'a11y.cartCount': {
    en: 'Order ({items})',
    fa: 'سفارش ({items})',
    ar: 'الطلب ({items})',
    tr: 'Sepet ({items})'
  },
  'a11y.openMenu': {
    en: 'Open menu',
    fa: 'باز کردن فهرست',
    ar: 'فتح القائمة',
    tr: 'Menüyü aç'
  },
  'a11y.closeMenu': {
    en: 'Close menu',
    fa: 'بستن فهرست',
    ar: 'إغلاق القائمة',
    tr: 'Menüyü kapat'
  },
  'a11y.dec': {
    en: 'One less',
    fa: 'یکی کمتر',
    ar: 'إنقاص الكمية',
    tr: 'Bir tane azalt'
  },
  'a11y.inc': {
    en: 'One more',
    fa: 'یکی بیشتر',
    ar: 'زيادة الكمية',
    tr: 'Bir tane artır'
  },
  'a11y.add': {
    en: 'Add {s}',
    fa: 'افزودن {s}',
    ar: 'أضيفوا {s}',
    tr: '{s} ekle'
  },
  'a11y.hide': {
    en: 'Hide {s}',
    fa: 'پنهان کردن {s}',
    ar: 'إخفاء {s}',
    tr: '{s} öğesini gizle'
  },
  'a11y.hideSugg': {
    en: 'Hide suggestions',
    fa: 'بستن پیشنهادها',
    ar: 'إخفاء الاقتراحات',
    tr: 'Önerileri gizle'
  },
  'a11y.hideOne': {
    en: 'Hide this suggestion',
    fa: 'بستن این پیشنهاد',
    ar: 'إخفاء هذا الاقتراح',
    tr: 'Bu öneriyi gizle'
  },
  'a11y.remove': {
    en: 'Remove from order',
    fa: 'حذف از سفارش',
    ar: 'إزالة من الطلب',
    tr: 'Sepetten çıkar'
  },
  'a11y.openMaps': {
    en: 'Open Shaun the Sheep on Google Maps',
    fa: 'باز کردن موقعیت شان د شیپ در نقشه‌ی گوگل',
    ar: 'افتحوا موقع شون ذا شيب في خرائط جوجل',
    tr: "Google Haritalar'da Shaun the Sheep konumunu aç"
  },
  'a11y.search': {
    en: 'Search the menu',
    fa: 'جست‌وجو در منو',
    ar: 'ابحثوا في القائمة',
    tr: 'Menüde ara'
  },
  'a11y.clearSearch': {
    en: 'Clear search',
    fa: 'پاک کردن جست‌وجو',
    ar: 'مسح البحث',
    tr: 'Aramayı temizle'
  },
  'a11y.results': {
    en: 'Search results',
    fa: 'نتایج جست‌وجو',
    ar: 'نتائج البحث',
    tr: 'Arama sonuçları'
  },
  'a11y.menuCats': {
    en: 'Menu categories',
    fa: 'دسته‌های منو',
    ar: 'أقسام القائمة',
    tr: 'Menü kategorileri'
  }
};
const tr = (L, k) => {
  const e = T[k];
  return e ? e[L] != null ? e[L] : e.en : k;
};
const tra = (L, k) => {
  const e = T[k];
  return e ? e[ACCENT[L]] != null ? e[ACCENT[L]] : e.en : '';
};

// ── Item / section / area name helpers (menu data carries en + fa + ar) ────
const nm = (L, o) => (L === 'ar' ? o.ar || o.en : L === 'fa' ? o.fa || o.en : L === 'tr' ? o.tr || o.en : o.en) || '';
const nmA = (L, o) => {
  const a = ACCENT[L];
  return a === 'fa' ? o.fa || '' : o.en || '';
};
// Variant fields follow the pattern base / baseFa / baseAr (hint, desc, unit, serves).
const fld = (L, o, base) => {
  if (L === 'ar') return o[base + 'Ar'] != null ? o[base + 'Ar'] : o[base];
  if (L === 'fa') return o[base + 'Fa'] != null ? o[base + 'Fa'] : o[base];
  if (L === 'tr') return o[base + 'Tr'] != null ? o[base + 'Tr'] : o[base];
  return o[base];
};
const fldA = (L, o, base) => {
  const a = ACCENT[L];
  return a === 'fa' ? o[base + 'Fa'] : o[base];
};
// Dubai area names are proper nouns: tr shows the Latin name unchanged (a.tr never exists).
const areaName = (L, a) => (L === 'ar' ? a.ar || a.en : L === 'fa' ? a.fa || a.en : L === 'tr' ? a.tr || a.en : a.en) || '';
const areaNameA = (L, a) => {
  const x = ACCENT[L];
  return x === 'fa' ? a.fa || '' : a.en || '';
};

// Search normalisation for Arabic-script input: a Persian keyboard types ی/ک،
// an Arabic keyboard ي/ك، and locality names mix ة/ه and أ/إ/آ/ا — fold them
// all so typing on either keyboard matches either spelling.
const foldArabic = s => (s || '').replace(/[يى]/g, 'ی').replace(/ك/g, 'ک').replace(/ة/g, 'ه').replace(/[أإآ]/g, 'ا').replace(/[‌‏‎]/g, '').replace(/[ً-ْٰـ]/g, '');

// Search normalisation for Latin input (all languages): Turkish diners type
// without diacritics ("iskembe", "paca", "gozleme") and a Turkish keyboard types
// ı/İ — lower-case with tr-TR (İ→i, I→ı), then fold ı ş ğ ü ö ç (and â î û) to
// plain ASCII on BOTH the query and the haystack. Harmless for en/ar/fa.
const foldTr = s => (s || '').toLocaleLowerCase('tr-TR').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c').replace(/â/g, 'a').replace(/î/g, 'i').replace(/û/g, 'u').replace(/\u0307/g, '');

// ── Language switcher (React twin of the SSR pages' LangSwitcher.astro) ────
// Always the FULL language names, each in its own language (owner request
// 2026-07-17). `compact` only tightens the pill padding for the nav bar.
const LANG_LABELS = {
  en: 'English',
  ar: 'العربية',
  fa: 'فارسی',
  tr: 'Türkçe'
};
function LangSwitcher({
  lang,
  onPick,
  compact
}) {
  return /*#__PURE__*/React.createElement("nav", {
    className: 'lang-switch' + (compact ? ' lang-switch--compact' : ''),
    "aria-label": tr(lang, 'a11y.language')
  }, KNOWN_LANGS.map(l => /*#__PURE__*/React.createElement("button", {
    key: l,
    type: "button",
    lang: l,
    title: LANG_LABELS[l],
    className: 'lang-pill' + (l === lang ? ' on' : ''),
    "aria-current": l === lang ? 'true' : undefined,
    onClick: () => onPick(l)
  }, LANG_LABELS[l])));
}

// Phase 2 — admin-editable values from Directus restaurant_settings.
// Server injects via window.__SETTINGS__; we keep hardcoded fallbacks so the
// site is fully functional when Directus is empty or unreachable.
const SETTINGS = typeof window !== 'undefined' && window.__SETTINGS__ && typeof window.__SETTINGS__ === 'object' ? window.__SETTINGS__ : {};
const ZONES = typeof window !== 'undefined' && Array.isArray(window.__ZONES__) ? window.__ZONES__ : [];

// ── Delivery areas (Dubai) ───────────────────────────────────────────────────
// Per-area flat delivery fee in AED. Anchored on Dubai Marina = 30 and scaled
// by rough road distance from the restaurant (Jumeirah 1). The customer picks
// their area at checkout; the fee shown here is exactly what's charged.
//
// IMPORTANT: keep this list in sync with DELIVERY_AREAS in
//   src/pages/api/orders/create.ts
// The server re-derives the fee from the chosen area `key` and never trusts a
// client-supplied amount — so the two tables must agree on key→fee.
//
// This table is the SINGLE SOURCE OF TRUTH for delivery fees. We intentionally
// do NOT read fees from Directus delivery_zones — legacy zones seeded at a flat
// AED 8 would otherwise mask the real per-area pricing. (To change a fee, edit
// this table + DUBAI_AREAS in create.ts and redeploy.)
// KEEP IN STEP — there is a THIRD copy: src/lib/delivery-bands.ts DELIVERY_AREAS,
// which /success reads server-side to say "usually 45 to 80 minutes to Al Barsha".
// Add or re-price an area here and in create.ts WITHOUT updating that file and the
// tracking page quotes the band for the old fee tier (or the generic fallback).
// ── WhatsApp ordering context (set by /wa/<token> — null on the normal site).
// When present: name/phone are pre-filled (phone locked — verified by the
// WhatsApp channel itself), saved CRM addresses render as one-tap chips,
// "Dine in" becomes "Pickup", and orders submit with channel:'whatsapp' so
// the payment link also lands in the customer's chat.
const WA_CTX = typeof window !== 'undefined' && window.__WA_CONTEXT__ || null;
const DELIVERY_AREAS = [
// AED 15 — the restaurant's own neighbourhood
{
  key: 'al-badaa',
  en: "Al Bada'a",
  fa: 'البدع',
  ar: 'البدع',
  fee: 15
}, {
  key: 'al-manara',
  en: 'Al Manara',
  fa: 'المنارة',
  ar: 'المنارة',
  fee: 15
}, {
  key: 'satwa',
  en: 'Al Satwa',
  fa: 'السطوة',
  ar: 'السطوة',
  fee: 15
}, {
  key: 'al-wasl',
  en: 'Al Wasl',
  fa: 'الوصل',
  ar: 'الوصل',
  fee: 15
}, {
  key: 'jumeirah',
  en: 'Jumeirah',
  fa: 'جمیرا',
  ar: 'جميرا',
  fee: 15
},
// AED 20 — central Dubai
{
  key: 'al-jafiliya',
  en: 'Al Jafiliya',
  fa: 'الجافلیة',
  ar: 'الجافلية',
  fee: 20
}, {
  key: 'al-karama',
  en: 'Al Karama',
  fa: 'الکرامه',
  ar: 'الكرامة',
  fee: 20
}, {
  key: 'al-mankhool',
  en: 'Al Mankhool',
  fa: 'المنخول',
  ar: 'المنخول',
  fee: 20
}, {
  key: 'al-safa',
  en: 'Al Safa',
  fa: 'الصفا',
  ar: 'الصفا',
  fee: 20
}, {
  key: 'bur-dubai',
  en: 'Bur Dubai',
  fa: 'بر دبی',
  ar: 'بر دبي',
  fee: 20
}, {
  key: 'business-bay',
  en: 'Business Bay',
  fa: 'بیزنس‌بی',
  ar: 'الخليج التجاري (بزنس باي)',
  fee: 20
}, {
  key: 'city-walk',
  en: 'City Walk',
  fa: 'سیتی واک',
  ar: 'سيتي ووك',
  fee: 20
}, {
  key: 'difc',
  en: 'DIFC',
  fa: 'مرکز مالی دبی',
  ar: 'مركز دبي المالي العالمي',
  fee: 20
}, {
  key: 'downtown',
  en: 'Downtown Dubai',
  fa: 'داون‌تاون دبی',
  ar: 'وسط مدينة دبي (داون تاون)',
  fee: 20
}, {
  key: 'dubai-design-district',
  en: 'Dubai Design District (d3)',
  fa: 'دیستریکت طراحی دبی (d3)',
  ar: 'حي دبي للتصميم (d3)',
  fee: 20
}, {
  key: 'mina-rashid',
  en: 'Mina Rashid',
  fa: 'مینا راشد',
  ar: 'ميناء راشد',
  fee: 20
}, {
  key: 'oud-metha',
  en: 'Oud Metha',
  fa: 'عود میثا',
  ar: 'عود ميثاء',
  fee: 20
}, {
  key: 'pearl-jumeirah',
  en: 'Pearl Jumeirah',
  fa: 'پرل جمیرا',
  ar: 'بيرل جميرا',
  fee: 20
}, {
  key: 'umm-al-sheif',
  en: 'Umm Al Sheif',
  fa: 'ام‌الشیف',
  ar: 'أم الشيف',
  fee: 20
}, {
  key: 'umm-suqeim',
  en: 'Umm Suqeim',
  fa: 'ام‌سقیم',
  ar: 'أم سقيم',
  fee: 20
}, {
  key: 'trade-centre',
  en: 'World Trade Centre',
  fa: 'مرکز تجارت جهانی',
  ar: 'المركز التجاري العالمي',
  fee: 20
}, {
  key: 'zabeel',
  en: "Za'abeel",
  fa: 'زعبیل',
  ar: 'زعبيل',
  fee: 20
},
// AED 25 — inner ring
{
  key: 'al-baraha',
  en: 'Al Baraha',
  fa: 'البراحة',
  ar: 'البراحة',
  fee: 25
}, {
  key: 'al-barsha',
  en: 'Al Barsha',
  fa: 'البرشاء',
  ar: 'البرشاء',
  fee: 25
}, {
  key: 'al-garhoud',
  en: 'Al Garhoud',
  fa: 'القرهود',
  ar: 'القرهود',
  fee: 25
}, {
  key: 'al-jaddaf',
  en: 'Al Jaddaf',
  fa: 'الجداف',
  ar: 'الجداف',
  fee: 25
}, {
  key: 'al-muraqqabat',
  en: 'Al Muraqqabat',
  fa: 'المرقبات',
  ar: 'المرقبات',
  fee: 25
}, {
  key: 'al-muteena',
  en: 'Al Muteena',
  fa: 'المطینة',
  ar: 'المطينة',
  fee: 25
}, {
  key: 'al-quoz',
  en: 'Al Quoz',
  fa: 'القوز',
  ar: 'القوز',
  fee: 25
}, {
  key: 'al-rigga',
  en: 'Al Rigga',
  fa: 'الرقة',
  ar: 'الرقة',
  fee: 25
}, {
  key: 'al-sufouh',
  en: 'Al Sufouh',
  fa: 'الصفوح',
  ar: 'الصفوح',
  fee: 25
}, {
  key: 'azizi-riviera',
  en: 'Azizi Riviera (Meydan One)',
  fa: 'عزیزی ریویرا',
  ar: 'عزيزي ريفيرا (ميدان ون)',
  fee: 25
}, {
  key: 'deira',
  en: 'Deira',
  fa: 'دیره',
  ar: 'ديرة',
  fee: 25
}, {
  key: 'district-one',
  en: 'District One (MBR City)',
  fa: 'دیستریکت وان',
  ar: 'ديستريكت ون (مدينة محمد بن راشد)',
  fee: 25
}, {
  key: 'healthcare-city',
  en: 'Dubai Healthcare City',
  fa: 'شهرک پزشکی دبی',
  ar: 'مدينة دبي الطبية',
  fee: 25
}, {
  key: 'hor-al-anz',
  en: 'Hor Al Anz',
  fa: 'هور العنز',
  ar: 'هور العنز',
  fee: 25
}, {
  key: 'madinat-jumeirah',
  en: 'Madinat Jumeirah',
  fa: 'مدینه جمیرا',
  ar: 'مدينة جميرا',
  fee: 25
}, {
  key: 'meydan-city',
  en: 'Meydan City',
  fa: 'میدان سیتی',
  ar: 'مدينة ميدان (ميدان سيتي)',
  fee: 25
}, {
  key: 'naif',
  en: 'Naif',
  fa: 'نایف',
  ar: 'نايف',
  fee: 25
}, {
  key: 'port-saeed',
  en: 'Port Saeed',
  fa: 'پورت سعید',
  ar: 'بور سعيد',
  fee: 25
}, {
  key: 'sobha-hartland',
  en: 'Sobha Hartland',
  fa: 'سوبا هارتلند',
  ar: 'شوبا هارتلاند',
  fee: 25
}, {
  key: 'sobha-hartland-2',
  en: 'Sobha Hartland II',
  fa: 'سوبا هارتلند ۲',
  ar: 'شوبا هارتلاند 2',
  fee: 25
}, {
  key: 'the-polo-residence',
  en: 'The Polo Residence',
  fa: 'پولو رزیدنس',
  ar: 'ذا بولو ريزيدنس',
  fee: 25
}, {
  key: 'umm-hurair',
  en: 'Umm Hurair',
  fa: 'ام هریر',
  ar: 'أم هرير',
  fee: 25
}, {
  key: 'wilton-park',
  en: 'Wilton Park',
  fa: 'ویلتون پارک',
  ar: 'ويلتون بارك',
  fee: 25
},
// AED 30 — Marina belt & mid-far
{
  key: 'abu-hail',
  en: 'Abu Hail',
  fa: 'ابوهیل',
  ar: 'أبو هيل',
  fee: 30
}, {
  key: 'al-barsha-south',
  en: 'Al Barsha South',
  fa: 'البرشاء جنوب',
  ar: 'البرشاء جنوب',
  fee: 30
}, {
  key: 'al-mamzar',
  en: 'Al Mamzar',
  fa: 'الممزر',
  ar: 'الممزر',
  fee: 30
}, {
  key: 'barsha-heights',
  en: 'Barsha Heights (Tecom)',
  fa: 'برشا هایتس (تیکوم)',
  ar: 'برشا هايتس (تيكوم)',
  fee: 30
}, {
  key: 'bluewaters-island',
  en: 'Bluewaters Island',
  fa: 'جزیره بلوواترز',
  ar: 'جزيرة بلوواترز',
  fee: 30
}, {
  key: 'dubai-creek-harbour',
  en: 'Dubai Creek Harbour',
  fa: 'دبی کریک هاربر',
  ar: 'مرسى خور دبي (دبي كريك هاربر)',
  fee: 30
}, {
  key: 'dubai-harbour',
  en: 'Dubai Harbour',
  fa: 'دبی هاربر',
  ar: 'دبي هاربر',
  fee: 30
}, {
  key: 'dubai-internet-city',
  en: 'Dubai Internet City',
  fa: 'دبی اینترنت سیتی',
  ar: 'مدينة دبي للإنترنت',
  fee: 30
}, {
  key: 'dubai-marina',
  en: 'Dubai Marina',
  fa: 'مارینا دبی',
  ar: 'دبي مارينا',
  fee: 30
}, {
  key: 'dubai-media-city',
  en: 'Dubai Media City',
  fa: 'دبی مدیا سیتی',
  ar: 'مدينة دبي للإعلام',
  fee: 30
}, {
  key: 'emaar-beachfront',
  en: 'Emaar Beachfront',
  fa: 'اعمار بیچ‌فرانت',
  ar: 'إعمار بيتش فرونت',
  fee: 30
}, {
  key: 'jbr',
  en: 'JBR (Jumeirah Beach Residence)',
  fa: 'جی‌بی‌آر',
  ar: 'جي بي آر (جميرا بيتش ريزيدنس)',
  fee: 30
}, {
  key: 'jlt',
  en: 'Jumeirah Lakes Towers (JLT)',
  fa: 'جی‌ال‌تی',
  ar: 'أبراج بحيرات الجميرا (جي إل تي)',
  fee: 30
}, {
  key: 'knowledge-village',
  en: 'Knowledge Park / Village',
  fa: 'نالج ویلیج',
  ar: 'قرية المعرفة',
  fee: 30
}, {
  key: 'mall-of-emirates',
  en: 'Mall of the Emirates',
  fa: 'مال امارات',
  ar: 'مول الإمارات',
  fee: 30
}, {
  key: 'district-11',
  en: 'MBR City District 11 (The Fields)',
  fa: 'دیستریکت ۱۱ میدان',
  ar: 'ديستريكت 11 (ذا فيلدز)',
  fee: 30
}, {
  key: 'meydan-gated-community',
  en: 'Meydan Gated Community',
  fa: 'مجتمع میدان (میلنیوم استیتس)',
  ar: 'مجمع ميدان المسور (ميلينيوم إستيتس)',
  fee: 30
}, {
  key: 'mirdif',
  en: 'Mirdif',
  fa: 'مردف',
  ar: 'مردف',
  fee: 30
}, {
  key: 'nad-al-sheba',
  en: 'Nad Al Sheba',
  fa: 'ند الشبا',
  ar: 'ند الشبا',
  fee: 30
}, {
  key: 'nad-al-sheba-2',
  en: 'Nad Al Sheba 2',
  fa: 'ند الشبا ۲',
  ar: 'ند الشبا 2',
  fee: 30
}, {
  key: 'nad-al-sheba-3',
  en: 'Nad Al Sheba 3',
  fa: 'ند الشبا ۳',
  ar: 'ند الشبا 3',
  fee: 30
}, {
  key: 'nad-al-sheba-4',
  en: 'Nad Al Sheba 4',
  fa: 'ند الشبا ۴',
  ar: 'ند الشبا 4',
  fee: 30
}, {
  key: 'nadd-al-hamar',
  en: 'Nadd Al Hamar',
  fa: 'ند الحمر',
  ar: 'ند الحمر',
  fee: 30
}, {
  key: 'palm-jumeirah',
  en: 'Palm Jumeirah',
  fa: 'نخل جمیرا',
  ar: 'نخلة جميرا',
  fee: 30
}, {
  key: 'the-greens',
  en: 'The Greens',
  fa: 'ذا گرینز',
  ar: 'ذا جرينز',
  fee: 30
}, {
  key: 'the-views',
  en: 'The Views',
  fa: 'ذا ویوز',
  ar: 'ذا فيوز',
  fee: 30
}, {
  key: 'umm-ramool',
  en: 'Umm Ramool',
  fa: 'ام رمول',
  ar: 'أم رمول',
  fee: 30
},
// AED 40 — outer ring
{
  key: 'al-barari',
  en: 'Al Barari',
  fa: 'البراری',
  ar: 'البراري',
  fee: 40
}, {
  key: 'al-furjan',
  en: 'Al Furjan',
  fa: 'الفرجان',
  ar: 'الفرجان',
  fee: 40
}, {
  key: 'al-mizhar',
  en: 'Al Mizhar',
  fa: 'المزهر',
  ar: 'المزهر',
  fee: 40
}, {
  key: 'al-nahda',
  en: 'Al Nahda',
  fa: 'النهده',
  ar: 'النهدة',
  fee: 40
}, {
  key: 'al-qusais',
  en: 'Al Qusais',
  fa: 'القصیص',
  ar: 'القصيص',
  fee: 40
}, {
  key: 'al-rashidiya',
  en: 'Al Rashidiya (Rashidiya)',
  fa: 'الراشدیة',
  ar: 'الراشدية',
  fee: 40
}, {
  key: 'al-twar',
  en: 'Al Twar',
  fa: 'الطوار',
  ar: 'الطوار',
  fee: 40
}, {
  key: 'al-waha',
  en: 'Al Waha',
  fa: 'الواحه',
  ar: 'الواحة',
  fee: 40
}, {
  key: 'al-warsan',
  en: 'Al Warsan',
  fa: 'الورسان',
  ar: 'الورسان',
  fee: 40
}, {
  key: 'arabian-ranches',
  en: 'Arabian Ranches',
  fa: 'ارابین رنچز',
  ar: 'المرابع العربية',
  fee: 40
}, {
  key: 'arjan',
  en: 'Arjan',
  fa: 'ارجان',
  ar: 'أرجان',
  fee: 40
}, {
  key: 'discovery-gardens',
  en: 'Discovery Gardens',
  fa: 'دیسکاوری گاردنز',
  ar: 'ديسكفري جاردنز',
  fee: 40
}, {
  key: 'festival-city',
  en: 'Dubai Festival City',
  fa: 'فستیوال سیتی',
  ar: 'دبي فستيفال سيتي',
  fee: 40
}, {
  key: 'dubai-hills',
  en: 'Dubai Hills Estate',
  fa: 'دبی هیلز',
  ar: 'دبي هيلز استيت',
  fee: 40
}, {
  key: 'dubai-production-city',
  en: 'Dubai Production City (IMPZ)',
  fa: 'دبی پروداکشن سیتی',
  ar: 'مدينة دبي للإنتاج (IMPZ)',
  fee: 40
}, {
  key: 'sports-city',
  en: 'Dubai Sports City',
  fa: 'اسپورتس‌سیتی',
  ar: 'مدينة دبي الرياضية',
  fee: 40
}, {
  key: 'dubai-studio-city',
  en: 'Dubai Studio City',
  fa: 'دبی استودیو سیتی',
  ar: 'مدينة دبي للاستوديوهات',
  fee: 40
}, {
  key: 'dubailand-residence-complex',
  en: 'Dubailand Residence Complex',
  fa: 'مجتمع مسکونی دوبیلند',
  ar: 'مجمع دبي لاند السكني',
  fee: 40
}, {
  key: 'emirates-hills',
  en: 'Emirates Hills',
  fa: 'امارات هیلز',
  ar: 'تلال الإمارات',
  fee: 40
}, {
  key: 'falcon-city',
  en: 'Falcon City of Wonders',
  fa: 'فالکون سیتی',
  ar: 'فالكون سيتي أوف وندرز',
  fee: 40
}, {
  key: 'green-community',
  en: 'Green Community',
  fa: 'گرین کامیونیتی',
  ar: 'جرين كوميونيتي',
  fee: 40
}, {
  key: 'international-city',
  en: 'International City',
  fa: 'اینترنشنال سیتی',
  ar: 'المدينة العالمية',
  fee: 40
}, {
  key: 'jumeirah-golf-estates',
  en: 'Jumeirah Golf Estates',
  fa: 'گلف استیتس جمیرا',
  ar: 'جميرا جولف إستيتس',
  fee: 40
}, {
  key: 'jumeirah-heights',
  en: 'Jumeirah Heights',
  fa: 'جمیرا هایتس',
  ar: 'جميرا هايتس',
  fee: 40
}, {
  key: 'jumeirah-islands',
  en: 'Jumeirah Islands',
  fa: 'جزایر جمیرا',
  ar: 'جزر جميرا',
  fee: 40
}, {
  key: 'jumeirah-park',
  en: 'Jumeirah Park',
  fa: 'جمیرا پارک',
  ar: 'جميرا بارك',
  fee: 40
}, {
  key: 'jvc',
  en: 'Jumeirah Village Circle (JVC)',
  fa: 'جی‌وی‌سی',
  ar: 'قرية جميرا الدائرية (جي في سي)',
  fee: 40
}, {
  key: 'jvt',
  en: 'Jumeirah Village Triangle (JVT)',
  fa: 'جی‌وی‌تی',
  ar: 'مثلث قرية جميرا (جي في تي)',
  fee: 40
}, {
  key: 'living-legends',
  en: 'Living Legends',
  fa: 'لیوینگ لجندز',
  ar: 'ليفينج ليجيندز',
  fee: 40
}, {
  key: 'liwan',
  en: 'Liwan (Queue Point)',
  fa: 'لیوان',
  ar: 'ليوان (كيو بوينت)',
  fee: 40
}, {
  key: 'majan',
  en: 'Majan',
  fa: 'مجان',
  ar: 'مجان',
  fee: 40
}, {
  key: 'motor-city',
  en: 'Motor City',
  fa: 'موتورسیتی',
  ar: 'موتور سيتي',
  fee: 40
}, {
  key: 'mudon',
  en: 'Mudon',
  fa: 'مودون',
  ar: 'مدن',
  fee: 40
}, {
  key: 'muhaisnah',
  en: 'Muhaisnah',
  fa: 'محیصنه',
  ar: 'محيصنة',
  fee: 40
}, {
  key: 'the-gardens',
  en: 'The Gardens',
  fa: 'گاردنز',
  ar: 'ذا جاردنز',
  fee: 40
}, {
  key: 'the-lakes',
  en: 'The Lakes',
  fa: 'ذا لیکس',
  ar: 'ذا ليكس',
  fee: 40
}, {
  key: 'the-meadows',
  en: 'The Meadows',
  fa: 'ذا میدوز',
  ar: 'ذا ميدوز',
  fee: 40
}, {
  key: 'the-springs',
  en: 'The Springs',
  fa: 'ذا اسپرینگز',
  ar: 'ذا سبرينجز',
  fee: 40
}, {
  key: 'the-villa',
  en: 'The Villa (Dubailand)',
  fa: 'کامیونیتی ویلا',
  ar: 'ذا فيلا (دبي لاند)',
  fee: 40
}, {
  key: 'tilal-al-ghaf',
  en: 'Tilal Al Ghaf',
  fa: 'تلال الغاف',
  ar: 'تلال الغاف',
  fee: 40
},
// AED 50 — far outskirts
{
  key: 'academic-city',
  en: 'Academic City',
  fa: 'شهرک دانشگاهی',
  ar: 'مدينة دبي الأكاديمية',
  fee: 50
}, {
  key: 'al-khawaneej',
  en: 'Al Khawaneej',
  fa: 'الخوانیج',
  ar: 'الخوانيج',
  fee: 50
}, {
  key: 'al-ttay',
  en: 'Al Ttay',
  fa: 'الطي',
  ar: 'الطي',
  fee: 50
}, {
  key: 'al-warqa',
  en: "Al Warqa'a",
  fa: 'الورقاء',
  ar: 'الورقاء',
  fee: 50
}, {
  key: 'arabian-ranches-3',
  en: 'Arabian Ranches 3',
  fa: 'عربین رنچز ۳',
  ar: 'المرابع العربية 3',
  fee: 50
}, {
  key: 'damac-hills',
  en: 'Damac Hills',
  fa: 'داماک هیلز',
  ar: 'داماك هيلز',
  fee: 50
}, {
  key: 'damac-hills-2',
  en: 'Damac Hills 2 (Akoya)',
  fa: 'داماک هیلز ۲',
  ar: 'داماك هيلز 2 (أكويا)',
  fee: 50
}, {
  key: 'dubai-industrial-city',
  en: 'Dubai Industrial City (Sahara Meadows)',
  fa: 'شهر صنعتی دبی',
  ar: 'مدينة دبي الصناعية (صحارى ميدوز)',
  fee: 50
}, {
  key: 'dip',
  en: 'Dubai Investment Park (DIP)',
  fa: 'پارک سرمایه‌گذاری',
  ar: 'مجمع دبي للاستثمار',
  fee: 50
}, {
  key: 'silicon-oasis',
  en: 'Dubai Silicon Oasis',
  fa: 'سیلیکون اوآسیس',
  ar: 'واحة دبي للسيليكون',
  fee: 50
}, {
  key: 'dubai-south',
  en: 'Dubai South / Expo City',
  fa: 'دبی ساوث',
  ar: 'دبي الجنوب / مدينة إكسبو',
  fee: 50
}, {
  key: 'dubai-waterfront',
  en: 'Dubai Waterfront (Badrah/Veneto)',
  fa: 'واتر فرانت دبی',
  ar: 'دبي ووترفرونت (بدرة/فينيتو)',
  fee: 50
}, {
  key: 'emaar-south',
  en: 'Emaar South',
  fa: 'اعمار جنوب',
  ar: 'إعمار الجنوب',
  fee: 50
}, {
  key: 'expo-city',
  en: 'Expo City Dubai',
  fa: 'اکسپو سیتی دبی',
  ar: 'مدينة إكسبو دبي',
  fee: 50
}, {
  key: 'jebel-ali',
  en: 'Jebel Ali',
  fa: 'جبل علی',
  ar: 'جبل علي',
  fee: 50
}, {
  key: 'jebel-ali-downtown',
  en: 'Jebel Ali Downtown',
  fa: 'داون‌تاون جبل علی',
  ar: 'داون تاون جبل علي',
  fee: 50
}, {
  key: 'jebel-ali-village',
  en: 'Jebel Ali Village',
  fa: 'دهکده جبل علی',
  ar: 'قرية جبل علي',
  fee: 50
}, {
  key: 'layan',
  en: 'Layan Community',
  fa: 'لایان',
  ar: 'مجمع ليان',
  fee: 50
}, {
  key: 'madinat-hind',
  en: 'Madinat Hind (Damac Hills 2)',
  fa: 'مدینه هند',
  ar: 'مدينة هند (داماك هيلز 2)',
  fee: 50
}, {
  key: 'mag-5-boulevard',
  en: 'MAG 5 Boulevard',
  fa: 'مگ ۵ بلوار',
  ar: 'ماج 5 بوليفارد',
  fee: 50
}, {
  key: 'mira',
  en: 'Mira',
  fa: 'میرا',
  ar: 'ميرا',
  fee: 50
}, {
  key: 'mira-oasis',
  en: 'Mira Oasis',
  fa: 'میرا اوسیس',
  ar: 'ميرا أويسس',
  fee: 50
}, {
  key: 'remraam',
  en: 'Remraam',
  fa: 'رمرام',
  ar: 'رمرام',
  fee: 50
}, {
  key: 'rukan',
  en: 'Rukan',
  fa: 'روکان',
  ar: 'ركان',
  fee: 50
}, {
  key: 'serena',
  en: 'Serena',
  fa: 'سرنا',
  ar: 'سيرينا',
  fee: 50
}, {
  key: 'the-pulse',
  en: 'The Pulse (Dubai South)',
  fa: 'پالس',
  ar: 'ذا بالس (دبي الجنوب)',
  fee: 50
}, {
  key: 'the-sustainable-city',
  en: 'The Sustainable City',
  fa: 'شهر پایدار (ساستینبل سیتی)',
  ar: 'المدينة المستدامة',
  fee: 50
}, {
  key: 'town-square',
  en: 'Town Square (Nshama)',
  fa: 'تاون اسکوئر',
  ar: 'تاون سكوير (نشاما)',
  fee: 50
}, {
  key: 'villanova',
  en: 'Villanova',
  fa: 'ویلانووا',
  ar: 'فيلانوفا',
  fee: 50
}];

// The list that populates the area selector. The built-in Dubai table above is
// the single source of truth — we deliberately do NOT source options from
// Directus delivery_zones. (Legacy zones exist there at a flat AED 8 and, when
// used, masked the real per-area fees — every area showed "AED 8".) Each option
// carries a stable `key` the server re-prices from via DUBAI_AREAS.
const AREA_OPTIONS = DELIVERY_AREAS.map(a => ({
  ...a,
  zoneId: null
}));
function findArea(key) {
  if (!key) return null;
  return AREA_OPTIONS.find(a => a.key === key) || null;
}
function getAreaFee(key) {
  const a = findArea(key);
  // Returns the fee actually charged (promo-discounted while the promo is on).
  return a ? areaFeeNow(a.fee) : null;
}

// Free delivery at/above this FOOD SUBTOTAL (pre-discount, = the "Subtotal"
// line the customer sees). Display only — MUST stay in sync with
// FREE_DELIVERY_ABOVE_AED in src/pages/api/orders/create.ts, which is what's
// actually charged.
const FREE_DELIVERY_ABOVE_AED = 300;

// LIMITED-TIME PROMO — percent off every delivery fee (owner, 2026-08-27).
// Display only; the identical constant in src/pages/api/orders/create.ts is
// what's actually charged. Set BOTH to 0 to end the promo.
const DELIVERY_FEE_DISCOUNT_PCT = 50;
const DELIVERY_PROMO_ON = DELIVERY_FEE_DISCOUNT_PCT > 0;
// Discounted fee, fils-exact (15 → 7.5), matching create.ts arithmetic.
function areaFeeNow(fee) {
  return DELIVERY_PROMO_ON ? Math.round(fee * (100 - DELIVERY_FEE_DISCOUNT_PCT)) / 100 : fee;
}
// "7.50" for halves, "10" for whole AED — keeps receipts tidy.
function feeFmt(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// ── Delivery time bands (K6) ─────────────────────────────────────────────────
// Embedded from scratchpad phase2-bands.json (2026-09-19). METHOD: P25–P75 of
// the minutes from the ERP receiving a paid deliver-now website order
// (external_orders.created_at) to the cashier closing it with the courier fee
// (courier_recorded_at), n=182, 7 Aug–19 Sep 2026, grouped by the area's BASE
// fee (DELIVERY_AREAS .fee, i.e. BEFORE the promo discount), rounded outward
// to 5 min, min width 10, non-decreasing. The close is stamped at/after the
// handover, so the ranges lean long (the safe side). OWNER NOTE: each range is
// one line here — tighten by hand if the owner wants; re-measure in ~2 months.
// The fallback (no area chosen / fee above the table) is the data's overall
// band, and it is the ONE generic number: the hero's no-area line renders it
// too (it used to say a hard-coded "35–55 min", which contradicted the cart
// switch's "~55–85" on the same screen). To promise a different generic time,
// change DELIVERY_BAND_FALLBACK — both places follow.
// KEEP IN STEP — src/lib/delivery-bands.ts mirrors this table (and the fallback)
// for the SERVER-rendered pre-accept line on /success. Change one, change both in
// the SAME deploy, or the tracking page promises a band the cart never showed.
const DELIVERY_BANDS = [{
  maxFee: 15,
  band: '35–65'
}, {
  maxFee: 20,
  band: '45–65'
}, {
  maxFee: 25,
  band: '45–80'
}, {
  maxFee: 30,
  band: '60–85'
}, {
  maxFee: 40,
  band: '60–90'
}, {
  maxFee: 50,
  band: '65–90'
}];
const DELIVERY_BAND_FALLBACK = '55–85';
// Phase 3 (C7) company tax invoice at checkout — OFF until the owner/accountant
// settle the VAT calculation; must match TAX_INVOICE_ENABLED in src/lib/features.ts.
const TAX_INVOICE_ENABLED = false;
// Base (pre-promo) fee → band string "A–B".
function bandForFee(baseFee) {
  const f = Number(baseFee);
  if (!(f > 0)) return DELIVERY_BAND_FALLBACK;
  const hit = DELIVERY_BANDS.find(b => f <= b.maxFee);
  return hit ? hit.band : DELIVERY_BAND_FALLBACK;
}
function bandForArea(key) {
  const a = findArea(key);
  return a ? bandForFee(a.fee) : DELIVERY_BAND_FALLBACK;
}
// "45–60 min" · "من 45 إلى 60 دقيقة" · "۴۵ تا ۶۰ دقیقه".
function bandRange(L, band) {
  const m = /^(\d+)\D+(\d+)$/.exec(String(band || ''));
  if (!m) return '';
  if (L === 'ar') return 'من ' + m[1] + ' إلى ' + m[2] + ' دقيقة';
  if (L === 'fa') return numLocal('fa', m[1]) + ' تا ' + numLocal('fa', m[2]) + ' دقیقه';
  if (L === 'tr') return m[1] + '–' + m[2] + ' dk';
  return m[1] + '–' + m[2] + ' min';
}
function bandUsually(L, band) {
  return tr(L, 'band.usually').replace('{r}', bandRange(L, band));
}
// Second line under a fulfilment button (rail + checkout): Delivery "AED 7.50 ·
// usually 35–65 min" (or "Choose your area"), Pickup "Free · Jumeirah 1",
// Dine-in "Table on arrival". `free` = the free-delivery threshold is met.
function ModeSub({
  L,
  m,
  areaKey,
  free,
  className
}) {
  let body;
  if (m === 'delivery') {
    const a = findArea(areaKey);
    body = !a ? tr(L, 'mode.chooseArea') : /*#__PURE__*/React.createElement(React.Fragment, null, free ? tr(L, 'co.freeDelivery') : /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, "AED ", feeFmt(areaFeeNow(a.fee))), " \xB7 ", bandUsually(L, bandForFee(a.fee)));
  } else if (m === 'pickup') body = tr(L, 'mode.pickupSub');else body = tr(L, 'mode.dineinSub');
  return /*#__PURE__*/React.createElement("span", {
    className: "mode-sub " + SCR(L) + (className ? " " + className : "")
  }, body);
}

// Programmatic scrolling with a VERIFIED fallback. html{scroll-behavior:smooth}
// plus fixed-attachment backgrounds froze every programmatic scroll in Chrome
// (measured live 2026-08-27 — ORDER NOW, nav links and category tabs all dead),
// so we request smoothness per call and fall back to an instant jump if the
// viewport provably didn't move. Never re-add the blanket CSS smooth rule.
let _glideTimer = null;
function glideTo(top) {
  const from = window.scrollY;
  if (Math.abs(from - top) < 2) return;
  if (_glideTimer) {
    clearTimeout(_glideTimer);
    _glideTimer = null;
  }
  try {
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  } catch (e) {
    window.scrollTo(0, top);
    return;
  }
  _glideTimer = setTimeout(() => {
    _glideTimer = null;
    if (Math.abs(window.scrollY - from) < 2) window.scrollTo(0, top);
  }, 180);
}
// Lowest delivery minimum across active zones + the current shortfall — shared
// by the cart rail, the mobile sheet and the checkout modal so the customer
// learns about the minimum BEFORE spending effort, not at the finish line.
// Search speaks Finglish: romanizations Dubai customers actually type.
// Keyed by product slug; matched case-insensitively alongside en/fa/ar names.
const SEARCH_ALIASES = {
  'trotters': 'pacheh pache paa pacha paça ayak',
  'brain': 'maghz magz beyin',
  'brain-soup': 'maghz magz soup beyin çorba',
  'sandwich-sauce-maghz': 'maghz magz sauce sos sandwich sandviç sos kelle paça',
  'tongue': 'zaban zabun zabaan dil',
  'eye': 'cheshm chashm göz',
  'meat': 'gusht goosht gosht et',
  'tripe': 'sirabi sirab shirdan işkembe',
  'cheek': 'banagush banagoosh yanak kuzu yanağı',
  'soup': 'soop abgoosht ab çorba',
  'mojoon': 'majoon mojon majun shake özel karışık karışım',
  'dish-1': 'kalleh pacheh kaleh pache kalle dish set kelle paça tabak tek kişilik',
  'dish-2': 'kalleh pacheh kaleh pache kalle dish set kelle paça tabak iki kişilik',
  'dish-3': 'kalleh pacheh kaleh pache kalle dish set kelle paça tabak tam tabak',
  'dish-6': 'kalleh pacheh kaleh pache kalle dish set kelle paça tabak han tabağı altı kişilik',
  'sangak': 'nan noon bread naan ekmek sangak',
  'sangak-x': 'nan noon bread naan ekmek sangak susam çörek otu',
  'doogh': 'dough dugh dooq yogurt drink ayran yoğurt',
  'tea': 'chai chay çay',
  'tea-black': 'chai chay çay siyah çay',
  'tea-pot': 'chai chay kettle ghuri çay demlik çay',
  'torshi-mix': 'torshi pickle pickles turşu',
  'torshi-seer': 'torshi seer sir garlic turşu sarımsak',
  'omelette': 'omlet omelet nimroo omlet yumurta',
  'lemonade': 'limonata limon',
  'water': 'su',
  'bread-add': 'ekmek sangak',
  'chili': 'acı biber',
  'chili-pic': 'acı biber turşu',
  'capsicum': 'dolmalık biber',
  'onion': 'soğan'
};
// Arabic search words Arab customers actually type (matched through foldArabic,
// so ي/ی، ك/ک، ة/ه، أ/إ/آ/ا all fold). Keep Persian-only letters out of here.
const SEARCH_ALIASES_AR = {
  'trotters': 'باجة كوارع كراع أكارع مقادم',
  'brain': 'مخ مخاخ',
  'brain-soup': 'مخ مرق شوربة',
  'sandwich-sauce-maghz': 'مخ ساندويتش سندويش صوص صلصة باجة',
  'tongue': 'لسان ألسنة',
  'eye': 'عين عيون',
  'meat': 'لحم لحمة لحم راس لحم الراس',
  'tripe': 'كرش كرشة',
  'cheek': 'خد خدود',
  'soup': 'شوربة مرق تشريب',
  'mojoon': 'خلطة معجون',
  'dish-1': 'باجة كله باتشه رأس صينية طبق تشريب لحم راس لحم الراس',
  'dish-2': 'باجة كله باتشه رأس صينية طبق تشريب لحم راس لحم الراس',
  'dish-3': 'باجة كله باتشه رأس صينية طبق عائلة تشريب لحم راس لحم الراس',
  'dish-6': 'باجة كله باتشه رأس صينية طبق عائلة تشريب لحم راس لحم الراس',
  'sangak': 'خبز عيش سنكك سنجك إيراني',
  'sangak-x': 'خبز عيش سنكك سنجك إيراني سمسم',
  'bread-add': 'خبز عيش سنكك سنجك إيراني',
  'water': 'ماء ماي مياه موية',
  'doogh': 'دوغ لبن',
  'tea': 'شاي جاي',
  'tea-black': 'شاي جاي أحمر',
  'tea-pot': 'شاي جاي براد إبريق',
  'torshi-mix': 'مخلل مخللات طرشي ترشي',
  'torshi-seer': 'مخلل طرشي ترشي ثوم',
  'omelette': 'أومليت عجة بيض',
  'chili': 'فلفل حار شطة',
  'chili-pic': 'فلفل حار مخلل طرشي',
  'lemonade': 'ليمون عصير'
};

// Fulfilment modes. The server (src/pages/api/orders/create.ts) accepts exactly
// these three and re-derives fee, address rules and lead time from them.
// KEEP IN SYNC with PREP_LEAD_MIN there — a shorter lead here lets a customer
// pick a slot the kitchen cannot hit.
const SCHED_LEAD_BY_MODE = {
  delivery: 90,
  pickup: 45,
  dinein: 30
};

// ── Ad attribution capture ─────────────────────────────────────────────────
// FIRST-TOUCH and sticky. A customer clicks an Instagram ad, lands with an
// ?fbclid=..., browses, maybe comes back tomorrow from a bookmark, and orders.
// Only the FIRST visit carried the click-ID, so we freeze it on arrival and
// keep it for 30 days. Without this the server-side Purchase has nothing to
// tie the sale to an ad, and every platform is left guessing.
//
// Nothing here is personal data the order does not already hold — these are
// advertising identifiers, and they ride along with the order row so they can
// be deleted with it.
const ATTR_KEY = 'sf-attr';
const ATTR_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CLICK_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'ScCid', 'msclkid', 'twclid'];
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
function readCookie(name) {
  try {
    // '\\s' — in a JS string '\s' is just 's', which made this match only the
    // FIRST cookie in document.cookie (so _fbp/_fbc were usually missed).
    const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[2]) : '';
  } catch (e) {
    return '';
  }
}

/** Freeze arrival attribution once, then keep returning it. */
function captureAttribution() {
  if (typeof window === 'undefined') return null;
  let stored = null;
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p && p.t && Date.now() - p.t < ATTR_TTL_MS) stored = p;
    }
  } catch (e) {}
  let fresh = null;
  try {
    const q = new URLSearchParams(window.location.search);
    const hit = {};
    CLICK_PARAMS.concat(UTM_PARAMS).forEach(k => {
      const v = q.get(k);
      if (v) hit[k] = String(v).slice(0, 512);
    });
    // A new click-ID starts a NEW attribution window — the latest ad that
    // actually produced a visit is the one that deserves the credit.
    if (CLICK_PARAMS.some(k => hit[k])) {
      fresh = {
        ...hit,
        t: Date.now(),
        lp: String(window.location.pathname).slice(0, 120),
        lq: String(window.location.search || '').slice(0, 200),
        ref: String(document.referrer || '').slice(0, 200)
      };
    } else if (!stored && Object.keys(hit).length) {
      fresh = {
        ...hit,
        t: Date.now(),
        lp: String(window.location.pathname).slice(0, 120),
        lq: String(window.location.search || '').slice(0, 200),
        ref: String(document.referrer || '').slice(0, 200)
      };
    }
  } catch (e) {}
  const base = fresh || stored;
  if (fresh) {
    try {
      localStorage.setItem(ATTR_KEY, JSON.stringify(fresh));
    } catch (e) {}
  }
  if (!base) {
    // Even with no click-ID, Meta's own cookies materially improve matching.
    // Same for Snap's first-party _scid (sent to Snap CAPI as sc_cookie1).
    const fbp = readCookie('_fbp'),
      fbc = readCookie('_fbc'),
      scid = readCookie('_scid');
    if (!fbp && !fbc && !scid) return null;
    return {
      fbp: fbp || undefined,
      fbc: fbc || undefined,
      sc_cookie1: scid || undefined
    };
  }
  // _fbp/_fbc (and Snap's _scid) are read LIVE, not frozen: the pixels write
  // them after they load, so they often do not exist yet at first paint.
  const fbp = readCookie('_fbp'),
    fbc = readCookie('_fbc'),
    scid = readCookie('_scid');
  return {
    ...base,
    fbp: fbp || undefined,
    fbc: fbc || undefined,
    sc_cookie1: scid || undefined
  };
}

// Clarity helpers. Guarded like every window.sfTrack call: if the tag is
// absent (env unset, or before the delayed injector fires) these are no-ops.
// clarity() is stubbed inline in PaperLayout, so calls made before the real
// tag arrives are queued rather than lost.
function clarityTag(key, value) {
  try {
    if (window.clarity && value != null && value !== '') window.clarity('set', key, String(value));
  } catch (e) {}
}
function clarityEvent(name) {
  try {
    if (window.clarity) window.clarity('event', name);
  } catch (e) {}
}

// R5 CARD-PAGE COUNTER (2026-10-25): ONE first-party beacon, never a third-party tag. The request carries the
// event name and the order NUMBER only (no name, phone, address, amount or cart) in its URL, and an empty JSON
// body (application/json: Astro's checkOrigin refuses a body-less or text/plain cross-origin-looking POST).
// /api/payments/beacon stores and calls nothing and answers 204; the record is that request line in Caddy's
// 30-day shaunfood access log, joined per order with the order's N-Genius state. sendBeacon only queues the
// request (the browser delivers it after the page is gone), so it never delays the hand-off to the bank;
// fetch(keepalive) is the fallback. Every failure is swallowed. /success and /cancel send the pay_return twin.
function payBeacon(ev, order) {
  try {
    const o = String(order || '').toUpperCase();
    if (ev !== 'pay_redirect' || !/^SH-\d{8}-[A-Z0-9]{4}$/.test(o)) return;
    const url = '/api/payments/beacon?e=' + ev + '&order=' + o;
    let sent = false;
    try {
      sent = !!(navigator.sendBeacon && navigator.sendBeacon(url, new Blob(['{}'], {
        type: 'application/json'
      })));
    } catch (e) {}
    if (!sent) {
      try {
        fetch(url, {
          method: 'POST',
          body: '{}',
          headers: {
            'Content-Type': 'application/json'
          },
          keepalive: true
        }).catch(() => {});
      } catch (e) {}
    }
  } catch (e) {}
}

// Clarity dead-click fixes (2026-10-16): a short attention pulse on the control
// that resolves the problem (the GPS button, the chosen mode). paper.css
// `.sf-attn` — 1.6 s, a static ring under prefers-reduced-motion. timerRef (a
// useRef) lets a repeat tap restart it and the owner clear it on unmount. One
// timerRef is shared by several controls, so it also remembers WHICH element
// is pulsing (timerRef.el): a pulse on another control first takes the class
// off the previous one (whose removal timer is about to be cancelled).
function sfAttn(el, timerRef) {
  if (!el) return;
  try {
    if (timerRef) {
      clearTimeout(timerRef.current);
      const prev = timerRef.el;
      if (prev && prev !== el) {
        try {
          prev.classList.remove('sf-attn');
        } catch (e) {}
      }
      timerRef.el = el;
    }
    el.classList.remove('sf-attn');
    void el.offsetWidth; // restart the animation on a repeat tap
    el.classList.add('sf-attn');
    const t = setTimeout(() => {
      try {
        el.classList.remove('sf-attn');
      } catch (e) {}
      if (timerRef && timerRef.el === el) timerRef.el = null;
    }, 1600);
    if (timerRef) timerRef.current = t;
  } catch (e) {}
}
const IS_TOUCH = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

// Pin ↔ area answering each other (Phase 3). Nominatim is already this app's
// geocoder (the restaurant pin was geocoded with it); volume here is one
// request per pin/area choice — well inside usage policy.
async function reverseGeocodeArea(lat, lng) {
  try {
    const r = await fetch('https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=14&accept-language=en&lat=' + lat + '&lon=' + lng);
    if (!r.ok) return null;
    const j = await r.json();
    const a = j.address || {};
    const cands = [a.suburb, a.neighbourhood, a.quarter, a.city_district, a.residential, a.district].filter(Boolean);
    for (const raw of cands) {
      const name = String(raw).toLowerCase();
      const hit = AREA_OPTIONS.find(o => {
        const en = (o.en || '').toLowerCase();
        return en.length > 3 && (name.includes(en) || en.includes(name));
      });
      if (hit) return hit.key;
    }
    return null;
  } catch (e) {
    return null;
  }
}
async function geocodeAreaCenter(areaKey) {
  try {
    const a = findArea(areaKey);
    if (!a || !a.en) return null;
    const r = await fetch('https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&accept-language=en&q=' + encodeURIComponent(a.en + ', Dubai, United Arab Emirates'));
    if (!r.ok) return null;
    const j = await r.json();
    if (Array.isArray(j) && j[0]) return {
      lat: Number(j[0].lat),
      lng: Number(j[0].lon)
    };
    return null;
  } catch (e) {
    return null;
  }
}
function minOrderShortfall(mode, subtotal, cartHasTestItem) {
  const min = ZONES.length > 0 ? Math.min(...ZONES.map(z => Number(z.min_order_aed) || 0).filter(n => n > 0)) : 0;
  const shortfall = mode === "delivery" && min > 0 && subtotal < min && !cartHasTestItem ? Math.ceil(min - subtotal) : 0;
  return {
    min,
    shortfall
  };
}
function glideX(el, left) {
  if (!el) return;
  const from = el.scrollLeft;
  try {
    el.scrollTo({
      left,
      behavior: 'smooth'
    });
  } catch (e) {
    el.scrollLeft = left;
    return;
  }
  setTimeout(() => {
    if (Math.abs(el.scrollLeft - from) < 2 && Math.abs(from - left) > 2) el.scrollTo({
      left
    });
  }, 180);
}

// Localize digits to a script's numeral voice (۱۴۱ / ١٤١ / 141). Module-level
// so any component can match numbers to the line's language, not the app's.
function numLocal(lang, n) {
  const s = String(n);
  // ONE digit system per language: FA → Persian digits; AR → Western digits
  // (UAE convention); EN → Western. Prices stay Western/LTR everywhere.
  // TR → Western digits, NO thousands grouping (a Turkish comma is the decimal
  // mark, so "1,437" would read 1.437) and the UAE dot stays the decimal point:
  // plain String(n), i.e. "1437" / "10.75" (STYLE-GUIDE b "Thousands", j.1).
  if (lang === 'tr') return s;
  if (lang === 'fa') return s.replace(/(\d)\.(\d)/g, '$1٫$2').replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[+d]);
  return s;
}
const FALLBACK_PHONE = "04 321 8882";
const FALLBACK_ADDRESS_EN = "64 Jumeira Street, Jumeirah 1, Dubai";
const FALLBACK_ADDRESS_FA = "پلاک ۶۴، خیابان جمیرا، جمیرا ۱، دبی";
const FALLBACK_MAP_URL = "https://maps.google.com/?cid=18119860488021946509";
const PHONE_DISPLAY = SETTINGS.phone || FALLBACK_PHONE;
// tel: must be INTERNATIONAL (+971…) so a foreign SIM can dial it; the display
// text stays local ("04 321 8882"). 0X… → +971X…, 00… → +…, 971… → +971….
const PHONE_TEL = function (d) {
  if (d.charAt(0) === '+') return d;
  if (d.slice(0, 2) === '00') return '+' + d.slice(2);
  if (d.slice(0, 3) === '971') return '+' + d;
  if (d.charAt(0) === '0') return '+971' + d.slice(1);
  return d;
}(PHONE_DISPLAY.replace(/[^0-9+]/g, ''));
const ADDRESS_EN = SETTINGS.address_en || FALLBACK_ADDRESS_EN;
const ADDRESS_FA = SETTINGS.address_fa || FALLBACK_ADDRESS_FA;
const ADDRESS_AR = SETTINGS.address_ar || "64 شارع جميرا، جميرا 1، دبي";
const addressFor = l => l === 'ar' ? ADDRESS_AR : l === 'fa' ? ADDRESS_FA : ADDRESS_EN;
const RESTAURANT_MAP_URL = SETTINGS.map_url || FALLBACK_MAP_URL;
// Restaurant origin for the delivery map's fixed marker + route line. Prefers
// the admin-set restaurant_settings.location_lat/lng; falls back to the
// geocoded "64 Jumeira Street, Jumeirah 1" pin (Nominatim, 2026-06-13).
const RESTAURANT_COORDS = {
  lat: Number(SETTINGS.location_lat) || 25.2314011,
  lng: Number(SETTINGS.location_lng) || 55.2626267
};
// VAT is FIXED at the UAE statutory 5% — hardcoded, never read from settings,
// so it can't be changed by a Directus edit. Must stay in lock-step with the
// server, which charges Math.round(subtotal * 0.05) in api/orders/create.ts.
const SERVICE_FEE_PCT = 5;
const WHATSAPP_RAW = (SETTINGS.whatsapp_number || "").replace(/[^0-9]/g, '');
const WHATSAPP_URL = WHATSAPP_RAW ? "https://wa.me/" + WHATSAPP_RAW : "";

// ── Phone country codes ─────────────────────────────────────────────────────
// Mirrors the ERP's COUNTRIES list (src/server/lib/customer-normalize.ts) so
// the website and ERP agree on the canonical phone. UAE leads (default);
// Iran + the common Dubai-expat markets follow. dialCode has no leading "+".
const PHONE_COUNTRIES = [{
  iso2: "AE",
  dialCode: "971",
  name: "United Arab Emirates",
  flag: "🇦🇪"
}, {
  iso2: "IR",
  dialCode: "98",
  name: "Iran",
  flag: "🇮🇷"
}, {
  iso2: "SA",
  dialCode: "966",
  name: "Saudi Arabia",
  flag: "🇸🇦"
}, {
  iso2: "KW",
  dialCode: "965",
  name: "Kuwait",
  flag: "🇰🇼"
}, {
  iso2: "QA",
  dialCode: "974",
  name: "Qatar",
  flag: "🇶🇦"
}, {
  iso2: "BH",
  dialCode: "973",
  name: "Bahrain",
  flag: "🇧🇭"
}, {
  iso2: "OM",
  dialCode: "968",
  name: "Oman",
  flag: "🇴🇲"
}, {
  iso2: "EG",
  dialCode: "20",
  name: "Egypt",
  flag: "🇪🇬"
}, {
  iso2: "JO",
  dialCode: "962",
  name: "Jordan",
  flag: "🇯🇴"
}, {
  iso2: "LB",
  dialCode: "961",
  name: "Lebanon",
  flag: "🇱🇧"
}, {
  iso2: "IQ",
  dialCode: "964",
  name: "Iraq",
  flag: "🇮🇶"
}, {
  iso2: "IN",
  dialCode: "91",
  name: "India",
  flag: "🇮🇳"
}, {
  iso2: "PK",
  dialCode: "92",
  name: "Pakistan",
  flag: "🇵🇰"
}, {
  iso2: "BD",
  dialCode: "880",
  name: "Bangladesh",
  flag: "🇧🇩"
}, {
  iso2: "PH",
  dialCode: "63",
  name: "Philippines",
  flag: "🇵🇭"
}, {
  iso2: "GB",
  dialCode: "44",
  name: "United Kingdom",
  flag: "🇬🇧"
}, {
  iso2: "TR",
  dialCode: "90",
  name: "Türkiye",
  flag: "🇹🇷"
}, {
  iso2: "US",
  dialCode: "1",
  name: "United States",
  flag: "🇺🇸"
}];
const DEFAULT_DIAL_CODE = "971";

// Build a canonical +E.164 number from a chosen country dial code + the local
// number the customer typed. Idempotent for already-"+"-prefixed input, so the
// ERP's normalizePhone() (which honours a leading "+") produces the same key.
function buildE164(localInput, dialCode) {
  if (!localInput) return null;
  const trimmed = String(localInput).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)).trim();
  if (!trimmed) return null;
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/[^0-9]/g, "");
  if (!digits) return null;
  if (hasPlus) return "+" + digits; // explicit international
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  const local = digits.replace(/^0+/, ""); // strip trunk zero(s)
  return "+" + (dialCode || DEFAULT_DIAL_CODE) + local;
}

// Split a stored +E.164 number back into { dialCode, local } so a returning
// customer sees their country preselected and the local part in the field.
function splitE164(e164) {
  if (!e164 || typeof e164 !== "string" || !e164.startsWith("+")) {
    return {
      dialCode: DEFAULT_DIAL_CODE,
      local: (e164 || "").replace(/^\+/, "")
    };
  }
  const digits = e164.slice(1);
  // Longest-dialCode-first match so "971" wins over "9".
  const byLen = PHONE_COUNTRIES.slice().sort((a, b) => b.dialCode.length - a.dialCode.length);
  const hit = byLen.find(c => digits.startsWith(c.dialCode));
  if (hit) return {
    dialCode: hit.dialCode,
    local: digits.slice(hit.dialCode.length)
  };
  return {
    dialCode: DEFAULT_DIAL_CODE,
    local: digits
  };
}

// A valid UAE mobile in +E.164 is +971 5X XXXXXXX — national part 9 digits
// starting 5 (locally 05X XXX XXXX). Prefixes: 50/52/54/55/56/58. Online
// orders must give a reachable UAE mobile so the kitchen/rider can call.
const UAE_MOBILE_RE = /^\+9715[024568]\d{7}$/;
function isValidUaePhone(e164) {
  return UAE_MOBILE_RE.test(e164 || "");
}

// Phase 7+ — server-side Directus data is injected as window.__MENU__.
// When the restaurant adds real menu items via the admin, that data takes
// precedence. Until then (or if the fetch fails) we fall back to the
// hand-curated original menu below.
const MENU_SOURCE = typeof window !== 'undefined' && Array.isArray(window.__MENU__) && window.__MENU__.length > 0 ? window.__MENU__ : [{
  id: "mains",
  en: "Singles, by the bowl",
  fa: "آیتم‌های تکی",
  ar: "أصناف مفردة، على ذوقك",
  ornament: "I.",
  items: [{
    id: "eye",
    en: "Eye",
    fa: "چشم",
    ar: "عيون",
    unit: "2 pcs",
    unitFa: "۲ عدد",
    unitAr: "قطعتان",
    price: 16,
    image: "food-img/web-200/02_eye_2pcs_4x.jpg",
    tr: 'Göz'
  }, {
    id: "brain",
    en: "Brain",
    fa: "مغز",
    ar: "مخ",
    price: 24,
    image: "food-img/web-200/01_brain_4x.jpg",
    tr: 'Beyin'
  }, {
    id: "meat",
    en: "Meat",
    fa: "گوشت",
    ar: "لحم",
    price: 39,
    image: "food-img/web-200/04_meat_4x.jpg",
    tr: 'Et'
  }, {
    id: "tongue",
    en: "Tongue",
    fa: "زبان",
    ar: "لسان",
    price: 29,
    soldOut: true,
    image: "food-img/web-200/03_tongue_4x.jpg",
    tr: 'Dil'
  }, {
    id: "trotters",
    en: "Trotters",
    fa: "پاچه",
    ar: "كوارع",
    price: 19,
    image: "food-img/web-200/06_trotters_4x.jpg",
    tr: 'Paça'
  }, {
    id: "tripe",
    en: "Tripe",
    fa: "سیرابی",
    ar: "كرشة",
    price: 37,
    image: "food-img/web-200/05_tripe_4x.jpg",
    tr: 'İşkembe'
  }, {
    id: "cheek",
    en: "Lamb Cheek",
    fa: "بناگوش",
    ar: "لحم الخد",
    price: 29,
    hint: "meat & fat",
    hintFa: "گوشت و چربی",
    hintAr: "لحم ودهن",
    image: "food-img/web-200/07_meat_and_fat_4x.jpg",
    tr: 'Kuzu Yanağı'
  }, {
    id: "brain-soup",
    en: "Brain Soup",
    fa: "آبگوشت و مغز",
    ar: "شوربة بالمخ",
    price: 21,
    image: "food-img/web-200/09_brain_soup_4x.jpg",
    tr: 'Beyinli Çorba'
  }, {
    id: "soup",
    en: "Soup",
    fa: "آبگوشت",
    ar: "مرق الباجة",
    price: 12,
    image: "food-img/web-200/10_soup_4x.jpg",
    tr: 'Çorba'
  }, {
    id: "mojoon",
    en: "Special Mix",
    fa: "معجون مخصوص",
    ar: "معجون مخصوص",
    price: 64,
    hint: "house composition, the chef's pride",
    hintFa: "ترکیب ویژه‌ی آشپز",
    hintAr: "خلطة البيت الخاصة، فخر الشيف",
    image: "food-img/web-200/08_special_mix_4x.jpg",
    tr: 'Özel Karışık'
  }],
  tr: 'Tek Porsiyonlar'
}, {
  id: "platters",
  en: "Platters & Communal Dishes",
  fa: "دیس‌ها و پلاترها",
  ar: "صواني الباجة",
  ornament: "II.",
  items: [{
    id: "dish-1",
    en: "Single Dish",
    fa: "دیس ۱ نفره",
    ar: "صينية لشخص واحد",
    price: 59,
    desc: "Lamb cheek · 1 trotter · half brain · half sangak",
    descFa: "بناگوش، ۱ پاچه، نصف مغز، نصف نان",
    descAr: "لحم خد، كُراع واحدة، نصف مخ، نصف خبز سنكك",
    serves: "1 person",
    servesFa: "۱ نفره",
    servesAr: "لشخص واحد",
    image: "food-img/web-800/11_single_dish_4x.jpg",
    tr: 'Tek Kişilik Tabak'
  },
  // 2026-10-24 combos: a platter with soups at one price (mirrors src/lib/fallback-menu.ts; no photo yet = bowl icon)
  {
    id: "single-dish-soup-eye",
    en: "Single Dish + 1 Soup + 1 Eye",
    fa: "دیس ۱ نفره + ۱ آبگوشت + ۱ چشم",
    ar: "صينية لشخص واحد مع شوربة وعين",
    price: 69,
    desc: "Lamb cheek · 1 trotter · half brain · half sangak · 1 soup · 1 eye",
    descFa: "بناگوش، ۱ پاچه، نصف مغز، نصف نان، ۱ آبگوشت، ۱ چشم",
    descAr: "لحم خد، كُراع واحدة، نصف مخ، نصف خبز سنكك، شوربة واحدة، عين واحدة",
    serves: "1 person",
    servesFa: "۱ نفره",
    servesAr: "لشخص واحد",
    tr: 'Tek Kişilik Tabak + 1 Çorba + 1 Göz'
  }, {
    id: "dish-2",
    en: "Double Dish",
    fa: "دیس ۲ نفره",
    ar: "صينية لشخصين",
    price: 129,
    desc: "Lamb cheek · 2 trotters · half brain · tongue · 1 eye · whole sangak",
    descFa: "بناگوش، ۲ پاچه، نصف مغز، زبان، ۱ چشم، ۱ نان کامل",
    descAr: "لحم خد، كُراعان، نصف مخ، لسان، عين واحدة، خبز سنكك كامل",
    serves: "2 persons",
    servesFa: "۲ نفره",
    servesAr: "تكفي شخصين",
    image: "food-img/web-800/12_double_dish_4x.jpg",
    tr: 'İki Kişilik Tabak'
  }, {
    id: "double-dish-2-soups",
    en: "Double Dish + 2 Soups",
    fa: "دیس ۲ نفره + ۲ آبگوشت",
    ar: "صينية لشخصين مع شوربتين",
    price: 134,
    desc: "Lamb cheek · 2 trotters · half brain · tongue · 1 eye · whole sangak · 2 soups",
    descFa: "بناگوش، ۲ پاچه، نصف مغز، زبان، ۱ چشم، ۱ نان کامل، ۲ آبگوشت",
    descAr: "لحم خد، كُراعان، نصف مخ، لسان، عين واحدة، خبز سنكك كامل، شوربتان",
    serves: "2 persons",
    servesFa: "۲ نفره",
    servesAr: "تكفي شخصين",
    tr: 'İki Kişilik Tabak + 2 Çorba'
  }, {
    id: "dish-3",
    en: "The Full Dish",
    fa: "دیس ۳ نفره",
    ar: "الصينية الكاملة",
    price: 239,
    desc: "2 lamb cheeks · meat · 4 trotters · whole brain · tongue · 2 eyes · 2 sangak",
    descFa: "۲ بناگوش، گوشت، ۴ پاچه، مغز کامل، زبان، ۲ چشم، ۲ نان",
    descAr: "لحم خدين، لحم، 4 كوارع، مخ كامل، لسان، عينان، رغيفا سنكك",
    serves: "3 persons",
    servesFa: "۳ نفره",
    servesAr: "تكفي 3 أشخاص",
    crown: true,
    image: "food-img/web-800/13_full_dish_3_persons_platter_4x.jpg",
    tr: 'Tam Tabak'
  }, {
    id: "full-dish-3-soups",
    en: "The Full Dish + 3 Soups",
    fa: "دیس ۳ نفره + ۳ آبگوشت",
    ar: "الصينية الكاملة مع 3 شوربات",
    price: 259,
    desc: "2 lamb cheeks · meat · 4 trotters · whole brain · tongue · 2 eyes · 2 sangak · 3 soups",
    descFa: "۲ بناگوش، گوشت، ۴ پاچه، مغز کامل، زبان، ۲ چشم، ۲ نان، ۳ آبگوشت",
    descAr: "لحم خدين، لحم، 4 كوارع، مخ كامل، لسان، عينان، رغيفا سنكك، 3 شوربات",
    serves: "3 persons",
    servesFa: "۳ نفره",
    servesAr: "تكفي 3 أشخاص",
    tr: 'Tam Tabak + 3 Çorba'
  }, {
    id: "dish-6",
    en: "The Khan's Platter",
    fa: "دیس ۶ نفره",
    ar: "صينية الخان",
    price: 479,
    desc: "4 lamb cheeks · 2 meats · 8 trotters · 2 brains · 3 tongues · 4 eyes · 5 sangak · 6 soups",
    descFa: "۴ بناگوش، ۲ گوشت، ۸ پاچه، ۲ مغز، ۳ زبان، ۴ چشم، ۵ نان، ۶ آبگوشت",
    descAr: "لحم 4 خدود، حصتان من اللحم، 8 كوارع، مخّان، 3 ألسنة، 4 عيون، 5 أرغفة سنكك، 6 شوربات",
    serves: "6 persons",
    servesFa: "۶ نفره",
    servesAr: "تكفي 6 أشخاص",
    crown: true,
    image: "food-img/web-800/14_six_person_platter_4x.jpg",
    tr: 'Han Tabağı'
  }],
  tr: 'Tabaklar ve Ortak Sofra'
}, {
  id: "sides",
  en: "Bread, Pickles & Sides",
  fa: "مخلفات و کنار غذا",
  ar: "الخبز والمخللات والمقبلات",
  ornament: "III.",
  items: [{
    id: "sangak",
    en: "Sangak Bread",
    fa: "نان سنگک",
    ar: "خبز سنكك إيراني",
    price: 10.75,
    image: "food-img/web-200/16_sangak_4x.jpg",
    tr: 'Sangak Ekmeği'
  }, {
    id: "sangak-x",
    en: "Special Sangak",
    fa: "سنگک ویژه",
    ar: "خبز سنكك خاص",
    price: 15,
    hint: "with sesame & nigella",
    hintFa: "با کنجد و سیاه‌دانه",
    hintAr: "بالسمسم وحبة البركة",
    image: "food-img/web-200/16_sangak_4x.jpg",
    tr: 'Özel Sangak'
  }, {
    id: "torshi-mix",
    en: "Mixed Torshi",
    fa: "ترشی مخلوط",
    ar: "مخلل مشكل",
    price: 10,
    image: "food-img/web-200/17_seer_torshi_mix_torshi_4x.jpg",
    tr: 'Karışık Turşu'
  }, {
    id: "torshi-seer",
    en: "Garlic Torshi",
    fa: "سیر ترشی",
    ar: "مخلل ثوم",
    price: 10,
    hint: "aged seven years",
    hintFa: "هفت‌ساله",
    hintAr: "معتّق سبع سنين",
    image: "food-img/web-200/17_seer_torshi_mix_torshi_4x.jpg",
    tr: 'Sarımsak Turşusu'
  }],
  tr: 'Ekmek, Turşu ve Yan Lezzetler'
}, {
  id: "drinks",
  en: "From the Glassware",
  fa: "نوشیدنی‌ها",
  ar: "المشروبات",
  ornament: "IV.",
  items: [{
    id: "water",
    en: "Water",
    fa: "آب",
    ar: "ماء",
    price: 5,
    tr: 'Su'
  }, {
    id: "soda",
    en: "Soda",
    fa: "نوشابه",
    ar: "مشروب غازي",
    price: 5,
    tr: 'Gazlı İçecek'
  }, {
    id: "tea",
    en: "Tea",
    fa: "چای",
    ar: "شاي",
    price: 5,
    tr: 'Çay'
  }, {
    id: "lemonade",
    en: "Lemonade",
    fa: "لیموناد",
    ar: "ليمونادة",
    price: 10,
    tr: 'Limonata'
  }, {
    id: "doogh",
    en: "Doogh",
    fa: "دوغ",
    ar: "دوغ",
    price: 10,
    hint: "salted yogurt drink with mint",
    hintFa: "با نعناع کوهی",
    hintAr: "لبن مملّح بالنعناع",
    tr: 'Doogh'
  }],
  tr: 'İçecekler'
}, {
  id: "breakfast",
  en: "Daybreak Service",
  fa: "صبحانه",
  ar: "الفطور",
  ornament: "V.",
  items: [{
    id: "omelette",
    en: "Omelette",
    fa: "املت",
    ar: "أومليت",
    price: 34,
    tr: 'Omlet'
  }, {
    id: "tea-black",
    en: "Black Tea",
    fa: "چای سیاه",
    ar: "شاي أحمر",
    price: 5,
    tr: 'Siyah Çay'
  }, {
    id: "tea-pot",
    en: "Kettle of Tea",
    fa: "قوری چای",
    ar: "براد شاي",
    price: 20,
    hint: "for the table",
    hintFa: "برای سفره",
    hintAr: "للسفرة",
    tr: 'Demlik Çay'
  }],
  tr: 'Sabah Sofrası'
}, {
  id: "addons",
  en: "Extras at the Table",
  fa: "سرویس اضافه",
  ar: "إضافات على السفرة",
  ornament: "VI.",
  items: [{
    id: "capsicum",
    en: "Capsicum",
    fa: "فلفل دلمه‌ای",
    ar: "فلفل رومي",
    price: 10,
    tr: 'Dolmalık Biber'
  }, {
    id: "onion",
    en: "Onion",
    fa: "پیاز",
    ar: "بصل",
    price: 10,
    tr: 'Soğan'
  }, {
    id: "chili",
    en: "Fresh Chili",
    fa: "فلفل تازه",
    ar: "فلفل حار طازج",
    price: 10,
    tr: 'Taze Acı Biber'
  }, {
    id: "chili-pic",
    en: "Pickled Green Chili",
    fa: "فلفل سبز ترشی",
    ar: "فلفل أخضر مخلل",
    price: 10,
    tr: 'Yeşil Acı Biber Turşusu'
  }, {
    id: "bread-add",
    en: "Sangak (extra)",
    fa: "نان سنگک",
    ar: "خبز سنكك (إضافي)",
    price: 10.75,
    image: "food-img/web-200/16_sangak_4x.jpg",
    tr: 'Sangak (ekstra)'
  }],
  tr: 'Sofraya Ekstralar'
}];

// Talabat-style running order — the menu is one continuous scroll, so the
// chapters are arranged the way the kitchen sells: platters lead, then the
// single bowls, then everything else. Applies to BOTH the Directus payload
// and the fallback above; category ids not listed here keep their original
// relative order after the known ones. Ornament numerals are re-stamped to
// match the final order.
// ── Turkish menu overlay (tr) ───────────────────────────────────────────────
// Mirrors src/lib/menu-tr.ts (KEEP IN STEP) — approved Turkish dish / section
// names keyed by the Directus slug. Applied to BOTH the live Directus payload
// and the hardcoded fallback below (whose rows also carry a literal `tr` name):
// anything an item already carries wins, only gaps are filled. A slug missing
// here simply shows its English name in Turkish mode (nm() falls back). The hint doubles as the description (platters carry `desc`, singles
// `hint`, the Eye a `unit`): hintTr/descTr/unitTr are set only where the
// English item HAS that field, so Turkish never invents a line English lacks.
const SECTION_TR_SPA = {
  "mains": "Tek Porsiyonlar",
  "platters": "Tabaklar ve Ortak Sofra",
  "sides": "Ekmek, Turşu ve Yan Lezzetler",
  "drinks": "İçecekler",
  "breakfast": "Sabah Sofrası",
  "addons": "Sofraya Ekstralar"
};
const MENU_TR_SPA = {
  "sandwich-sauce-maghz": {
    "name": "Soslu Kelle Paça Sandviçi",
    "hint": "Kelle paçamız kendi sosuyla, sandviç olarak servis edilir."
  },
  "eye": {
    "name": "Göz",
    "hint": "2 adet"
  },
  "brain": {
    "name": "Beyin"
  },
  "meat": {
    "name": "Et"
  },
  "tongue": {
    "name": "Dil"
  },
  "trotters": {
    "name": "Paça"
  },
  "tripe": {
    "name": "İşkembe",
    "hint": "berrak et suyunda"
  },
  "cheek": {
    "name": "Kuzu Yanağı",
    "hint": "et ve yağ"
  },
  "brain-soup": {
    "name": "Beyinli Çorba"
  },
  "soup": {
    "name": "Çorba",
    "hint": "berrak et suyu, kıvam verilmemiş"
  },
  "mojoon": {
    "name": "Özel Karışık",
    "hint": "mutfağımızın özel karışımı, ustanın gururu"
  },
  "dish-1": {
    "name": "Tek Kişilik Tabak",
    "hint": "Kuzu yanağı · 1 paça · yarım beyin · yarım ekmek"
  },
  "single-dish-soup-eye": {
    "name": "Tek Kişilik Tabak + 1 Çorba + 1 Göz",
    "hint": "Kuzu yanağı · 1 paça · yarım beyin · yarım ekmek · 1 çorba · 1 göz"
  },
  "dish-2": {
    "name": "İki Kişilik Tabak",
    "hint": "Kuzu yanağı · 2 paça · yarım beyin · dil · 1 göz · bütün ekmek"
  },
  "double-dish-2-soups": {
    "name": "İki Kişilik Tabak + 2 Çorba",
    "hint": "Kuzu yanağı · 2 paça · yarım beyin · dil · 1 göz · bütün ekmek · 2 çorba"
  },
  "dish-3": {
    "name": "Tam Tabak",
    "hint": "2 kuzu yanağı · et · 4 paça · bütün beyin · dil · 2 göz · 2 ekmek"
  },
  "full-dish-3-soups": {
    "name": "Tam Tabak + 3 Çorba",
    "hint": "2 kuzu yanağı · et · 4 paça · bütün beyin · dil · 2 göz · 2 ekmek · 3 çorba"
  },
  "dish-6": {
    "name": "Han Tabağı",
    "hint": "4 kuzu yanağı · 2 porsiyon et · 8 paça · 2 beyin · 3 dil · 4 göz · 5 ekmek · 6 çorba"
  },
  "sangak": {
    "name": "Sangak Ekmeği"
  },
  "sangak-x": {
    "name": "Özel Sangak",
    "hint": "susam ve çörek otu ile"
  },
  "torshi-mix": {
    "name": "Karışık Turşu"
  },
  "torshi-seer": {
    "name": "Sarımsak Turşusu",
    "hint": "7 yıl bekletilmiş"
  },
  "water": {
    "name": "Su"
  },
  "soda": {
    "name": "Gazlı İçecek"
  },
  "tea": {
    "name": "Çay"
  },
  "lemonade": {
    "name": "Limonata"
  },
  "doogh": {
    "name": "Doogh",
    "hint": "naneli tuzlu yoğurt içeceği"
  },
  "pepsi": {
    "name": "Pepsi"
  },
  "coca-cola": {
    "name": "Coca-Cola"
  },
  "coca-cola-zero": {
    "name": "Coca-Cola Zero"
  },
  "coca-cola-diet": {
    "name": "Coca-Cola Diet"
  },
  "pepsi-diet": {
    "name": "Pepsi Diet"
  },
  "7-up": {
    "name": "7 Up"
  },
  "sprite": {
    "name": "Sprite"
  },
  "fanta": {
    "name": "Fanta"
  },
  "mirinda": {
    "name": "Mirinda"
  },
  "mountain-dew": {
    "name": "Mountain Dew"
  },
  "omelette": {
    "name": "Omlet"
  },
  "tea-black": {
    "name": "Siyah Çay"
  },
  "tea-pot": {
    "name": "Demlik Çay",
    "hint": "masa için"
  },
  "capsicum": {
    "name": "Dolmalık Biber"
  },
  "onion": {
    "name": "Soğan"
  },
  "chili": {
    "name": "Taze Acı Biber"
  },
  "chili-pic": {
    "name": "Yeşil Acı Biber Turşusu"
  },
  "bread-add": {
    "name": "Sangak (ekstra)"
  }
};
const trMenuItem = it => {
  const m = it && MENU_TR_SPA[it.id];
  if (!m) return it;
  const o = {
    ...it
  }; // fills GAPS only: a hardcoded fallback row already carries its literal `tr`
  if (!o.tr) o.tr = m.name;
  if (m.hint) {
    if (it.hint && o.hintTr == null) o.hintTr = m.hint;
    if (it.desc && o.descTr == null) o.descTr = m.hint;
    if (it.unit && !it.hint && o.unitTr == null) o.unitTr = m.hint;
  }
  // "3 persons" → "3 kişilik" (glossary: Feeds {n} = {n} kişilik); the number stays Western.
  const sv = /^\s*(\d+)/.exec(it.serves || '');
  if (sv && o.servesTr == null) o.servesTr = sv[1] + ' kişilik';
  return o;
};
const CATEGORY_RUN_ORDER = ['platters', 'mains', 'sides', 'drinks', 'breakfast', 'addons'];
const MENU = MENU_SOURCE.slice().sort((a, b) => {
  const ra = CATEGORY_RUN_ORDER.indexOf(a.id),
    rb = CATEGORY_RUN_ORDER.indexOf(b.id);
  return (ra === -1 ? CATEGORY_RUN_ORDER.length : ra) - (rb === -1 ? CATEGORY_RUN_ORDER.length : rb);
}).map((s, i) => ({
  ...s,
  ornament: (['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'][i] || String(i + 1)) + '.',
  ...(!s.tr && SECTION_TR_SPA[s.id] ? {
    tr: SECTION_TR_SPA[s.id]
  } : null),
  items: (s.items || []).map(trMenuItem)
}));
// Cheapest ORDERABLE platter, for the hero's "platters from" line — computed
// at render from the live items (App memo), so neither a price change nor a
// sold-out / switched-off platter leaves the hero lying. null = none orderable
// → the hero drops the "platters from" clause.
const PLATTER_IDS = new Set(((MENU.find(s => s.id === 'platters') || {}).items || []).map(i => i.id));
const minPlatterPrice = items => {
  const p = (items || []).filter(i => PLATTER_IDS.has(i.id) && !i.soldOut).map(i => Number(i.price)).filter(n => n > 0);
  return p.length ? Math.min(...p) : null;
};

// ── Live availability (ERP pause + "86" list) ───────────────────────────────
// The kitchen can switch a dish off (or pause new orders) on the POS; the site
// hears it via /api/online-status (SSR seeds window.__AVAIL__). A live "off"
// dish is treated EXACTLY like a Directus sold-out one: we flip `soldOut` on
// the MENU item, so every existing path (MenuRow/PlatterCard stamps, cart
// sanitisers, reorder, ?cart=, teaser, suggestions) follows with no second UI.
// Item objects are REPLACED, never mutated, so the memo'd rows re-render.
// Fail-open: a missing/empty status leaves the Directus soldOut untouched.
const BASE_SOLD_OUT = new Set(MENU.flatMap(s => s.items).filter(i => i.soldOut).map(i => i.id));
let LIVE_UNAVAIL = []; // [{ slug, until }] as last received
// The live-off set that is ACTUALLY applied to MENU right now. The change check
// diffs against THIS, never against LIVE_UNAVAIL re-read on today's clock — a
// timed 86 whose `until` has passed is gone from both of those, so that diff
// would never clear the dish's soldOut again.
let LIVE_OUT = new Set();
// Earliest future `until` in the live list (ms, 0 = none): App re-applies the
// list then, so a timed dish comes back on time without waiting for a poll.
const nextLiveUntil = () => {
  const now = Date.now();
  let min = 0;
  (Array.isArray(LIVE_UNAVAIL) ? LIVE_UNAVAIL : []).forEach(u => {
    const t = u && u.until ? Date.parse(u.until) : NaN;
    if (t > now && (!min || t < min)) min = t;
  });
  return min;
};
const liveOutSet = list => {
  const now = Date.now();
  return new Set((Array.isArray(list) ? list : []).filter(u => u && typeof u.slug === 'string' && (!u.until || Date.parse(u.until) > now)).map(u => u.slug));
};
// Returns true when any item's soldOut actually changed.
function applyLiveAvail(unavailable) {
  const prev = LIVE_OUT;
  const next = liveOutSet(unavailable);
  LIVE_UNAVAIL = Array.isArray(unavailable) ? unavailable : [];
  if (prev.size === next.size && [...next].every(s => prev.has(s))) return false;
  LIVE_OUT = next;
  let changed = false;
  MENU.forEach(sec => {
    sec.items = (sec.items || []).map(it => {
      const out = BASE_SOLD_OUT.has(it.id) || next.has(it.id);
      if (!!it.soldOut === out) return it;
      changed = true;
      return {
        ...it,
        soldOut: out
      };
    });
  });
  return changed;
}
// Pause is live only while `until` is in the future (the ERP says the same).
const livePaused = st => {
  const p = st && st.paused;
  return p && typeof p.until === 'string' && Date.parse(p.until) > Date.now() ? {
    until: p.until,
    reason: p.reason || ''
  } : null;
};
// Seed from SSR BEFORE the first render, so loadStoredCart's ?cart= filter and
// the mount-time cart cleanup already see today's live list.
try {
  if (typeof window !== 'undefined' && window.__AVAIL__) applyLiveAvail(window.__AVAIL__.unavailable);
} catch (e) {}
// Dubai wall-clock for an ISO time, in the site's time voice: Arabic 12-hour
// with ص/م (Western digits), Farsi 24-hour in Persian digits, English 24-hour —
// the same formats the checkout's slot picker uses.
function dubaiClock(L, iso) {
  try {
    const p = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(iso));
    const h = Number((p.find(x => x.type === 'hour') || {}).value),
      m = (p.find(x => x.type === 'minute') || {}).value;
    if (L === 'ar') return (h % 12 || 12) + ':' + m + (h < 12 ? ' ص' : ' م');
    return numLocal(L, String(h).padStart(2, '0') + ':' + m);
  } catch (e) {
    return '';
  }
}
// Slim, non-modal pause line (menu top + checkout). The time is isolated so it
// never reorders inside the sentence: Farsi's all-digit "۰۷:۳۰" is forced LTR;
// Arabic "7:30 م" is only isolated (auto direction keeps م after the digits).
function PausedNote({
  paused
}) {
  const L = useLang();
  if (!paused) return null;
  const [a, b] = tr(L, 'avail.paused').split('{t}');
  return /*#__PURE__*/React.createElement("div", {
    className: "avail-paused " + SCR(L),
    role: "status"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\u23F8"), " ", /*#__PURE__*/React.createElement("span", null, a, /*#__PURE__*/React.createElement("bdi", {
    dir: L === 'fa' ? 'ltr' : undefined,
    className: L === 'fa' ? 'ltr-num' : undefined,
    style: {
      whiteSpace: 'nowrap'
    }
  }, dubaiClock(L, paused.until)), b));
}

// ── Phone Back button closes the top layer ──────────────────────────────────
// Every full-screen layer (item sheet, cart sheet, checkout, area sheet, the
// drawer) owns one history entry {sfLayer, sfDepth}. Back pops the top layer
// instead of leaving the site; closing a layer from the UI (✕, backdrop, Add)
// removes its entry again. History ops are reconciled once per tick against
// history.state.sfDepth, so a same-tick swap (cart sheet → checkout) touches
// nothing and nested layers unwind one at a time. We never go further back
// than our own entries, and never while the page is navigating away (the
// payment redirect never closes checkout, so it never reaches history.back).
const BACK_LAYERS = [];
// True while CheckoutModal is submitting (order being created → bank redirect).
let CO_SUBMITTING = false;
let backPending = false,
  backPendingTimer = 0,
  backTimer = 0,
  backLeaving = false;
const backDepth = () => {
  try {
    const s = window.history.state;
    return (s && s.sfDepth) | 0;
  } catch (e) {
    return 0;
  }
};
function backSync() {
  backTimer = 0;
  if (backPending || backLeaving) return;
  const want = BACK_LAYERS.length,
    have = backDepth();
  try {
    if (want > have) {
      for (let d = have + 1; d <= want; d++) window.history.pushState({
        sfLayer: BACK_LAYERS[d - 1].name,
        sfDepth: d
      }, '');
    } else if (want < have) {
      backPending = true;
      // Safety valve: if the browser swallows the traversal, don't wedge.
      clearTimeout(backPendingTimer);
      backPendingTimer = setTimeout(() => {
        backPending = false;
      }, 1500);
      window.history.go(want - have);
    }
  } catch (e) {
    backPending = false;
  }
}
const backSchedule = () => {
  if (!backTimer) backTimer = setTimeout(backSync, 0);
};
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (backPending) {
      backPending = false;
      clearTimeout(backPendingTimer);
      backSchedule();
      return;
    }
    // The customer pressed Back: close every layer above the entry we landed on.
    const have = backDepth();
    // …unless one of them is holding (checkout mid-payment: the order is being
    // created / the bank page is loading). Closing then would strand the
    // submit — ignore this Back and re-push the layer entries instead.
    if (BACK_LAYERS.slice(have).some(l => l.hold && l.hold())) {
      backSchedule();
      return;
    }
    while (BACK_LAYERS.length > have) {
      const top = BACK_LAYERS.pop();
      try {
        top.close();
      } catch (e) {}
    }
    backSchedule();
  });
  // A real link out (drawer → /orders etc.) closes its layer on the way; a
  // history.go() racing that navigation could cancel it — so freeze syncing.
  document.addEventListener('click', e => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target && a.target !== '_self') return;
    const href = a.getAttribute('href') || '';
    if (/^(#|tel:|mailto:|javascript:)/i.test(href)) return;
    backLeaving = true;
    setTimeout(() => {
      backLeaving = false;
    }, 3000); // navigation cancelled → resume
  }, true);
}
// Register a layer while `open` is true. onClose must close it in the UI.
// Optional `hold()`: while it returns true, Back does not close this layer.
function useBackLayer(open, name, onClose, hold) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const holdRef = useRef(hold);
  holdRef.current = hold;
  useEffect(() => {
    if (!open) return;
    const entry = {
      name,
      close: () => {
        if (closeRef.current) closeRef.current();
      },
      hold: () => !!(holdRef.current && holdRef.current())
    };
    BACK_LAYERS.push(entry);
    backSchedule();
    return () => {
      const i = BACK_LAYERS.indexOf(entry);
      if (i >= 0) {
        BACK_LAYERS.splice(i, 1);
        backSchedule();
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ── Dialog keyboard/focus behaviour (Phase 3 · C8) ─────────────────────────
// Every overlay (item sheet, cart sheet, checkout, area sheet, drawer, welcome
// card) registers here while open, in opening order. Escape closes the TOP
// one through its normal close path (so useBackLayer's cleanup still unwinds
// the history entry) — never while it holds (checkout mid-submit). Focus moves
// into the dialog when it opens, Tab/Shift+Tab cycle inside it, and focus goes
// back to the element that opened it when it closes. `noFocus` layers (the
// area sheet, whose own input already has focus) only take Escape + Tab.
const DIALOG_STACK = [];
const DIALOG_FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
function dialogFocusables(root) {
  return Array.prototype.filter.call(root.querySelectorAll(DIALOG_FOCUSABLE), el => {
    if (el.getClientRects().length === 0) return false; // display:none / closed <details>
    const h = el.closest('[aria-hidden="true"]');
    return !h || !root.contains(h) || h === root;
  });
}
function focusSoft(el) {
  if (!el || typeof el.focus !== 'function') return false;
  try {
    el.focus({
      preventScroll: true
    });
  } catch (e) {
    try {
      el.focus();
    } catch (e2) {
      return false;
    }
  }
  return document.activeElement === el;
}
function useDialog(open, onClose, opts) {
  const o = opts || {};
  const own = useRef(null);
  const ref = o.ref || own;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const holdRef = useRef(o.hold);
  holdRef.current = o.hold;
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    const entry = {
      ref,
      noFocus: !!o.noFocus,
      el: ref.current,
      close: () => {
        if (closeRef.current) closeRef.current();
      },
      hold: () => !!(holdRef.current && holdRef.current())
    };
    DIALOG_STACK.push(entry);
    const timers = [];
    if (!entry.noFocus) {
      // Twice: the drawer/cart sheet slide in (visibility flips with the
      // transition), so a first attempt can land on a not-yet-visible panel.
      const into = () => {
        const el = ref.current;
        if (el && !el.contains(document.activeElement)) focusSoft(el);
      };
      timers.push(setTimeout(into, 0), setTimeout(into, 320));
    }
    return () => {
      timers.forEach(clearTimeout);
      const i = DIALOG_STACK.indexOf(entry);
      if (i >= 0) DIALOG_STACK.splice(i, 1);
      if (entry.noFocus) return;
      const dlg = entry.el || ref.current;
      setTimeout(() => {
        const a = document.activeElement;
        const lost = !a || a === document.body || !document.contains(a) || dlg && dlg.contains(a);
        if (!lost) return;
        const top = DIALOG_STACK[DIALOG_STACK.length - 1];
        const topEl = top && !top.noFocus ? top.ref.current : null;
        if (opener && opener !== document.body && document.contains(opener) && (!topEl || topEl.contains(opener)) && focusSoft(opener)) return;
        if (topEl) focusSoft(topEl);
      }, 0);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}
if (typeof window !== 'undefined') {
  document.addEventListener('keydown', e => {
    if (!DIALOG_STACK.length) return;
    const top = DIALOG_STACK[DIALOG_STACK.length - 1];
    if (e.key === 'Escape' || e.key === 'Esc') {
      // A widget inside already used this Escape (e.g. the area dropdown).
      if (e.defaultPrevented || e.isComposing) return;
      e.preventDefault();
      if (!top.hold()) top.close();
      return;
    }
    if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
    const root = top.ref.current;
    if (!root) return;
    const list = dialogFocusables(root);
    const a = document.activeElement;
    if (!list.length) {
      e.preventDefault();
      focusSoft(root);
      return;
    }
    const first = list[0],
      last = list[list.length - 1];
    if (!root.contains(a) || a === root) {
      e.preventDefault();
      focusSoft(e.shiftKey ? last : first);
    } else if (e.shiftKey && a === first) {
      e.preventDefault();
      focusSoft(last);
    } else if (!e.shiftKey && a === last) {
      e.preventDefault();
      focusSoft(first);
    }
  });
}
function Nav({
  cartCount,
  onCartClick,
  onNav,
  onMenuOpen,
  lang,
  onLang
}) {
  const L = lang;
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  // Desktop nav links are SINGLE-LANGUAGE (the chosen one). The bilingual
  // pairing lives on in the drawer/menu — seven paired links plus the
  // switcher simply don't fit one bar; they wrapped into a mess.
  const link = key => /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, key));
  return /*#__PURE__*/React.createElement("nav", {
    className: "nav"
  }, /*#__PURE__*/React.createElement("div", {
    className: "nav-inner"
  }, /*#__PURE__*/React.createElement("div", {
    className: "brand",
    onClick: () => onNav("hero"),
    style: {
      cursor: "pointer"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "brand-logo",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("img", {
    src: "food-img/logo.jpg",
    alt: "",
    onError: e => {
      e.currentTarget.parentElement.style.display = 'none';
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "lockup"
  }, /*#__PURE__*/React.createElement("span", {
    className: "en"
  }, "Shaun the Sheep"), /*#__PURE__*/React.createElement("span", {
    className: "fa"
  }, L === 'ar' ? 'شون ذا شيب · جميرا' : 'شان د شیپ · جمیرا'))), /*#__PURE__*/React.createElement("div", {
    className: "nav-links"
  }, /*#__PURE__*/React.createElement("button", {
    className: "nav-link",
    onClick: () => onNav("order")
  }, link('nav.menu')), /*#__PURE__*/React.createElement("button", {
    className: "nav-link",
    onClick: () => onNav("platters")
  }, link('nav.platters')), /*#__PURE__*/React.createElement("button", {
    className: "nav-link",
    onClick: () => onNav("howmade")
  }, link('nav.howmade')), /*#__PURE__*/React.createElement("button", {
    className: "nav-link",
    onClick: () => onNav("findus")
  }, link('nav.findus')), /*#__PURE__*/React.createElement("a", {
    className: "nav-link",
    href: "/reservation"
  }, link('nav.reserve')), /*#__PURE__*/React.createElement("a", {
    className: "nav-link",
    href: "/orders"
  }, link('nav.orders')), /*#__PURE__*/React.createElement("a", {
    className: "nav-link",
    href: "/me"
  }, link('nav.account'))), /*#__PURE__*/React.createElement("div", {
    className: "nav-actions"
  }, /*#__PURE__*/React.createElement(LangSwitcher, {
    lang: L,
    onPick: onLang,
    compact: true
  }), /*#__PURE__*/React.createElement("a", {
    className: "nav-phone",
    href: `tel:${PHONE_TEL}`,
    "aria-label": tr(L, 'nav.call')
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'nav.call')), /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, PHONE_DISPLAY)), /*#__PURE__*/React.createElement("button", {
    className: "nav-cart",
    onClick: onCartClick,
    "aria-label": tr(L, 'a11y.cartCount').replace('{items}', itemsText(L, cartCount))
  }, /*#__PURE__*/React.createElement("span", {
    className: "nc-label " + P
  }, tr(L, 'nav.order')), /*#__PURE__*/React.createElement("span", {
    className: "badge"
  }, cartCount)), /*#__PURE__*/React.createElement("button", {
    className: "nav-burger",
    onClick: onMenuOpen,
    "aria-label": tr(L, 'a11y.openMenu')
  }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null)))));
}
function MobileMenu({
  open,
  onClose,
  onNav,
  lang,
  onLang
}) {
  const go = id => {
    onClose();
    setTimeout(() => onNav(id), 220);
  };
  const L = lang;
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const link = key => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, key)), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa-mini " + A
  }, tra(L, key)));
  const panelRef = useDialog(open, onClose);
  return /*#__PURE__*/React.createElement("div", {
    className: "m-menu" + (open ? " open" : ""),
    onClick: onClose,
    "aria-hidden": !open
  }, /*#__PURE__*/React.createElement("div", {
    className: "m-menu-panel",
    ref: panelRef,
    tabIndex: -1,
    onClick: e => e.stopPropagation(),
    role: "dialog",
    "aria-modal": open ? "true" : undefined,
    "aria-labelledby": "m-menu-title"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "m-menu-title",
    className: "sf-vh"
  }, tr(L, 'nav.menu')), /*#__PURE__*/React.createElement("button", {
    className: "m-menu-x",
    onClick: onClose,
    "aria-label": tr(L, 'a11y.closeMenu')
  }, "\u2715"), /*#__PURE__*/React.createElement("div", {
    className: "m-menu-brand"
  }, /*#__PURE__*/React.createElement("div", {
    className: "brand-logo",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("img", {
    src: "food-img/logo.jpg",
    alt: "",
    onError: e => {
      e.currentTarget.parentElement.style.display = 'none';
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "lockup"
  }, /*#__PURE__*/React.createElement("span", {
    className: "en"
  }, "Shaun the Sheep"), /*#__PURE__*/React.createElement("span", {
    className: "fa"
  }, L === 'ar' ? 'شون ذا شيب · جميرا' : 'شان د شیپ · جمیرا'))), /*#__PURE__*/React.createElement("div", {
    className: "m-menu-lang"
  }, /*#__PURE__*/React.createElement(LangSwitcher, {
    lang: L,
    onPick: onLang
  })), /*#__PURE__*/React.createElement("button", {
    className: "m-menu-link",
    onClick: () => go("order")
  }, link('nav.menu')), /*#__PURE__*/React.createElement("button", {
    className: "m-menu-link",
    onClick: () => go("platters")
  }, link('nav.platters')), /*#__PURE__*/React.createElement("button", {
    className: "m-menu-link",
    onClick: () => go("howmade")
  }, link('nav.howmade')), /*#__PURE__*/React.createElement("button", {
    className: "m-menu-link",
    onClick: () => go("findus")
  }, link('nav.findus')), /*#__PURE__*/React.createElement("a", {
    className: "m-menu-link",
    href: "/reservation",
    onClick: onClose
  }, link('nav.reserveTable')), /*#__PURE__*/React.createElement("a", {
    className: "m-menu-link",
    href: "/bread",
    onClick: onClose
  }, link('nav.bread')), /*#__PURE__*/React.createElement("a", {
    className: "m-menu-link",
    href: "/orders",
    onClick: onClose
  }, link('nav.orders')), /*#__PURE__*/React.createElement("a", {
    className: "m-menu-link",
    href: "/me",
    onClick: onClose
  }, link('nav.myAccount')), /*#__PURE__*/React.createElement("div", {
    className: "m-menu-foot"
  }, /*#__PURE__*/React.createElement("a", {
    className: "m-menu-phone",
    href: `tel:${PHONE_TEL}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'nav.call'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'nav.call'))), /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, PHONE_DISPLAY)))));
}
function MobileCartBar({
  cart,
  items,
  mode,
  deliveryArea,
  introPct,
  onOpen,
  onCheckout
}) {
  const L = useLang();
  const lines = cart.map(l => ({
    l,
    item: items.find(i => i.id === l.id)
  })).filter(x => x.item);
  const subtotal = lines.reduce((s, x) => s + x.item.price * x.l.qty, 0);
  const count = cart.reduce((s, l) => s + l.qty, 0);
  const itemsWord = L === 'en' ? count !== 1 ? 'items' : 'item' : tr(L, count !== 1 ? 'cart.items' : 'cart.item');
  // ONE story for the numbers: mirror OrderRail's exact formula (discount →
  // VAT → per-area fee) so the bar, the rail and the checkout modal never
  // disagree. When the fee isn't knowable yet, say so instead of lying.
  const promoAed = wcPromoDiscount(subtotal);
  const loyaltyAed = introPct > 0 ? Math.round(subtotal * introPct / 100) : 0;
  const discountedSubtotal = subtotal - Math.max(promoAed, loyaltyAed);
  const service = Math.round(discountedSubtotal * SERVICE_FEE_PCT / 100);
  const cartHasTestItem = lines.some(x => x.item.id === 'test-1aed');
  const area = findArea(deliveryArea);
  const areaFee = area ? areaFeeNow(area.fee) : null;
  const freeDelivery = mode === "delivery" && !cartHasTestItem && subtotal >= FREE_DELIVERY_ABOVE_AED;
  const delivery = mode === "delivery" && subtotal > 0 && !cartHasTestItem && areaFee != null && !freeDelivery ? areaFee : 0;
  const total = discountedSubtotal + service + delivery;
  const feeUnknown = mode === "delivery" && !cartHasTestItem && !freeDelivery && areaFee == null;
  return /*#__PURE__*/React.createElement("div", {
    className: "mobile-cart-bar" + (count > 0 ? " has-items" : "")
  }, /*#__PURE__*/React.createElement("div", {
    className: "mcb-info",
    role: "button",
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
    "aria-label": tr(L, 'cart.view')
  }, /*#__PURE__*/React.createElement("div", {
    className: "mcb-count " + SCR(L)
  }, L === 'en' ? count + ' ' + itemsWord : itemsText(L, count), " \xB7 ", tr(L, count > 0 ? 'cart.ready' : 'cart.empty')), /*#__PURE__*/React.createElement("div", {
    className: "mcb-total"
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, feeFmt(total), " AED"), feeUnknown && /*#__PURE__*/React.createElement("span", {
    className: "mcb-plus " + SCR(L)
  }, " ", tr(L, 'bar.plusDelivery')))), /*#__PURE__*/React.createElement("button", {
    className: "mcb-cta",
    onClick: onCheckout || onOpen
  }, /*#__PURE__*/React.createElement("span", {
    className: SCR(L)
  }, tr(L, 'rail.checkout')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + SCR(ACCENT[L])
  }, tra(L, 'rail.checkout'))));
}
function MobileCartSheet({
  open,
  onClose,
  cart,
  items,
  mode,
  setMode,
  deliveryArea,
  onAreaChange,
  onCheckout,
  onAddById,
  suggestions,
  onDismissSuggestion,
  introPct,
  when,
  onWhen,
  paused
}) {
  const L = useLang();
  const panelRef = useDialog(open, onClose);
  return /*#__PURE__*/React.createElement("div", {
    className: "m-cart" + (open ? " open" : ""),
    onClick: onClose,
    "aria-hidden": !open
  }, /*#__PURE__*/React.createElement("div", {
    className: "m-cart-panel",
    ref: panelRef,
    tabIndex: -1,
    onClick: e => e.stopPropagation(),
    role: "dialog",
    "aria-modal": open ? "true" : undefined,
    "aria-labelledby": "m-cart-title"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "m-cart-title",
    className: "sf-vh"
  }, tr(L, 'rail.yourOrder')), /*#__PURE__*/React.createElement("div", {
    className: "m-cart-grab"
  }), /*#__PURE__*/React.createElement(OrderRail, {
    cart: cart,
    items: items,
    mode: mode,
    setMode: setMode,
    deliveryArea: deliveryArea,
    onAddById: onAddById,
    suggestions: suggestions,
    onDismissSuggestion: onDismissSuggestion,
    onCheckout: onCheckout,
    stripActive: open,
    introPct: introPct,
    onDismiss: onClose,
    onAreaChange: onAreaChange,
    when: when,
    onWhen: onWhen,
    paused: paused
  })));
}
function Hero({
  onOrderClick,
  minPlatter,
  deliveryArea
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  // K6: the generic range until we know where they are — then that area's band
  // (the remembered area first, else the newest saved delivery address).
  const heroArea = findArea(deliveryArea) || (() => {
    const s = loadSavedAddrs();
    return s.length ? findArea(s[0].area) : null;
  })();
  const heroMins = lang => heroArea ? tr(lang, 'hero.minsTo').replace('{r}', bandRange(lang, bandForFee(heroArea.fee))).replace('{a}', areaName(lang, heroArea)) : bandRange(lang, DELIVERY_BAND_FALLBACK);
  return /*#__PURE__*/React.createElement("section", {
    className: "hero",
    id: "hero"
  }, /*#__PURE__*/React.createElement("div", {
    className: "hero-col-l"
  }, L !== 'en' && /*#__PURE__*/React.createElement("div", {
    className: "hero-welcome"
  }, /*#__PURE__*/React.createElement("span", {
    className: "hw-line",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "hw-text"
  }, tr(L, 'hero.welcome')), /*#__PURE__*/React.createElement("span", {
    className: "hw-line",
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement("div", {
    className: "hero-tagline"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'hero.tagline')), /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'hero.tagline'))), /*#__PURE__*/React.createElement("p", {
    className: "hero-plain " + P
  }, (() => {
    const s = tr(L, 'hero.plain');
    // Every language puts the platter clause last, after " · ".
    return minPlatter != null ? s.replace('{p}', minPlatter) /* prices stay Western, as before */ : s.slice(0, s.lastIndexOf(' · '));
  })()), /*#__PURE__*/React.createElement("h1", {
    className: P
  }, tr(L, 'hero.h1.l1'), /*#__PURE__*/React.createElement("br", null), tr(L, 'hero.h1.l2'), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
    className: "light"
  }, tr(L, 'hero.h1.l3'))), /*#__PURE__*/React.createElement("div", {
    className: "hero-fa-h " + (isLatin(L) ? A : P)
  }, isLatin(L) ? tra(L, 'hero.sub') : tr(L, 'hero.sub')), /*#__PURE__*/React.createElement("div", {
    className: "hero-meta"
  }, /*#__PURE__*/React.createElement("div", {
    className: "is-open"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wax-seal",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "wax-num"
  }, "24/7"), /*#__PURE__*/React.createElement("span", {
    className: "wax-sub " + P
  }, tr(L, 'hero.always'))), /*#__PURE__*/React.createElement("div", {
    className: "hours-text"
  }, /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'hero.hours')), /*#__PURE__*/React.createElement("div", {
    className: "val " + P
  }, tr(L, 'hero.dayNight'), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'hero.dayNight'))))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'hero.delivery')), /*#__PURE__*/React.createElement("div", {
    className: "val " + P
  }, heroMins(L), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, heroMins(ACCENT[L])))), REVIEWS && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'hero.google')), /*#__PURE__*/React.createElement("div", {
    className: "val " + P
  }, /*#__PURE__*/React.createElement("span", {
    className: "num",
    style: {
      color: 'var(--gold, #b8860b)'
    }
  }, "\u2605 ", numLocal(L, REVIEWS.rating)), " \xB7 ", tr(L, 'hero.reviews').replace('{n}', numLocal(L, REVIEWS.count)), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'hero.reviews').replace('{n}', numLocal(ACCENT[L], REVIEWS.count))))), /*#__PURE__*/React.createElement("div", {
    className: "hero-halal"
  }, /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'hero.meat')), /*#__PURE__*/React.createElement("div", {
    className: "val " + P
  }, /*#__PURE__*/React.createElement("span", {
    className: "halal-mark",
    "aria-hidden": "true"
  }, "\u2713"), " ", tr(L, 'hero.halal'), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'hero.halal'))))), /*#__PURE__*/React.createElement("div", {
    className: "hero-cta-row"
  }, /*#__PURE__*/React.createElement("button", {
    className: "btn-primary",
    onClick: onOrderClick
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'hero.orderNow')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'hero.orderNow'))), /*#__PURE__*/React.createElement("a", {
    className: "btn-ghost",
    href: "/reservation"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'nav.reserveTable')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'nav.reserveTable'))), /*#__PURE__*/React.createElement("a", {
    className: "btn-ghost",
    href: `tel:${PHONE_TEL}`
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'nav.call'))))), /*#__PURE__*/React.createElement("div", {
    className: "hero-col-r"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sign-stage"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sign-bracket"
  }, /*#__PURE__*/React.createElement("div", {
    className: "top-bar"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pin l"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pin r"
  })), /*#__PURE__*/React.createElement("div", {
    className: "sign-rope l"
  }), /*#__PURE__*/React.createElement("div", {
    className: "sign-rope r"
  }), /*#__PURE__*/React.createElement("div", {
    className: "sign"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sign-board"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sign-inner"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sn-fa"
  }, L === 'ar' ? 'شون ذا شيب' : SIGN_FA), /*#__PURE__*/React.createElement("div", {
    className: "sn-orn"
  }, "\u2726 \xB7 \u2726 \xB7 \u2726"), /*#__PURE__*/React.createElement("div", {
    className: "sn-en"
  }, SIGN_EN)))))));
}
function StatusStrip() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(i);
  }, []);
  const now = new Date();
  // Asia/Dubai is UTC+4 year-round (no DST), so we derive Dubai time from UTC.
  const dubaiHour = (now.getUTCHours() + 4) % 24;
  const dubaiMin = now.getUTCMinutes();
  const clockStr = `${String(dubaiHour).padStart(2, '0')}:${String(dubaiMin).padStart(2, '0')}`;
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const msgKey = dubaiHour >= 4 && dubaiHour < 11 ? 'status.morning' : dubaiHour >= 11 && dubaiHour < 17 ? 'status.midday' : dubaiHour >= 17 && dubaiHour < 22 ? 'status.evening' : 'status.night';
  return /*#__PURE__*/React.createElement("div", {
    className: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: "status-inner"
  }, /*#__PURE__*/React.createElement("span", {
    className: "status-tag " + P
  }, tr(L, 'status.always'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'status.always'))), /*#__PURE__*/React.createElement("div", {
    className: "status-msg " + P
  }, tr(L, msgKey), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, msgKey))), /*#__PURE__*/React.createElement("div", {
    className: "status-stat"
  }, /*#__PURE__*/React.createElement("div", {
    className: "v " + P
  }, tr(L, 'status.always')), /*#__PURE__*/React.createElement("span", {
    className: "l " + P
  }, tr(L, 'status.simmering'))), /*#__PURE__*/React.createElement("div", {
    className: "status-stat"
  }, /*#__PURE__*/React.createElement("div", {
    className: "v"
  }, clockStr), /*#__PURE__*/React.createElement("span", {
    className: "l " + P
  }, tr(L, 'status.city'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'status.city')))), /*#__PURE__*/React.createElement("div", {
    className: "status-stat"
  }, /*#__PURE__*/React.createElement("div", {
    className: "v " + P
  }, tr(L, 'status.amber')), /*#__PURE__*/React.createElement("span", {
    className: "l " + P
  }, tr(L, 'status.brothColor')))));
}
function SecHead({
  num,
  section,
  ornament
}) {
  // Primary title in the chosen language, accent title in the complement.
  const L = useLang();
  return /*#__PURE__*/React.createElement("div", {
    className: "sec-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "num"
  }, num), /*#__PURE__*/React.createElement("span", {
    className: "t-en " + SCR(L)
  }, nm(L, section)), /*#__PURE__*/React.createElement("span", {
    className: "ornament"
  }, "\u2014 ", ornament, " \u2014"), /*#__PURE__*/React.createElement("span", {
    className: "t-fa " + SCR(ACCENT[L])
  }, nmA(L, section)));
}

// Hand-inked SVG illustrations, one shape per dish-family.
// Single ink colour (currentColor) so they pick up the row's text colour
// and warm on hover — like ink on parchment, not photos.
const ITEM_ICONS = {
  // Stew bowl with steam — for every head/trotter component served by the bowl
  bowl: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22 14 c -2 3 -2 6 0 8",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M30 11 c -2 3 -2 7 0 9",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M38 14 c -2 3 -2 6 0 8",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "30",
    cy: "28",
    rx: "20",
    ry: "4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10 28 c 0 14 8 16 20 16 c 12 0 20 -2 20 -16"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 36 c 4 2 24 2 28 0",
    opacity: "0.45"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "6",
    y1: "46",
    x2: "54",
    y2: "46",
    opacity: "0.4"
  })),
  // Sangak flatbread — long oval, seed dots, dough creases
  bread: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M8 30 c 0 -8 8 -12 22 -12 c 14 0 22 4 22 12 c 0 8 -8 12 -22 12 c -14 0 -22 -4 -22 -12 z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "20",
    cy: "26",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "28",
    cy: "24",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "36",
    cy: "26",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "44",
    cy: "28",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "22",
    cy: "32",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "32",
    cy: "34",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "40",
    cy: "32",
    r: "0.9",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 30 c 8 -2 14 -2 20 0",
    opacity: "0.4"
  })),
  // Pickle jar with lid + contents
  jar: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "22",
    y: "9",
    width: "16",
    height: "4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20 13 h 20 v 4 h -20 z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22 17 c 0 2 -1 3 -2 4 c -2 1 -2 4 -2 6 v 14 c 0 5 4 7 12 7 c 8 0 12 -2 12 -7 v -14 c 0 -2 0 -5 -2 -6 c -1 -1 -2 -2 -2 -4"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "20",
    y1: "33",
    x2: "40",
    y2: "33",
    opacity: "0.4"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "26",
    cy: "38",
    r: "2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "34",
    cy: "40",
    r: "2"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "30",
    cy: "44",
    r: "1.5"
  })),
  // Tall glass — water, soda, lemonade, doogh
  glass: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M21 12 l 2 36 c 0 4 3 4 7 4 c 4 0 7 0 7 -4 l 2 -36 z"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "21",
    y1: "12",
    x2: "39",
    y2: "12"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22 22 c 6 1 10 1 16 0",
    opacity: "0.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23 30 c 5 1 9 1 14 0",
    opacity: "0.3"
  })),
  // Tea cup on saucer with steam
  cup: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M26 14 c -2 -3 -2 -6 0 -8",
    strokeDasharray: "2 2",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M34 14 c -2 -3 -2 -6 0 -8",
    strokeDasharray: "2 2",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14 18 v 18 c 0 6 6 8 16 8 c 10 0 16 -2 16 -8 v -18 z"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "14",
    y1: "18",
    x2: "46",
    y2: "18"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M46 24 c 6 0 6 4 6 6 c 0 3 -2 6 -6 7"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "30",
    cy: "48",
    rx: "18",
    ry: "2.5"
  })),
  // Teapot with spout, handle, lid knob, rising steam
  teapot: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M30 6 c -2 3 -2 5 0 8",
    strokeDasharray: "2 2",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "30",
    y1: "14",
    x2: "30",
    y2: "18"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "30",
    cy: "13",
    r: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 28 c 0 -6 4 -10 14 -10 c 10 0 14 4 14 10 v 12 c 0 6 -6 8 -14 8 c -8 0 -14 -2 -14 -8 z"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "16",
    y1: "28",
    x2: "44",
    y2: "28"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 30 c -6 0 -8 -2 -8 -5 c 0 -2 4 -2 8 -1"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M44 28 c 6 2 8 4 8 8 c 0 4 -2 6 -4 7"
  })),
  // Bell pepper (capsicum) with stem
  capsicum: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "28",
    y: "8",
    width: "4",
    height: "6",
    rx: "1"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M30 14 c -16 0 -16 6 -16 18 c 0 12 4 16 16 16 c 12 0 16 -4 16 -16 c 0 -12 0 -18 -16 -18 z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22 18 c -2 12 0 22 4 28",
    opacity: "0.4"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "30",
    y1: "14",
    x2: "30",
    y2: "48",
    opacity: "0.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M38 18 c 2 12 0 22 -4 28",
    opacity: "0.4"
  })),
  // Chili pepper with curled stem
  pepper: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M28 10 c -2 4 0 8 4 8 c 4 0 6 -3 6 -6"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "28",
    y1: "10",
    x2: "32",
    y2: "6"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M32 18 c -10 4 -14 14 -10 26 c 4 10 16 12 22 4 c 6 -8 0 -22 -8 -28 c -2 -2 -4 -2 -4 -2 z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M28 24 c 0 8 2 14 6 22",
    opacity: "0.35"
  })),
  // Onion bulb with sprout leaves and root
  onion: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M27 16 c -2 -6 -3 -10 -5 -12"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M30 16 v -10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M33 16 c 2 -6 3 -10 5 -12"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14 30 c 0 -10 6 -16 16 -16 c 10 0 16 6 16 16 c 0 14 -6 18 -16 18 c -10 0 -16 -4 -16 -18 z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M22 30 c 6 -2 10 -2 16 0",
    opacity: "0.35"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20 38 c 8 -2 12 -2 20 0",
    opacity: "0.35"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M30 48 v 4 m -2 -2 l 2 2 l 2 -2",
    opacity: "0.45"
  })),
  // Frying pan with omelette
  pan: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M48 32 l 8 -2 v 4 l -8 2"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "26",
    cy: "32",
    rx: "22",
    ry: "4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 32 c 0 8 8 10 22 10 c 14 0 22 -2 22 -10"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "26",
    cy: "30",
    rx: "14",
    ry: "2.4",
    opacity: "0.35"
  })),
  // Large platter / feast spread — a wide oval plate carrying multiple
  // small bowls + a piece of bread, with steam rising from the centre.
  platter: /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 60 60",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22 12 c -2 3 -2 6 0 8",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M30 9 c -2 3 -2 7 0 9",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M38 12 c -2 3 -2 6 0 8",
    strokeDasharray: "2 3",
    opacity: "0.55"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "30",
    cy: "26",
    rx: "11",
    ry: "2.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M19 26 c 0 6 4 8 11 8 c 7 0 11 -2 11 -8"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "10",
    cy: "34",
    rx: "5",
    ry: "2.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "9",
    cy: "33",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "35",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "50",
    cy: "32",
    rx: "4",
    ry: "1.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M46 32 c 0 3 2 4 4 4 c 2 0 4 -1 4 -4"
  }), /*#__PURE__*/React.createElement("ellipse", {
    cx: "30",
    cy: "42",
    rx: "26",
    ry: "4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 42 c 0 4 4 6 26 6 c 22 0 26 -2 26 -6"
  }), /*#__PURE__*/React.createElement("line", {
    x1: "6",
    y1: "50",
    x2: "54",
    y2: "50",
    opacity: "0.35"
  }))
};
const ITEM_ICON_MAP = {
  // Bowl items — head & trotter components served from the pot
  eye: 'bowl',
  brain: 'bowl',
  meat: 'bowl',
  tongue: 'bowl',
  trotters: 'bowl',
  tripe: 'bowl',
  cheek: 'bowl',
  'brain-soup': 'bowl',
  soup: 'bowl',
  mojoon: 'bowl',
  // Bread
  sangak: 'bread',
  'sangak-x': 'bread',
  'bread-add': 'bread',
  // Jarred pickles
  'torshi-mix': 'jar',
  'torshi-seer': 'jar',
  'chili-pic': 'jar',
  // Cold drinks (glass)
  water: 'glass',
  soda: 'glass',
  lemonade: 'glass',
  doogh: 'glass',
  // Tea
  tea: 'cup',
  'tea-black': 'cup',
  'tea-pot': 'teapot',
  // Vegetables
  capsicum: 'capsicum',
  chili: 'pepper',
  onion: 'onion',
  // Pan
  omelette: 'pan'
};

// ── Category-level icon fallback for the hero grid when no photo is set ────
// Maps each category slug to one of the keys in ITEM_ICONS. Used by CategoryGrid
// when Directus has no category_photo for that category.
const CATEGORY_ICONS = {
  mains: 'bowl',
  platters: 'platter',
  sides: 'bread',
  drinks: 'glass',
  breakfast: 'pan',
  addons: 'onion'
};

// Editorial taglines — short bilingual line per category (used by older
// thumb layouts; kept here in case the hero ever wants a per-card tagline).
const CATEGORY_TAGLINES = {
  mains: {
    en: 'From the pot, item by item',
    fa: 'از قابلمه، تک به تک'
  },
  platters: {
    en: 'Feasts for the whole sofreh',
    fa: 'ضیافت برای سفره'
  },
  sides: {
    en: 'Bread, pickle, salt',
    fa: 'نان، ترشی، نمک'
  },
  drinks: {
    en: 'Cold glass, hot kettle',
    fa: 'لیوان سرد، قوری گرم'
  },
  breakfast: {
    en: 'Daybreak at the steam pot',
    fa: 'سحرگاه با بخار'
  },
  addons: {
    en: 'The table trio & extras',
    fa: 'اضافات سر سفره'
  }
};

// ── V8 "Tonight's Service" cinematic-hero data ─────────────────────────
// One curated hero blurb per category. EN-only by design — Persian voice
// is honoured by the FA title at large size; forcing FA prose bloats the
// hero and dilutes the cinematic moment.
const CATEGORY_BLURBS = {
  mains: "Ten variations on one ritual. The bowl arrives steaming. You choose what's inside it.",
  platters: "Sofreh spreads, set down once and shared by all hands at the table.",
  sides: "Sangak hot off the stone. Torshi aged seven years. Salt and patience.",
  drinks: "Tea hot from the samovar. Doogh cool with mint. What you drink between bites.",
  breakfast: "The kalle pache house opens at dawn. Omelette, kettle, the first light.",
  addons: "Capsicum, onion, fresh chili. The trio that lives in the middle of every sofreh."
};

// Curated hero photo per category — by item slug. The component falls back
// to the first item-with-photo if the named slug isn't found, then to the
// embossed-numeral treatment if no items have photos at all.
const CATEGORY_HERO_PICKS = {
  mains: 'brain',
  platters: 'dish-3',
  sides: 'sangak'
  // 4-6 intentionally null — embossed-numeral panels.
};

// Two-line title splits — only set for titles where breaking onto two lines
// reads stronger than wrapping naturally.
const CATEGORY_TITLE_SPLITS = {
  mains: ['SINGLES', 'BY THE BOWL'],
  platters: ['PLATTERS', '& COMMUNAL DISHES'],
  sides: ['BREAD,', 'PICKLES & SIDES'],
  drinks: ['FROM THE', 'GLASSWARE'],
  breakfast: ['DAYBREAK', 'SERVICE'],
  addons: ['EXTRAS', 'AT THE TABLE']
};

// Bilingual chapter titles in FA. The page's natural fa labels are good,
// but the hero is a chance for a more poetic / chapter-style FA title.
const CATEGORY_HERO_FA = {
  mains: 'تک‌نفره، در کاسه',
  platters: 'دیس‌ها و پلاترها',
  sides: 'نان، ترشی، مخلفات',
  drinks: 'از شیشه‌آلات',
  breakfast: 'سحرگاهانه',
  addons: 'مخلفات سر سفره'
};

// Plain Roman numerals (no trailing dot) for the hero.
const CATEGORY_ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

// Per-category visual identity — STRONG colors (not subtle tints) and a
// deep ink colour that the SVG illustration adopts via currentColor. The
// goal: each card is visually distinct enough that you can scan the grid
// from across the room and know what's what. These are still warm, earthy
// tones that sit on the cream paper palette.
const CATEGORY_TINTS = {
  // [bg = wash behind icon, ink = solid icon stroke colour, accent = bottom bar]
  mains: {
    bg: '#f1d6c9',
    ink: '#7a1d18',
    accent: '#a93026',
    hue: 'crimson'
  },
  platters: {
    bg: '#f1deba',
    ink: '#7a5413',
    accent: '#b07a1f',
    hue: 'ochre'
  },
  sides: {
    bg: '#d8e2c4',
    ink: '#3a5a2a',
    accent: '#5e8a44',
    hue: 'olive'
  },
  drinks: {
    bg: '#cee0e3',
    ink: '#2c5868',
    accent: '#4a818f',
    hue: 'teal'
  },
  breakfast: {
    bg: '#f7e5c2',
    ink: '#915e15',
    accent: '#c98a25',
    hue: 'amber'
  },
  addons: {
    bg: '#e3d8c1',
    ink: '#544326',
    accent: '#7d6537',
    hue: 'oat'
  }
};

// ── Suggestion engine ──────────────────────────────────────────────────────
// Static pairing rules keyed by item KIND (see ITEM_ICON_MAP). Each rule:
//   "if the customer's last-added item is of kind <when>, suggest from <then>
//    (top-first), filtering out already-in-cart, sold-out and dismissed."
// Lives close to ITEM_ICON_MAP so they evolve together.
const SUGGEST_RULES = [{
  when: 'bowl',
  then: ['sangak', 'torshi-mix', 'doogh']
}, {
  when: 'bread',
  then: ['torshi-seer', 'doogh']
}, {
  when: 'pan',
  then: ['sangak', 'doogh']
}, {
  when: 'jar',
  then: ['sangak']
}, {
  when: 'capsicum',
  then: ['onion', 'chili']
}, {
  when: 'onion',
  then: ['capsicum', 'chili']
}, {
  when: 'pepper',
  then: ['onion', 'capsicum']
}];

// Soups a platter already includes (2026-10-24: the Khan's Platter holds 6,
// each combo its own count). Read from the live English description
// ("… · 6 soups") so a Directus edit wins; the constant covers a description
// without the count. 0 for anything that is not a platter.
// cartSoupsIn = Σ qty × soupsIn over the cart's lines.
const SOUPS_INCLUDED = {
  'dish-6': 6,
  'single-dish-soup-eye': 1,
  'double-dish-2-soups': 2,
  'full-dish-3-soups': 3
};
const soupsIn = item => {
  if (!item || !PLATTER_IDS.has(item.id)) return 0;
  const m = /(\d+)\s+soups?\b/i.exec(String(item.desc || ''));
  return m ? +m[1] : SOUPS_INCLUDED[item.id] || 0;
};
const cartSoupsIn = (cart, items) => (cart || []).reduce((s, l) => {
  const it = l && l.qty > 0 ? (items || []).find(i => i.id === l.id) : null;
  return s + (it ? Math.floor(Number(l.qty) || 0) * soupsIn(it) : 0);
}, 0);

// Per-item overrides — used in place of kind-based rules when present.
// Platters include bread (owner 2026-10-02); the plain ones no broth — lead
// with soup. The Khan's Platter and the three combos include their soups
// (soupsIn) — never soup for them.
const SUGGEST_OVERRIDES = {
  mojoon: ['sangak', 'torshi-seer', 'doogh'],
  'dish-1': ['soup', 'doogh', 'torshi-seer'],
  'dish-2': ['soup', 'doogh', 'torshi-seer'],
  'dish-3': ['soup', 'doogh', 'torshi-seer'],
  'dish-6': ['doogh', 'lemonade', 'torshi-seer'],
  'single-dish-soup-eye': ['doogh', 'lemonade', 'torshi-seer'],
  'double-dish-2-soups': ['doogh', 'lemonade', 'torshi-seer'],
  'full-dish-3-soups': ['doogh', 'lemonade', 'torshi-seer']
};

// Items that physically can't be sent by a delivery rider (served hot at the
// table only). They stay orderable for dine-in but are filtered out of the
// pairing suggestions AND blocked from delivery carts. The kettle of tea
// (`tea-pot`, قوری چای) is the first — it can't survive a delivery trip.
const DINE_IN_ONLY = new Set(['tea-pot']);

// Returns up to N item objects suggested for the customer's current cart.
// Pure function — no side effects, safe to wrap in useMemo.
//   cart           : Array<{id, qty}>
//   lastAddedId    : string | null (drives "inline ribbon for this row")
//   allItems       : the flattened MENU items array
//   dismissedIds   : Set<string> of slugs the customer dismissed this session
//   limit          : max number of suggestions to return (default 3)
function getSuggestions(cart, lastAddedId, allItems, dismissedIds, limit) {
  const max = typeof limit === 'number' ? limit : 3;
  if (!Array.isArray(cart) || !Array.isArray(allItems)) return [];
  const inCart = new Set(cart.filter(l => l.qty > 0).map(l => l.id));
  if (inCart.size === 0 && !lastAddedId) return [];

  // Pick the "anchor" item — what the customer most recently expressed
  // interest in. Falls back to the last item in the cart.
  const anchorId = lastAddedId || (cart.length ? cart[cart.length - 1].id : null);
  if (!anchorId) return [];

  // Pick a rule: per-item override beats kind-based.
  let candidateSlugs;
  if (SUGGEST_OVERRIDES[anchorId]) {
    candidateSlugs = SUGGEST_OVERRIDES[anchorId];
  } else {
    const kind = ITEM_ICON_MAP[anchorId];
    const rule = SUGGEST_RULES.find(r => r.when === kind);
    candidateSlugs = rule ? rule.then : [];
  }
  if (!candidateSlugs.length) return [];

  // Resolve slugs → items, filter out anchor / in-cart / sold-out / dismissed.
  const dismissed = dismissedIds instanceof Set ? dismissedIds : new Set();
  // Same rule as resolveErpSuggestions: a platter in the cart that already
  // includes soups (soupsIn) → no soup / brain soup.
  const soupInCart = cartSoupsIn(cart, allItems) > 0;
  const out = [];
  for (const slug of candidateSlugs) {
    if (slug === anchorId) continue;
    if (inCart.has(slug)) continue;
    if (dismissed.has(slug)) continue;
    if (DINE_IN_ONLY.has(slug)) continue; // never suggest a non-deliverable item
    if (soupInCart && BROTH_SLUGS.has(slug)) continue;
    const item = allItems.find(i => i.id === slug);
    if (!item) continue;
    if (item.soldOut) continue;
    out.push(item);
    if (out.length >= max) break;
  }
  return out;
}

// ── ERP-driven upsell intelligence (window.SFUpsell / suggestions.js) ───────
// SFUpsell fetches real "bought together" data through the same-origin proxy
// /api/upsell/suggestions (ERP is the brain). This resolver turns that data
// into menu-item objects for the SAME surfaces the static engine feeds,
// applying the SAME safety filters (in-cart / dismissed / sold-out /
// non-deliverable), plus cart GAP DETECTION: a missing drink/side/tea slot is
// filled first — the ERP ranking decides WHICH item fills it. Returns [] when
// there is no ERP data, and callers fall back to the static getSuggestions
// rules — so a missing/down endpoint is invisible to the customer.
const ITEM_SECTION = {};
try {
  MENU.forEach(sec => (sec.items || []).forEach(it => {
    ITEM_SECTION[it.id] = sec.id;
  }));
} catch (e) {}
const TEA_SLUGS = new Set(['tea', 'tea-black', 'tea-pot']);
const MAIN_SECTIONS = new Set(['mains', 'main-dishes', 'platters']);
const BROTH_SLUGS = new Set(['soup', 'brain-soup']);
function resolveErpSuggestions(erpSugg, cart, allItems, dismissedIds, limit) {
  if (!Array.isArray(erpSugg) || erpSugg.length === 0) return [];
  const max = typeof limit === 'number' ? limit : 3;
  const inCart = new Set((cart || []).filter(l => l.qty > 0).map(l => l.id));
  const dismissed = dismissedIds instanceof Set ? dismissedIds : new Set();
  const cartHasPlatter = [...inCart].some(id => ITEM_SECTION[id] === 'platters');
  const cartHasSoups = cartSoupsIn(cart, allItems) > 0;

  // Candidates in ERP-rank order, carrying pairedCount for social proof.
  // Water is skipped for every cart (1,941 shows → 1 sale, 2026-10). With a
  // platter in the cart, black tea is dropped when the ERP also offers tea.
  // A platter that already includes soups (the Khan's Platter, the combos;
  // soupsIn) drops soup / brain soup — the ERP ranks Soup first for dish-6.
  const cands = [];
  for (const s of erpSugg) {
    if (!s || typeof s.slug !== 'string') continue;
    if (inCart.has(s.slug) || dismissed.has(s.slug) || DINE_IN_ONLY.has(s.slug)) continue;
    if (s.slug === 'water') continue;
    if (cartHasSoups && BROTH_SLUGS.has(s.slug)) continue;
    if (cartHasPlatter && s.slug === 'tea-black' && erpSugg.some(x => x && x.slug === 'tea')) continue;
    const item = allItems.find(i => i.id === s.slug);
    if (!item || item.soldOut) continue;
    if (cands.some(c => c.id === item.id)) continue;
    cands.push({
      ...item,
      _pc: Number(s.pairedCount) || 0,
      _kind: s.kind === 'addon' ? 'addon' : 'pairing'
    });
  }
  if (!cands.length) return [];

  // Gap detection — what is missing from the meal?
  const cartSections = new Set();
  let cartHasTea = false,
    cartHasMain = false;
  inCart.forEach(id => {
    const sec = ITEM_SECTION[id];
    if (sec) cartSections.add(sec);
    if (TEA_SLUGS.has(id)) cartHasTea = true;
    if (MAIN_SECTIONS.has(sec)) cartHasMain = true;
  });
  // A platter is already a complete meal: every dish-1..6 lists the mains AND
  // the bread in its own description (dish-2 = "Lamb cheek · 2 trotters · half
  // a brain · tongue · 1 eye · a whole bread"). Recommending trotters to
  // someone who just bought a platter containing two of them is noise, and it
  // was 436 of 2,854 impressions — 15% of everything the system has ever
  // shown. With a platter in the cart, drop the mains entirely and treat the
  // bread gap as already closed; a drink or a pickle is the only sensible
  // addition left. A plain platter includes no broth, so soup/brain-soup are a
  // side (one at most; none when a platter in the cart includes its soups —
  // dropped above); every other main is still trimmed.
  if (cartHasPlatter) {
    let brothTaken = [...inCart].some(id => BROTH_SLUGS.has(id));
    const trimmed = cands.filter(c => {
      if (BROTH_SLUGS.has(c.id)) {
        if (brothTaken) return false;
        brothTaken = true;
        return true;
      }
      return !MAIN_SECTIONS.has(ITEM_SECTION[c.id]);
    });
    // Only apply the trim if it leaves us something to say.
    if (trimmed.length) {
      cands.length = 0;
      cands.push(...trimmed);
    }
  }

  // Bread first, then a drink, then tea. Bread leads because sangak is the
  // best-converting suggestion on the site by a wide margin (9.9% accept vs
  // 3.1% for lemonade), and because a kalle-pache order without bread is an
  // incomplete meal rather than a missed sale.
  const gaps = [];
  if (!cartSections.has('sides') && !cartHasPlatter) gaps.push({
    kind: 'sides',
    test: it => ITEM_SECTION[it.id] === 'sides'
  });
  if (!cartSections.has('drinks')) gaps.push({
    kind: 'drinks',
    test: it => ITEM_SECTION[it.id] === 'drinks' && !(cartHasPlatter && TEA_SLUGS.has(it.id))
  });
  if (cartHasMain && !cartHasTea && !cartHasPlatter) gaps.push({
    kind: 'tea',
    test: it => TEA_SLUGS.has(it.id)
  });

  // Ordering rule. Gap-filling used to claim EVERY slot in order
  // (drinks → sides → tea), which meant a cart without a drink always led with
  // a drink. Because almost no cart starts with one, the lead slot — the slot
  // customers actually act on — was permanently owned by the cheapest drink,
  // and the strongest real pairings from the POS were pushed off the end of
  // `max`. Measured over 15 days that produced 511 lemonade impressions and
  // half of all upsell revenue from a single 10 AED item.
  //
  // Now: the strongest affinity pick leads, then AT MOST ONE meal gap is
  // filled (so "round out the meal" still happens), then the rest follow in
  // affinity order. `cands` is already in ERP rank order (pairedCount desc).
  const out = [];
  out.push(cands[0]);

  // The ERP brain is ~84% dine-in history, and dine-in bread goes out with the
  // meal instead of being rung up as its own line — so the endpoint NEVER
  // returns sangak for a mains cart (verified: 0 of 5 main-dish carts). Bread
  // reached customers only through the old static rules. Now that the ERP list
  // wins, an unfillable gap must be able to fall back to the menu, or the
  // single best-converting suggestion on the site would silently disappear.
  const GAP_FALLBACK = {
    sides: ['sangak', 'torshi-seer', 'torshi-mix'],
    drinks: ['lemonade', 'doogh'],
    tea: ['tea-black', 'tea']
  };
  const pickFromMenu = slugs => {
    for (const slug of slugs) {
      if (inCart.has(slug) || dismissed.has(slug) || DINE_IN_ONLY.has(slug)) continue;
      if (out.some(o => o.id === slug)) continue;
      const item = allItems.find(i => i.id === slug);
      if (!item || item.soldOut) continue;
      // _pc 0 → never claims social proof it has no evidence for.
      return {
        ...item,
        _pc: 0,
        _kind: 'addon'
      };
    }
    return null;
  };
  for (const g of gaps) {
    if (out.length >= max) break;
    const hit = cands.find(c => out.indexOf(c) === -1 && g.test(c)) || pickFromMenu(GAP_FALLBACK[g.kind] || []);
    if (hit) {
      out.push(hit);
      break;
    }
  }
  for (const c of cands) {
    if (out.length >= max) break;
    if (out.indexOf(c) === -1) out.push(c);
  }
  return out.slice(0, max);
}

// Recipe card — the tap-to-look-closer sheet every Dubai ordering app has.
// Photo large, what's inside, one honest line, Add from here. Bottom sheet on
// phones, centered card on desktop; same paper idiom as everything else.
function ItemSheet({
  item,
  onClose,
  onAdd,
  mode,
  note,
  cart,
  items,
  host,
  onOpenItem
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const cardRef = useDialog(!!item, onClose);
  // ItemSheet is always mounted (item=null): hooks stay above the early return.
  const itemId = item ? item.id : null;
  // G1 = item_sheet_add_platter ÷ item_sheet_open_platter: a platter already on
  // the order opens without an Add button, so it counts apart (_in).
  useEffect(() => {
    if (itemId) clarityEvent(!PLATTER_IDS.has(itemId) ? 'item_sheet_open' : (cart || []).some(l => l.id === itemId && l.qty > 0) ? 'item_sheet_open_platter_in' : 'item_sheet_open_platter');
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Host at the dish (WP-7): hostAddedAt = when Add kept the sheet open (the
  // scroll-to-step target + the foot's 700 ms double-tap guard); hostFlash =
  // the "table is set" line shown for 0.8 s before Done closes the sheet.
  // hostTapAt: the foot's set tap — the foot then becomes "Done" under the same finger (700 ms guard).
  const hostStepRef = useRef(null),
    hostTimer = useRef(0),
    hostTapAt = useRef(0);
  const [hostAddedAt, setHostAddedAt] = useState(0);
  const [hostFlash, setHostFlash] = useState(false);
  useEffect(() => {
    setHostAddedAt(0);
    setHostFlash(false);
    clearTimeout(hostTimer.current);
  }, [itemId]);
  useEffect(() => () => clearTimeout(hostTimer.current), []);
  // Clarity 2026-10-16: the per-person line ('menu.each') on a multi-person
  // platter opens one "Ordering for one?" line; closed again for every item.
  const [eachOpen, setEachOpen] = useState(false);
  useEffect(() => {
    setEachOpen(false);
  }, [itemId]);
  useEffect(() => {
    if (!hostAddedAt) return;
    let t2 = 0;
    const t = setTimeout(() => {
      const card = cardRef.current,
        el = hostStepRef.current;
      if (!card || !el) return;
      const y0 = card.scrollTop,
        top = Math.max(0, el.getBoundingClientRect().top - card.getBoundingClientRect().top + y0 - 8);
      try {
        card.scrollTo({
          top,
          behavior: hostReduced() ? 'auto' : 'smooth'
        });
      } catch (e) {}
      t2 = setTimeout(() => {
        if (card.scrollTop === y0) card.scrollTop = top;
      }, 400);
      try {
        el.focus({
          preventScroll: true
        });
      } catch (e) {}
    }, 60);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [hostAddedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const desc0 = fld(L, item, 'desc') || fld(L, item, 'hint');
  const descA0 = fldA(L, item, 'desc') || fldA(L, item, 'hint');
  const descA = descA0 === desc0 ? '' : descA0;
  const blocked = mode !== "dinein" && DINE_IN_ONLY.has(item.id);
  const img = item.imageLg || item.image;
  // Platters (opened from a platter card or ?item=dish-2) read like the card:
  // an "Includes" list instead of one run-on line, plus who it feeds.
  const platter = PLATTER_IDS.has(item.id);
  const parts = platter ? splitPlatterDesc(desc0) : [];
  const partsAcc = platter ? splitPlatterDesc(descA) : [];
  const n = platter ? servesOf(item) : null;
  const serves = fld(L, item, 'serves') || (platter ? feedsText(L, n) : '');
  // KHAN: value line + badge + book line (dish-6 only; see khanDiff).
  const khan = item.id === KHAN_ID,
    kd = khan ? khanDiff(items) : null,
    kOk = kd != null;
  // Per-person line → "Ordering for one?" link to the Single Dish, priced from
  // the LIVE menu; no orderable Single Dish → the plain line as before.
  const showEach = n >= 2 && item.price && !kOk;
  const single = showEach && onOpenItem ? singleDishOf(items, mode) : null;
  const openSingle = () => {
    clarityEvent('each_single_link');
    const c = cardRef.current;
    onOpenItem(single);
    // Same sheet, new dish: start it from the top and keep focus in the dialog
    // (the tapped button unmounts).
    setTimeout(() => {
      if (c) {
        c.scrollTop = 0;
        try {
          c.focus({
            preventScroll: true
          });
        } catch (e) {}
      }
    }, 0);
  };
  // sheetPlan: the step for a platter already on the order (not after a
  // decline this order, unless this anchor was set). addPlan: Add keeps the
  // sheet open and hosts the step here; otherwise Add adds + closes as before.
  // host is null (switch off, WA, paused, gather) → the sheet is unchanged.
  const hs = platter && host && !blocked ? host : null;
  const line = hs && (cart || []).find(l => l.id === item.id && l.qty > 0);
  const hState = hs ? hs.done[item.id] : undefined;
  const sheetPlan = line && hState !== 'none' && (hState === 'set' || !hs.declinedAny) ? hostPlanSafe(cart, items, mode, item.id, L) : null;
  const addPlan = hs && HOST_SHEET_STAY && !line && !item.soldOut && !hs.declinedAny ? hostPlanSafe([...(cart || []), {
    id: item.id,
    qty: 1
  }], items, mode, item.id, L) : null;
  const engaged = !!(hs && (hs.engaged[item.id] || hState === 'set'));
  const tooSoon = () => Date.now() - Math.max(hostAddedAt, hostTapAt.current) < 700;
  // v2 #2: an UN-done set rides the sticky foot (primary) + No thanks; tapping
  // it is exactly the set action, then the foot shows Done as before.
  const fb = sheetPlan && sheetPlan.bundle && !sheetPlan.bundle.done ? sheetPlan.bundle : null;
  const fPx = fb ? hostSetPx(sheetPlan) : null,
    fL = fb ? tr(L, sheetPlan.n > 1 ? 'host.footSetN' : 'host.footSet1') : '';
  const footSet = () => {
    if (tooSoon()) return;
    hostTapAt.current = Date.now();
    hs.setLines(fb.lines, 'bundle', 'sheet', item.id);
    // The tapped button unmounts (foot becomes '✓ + Done'): put focus on Done
    // instead of letting it fall to <body> inside the modal.
    const refocus = () => {
      const a = document.activeElement;
      if (a && a !== document.body && document.contains(a)) return;
      const c = cardRef.current,
        b = c && c.querySelector('.is-foot .host-btn, .is-foot .host-fno');
      if (b) try {
        b.focus({
          preventScroll: true
        });
      } catch (e) {}
    };
    try {
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(refocus);else setTimeout(refocus, 0);
    } catch (e) {}
  };
  const finish = () => {
    if (tooSoon()) return;
    const st = engaged ? 'set' : 'none';
    hs.resolve(item.id, st, 'sheet');
    if (st === 'set') {
      setHostFlash(true);
      hostTimer.current = setTimeout(onClose, 800);
    } else onClose();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "item-sheet",
    onClick: onClose
  }, /*#__PURE__*/React.createElement("div", {
    className: "item-sheet-card",
    ref: cardRef,
    tabIndex: -1,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": "is-title",
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("button", {
    className: "modal-x",
    onClick: onClose,
    "aria-label": tr(L, 'ok.close')
  }, "\u2715"), img ? /*#__PURE__*/React.createElement("div", {
    className: "is-photo"
  }, /*#__PURE__*/React.createElement("img", {
    src: img,
    alt: nm(L, item)
  })) : /*#__PURE__*/React.createElement("div", {
    className: "is-photo is-photo--icon",
    "aria-hidden": "true"
  }, ITEM_ICONS[ITEM_ICON_MAP[item.id] || 'bowl']), /*#__PURE__*/React.createElement("div", {
    className: "is-body"
  }, kOk ? /*#__PURE__*/React.createElement("div", {
    className: "khan-badge " + P
  }, '★ ' + tr(L, 'khan.badge')) : null, /*#__PURE__*/React.createElement("h3", {
    id: "is-title",
    className: P
  }, nm(L, item)), nmA(L, item) ? /*#__PURE__*/React.createElement("div", {
    className: "is-acc " + A
  }, nmA(L, item)) : null, note ? /*#__PURE__*/React.createElement("div", {
    className: "is-note " + P
  }, tr(L, note)) : null, parts.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "is-includes-label " + P
  }, tr(L, 'menu.includes')), /*#__PURE__*/React.createElement("ul", {
    className: "is-includes " + P
  }, parts.map((c, i) => /*#__PURE__*/React.createElement("li", {
    key: i
  }, c, partsAcc[i] ? /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, partsAcc[i]) : null)))) : /*#__PURE__*/React.createElement(React.Fragment, null, desc0 ? /*#__PURE__*/React.createElement("p", {
    className: "is-desc " + P
  }, desc0) : null, descA ? /*#__PURE__*/React.createElement("p", {
    className: "is-desc is-desc-acc " + A
  }, descA) : null), serves ? /*#__PURE__*/React.createElement("div", {
    className: "is-serves " + P
  }, serves, showEach ? single ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "num is-each-btn",
    "aria-expanded": eachOpen,
    onClick: () => setEachOpen(o => !o)
  }, tr(L, 'menu.each').replace('{p}', numLocal(L, Math.ceil(item.price / n))), /*#__PURE__*/React.createElement("span", {
    className: "is-each-chev",
    "aria-hidden": "true"
  }, "\u25BE"))) : /*#__PURE__*/React.createElement("span", {
    className: "num"
  }, " \xB7 ", tr(L, 'menu.each').replace('{p}', numLocal(L, Math.ceil(item.price / n)))) : null) : null, eachOpen && single ? (() => {
    const t = tr(L, 'menu.single').replace('{name}', nm(L, single)),
      i = t.indexOf('{p}');
    const px = /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, numLocal(L, single.price));
    // ONE inline wrapper: text + <bdi> price + text must stay a single
    // run of text (as separate flex items they lose their spaces).
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "is-single-btn " + P,
      onClick: openSingle
    }, /*#__PURE__*/React.createElement("span", {
      className: "is-single-txt"
    }, i < 0 ? t : /*#__PURE__*/React.createElement(React.Fragment, null, t.slice(0, i), px, t.slice(i + 3))));
  })() : null, kOk ? /*#__PURE__*/React.createElement("div", {
    className: "khan-value khan-value--sheet " + P
  }, khanValueLine(L, kd)) : null, khan ? /*#__PURE__*/React.createElement("div", {
    className: "khan-book khan-book--sheet " + P
  }, tr(L, 'khan.book')) : null, addPlan && !soupsIn(item) && addPlan.rows.filter(r => r.role === 'drink' || r.role === 'broth').length > 1 ? /*#__PURE__*/React.createElement("p", {
    className: "is-pre-note " + P
  }, tr(L, 'host.preNote')) : null, sheetPlan ? /*#__PURE__*/React.createElement(HostBoundary, null, /*#__PURE__*/React.createElement("div", {
    className: "host-sheet",
    ref: hostStepRef,
    tabIndex: -1
  }, /*#__PURE__*/React.createElement("div", {
    className: "host-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "hh-thumb",
    "aria-hidden": "true"
  }, item.image ? /*#__PURE__*/React.createElement("img", {
    src: item.image,
    alt: ""
  }) : ITEM_ICONS.bowl), /*#__PURE__*/React.createElement("span", {
    className: "hh-txt " + P
  }, '✓ ' + tr(L, 'host.onOrder').replace('{dish}', nm(L, item))), hostStepper(L, 's', nm(L, item), line.qty, () => hs.changeQty(item.id, -1), () => hs.changeQty(item.id, 1))), /*#__PURE__*/React.createElement(TableStep, {
    variant: "sheet",
    anchorId: item.id,
    plan: sheetPlan,
    mode: mode,
    covered: false,
    engaged: engaged,
    onSetLines: (lines, how) => hs.setLines(lines, how, 'sheet', item.id),
    onMount: hs.onStep
  }))) : null, /*#__PURE__*/React.createElement("div", {
    className: "is-foot" + (hs ? " is-foot--sticky" : "")
  }, sheetPlan ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: hostFlash ? "host-flash " + P : "",
    role: "status"
  }, hostFlash ? tr(L, 'host.set') : null), hostFlash ? null : fb ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    className: "host-fset " + P,
    onClick: footSet,
    "aria-label": hostA11y(L, fL, hostBundleSub(L, sheetPlan), hostPxT(L, fPx[0], fPx[1]))
  }, /*#__PURE__*/React.createElement("b", null, fL), hostPx(L, fPx[0], fPx[1], 'hb-p')), /*#__PURE__*/React.createElement("button", {
    className: "host-fno " + P,
    onClick: finish
  }, tr(L, engaged ? 'host.done' : 'host.noThanks'))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "hs-seal",
    "aria-hidden": "true"
  }, "\u2713"), /*#__PURE__*/React.createElement("button", {
    className: "host-btn " + P + (engaged ? " is-done" : ""),
    onClick: finish,
    "aria-label": engaged && sheetPlan.bundle && sheetPlan.bundle.done ? tr(L, 'host.bundleDone').replace(/^✓\s*/, '') + '. ' + tr(L, 'host.done') : undefined
  }, tr(L, engaged ? 'host.done' : 'host.none')))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "is-price num"
  }, item.price, " ", /*#__PURE__*/React.createElement("span", {
    className: "cur"
  }, "AED")), item.soldOut ? /*#__PURE__*/React.createElement("span", {
    className: "is-out " + P
  }, tr(L, 'menu.outToday')) : blocked ? /*#__PURE__*/React.createElement("span", {
    className: "is-out " + P
  }, tr(L, 'menu.dineinOnly')) : /*#__PURE__*/React.createElement("button", {
    className: "add-btn " + P,
    onClick: () => {
      clarityEvent(platter ? 'item_sheet_add_platter' : 'item_sheet_add');
      if (addPlan) {
        onAdd(item, {
          host: 'sheet'
        });
        setHostAddedAt(Date.now());
      } else {
        onAdd(item);
        onClose();
      }
    }
  }, tr(L, platter ? 'menu.addToOrder' : 'menu.add')))))));
}
function ItemIcon({
  item
}) {
  if (item.image) {
    return /*#__PURE__*/React.createElement("span", {
      className: "m-row-icon m-row-icon--photo",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("img", {
      src: item.image,
      alt: "",
      loading: "lazy"
    }));
  }
  const name = ITEM_ICON_MAP[item.id] || 'bowl';
  return /*#__PURE__*/React.createElement("span", {
    className: "m-row-icon",
    "aria-hidden": "true"
  }, ITEM_ICONS[name]);
}

// React.memo (2026-08-05 perf pass): App carries ~20 effects and several of
// them setState during mount (ERP suggestions settling, cart sanitising, the
// welcome-popup timer, toasts…). Each one re-rendered EVERY menu row — ~37
// rows × 3 scripts of text — and each re-render re-matched the expensive i18n
// CSS over the whole tree. Lighthouse mobile: 9.2s Style&Layout, 5.3s script
// eval through react. memo() cuts the rows out of every re-render whose props
// didn't change; addItem/changeQty are useCallback'd at the App level for
// exactly this reason. Language changes still propagate — useLang() reads
// context, and context updates bypass memo by design.
const MenuRow = React.memo(function MenuRow({
  item,
  qty,
  onAdd,
  onChange,
  mode,
  onOpen
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const priExtra = fld(L, item, 'unit') || fld(L, item, 'hint');
  const accExtra0 = fldA(L, item, 'unit') || fldA(L, item, 'hint');
  // When a translation is missing the primary falls back to the accent's
  // string — don't render the identical text twice side by side.
  const accExtra = accExtra0 === priExtra ? '' : accExtra0;
  const out = !!item.soldOut;
  // Dine-in-only items (e.g. the kettle of tea) can't be delivered — show a
  // tag in place of the Add button while the order is in delivery mode.
  const blocked = mode !== "dinein" && DINE_IN_ONLY.has(item.id);
  return /*#__PURE__*/React.createElement("div", {
    className: "m-row" + (out ? " m-row--out" : "") + (blocked ? " m-row--dinein" : "") + (onOpen ? " m-row--tap" : ""),
    "data-item": item.id,
    onClick: onOpen ? e => {
      if (e.target.closest('button, .stepper')) return;
      onOpen(item);
    } : undefined
  }, /*#__PURE__*/React.createElement(ItemIcon, {
    item: item
  }), /*#__PURE__*/React.createElement("div", {
    className: "m-row-en " + P
  }, nm(L, item), priExtra && /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, priExtra), POPULAR_TOP3.has(item.id) ? /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, /*#__PURE__*/React.createElement("span", {
    className: "m-badge " + P,
    style: {
      display: 'inline-block',
      background: 'var(--seal, #8a1c1c)',
      color: '#fff',
      borderRadius: 999,
      padding: '1px 7px',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '.04em',
      whiteSpace: 'nowrap'
    }
  }, "\u2605 ", tr(L, 'menu.popular'))) : null), /*#__PURE__*/React.createElement("div", {
    className: "m-row-leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: "m-row-fa " + A
  }, nmA(L, item), accExtra && /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, accExtra)), /*#__PURE__*/React.createElement("div", {
    className: "m-row-price"
  }, /*#__PURE__*/React.createElement("span", {
    className: "num"
  }, item.price), /*#__PURE__*/React.createElement("span", {
    className: "cur"
  }, "AED")), /*#__PURE__*/React.createElement("div", {
    className: "m-row-action"
  }, out ? /*#__PURE__*/React.createElement("div", {
    className: "out-stamp",
    "aria-label": tr(L, 'menu.outToday')
  }, /*#__PURE__*/React.createElement("span", {
    className: "en " + P
  }, tr(L, 'menu.outToday')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'menu.outToday'))) : blocked ? /*#__PURE__*/React.createElement("div", {
    className: "dinein-tag",
    title: tr(L, 'menu.dineinOnly')
  }, /*#__PURE__*/React.createElement("span", {
    className: "en " + P
  }, tr(L, 'menu.dineinOnly')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'menu.dineinOnly'))) : qty === 0 ? /*#__PURE__*/React.createElement("button", {
    className: "add-btn " + P,
    onClick: () => onAdd(item),
    "aria-label": tr(L, 'a11y.add').replace('{s}', nm(L, item))
  }, tr(L, 'menu.add')) : /*#__PURE__*/React.createElement("div", {
    className: "stepper"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => onChange(item.id, -1),
    "aria-label": tr(L, 'a11y.dec')
  }, "\u2212"), /*#__PURE__*/React.createElement("span", {
    className: "qty"
  }, qty), /*#__PURE__*/React.createElement("button", {
    onClick: () => onChange(item.id, +1),
    "aria-label": tr(L, 'a11y.inc')
  }, "+"))));
});

// The EN description separates parts with '·'; FA and AR use '،'. A language
// may fall back to another's string, so split on either separator. Shared by
// PlatterCard and the ItemSheet's platter view.
const splitPlatterDesc = s => (s || "").split(/[·،]/).map(x => x.trim()).filter(Boolean);

// ═══ KHAN (2026-10-04) ═══
// The Khan's Platter (dish-6) holds everything in two Full Dishes (dish-3)
// plus an extra tongue and sangak (and, since 2026-10-24, 6 soups — soupsIn).
// Its card/sheet used to print "~67 AED each", the worst per-head figure next
// to the Double's ~50; instead they say what it is, priced from the LIVE
// menu: diff = Khan − 2 × Full Dish. Shown
// only when both exist and 0 ≤ diff ≤ KHAN_VALUE_MAX; otherwise the old
// per-head line (and no badge). KHAN_MAX_DAYS mirrors src/lib/khan.ts: a cart
// holding the Khan books up to Dubai today + 7 (no gathering rules).
const KHAN_ID = 'dish-6';
const KHAN_FULL_ID = 'dish-3';
const KHAN_VALUE_MAX = 50;
const KHAN_MAX_DAYS = 7;
const khanDiff = items => {
  try {
    const all = Array.isArray(items) && items.length ? items : MENU.flatMap(s => s.items || []);
    const k = all.find(i => i && i.id === KHAN_ID),
      f = all.find(i => i && i.id === KHAN_FULL_ID);
    const pk = k ? Number(k.price) : NaN,
      pf = f ? Number(f.price) : NaN;
    if (!(pk > 0) || !(pf > 0)) return null;
    const d = Math.round((pk - 2 * pf) * 100) / 100;
    return d >= 0 && d <= KHAN_VALUE_MAX ? d : null;
  } catch (e) {
    return null;
  }
};
// The value line (string or fragment); null when there is no honest diff.
const khanValueLine = (L, d) => {
  if (d == null) return null;
  if (d === 0) return tr(L, 'khan.valueSame');
  const t = tr(L, 'khan.value'),
    i = t.indexOf('{p}');
  const px = /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, (Number.isInteger(d) ? String(d) : d.toFixed(2)) + ' AED');
  return i < 0 ? t : /*#__PURE__*/React.createElement(React.Fragment, null, t.slice(0, i), px, t.slice(i + 3));
};
const cartHasKhan = cart => (cart || []).some(l => l && l.id === KHAN_ID && Math.floor(Number(l.qty) || 0) >= 1);
// ═══ END KHAN ═══

// Clarity 2026-10-16: the 1-person Single Dish for the item sheet's "Ordering
// for one?" line — from the LIVE menu (price never hard-coded). null when it is
// missing, sold out, priceless or not orderable in this mode.
const SINGLE_DISH_ID = 'dish-1';
const singleDishOf = (items, mode) => {
  try {
    const all = Array.isArray(items) && items.length ? items : MENU.flatMap(s => s.items || []);
    const s = all.find(i => i && i.id === SINGLE_DISH_ID);
    if (!s || s.soldOut || !(Number(s.price) > 0)) return null;
    if (mode !== 'dinein' && DINE_IN_ONLY.has(s.id)) return null;
    return s;
  } catch (e) {
    return null;
  }
};

// Same memo treatment as MenuRow (same props contract, same reason).
const PlatterCard = React.memo(function PlatterCard({
  item,
  qty,
  onAdd,
  onChange,
  mode,
  onOpen,
  fit
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const components = splitPlatterDesc(fld(L, item, 'desc'));
  // Suppress the accent list when it would duplicate the primary (fallbacks).
  const componentsAcc = fldA(L, item, 'desc') === fld(L, item, 'desc') ? [] : splitPlatterDesc(fldA(L, item, 'desc'));
  const out = !!item.soldOut;
  const blocked = mode !== "dinein" && DINE_IN_ONLY.has(item.id);
  // KHAN: the crown ribbon is set from code for dish-6 (Directus carries no
  // crown flag) and reads khan.badge via data-badge (paper.css KHAN block).
  const khan = item.id === KHAN_ID,
    kd = khan ? khanDiff(null) : null,
    kOk = kd != null;
  return /*#__PURE__*/React.createElement("div", {
    className: "platter" + ((khan ? kOk : item.crown) ? " crown" : "") + (out ? " platter--out" : "") + (onOpen ? " platter--tap" : "") + (fit ? " platter--fit" : ""),
    "data-item": item.id,
    "data-badge": kOk ? '★ ' + tr(L, 'khan.badge') : undefined,
    onClick: onOpen ? e => {
      if (e.target.closest('button, .stepper')) return;
      onOpen(item);
    } : undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: "platter-img"
  }, item.image ?
  /*#__PURE__*/
  // imageLg (800px), NOT image (200px): this box is a 280px column on
  // desktop and full-width on mobile, so the small thumbnail upscaled
  // and looked soft. Falls back to `image` for any item published
  // before imageLg existed.
  React.createElement("img", {
    src: item.imageLg || item.image,
    alt: nm(L, item),
    loading: "lazy"
  }) : /*#__PURE__*/React.createElement("div", {
    className: "platter-icon",
    "aria-hidden": "true"
  }, ITEM_ICONS.bowl)), /*#__PURE__*/React.createElement("div", {
    className: "platter-info"
  }, /*#__PURE__*/React.createElement("h3", null, /*#__PURE__*/React.createElement("span", {
    className: "en " + P
  }, nm(L, item)), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, nmA(L, item))), /*#__PURE__*/React.createElement("div", {
    className: "serves " + P
  }, fld(L, item, 'serves') || feedsText(L, servesOf(item)), fldA(L, item, 'serves') ? /*#__PURE__*/React.createElement("span", {
    className: A
  }, " \xB7 ", fldA(L, item, 'serves')) : null, servesOf(item) >= 2 && item.price && !kOk ? /*#__PURE__*/React.createElement("span", {
    className: "num",
    style: {
      opacity: .85
    }
  }, " \xB7 ", tr(L, 'menu.each').replace('{p}', numLocal(L, Math.ceil(item.price / servesOf(item))))) : null, POPULAR_TOP3.has(item.id) ? /*#__PURE__*/React.createElement("span", {
    className: "m-badge",
    style: {
      marginInlineStart: 8,
      background: 'var(--seal, #8a1c1c)',
      color: '#fff',
      borderRadius: 999,
      padding: '1px 8px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.04em',
      whiteSpace: 'nowrap'
    }
  }, "\u2605 ", tr(L, 'menu.popular')) : null), kOk ? /*#__PURE__*/React.createElement("div", {
    className: "khan-value " + P
  }, khanValueLine(L, kd)) : null, khan ? /*#__PURE__*/React.createElement("div", {
    className: "khan-book " + P
  }, tr(L, 'khan.book')) : null, L !== 'en' && servesOf(item) >= 3 ? /*#__PURE__*/React.createElement("div", {
    className: "platter-lamma " + P
  }, tr(L, 'menu.lamma')) : null, components.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "platter-includes-label " + P
  }, tr(L, 'menu.includes')), /*#__PURE__*/React.createElement("ul", {
    className: P
  }, components.map((c, i) => /*#__PURE__*/React.createElement("li", {
    key: i
  }, c, componentsAcc[i] && /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, componentsAcc[i]))))), /*#__PURE__*/React.createElement("div", {
    className: "platter-action"
  }, /*#__PURE__*/React.createElement("div", {
    className: "price-tag"
  }, /*#__PURE__*/React.createElement("span", {
    className: "num"
  }, item.price), /*#__PURE__*/React.createElement("span", {
    className: "cur"
  }, "AED")), out ? /*#__PURE__*/React.createElement("div", {
    className: "out-stamp out-stamp--lg",
    "aria-label": tr(L, 'menu.outToday')
  }, /*#__PURE__*/React.createElement("span", {
    className: "en " + P
  }, tr(L, 'menu.outToday')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'menu.outToday'))) : blocked ? /*#__PURE__*/React.createElement("div", {
    className: "dinein-tag dinein-tag--lg",
    title: tr(L, 'menu.dineinOnly')
  }, /*#__PURE__*/React.createElement("span", {
    className: "en " + P
  }, tr(L, 'menu.dineinOnly')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'menu.dineinOnly'))) : qty === 0 ? /*#__PURE__*/React.createElement("button", {
    className: "add-btn " + P,
    onClick: () => onAdd(item)
  }, tr(L, 'menu.addToOrder')) : /*#__PURE__*/React.createElement("div", {
    className: "stepper"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => onChange(item.id, -1)
  }, "\u2212"), /*#__PURE__*/React.createElement("span", {
    className: "qty"
  }, qty), /*#__PURE__*/React.createElement("button", {
    onClick: () => onChange(item.id, +1)
  }, "+"))));
});

// ── V9 CategoryShelf — Spotify-style portrait row ──────────────────────
// Native CSS scroll-snap horizontal rail of 4:5 portrait cards. 3.5 cards
// visible on desktop (4th half-peeked = the "this is a row, not a grid"
// signal), ~2.3 on mobile (active + next peek + prev sliver). Each card:
// top 60% photo OR kraft-with-line-art-glyph (for photoless cats), bottom
// 40% cream paper with EN+FA bilingual title and italic tagline. Chevron
// nav on desktop, dots indicator on mobile, one-time scroll-nudge on
// first mobile load.

// Pick the hero image per category — explicit photo wins, then curated
// item slug, then first item-with-image.
function pickCategoryHero(section) {
  if (section.photo) return section.photo;
  const pickedSlug = CATEGORY_HERO_PICKS[section.id];
  const items = section.items || [];
  // The hero renders large, so prefer imageLg over the 70px-row thumbnail.
  if (pickedSlug) {
    const picked = items.find(i => i.id === pickedSlug && i.image);
    if (picked) return picked.imageLg || picked.image;
  }
  const anyWithImg = items.find(i => i.image);
  return anyWithImg ? anyWithImg.imageLg || anyWithImg.image : null;
}
function ShelfCard({
  section,
  idx,
  isActive,
  onPick
}) {
  const heroImg = pickCategoryHero(section);
  const hasPhoto = !!heroImg;
  const iconKey = CATEGORY_ICONS[section.id] || 'bowl';
  const enLabel = section.en.split(/[,&]/)[0].trim();
  const taglineFull = CATEGORY_TAGLINES[section.id] && CATEGORY_TAGLINES[section.id].en || '';
  const availableCount = (section.items || []).filter(i => !i.soldOut).length;
  const numStr = String(idx + 1).padStart(2, '0');
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "cshelf-card " + (hasPhoto ? "has-photo " : "no-photo ") + (isActive ? "is-active" : ""),
    "data-cat": section.id,
    "data-idx": idx,
    onClick: () => onPick(section.id),
    "aria-label": section.en + " · " + section.fa + ", " + availableCount + " items"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cshelf-media",
    "aria-hidden": "true"
  }, hasPhoto ? /*#__PURE__*/React.createElement("img", {
    className: "cshelf-photo",
    src: heroImg,
    alt: "",
    loading: "lazy",
    decoding: "async"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "cshelf-glyph-wrap"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cshelf-glyph-ring",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-glyph-svg",
    "aria-hidden": "true"
  }, ITEM_ICONS[iconKey])), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-stamp",
    "aria-hidden": "true"
  }, "No. ", numStr), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-count",
    "aria-hidden": "true"
  }, availableCount, " items")), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-panel"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cshelf-name-en"
  }, enLabel), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-name-fa",
    dir: "rtl"
  }, section.fa), taglineFull && /*#__PURE__*/React.createElement("span", {
    className: "cshelf-tag"
  }, taglineFull), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-arrow",
    "aria-hidden": "true"
  }, "\u2192")));
}
function CategoryShelf({
  sections,
  activeCat,
  onPick
}) {
  const railRef = useRef(null);
  const cardRefs = useRef({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  // Sync activeIdx ⇆ activeCat
  useEffect(() => {
    const i = sections.findIndex(s => s.id === activeCat);
    if (i >= 0) setActiveIdx(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat, sections.length]);

  // Scroll-position based chevron enable/disable
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    let raf = null;
    const update = () => {
      raf = null;
      setCanScrollPrev(el.scrollLeft > 4);
      setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(update);
    };
    update();
    el.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Active-index tracker for mobile dots — IntersectionObserver
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const obs = new IntersectionObserver(entries => {
      let best = null,
        bestRatio = 0;
      for (const e of entries) {
        if (e.intersectionRatio > bestRatio) {
          best = e.target;
          bestRatio = e.intersectionRatio;
        }
      }
      if (best && bestRatio > 0.55) {
        const i = parseInt(best.dataset.idx || '0', 10);
        setActiveIdx(i);
      }
    }, {
      root: rail,
      threshold: [0.55, 0.8, 0.95]
    });
    Object.values(cardRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [sections.length]);

  // One-time first-paint scroll nudge on mobile (only if user hasn't seen it)
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    try {
      if (localStorage.getItem('cshelf_hint_seen')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(max-width: 1024px)').matches) return;
    } catch {
      return;
    }
    const timer = setTimeout(() => {
      glideX(rail, 28);
      setTimeout(() => glideX(rail, 0), 600);
      try {
        localStorage.setItem('cshelf_hint_seen', '1');
      } catch {/* ignore */}
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  const scrollByCard = dir => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector('.cshelf-card');
    if (!card) return;
    const gap = 20;
    const step = card.getBoundingClientRect().width + gap;
    glideX(rail, rail.scrollLeft + dir * step);
  };
  const handlePick = catId => {
    onPick(catId);
    requestAnimationFrame(() => {
      const target = document.getElementById('cat-' + catId);
      if (target && target.scrollIntoView) {
        glideTo(target.getBoundingClientRect().top + window.scrollY - 70);
      }
    });
  };
  if (!sections || !sections.length) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "cshelf",
    "aria-label": "Menu categories"
  }, /*#__PURE__*/React.createElement("header", {
    className: "cshelf-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cshelf-head-titles"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "cshelf-title"
  }, "Browse the menu"), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-title-fa",
    dir: "rtl"
  }, "\u0645\u0631\u0648\u0631 \u0645\u0646\u0648")), /*#__PURE__*/React.createElement("div", {
    className: "cshelf-head-meta"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cshelf-head-count"
  }, sections.length, " chapters"), /*#__PURE__*/React.createElement("span", {
    className: "cshelf-head-hint"
  }, "swipe \u2192"))), /*#__PURE__*/React.createElement("div", {
    className: "cshelf-rail-wrap"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "cshelf-nav cshelf-nav-prev" + (canScrollPrev ? "" : " is-disabled"),
    onClick: () => scrollByCard(-1),
    disabled: !canScrollPrev,
    "aria-label": "Previous categories"
  }, "\u2039"), /*#__PURE__*/React.createElement("ul", {
    className: "cshelf-rail",
    ref: railRef,
    role: "list"
  }, sections.map((s, i) => /*#__PURE__*/React.createElement("li", {
    key: s.id,
    className: "cshelf-card-wrap",
    ref: el => {
      cardRefs.current[s.id] = el;
    },
    "data-idx": i
  }, /*#__PURE__*/React.createElement(ShelfCard, {
    section: s,
    idx: i,
    isActive: activeIdx === i,
    onPick: handlePick
  })))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "cshelf-nav cshelf-nav-next" + (canScrollNext ? "" : " is-disabled"),
    onClick: () => scrollByCard(1),
    disabled: !canScrollNext,
    "aria-label": "Next categories"
  }, "\u203A")), /*#__PURE__*/React.createElement("ol", {
    className: "cshelf-dots",
    "aria-hidden": "true"
  }, sections.map((s, i) => /*#__PURE__*/React.createElement("li", {
    key: s.id,
    className: i === activeIdx ? "is-on" : ""
  }))));
}

// (The legacy V8 cinematic hero — HeroPanel + CategoryHero, never rendered —
// was removed 2026-10 to keep app.js inside the gzip budget; the removed
// source is kept in the host-feature scratchpad: removed-legacy-v8-hero.js.)

// ── (Legacy V7 FeaturedHero kept below for fallback; not rendered by V8) ──
function FeaturedHero({
  section,
  allOut,
  availableCount,
  previewNames,
  extraCount,
  photoItems,
  hasPhotos
}) {
  const iconKey = CATEGORY_ICONS[section.id] || 'bowl';
  const tagline = CATEGORY_TAGLINES[section.id] || {
    en: '',
    fa: ''
  };
  const tint = CATEGORY_TINTS[section.id] || CATEGORY_TINTS.mains;
  const cardStyle = {
    '--cat-bg': tint.bg,
    '--cat-ink': tint.ink,
    '--cat-accent': tint.accent
  };
  return /*#__PURE__*/React.createElement("article", {
    key: section.id,
    className: "cat-feat" + (allOut ? " is-out" : "") + (hasPhotos ? " has-photos" : " icon-only"),
    style: cardStyle,
    "data-cat": section.id
  }, /*#__PURE__*/React.createElement("div", {
    className: "cf-hero",
    "aria-hidden": "true"
  }, section.photo ? /*#__PURE__*/React.createElement("img", {
    className: "cf-hero-photo",
    src: section.photo,
    alt: "",
    loading: "lazy",
    decoding: "async"
  }) : photoItems.length >= 2 ? /*#__PURE__*/React.createElement("span", {
    className: "cf-collage"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-collage-main"
  }, /*#__PURE__*/React.createElement("img", {
    src: photoItems[0].image,
    alt: "",
    loading: "lazy",
    decoding: "async"
  })), /*#__PURE__*/React.createElement("span", {
    className: "cf-collage-side"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-collage-thumb"
  }, /*#__PURE__*/React.createElement("img", {
    src: photoItems[1].image,
    alt: "",
    loading: "lazy",
    decoding: "async"
  })), photoItems[2] && /*#__PURE__*/React.createElement("span", {
    className: "cf-collage-thumb"
  }, /*#__PURE__*/React.createElement("img", {
    src: photoItems[2].image,
    alt: "",
    loading: "lazy",
    decoding: "async"
  })), photoItems[3] && /*#__PURE__*/React.createElement("span", {
    className: "cf-collage-thumb"
  }, /*#__PURE__*/React.createElement("img", {
    src: photoItems[3].image,
    alt: "",
    loading: "lazy",
    decoding: "async"
  })))) : photoItems.length === 1 ? /*#__PURE__*/React.createElement("img", {
    className: "cf-hero-photo",
    src: photoItems[0].image,
    alt: "",
    loading: "lazy",
    decoding: "async"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "cf-icon-big"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-icon-glow",
    "aria-hidden": "true"
  }), ITEM_ICONS[iconKey]), /*#__PURE__*/React.createElement("span", {
    className: "cf-shade",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "cf-chapter"
  }, section.ornament || "")), /*#__PURE__*/React.createElement("div", {
    className: "cf-body"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "cf-title"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-title-en"
  }, section.en), /*#__PURE__*/React.createElement("span", {
    className: "cf-title-fa"
  }, section.fa)), (tagline.en || tagline.fa) && /*#__PURE__*/React.createElement("p", {
    className: "cf-tag"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-tag-en"
  }, "\"", tagline.en, "\""), /*#__PURE__*/React.createElement("span", {
    className: "cf-tag-fa"
  }, "\xAB", tagline.fa, "\xBB")), !allOut && previewNames && /*#__PURE__*/React.createElement("p", {
    className: "cf-preview"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-preview-text"
  }, previewNames), extraCount > 0 && /*#__PURE__*/React.createElement("span", {
    className: "cf-preview-more"
  }, "+", extraCount, " more \xB7 \u200F\u06F6 \u0645\u0648\u0631\u062F \u062F\u06CC\u06AF\u0631")), /*#__PURE__*/React.createElement("div", {
    className: "cf-cta-row"
  }, allOut ? /*#__PURE__*/React.createElement("span", {
    className: "cf-cta cf-cta-out"
  }, "Sold out today \xB7 \u06AF\u0630\u0634\u062A") : /*#__PURE__*/React.createElement("span", {
    className: "cf-cta"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cf-cta-count"
  }, availableCount, " items \xB7 ", availableCount, " \u0645\u0648\u0631\u062F"), /*#__PURE__*/React.createElement("span", {
    className: "cf-cta-arrow",
    "aria-hidden": "true"
  }, "\u2192")))), /*#__PURE__*/React.createElement("span", {
    className: "cf-accent",
    "aria-hidden": "true"
  }));
}

// ── CategoryGrid (editorial magazine spread) ───────────────────────────────
// One massive featured-hero card showing the active category, plus a side
// rail of compact thumbnail cards for the other 5 categories. Tapping a
// thumb cinematically swaps it into the featured slot. On mobile the layout
// collapses to: featured on top, thumbs in a horizontal swipe strip below.
function CategoryGrid(props) {
  // V9 — delegate to the Spotify-style portrait shelf.
  return /*#__PURE__*/React.createElement(CategoryShelf, props);
}

// eslint-disable-next-line no-unused-vars
function _LegacyEditorialCategoryGrid({
  sections,
  activeCat,
  onPick
}) {
  if (!sections || !sections.length) return null;

  // Featured = active category, others = everything else (preserving order).
  const featured = sections.find(s => s.id === activeCat) || sections[0];
  const others = sections.filter(s => s.id !== featured.id);

  // Extract render data for both featured + thumb shapes.
  const dataFor = s => {
    const availableItems = (s.items || []).filter(i => !i.soldOut);
    const availableCount = availableItems.length;
    const totalCount = (s.items || []).length;
    const allOut = totalCount > 0 && availableCount === 0;
    const photoItems = availableItems.filter(i => i.image).slice(0, 4);
    const hasPhotos = photoItems.length > 0 || !!s.photo;
    const previewNames = availableItems.slice(0, 4).map(i => i.en.split(/[,&]/)[0].trim()).join(" · ");
    const extraCount = Math.max(0, availableCount - 4);
    return {
      availableItems,
      availableCount,
      totalCount,
      allOut,
      photoItems,
      hasPhotos,
      previewNames,
      extraCount
    };
  };

  // The whole section takes its theme from the featured card's tint.
  const featuredTint = CATEGORY_TINTS[featured.id] || CATEGORY_TINTS.mains;
  const sectionStyle = {
    '--section-tint': featuredTint.bg,
    '--section-edge': featuredTint.accent,
    '--section-ink': featuredTint.ink
  };
  const featuredData = dataFor(featured);
  return /*#__PURE__*/React.createElement("section", {
    className: "cat-edit",
    style: sectionStyle,
    "aria-label": "Browse menu categories"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cat-edit-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ceh-kicker"
  }, "Chapters of the menu \xB7 \u0641\u0635\u0644\u200C\u0647\u0627\u06CC \u0645\u0646\u0648"), /*#__PURE__*/React.createElement("h2", {
    className: "ceh-title"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ceh-en"
  }, "Choose your chapter"), /*#__PURE__*/React.createElement("span", {
    className: "ceh-divider",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ceh-line"
  }), /*#__PURE__*/React.createElement("span", {
    className: "ceh-orn"
  }, "\u2726"), /*#__PURE__*/React.createElement("span", {
    className: "ceh-line"
  })), /*#__PURE__*/React.createElement("span", {
    className: "ceh-fa"
  }, "\u0641\u0635\u0644\u06CC \u0628\u0631\u06AF\u0632\u06CC\u0646\u06CC\u062F"))), /*#__PURE__*/React.createElement("div", {
    className: "cat-edit-layout"
  }, /*#__PURE__*/React.createElement(FeaturedHero, {
    section: featured,
    allOut: featuredData.allOut,
    availableCount: featuredData.availableCount,
    previewNames: featuredData.previewNames,
    extraCount: featuredData.extraCount,
    photoItems: featuredData.photoItems,
    hasPhotos: featuredData.hasPhotos
  }), /*#__PURE__*/React.createElement("div", {
    className: "cat-edit-rail",
    role: "tablist",
    "aria-label": "Other categories"
  }, others.map(s => {
    const d = dataFor(s);
    const tint = CATEGORY_TINTS[s.id] || CATEGORY_TINTS.mains;
    const tagline = CATEGORY_TAGLINES[s.id] || {
      en: '',
      fa: ''
    };
    const iconKey = CATEGORY_ICONS[s.id] || 'bowl';
    const thumbStyle = {
      '--cat-bg': tint.bg,
      '--cat-ink': tint.ink,
      '--cat-accent': tint.accent
    };
    return /*#__PURE__*/React.createElement("button", {
      key: s.id,
      type: "button",
      role: "tab",
      "aria-selected": false,
      className: "cat-thumb" + (d.allOut ? " is-out" : "") + (d.hasPhotos ? " has-photos" : " icon-only"),
      style: thumbStyle,
      onClick: () => onPick(s.id),
      title: s.en + " · " + s.fa
    }, /*#__PURE__*/React.createElement("span", {
      className: "ct-art",
      "aria-hidden": "true"
    }, s.photo ? /*#__PURE__*/React.createElement("img", {
      src: s.photo,
      alt: "",
      loading: "lazy",
      decoding: "async"
    }) : d.photoItems[0] ? /*#__PURE__*/React.createElement("img", {
      src: d.photoItems[0].image,
      alt: "",
      loading: "lazy",
      decoding: "async"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "ct-icon"
    }, ITEM_ICONS[iconKey])), /*#__PURE__*/React.createElement("span", {
      className: "ct-info"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ct-num"
    }, s.ornament || ""), /*#__PURE__*/React.createElement("span", {
      className: "ct-title-en"
    }, s.en), /*#__PURE__*/React.createElement("span", {
      className: "ct-title-fa"
    }, s.fa), tagline.en && /*#__PURE__*/React.createElement("span", {
      className: "ct-tag"
    }, tagline.en), /*#__PURE__*/React.createElement("span", {
      className: "ct-meta"
    }, d.allOut ? /*#__PURE__*/React.createElement("span", {
      className: "ct-meta-out"
    }, "Sold out \xB7 \u06AF\u0630\u0634\u062A") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, d.availableCount, " items"), /*#__PURE__*/React.createElement("span", {
      className: "dot"
    }, "\xB7"), /*#__PURE__*/React.createElement("span", null, d.availableCount, " \u0645\u0648\u0631\u062F")))), /*#__PURE__*/React.createElement("span", {
      className: "ct-arrow",
      "aria-hidden": "true"
    }, "\u2192"));
  }))));
}

// ── CategoryJumpPill ───────────────────────────────────────────────────────
// Sticky compact strip with Roman-numeral chips. Appears once the user has
// scrolled past the CategoryGrid, so categories stay one tap away without
// stealing the hero space on first paint. Visibility is controlled by an
// IntersectionObserver in the App component.
function CategoryJumpPill({
  sections,
  activeCat,
  onPick,
  visible
}) {
  if (!sections || !sections.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "cat-jump-pill" + (visible ? " is-visible" : ""),
    role: "tablist",
    "aria-label": "Quick category jump",
    "aria-hidden": !visible
  }, /*#__PURE__*/React.createElement("div", {
    className: "cjp-inner"
  }, sections.map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    role: "tab",
    "aria-selected": activeCat === s.id,
    "aria-label": s.en + " · " + s.fa,
    title: s.en + " · " + s.fa,
    className: "cjp-chip" + (activeCat === s.id ? " active" : ""),
    onClick: () => onPick(s.id)
  }, /*#__PURE__*/React.createElement("span", {
    className: "cjp-num"
  }, (s.ornament || "").replace(".", "")), /*#__PURE__*/React.createElement("span", {
    className: "cjp-tip"
  }, /*#__PURE__*/React.createElement("span", {
    className: "cjp-tip-en"
  }, s.en), /*#__PURE__*/React.createElement("span", {
    className: "cjp-tip-fa"
  }, s.fa))))));
}

// ── InlineSuggestion ───────────────────────────────────────────────────────
// Slim 1-line ribbon rendered BELOW a MenuRow that was just added to the cart.
// Shows up to two complementary chips for the specific item that was tapped.
// Auto-clears after 12s via the App-level lastAddedId timer.
function InlineSuggestion({
  forItem,
  suggestions,
  onAdd,
  onDismiss,
  onClose
}) {
  const L = useLang();
  // Push discipline (SFUpsell): the ribbon is a "push" surface — respect the
  // session dismiss list, never re-push the same item twice per order, and
  // stop after the per-order touch budget. The list is FROZEN at mount (the
  // ribbon mounts fresh per lastAddedId) so notePush can't hide it mid-view.
  // Without SFUpsell (script blocked/failed) it behaves exactly as before.
  const [visible] = useState(() => {
    const all = suggestions || [];
    try {
      if (window.SFUpsell && window.SFUpsell.enabled()) {
        if (!window.SFUpsell.placementOn('add')) return [];
        // Ribbon reserves the last nudge for the checkout last-chance line.
        return all.filter(s => window.SFUpsell.canPushRibbon(s.id)).slice(0, 2);
      }
    } catch (e) {}
    return all.slice(0, 2);
  });
  useEffect(() => {
    if (!visible.length) return;
    try {
      if (window.SFUpsell && window.SFUpsell.enabled()) {
        window.SFUpsell.notePush(visible.map(s => s.id));
        visible.forEach(s => window.SFUpsell.track('shown', 'add', s.id, {
          value: s.price
        }));
      }
    } catch (e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (visible.length === 0) return null;
  // Social proof from real order history ("Ordered together 161×") — only
  // when the ERP data marked the top chip with a meaningful pair count.
  // Count digits localized to match the site's numeral voice (۱۶۱ / ١٦١).
  const proofMin = window.SFUpsell && window.SFUpsell.socialProofMin || 30;
  const proof = visible[0] && visible[0]._pc >= proofMin ? visible[0]._pc : 0;
  const localDigits = n => numLocal(L, n);
  return /*#__PURE__*/React.createElement("div", {
    className: "row-inline-suggest",
    role: "region",
    "aria-label": tr(L, 'menu.pairsWith')
  }, /*#__PURE__*/React.createElement("span", {
    className: "ris-label"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ris-en " + SCR(L)
  }, tr(L, 'menu.pairsWith')), /*#__PURE__*/React.createElement("span", {
    className: "ris-fa " + SCR(ACCENT[L])
  }, tra(L, 'menu.pairsWith')), proof > 0 && /*#__PURE__*/React.createElement("span", {
    className: "up-proof " + SCR(L)
  }, tr(L, 'up.together').replace('{n}', localDigits(proof)))), /*#__PURE__*/React.createElement("span", {
    className: "ris-chips"
  }, visible.map(s => /*#__PURE__*/React.createElement("button", {
    key: s.id,
    type: "button",
    className: "ris-chip",
    onClick: () => onAdd(s.id, {
      upsell: true,
      placement: 'add'
    }),
    "aria-label": tr(L, 'a11y.add').replace('{s}', nm(L, s))
  }, /*#__PURE__*/React.createElement("span", {
    className: "ris-chip-en " + SCR(L)
  }, "+ ", nm(L, s).split(/[,&]/)[0].trim()), /*#__PURE__*/React.createElement("span", {
    className: "ris-chip-price"
  }, s.price)))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ris-dismiss",
    onClick: () => {
      // Dismiss the SUGGESTED chips (through App state so the rail hides
      // them too) — NOT the anchor item the customer just bought. Closing
      // the ribbon itself is a separate, non-recorded action.
      visible.forEach(s => onDismiss(s.id, 'add'));
      if (onClose) onClose();
    },
    "aria-label": tr(L, 'a11y.hideSugg')
  }, "\u2715"));
}

// ── PairingChips ──────────────────────────────────────────────────────────
// Persistent suggestion strip inside the OrderRail / MobileCartSheet.
// Replaces the old hardcoded "with sangak?" nudge with a general 1-3-chip
// strip reflecting the whole cart's composition.
function PairingChips({
  suggestions,
  onAdd,
  onDismiss,
  active
}) {
  const L = useLang();
  const rootRef = useRef(null);
  const visible = (suggestions || []).slice(0, 3);
  // Telemetry: ambient strip — log 'shown' once per slug per order (SFUpsell
  // dedupes, so a double fire from the twin desktop/sheet instances is ONE
  // beacon). Visibility gating is deterministic: `active` (the sheet's open
  // state; true for the desktop rail) plus a laid-out check via
  // getClientRects (zero inside any display:none subtree). offsetParent and
  // IntersectionObserver were both tried and are unreliable here.
  const sig = visible.map(s => s.id).join(',');
  useEffect(() => {
    if (!sig || active === false) return;
    const el = rootRef.current;
    if (!el || el.getClientRects().length === 0) return; // hidden — not seen
    try {
      if (window.SFUpsell && window.SFUpsell.enabled()) {
        visible.forEach(s => window.SFUpsell.track('shown', 'cart', s.id, {
          value: s.price
        }));
        // Record exposure (NOT a push — this must not cost a touch, see
        // SFUpsell.noteSeen) so the checkout last-chance line can prefer
        // something the customer has not already been offered here. Guarded:
        // an older cached suggestions.js would not have noteSeen.
        if (window.SFUpsell.noteSeen) window.SFUpsell.noteSeen(visible.map(s => s.id));
      }
    } catch (e) {}
  }, [sig, active]); // eslint-disable-line react-hooks/exhaustive-deps
  if (visible.length === 0) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "pairing-chips",
    ref: rootRef,
    role: "region",
    "aria-label": tr(L, 'menu.roundOut')
  }, /*#__PURE__*/React.createElement("div", {
    className: "pc-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pc-en " + SCR(L)
  }, tr(L, 'menu.roundOut')), /*#__PURE__*/React.createElement("span", {
    className: "pc-fa " + SCR(ACCENT[L])
  }, tra(L, 'menu.roundOut'))), /*#__PURE__*/React.createElement("div", {
    className: "pc-row"
  }, visible.map(s => /*#__PURE__*/React.createElement("div", {
    key: s.id,
    className: "pc-chip",
    role: "button",
    tabIndex: 0,
    onClick: () => onAdd(s.id),
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onAdd(s.id);
      }
    },
    "aria-label": tr(L, 'a11y.add').replace('{s}', nm(L, s))
  }, /*#__PURE__*/React.createElement("span", {
    className: "pc-icon" + (s.image ? " pc-icon--photo" : ""),
    "aria-hidden": "true"
  }, s.image ? /*#__PURE__*/React.createElement("img", {
    src: s.image,
    alt: "",
    loading: "lazy"
  }) : ITEM_ICONS[ITEM_ICON_MAP[s.id] || 'bowl']), /*#__PURE__*/React.createElement("span", {
    className: "pc-info"
  }, /*#__PURE__*/React.createElement("span", {
    className: "pc-name-en " + SCR(L)
  }, nm(L, s).split(/[,&]/)[0].trim()), /*#__PURE__*/React.createElement("span", {
    className: "pc-name-fa " + SCR(ACCENT[L])
  }, nmA(L, s))), /*#__PURE__*/React.createElement("span", {
    className: "pc-add",
    "aria-hidden": "true"
  }, "+ ", s.price), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "pc-dismiss",
    onClick: e => {
      e.stopPropagation();
      onDismiss(s.id);
    },
    "aria-label": tr(L, 'a11y.hide').replace('{s}', nm(L, s))
  }, "\u2715")))));
}

// Flash-promo (World Cup Night etc.) — mirrors src/lib/promo.ts. window.__PROMO__
// is injected by PaperLayout; returns the AED discount when now is in the window.
// The SERVER re-derives the authoritative charge in api/orders/create.ts.
function wcPromoDiscount(subtotal) {
  try {
    const p = typeof window !== 'undefined' && window.__PROMO__ || null;
    if (!p) return 0;
    const now = Date.now();
    if (now >= p.startsAtMs && now < p.endsAtMs) return Math.round(subtotal * p.percent / 100);
    return 0;
  } catch (e) {
    return 0;
  }
}
function OrderRail({
  cart,
  items,
  mode,
  setMode,
  deliveryArea,
  onAreaChange,
  onCheckout,
  onAddById,
  suggestions,
  onDismissSuggestion,
  stripActive,
  introPct,
  onDismiss,
  when,
  onWhen,
  paused
}) {
  const lines = cart.map(l => ({
    l,
    item: items.find(i => i.id === l.id)
  })).filter(x => x.item);
  const subtotal = lines.reduce((s, x) => s + x.item.price * x.l.qty, 0);
  const promoAed = wcPromoDiscount(subtotal);
  // Intro-loyalty shown in the CART too (not just checkout), so the reward the
  // visitor just claimed is visible while they build the order. Whole-AED
  // rounding matches the server; best single discount wins (never stacked).
  const loyaltyAed = introPct > 0 ? Math.round(subtotal * introPct / 100) : 0;
  const loyaltyApplied = loyaltyAed > 0 && loyaltyAed > promoAed;
  const discountAed = Math.max(promoAed, loyaltyAed);
  const discountedSubtotal = subtotal - discountAed;
  const service = Math.round(discountedSubtotal * SERVICE_FEE_PCT / 100);
  // Test bypass: a cart with the payment-test item skips delivery fee (so total = 1 AED).
  const cartHasTestItem = lines.some(x => x.item.id === 'test-1aed');
  // Per-area delivery fee. The area is chosen in the checkout modal; until then
  // (and for a first-time visitor) the fee is unknown, so the rail marks it
  // "at checkout" and leaves it out of the running total.
  const area = findArea(deliveryArea);
  // areaFee is the DISCOUNTED fee (what create.ts charges); area.fee stays the
  // original for the struck-through "was" price next to it.
  const areaFee = area ? areaFeeNow(area.fee) : null;
  // Free delivery once the food subtotal reaches the threshold (create.ts
  // enforces the identical rule; this is only the matching display).
  const freeDelivery = mode === "delivery" && !cartHasTestItem && subtotal >= FREE_DELIVERY_ABOVE_AED;
  const delivery = mode === "delivery" && subtotal > 0 && !cartHasTestItem && areaFee != null && !freeDelivery ? areaFee : 0;
  const total = discountedSubtotal + service + delivery;

  // Generalised pairing strip — driven by the App-level suggestion engine
  // (see getSuggestions). The old hardcoded "with sangak?" rule is now one
  // entry inside SUGGEST_RULES, so it still fires for the same cart shapes
  // but the engine also handles other compositions (breakfast → tea-pot,
  // platters → doogh, etc.).
  const showPairingChips = lines.length > 0 && Array.isArray(suggestions) && suggestions.length > 0;
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  // Clarity 2026-10-16: with no area yet, the delivery row ("by area") and the
  // delivery-promo line open the area picker above (rendered under the same
  // conditions: delivery, onAreaChange, a non-empty order).
  const railAreaRef = useRef(null);
  const railAreaTap = !area && !!onAreaChange && !freeDelivery;
  const openRailArea = ev => {
    clarityEvent(ev);
    if (railAreaRef.current) railAreaRef.current();
  };
  return /*#__PURE__*/React.createElement("aside", {
    className: "rail"
  }, /*#__PURE__*/React.createElement("div", {
    className: "rail-head"
  }, /*#__PURE__*/React.createElement("span", {
    className: "t " + P
  }, tr(L, 'rail.yourOrder')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'rail.yourOrder'))), onWhen && lines.length > 0 && (() => {
    const later = !!paused || when === 'later';
    const nowTxt = tr(L, 'cart.now') + (mode === 'delivery' ? ' · ' + tr(L, 'band.approx').replace('{r}', bandRange(L, bandForArea(deliveryArea))) : '');
    return /*#__PURE__*/React.createElement("div", {
      className: "rail-when",
      role: "group",
      "aria-label": tr(L, 'cart.whenLbl')
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-pressed": !later,
      disabled: !!paused,
      className: "rail-when-btn " + P + (!later ? " on" : ""),
      onClick: () => onWhen('asap')
    }, nowTxt), /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-pressed": later,
      className: "rail-when-btn " + P + (later ? " on" : ""),
      onClick: () => {
        if (when !== 'later') clarityEvent('cart_schedule');
        onWhen('later');
      }
    }, tr(L, 'co.schedule')));
  })(), /*#__PURE__*/React.createElement("div", {
    className: "rail-mode"
  }, (WA_CTX ? [["delivery", 'rail.delivery'], ["pickup", 'co.pickup']] : [["delivery", 'rail.delivery'], ["pickup", 'co.pickup'], ["dinein", 'rail.dinein']]).map(([m, k]) => /*#__PURE__*/React.createElement("button", {
    key: m,
    className: "rail-mode-btn" + (mode === m ? " on" : ""),
    onClick: () => setMode(m)
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, k)), /*#__PURE__*/React.createElement("span", {
    className: "rail-mode-acc " + A
  }, tra(L, k)), /*#__PURE__*/React.createElement(ModeSub, {
    L: L,
    m: m,
    areaKey: deliveryArea,
    free: subtotal >= FREE_DELIVERY_ABOVE_AED && !cartHasTestItem
  })))), mode === "delivery" && onAreaChange && lines.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "rail-area"
  }, /*#__PURE__*/React.createElement("div", {
    className: "rail-area-lbl " + P
  }, tr(L, 'co.area'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.area'))), /*#__PURE__*/React.createElement(AreaPicker, {
    value: deliveryArea,
    onChange: onAreaChange,
    hasTestItem: cartHasTestItem,
    openRef: railAreaRef
  }), area && /*#__PURE__*/React.createElement("div", {
    className: "rail-area-band " + P
  }, tr(L, 'band.to').replace('{a}', areaName(L, area)).replace('{r}', bandRange(L, bandForFee(area.fee))))), /*#__PURE__*/React.createElement("div", {
    className: "rail-body"
  }, lines.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "rail-empty"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ic"
  }, "\u06F0"), /*#__PURE__*/React.createElement("div", {
    className: "t1 " + P
  }, tr(L, 'rail.emptyT1')), /*#__PURE__*/React.createElement("div", {
    className: "t2 " + A
  }, tra(L, 'rail.emptyT1')), /*#__PURE__*/React.createElement("div", {
    className: "rail-empty-hint"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'rail.emptyHint')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'rail.emptyHint'))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "rail-empty-cta",
    onClick: () => {
      if (onDismiss) onDismiss(); // in the mobile sheet: close it first, then glide
      const el = document.getElementById('order');
      if (el) glideTo(el.getBoundingClientRect().top + window.scrollY - 70);
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'rail.browse')), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'rail.browse')))) : hostGroup(lines).map(({
    l,
    item,
    sub
  }) => /*#__PURE__*/React.createElement("div", {
    key: item.id,
    className: "rail-line" + (sub ? " host-grp" : "")
  }, /*#__PURE__*/React.createElement("div", {
    className: "rl-row1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "rl-name"
  }, /*#__PURE__*/React.createElement("span", {
    className: "rl-qty"
  }, l.qty, "\xD7"), /*#__PURE__*/React.createElement("span", {
    className: "rl-en " + P
  }, nm(L, item)), /*#__PURE__*/React.createElement("span", {
    className: "rl-fa " + A
  }, nmA(L, item))), /*#__PURE__*/React.createElement("span", {
    className: "rl-leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "rl-price"
  }, feeFmt(item.price * l.qty))), /*#__PURE__*/React.createElement("div", {
    className: "rl-row2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "stepper-mini"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => onCheckout.changeQty(item.id, -1),
    "aria-label": tr(L, 'a11y.dec')
  }, "\u2212"), /*#__PURE__*/React.createElement("span", {
    className: "q"
  }, l.qty), /*#__PURE__*/React.createElement("button", {
    onClick: () => onCheckout.changeQty(item.id, +1),
    "aria-label": tr(L, 'a11y.inc')
  }, "+")), /*#__PURE__*/React.createElement("button", {
    className: "rl-remove",
    onClick: () => onCheckout.remove(item.id),
    "aria-label": tr(L, 'a11y.remove')
  }, "\u2715")))), showPairingChips && /*#__PURE__*/React.createElement(PairingChips, {
    suggestions: suggestions,
    onAdd: id => onAddById && onAddById(id, {
      upsell: true,
      placement: 'cart'
    }),
    onDismiss: slug => onDismissSuggestion && onDismissSuggestion(slug, 'cart'),
    active: stripActive !== false
  })), lines.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "rail-totals"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ln"
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'rail.subtotal'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'rail.subtotal'))), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt"
  }, feeFmt(subtotal), " AED")), loyaltyApplied && /*#__PURE__*/React.createElement("div", {
    className: "ln",
    style: {
      color: '#1a7a3a'
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'co.introDiscount').replace('{n}', numLocal(L, introPct))), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt"
  }, "\u2212", loyaltyAed, " AED")), promoAed > 0 && !loyaltyApplied && /*#__PURE__*/React.createElement("div", {
    className: "ln",
    style: {
      color: '#1a7a3a'
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'co.promo')), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt"
  }, "\u2212", promoAed, " AED")), /*#__PURE__*/React.createElement("div", {
    className: "ln"
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'rail.vat'), " ", L === 'tr' ? '%' : null, numLocal(L, SERVICE_FEE_PCT), L === 'tr' ? null : L === 'fa' ? '٪' : '%'), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt"
  }, service, " AED")), mode === "delivery" && !cartHasTestItem && /*#__PURE__*/React.createElement("div", {
    className: "ln"
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'rail.delivery'), area ? " — " + areaName(L, area) : ""), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt" + (areaFee == null && !freeDelivery ? " " + P : ""),
    style: freeDelivery ? {
      color: '#1a7a3a'
    } : undefined
  }, freeDelivery ? tr(L, 'co.freeDelivery') : areaFee != null ? /*#__PURE__*/React.createElement(React.Fragment, null, DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
    style: {
      opacity: .55,
      marginInlineEnd: '.35em'
    }
  }, area.fee), feeFmt(delivery) + " AED") : railAreaTap ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "rail-area-tap",
    onClick: () => openRailArea('area_hint_tap_cart')
  }, tr(L, 'rail.atCheckout'), /*#__PURE__*/React.createElement("span", {
    className: "sf-vh"
  }, " \xB7 ", tr(L, 'mode.chooseArea')), /*#__PURE__*/React.createElement("span", {
    className: "area-hint-chev",
    "aria-hidden": "true"
  }, "\u25BE")) : tr(L, 'rail.atCheckout'))), mode === "delivery" && !cartHasTestItem && DELIVERY_PROMO_ON && !freeDelivery && /*#__PURE__*/React.createElement("div", {
    className: "ln promo-d50-ln",
    style: {
      color: 'var(--gold)'
    }
  }, railAreaTap ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "lbl promo-d50 promo-d50-tap " + P,
    onClick: () => openRailArea('promo_area_tap')
  }, tr(L, 'promo.delivery50').replace('{p}', numLocal(L, DELIVERY_FEE_DISCOUNT_PCT)), /*#__PURE__*/React.createElement("span", {
    className: "promo-d50-act"
  }, " \xB7 ", tr(L, 'mode.chooseArea'), /*#__PURE__*/React.createElement("span", {
    className: "area-hint-chev",
    "aria-hidden": "true"
  }, "\u25BE"))) : /*#__PURE__*/React.createElement("span", {
    className: "lbl promo-d50 " + P
  }, tr(L, 'promo.delivery50').replace('{p}', numLocal(L, DELIVERY_FEE_DISCOUNT_PCT)))), mode === "delivery" && !cartHasTestItem && !freeDelivery && subtotal > 0 && FREE_DELIVERY_ABOVE_AED - subtotal <= 100 && /*#__PURE__*/React.createElement("div", {
    className: "ln",
    style: {
      color: 'var(--gold)'
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'rail.freeNudge').replace('{n}', numLocal(L, FREE_DELIVERY_ABOVE_AED - subtotal)))), /*#__PURE__*/React.createElement("div", {
    className: "ln ln-grand"
  }, /*#__PURE__*/React.createElement("span", {
    className: "lbl " + P
  }, tr(L, 'rail.total'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'rail.total'))), /*#__PURE__*/React.createElement("span", {
    className: "leader",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: "amt"
  }, feeFmt(total), " AED"), /*#__PURE__*/React.createElement("span", {
    className: "grand-stamp",
    "aria-hidden": "true"
  }, L === 'ar' ? 'طلبك' : 'سفارش'))), (() => {
    const _short = minOrderShortfall(mode, subtotal, cartHasTestItem);
    return /*#__PURE__*/React.createElement(React.Fragment, null, _short.shortfall > 0 && lines.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "rail-minwarn " + P
    }, tr(L, 'co.minWarn1'), " ", numLocal(L, _short.shortfall), " ", tr(L, 'co.minWarn2'), " ", numLocal(L, _short.min), tr(L, 'co.minWarn3')), /*#__PURE__*/React.createElement("button", {
      className: "rail-cta",
      disabled: lines.length === 0,
      onClick: onCheckout.open
    }, _short.shortfall > 0 && lines.length > 0 ? /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.btnShort').replace('{n}', numLocal(L, _short.shortfall))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'rail.checkout')), " ", /*#__PURE__*/React.createElement("span", {
      className: "fa " + A
    }, tra(L, 'rail.checkout')))));
  })());
}
function HowMade() {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const steps = [{
    n: "01",
    key: 'how.s1'
  }, {
    n: "02",
    key: 'how.s2'
  }, {
    n: "03",
    key: 'how.s3'
  }, {
    n: "04",
    key: 'how.s4'
  }];
  return /*#__PURE__*/React.createElement("section", {
    className: "howmade",
    id: "howmade"
  }, /*#__PURE__*/React.createElement("div", {
    className: "shell"
  }, /*#__PURE__*/React.createElement("div", {
    className: "howmade-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "kicker " + P
  }, tr(L, 'how.kicker'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'how.kicker'))), /*#__PURE__*/React.createElement("h2", {
    className: P
  }, tr(L, 'how.h2')), /*#__PURE__*/React.createElement("div", {
    className: "h-fa " + A
  }, tra(L, 'how.h2'))), /*#__PURE__*/React.createElement("div", {
    className: "howmade-grid"
  }, steps.map(s => /*#__PURE__*/React.createElement("div", {
    key: s.n,
    className: "howmade-step"
  }, /*#__PURE__*/React.createElement("div", {
    className: "nm " + P
  }, /*#__PURE__*/React.createElement("span", {
    className: "num"
  }, s.n), " ", tr(L, s.key)), /*#__PURE__*/React.createElement("h4", {
    className: P
  }, tr(L, s.key)), /*#__PURE__*/React.createElement("div", {
    className: "h-fa " + A
  }, tra(L, s.key)), /*#__PURE__*/React.createElement("p", {
    className: P
  }, tr(L, s.key + '.text')))))));
}
function FindUs() {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const dt = key => /*#__PURE__*/React.createElement("dt", {
    className: P
  }, tr(L, key), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, key)));
  return /*#__PURE__*/React.createElement("section", {
    className: "findus",
    id: "findus"
  }, /*#__PURE__*/React.createElement("div", {
    className: "findus-info"
  }, /*#__PURE__*/React.createElement("h2", {
    className: P
  }, tr(L, 'find.h2')), /*#__PURE__*/React.createElement("div", {
    className: "h-fa " + A
  }, tra(L, 'find.h2')), /*#__PURE__*/React.createElement("dl", null, dt('find.address'), /*#__PURE__*/React.createElement("dd", null, /*#__PURE__*/React.createElement("a", {
    className: "map-link",
    href: RESTAURANT_MAP_URL,
    target: "_blank",
    rel: "noopener noreferrer"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, addressFor(L)), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, addressFor(ACCENT[L])), /*#__PURE__*/React.createElement("span", {
    className: "map-cta " + P
  }, tr(L, 'find.openMaps')))), dt('find.phone'), /*#__PURE__*/React.createElement("dd", {
    style: {
      fontFamily: '"JetBrains Mono",monospace',
      fontSize: 17
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: `tel:${PHONE_TEL}`
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, PHONE_DISPLAY))), dt('find.hours'), /*#__PURE__*/React.createElement("dd", null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'find.hoursVal')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'find.hoursVal'))), dt('find.order'), /*#__PURE__*/React.createElement("dd", null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'find.orderVal')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'find.orderVal'))), WHATSAPP_URL && /*#__PURE__*/React.createElement(React.Fragment, null, dt('find.whatsapp'), /*#__PURE__*/React.createElement("dd", null, /*#__PURE__*/React.createElement("a", {
    href: WHATSAPP_URL,
    target: "_blank",
    rel: "noopener noreferrer"
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, `+${WHATSAPP_RAW}`), /*#__PURE__*/React.createElement("span", {
    className: "fa " + P
  }, tr(L, 'find.whatsappMsg')))))), /*#__PURE__*/React.createElement("div", {
    className: "delivery-zones"
  }, /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'find.zones'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'find.zones'))), /*#__PURE__*/React.createElement("div", {
    className: "zones-note " + P,
    style: {
      fontSize: '.92em',
      lineHeight: 1.65,
      color: 'var(--ink-soft)'
    }
  }, tr(L, 'find.zonesNote').replace('{n}', numLocal(L, DELIVERY_AREAS.length)).replace('{f}', numLocal(L, FREE_DELIVERY_ABOVE_AED)), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A,
    style: {
      display: 'block',
      marginTop: 5
    }
  }, tra(L, 'find.zonesNote').replace('{n}', numLocal(ACCENT[L], DELIVERY_AREAS.length)).replace('{f}', numLocal(ACCENT[L], FREE_DELIVERY_ABOVE_AED)))))), /*#__PURE__*/React.createElement("a", {
    className: "findus-map",
    href: RESTAURANT_MAP_URL,
    target: "_blank",
    rel: "noopener noreferrer",
    "aria-label": tr(L, 'a11y.openMaps')
  }, /*#__PURE__*/React.createElement("div", {
    className: "compass"
  }, "N"), /*#__PURE__*/React.createElement("div", {
    className: "road-1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "road-2"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pin"
  }, /*#__PURE__*/React.createElement("div", {
    className: "dot"
  }), /*#__PURE__*/React.createElement("div", {
    className: "lbl " + P
  }, tr(L, 'find.here'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'find.here')))), /*#__PURE__*/React.createElement("div", {
    className: "stamp"
  }, /*#__PURE__*/React.createElement("div", {
    className: "top"
  }, "JUMEIRA"), /*#__PURE__*/React.createElement("div", {
    className: "mid"
  }, L === 'ar' ? 'جميرا 1' : 'جمیرا ۱'), /*#__PURE__*/React.createElement("div", {
    className: "bot"
  }, "DUBAI \xB7 UAE")), /*#__PURE__*/React.createElement("div", {
    className: "map-overlay-cta"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'find.openMaps')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'find.openMaps')))));
}

// ── Leaflet lazy-loader ──────────────────────────────────────────────────
// Leaflet (~45KB) is only needed at checkout, so we inject it on first use
// rather than shipping it on every page load. Served same-origin from
// /vendor/leaflet — no third-party CDN in the ordering flow.
let _leafletPromise = null;
function ensureLeaflet() {
  if (typeof window !== 'undefined' && window.L) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = new Promise((resolve, reject) => {
    try {
      if (!document.querySelector('link[data-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/vendor/leaflet/leaflet.css';
        link.setAttribute('data-leaflet', '1');
        document.head.appendChild(link);
      }
      const s = document.createElement('script');
      s.src = '/vendor/leaflet/leaflet.js';
      s.async = true;
      s.onload = () => resolve(window.L);
      s.onerror = () => reject(new Error('Leaflet failed to load'));
      document.head.appendChild(s);
    } catch (e) {
      reject(e);
    }
  });
  return _leafletPromise;
}
function haversineKmJS(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// ── MapPicker ───────────────────────────────────────────────────────────────
// Real Leaflet/OpenStreetMap map for delivery. Delivery-app pattern: a fixed
// pin floats at the map centre; the customer drags the map so the pin lands on
// their door. The restaurant sits as a labelled marker, joined to the centre
// by a dashed line with a live distance read-out. Tiles get a warm sepia
// filter (CSS) so the map blends with the parchment design. `onPick` reports
// the centre on every user pan/zoom; GPS (via `coords`) re-centres the map.
function MapPicker({
  restaurant,
  coords,
  onPick,
  focusPoint
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const lineRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const [liveDist, setLiveDist] = useState(null);
  // The map's handlers are bound once; these refs keep them reading the LIVE
  // props instead of the first render's closure.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const coordsRef = useRef(coords);
  coordsRef.current = coords;
  // "Set pin here" offer: shown after the customer deliberately positions the
  // map — pinning is always an explicit tap, never a side effect of a swipe.
  const [pinOffer, setPinOffer] = useState(null);
  useEffect(() => {
    let cancelled = false;
    ensureLeaflet().then(L => {
      if (cancelled || mapRef.current || !elRef.current) return;
      const start = coords || restaurant;
      const map = L.map(elRef.current, {
        zoomControl: true,
        attributionControl: true,
        // Touch: one finger scrolls the PAGE (no more scroll-trap); two-finger
        // pinch pans/zooms the map. Desktop keeps normal dragging.
        dragging: !IS_TOUCH
      }).setView([start.lat, start.lng], coords ? 16 : 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(map);
      const restIcon = L.divIcon({
        className: 'mp-rest-wrap',
        html: '<span class="mp-rest-dot">🍲</span><span class="mp-rest-lbl">' + SIGN_EN + '</span>',
        iconSize: [0, 0]
      });
      L.marker([restaurant.lat, restaurant.lng], {
        icon: restIcon,
        interactive: false,
        keyboard: false
      }).addTo(map);
      const redraw = () => {
        const c = map.getCenter();
        const d = haversineKmJS(restaurant.lat, restaurant.lng, c.lat, c.lng);
        if (d < 0.06) {
          if (lineRef.current) {
            map.removeLayer(lineRef.current);
            lineRef.current = null;
          }
          setLiveDist(null);
        } else {
          const pts = [[restaurant.lat, restaurant.lng], [c.lat, c.lng]];
          if (lineRef.current) lineRef.current.setLatLngs(pts);else lineRef.current = L.polyline(pts, {
            color: '#8a1c1c',
            weight: 3,
            dashArray: '2 7',
            opacity: 0.9
          }).addTo(map);
          setLiveDist(d);
        }
      };
      redraw();
      map.on('move', redraw);
      // A pin only when the customer MEANS it: moving the map never commits by
      // itself — it surfaces a "Set pin here" chip, and only that tap (or GPS)
      // writes coordinates. The restaurant-centered initial view can never
      // satisfy the mandatory-pin validation by accident.
      map.on('moveend', () => {
        const c = map.getCenter();
        const anchor = coordsRef.current || restaurant;
        const moved = haversineKmJS(anchor.lat, anchor.lng, c.lat, c.lng) > 0.06;
        setPinOffer(moved ? {
          lat: c.lat,
          lng: c.lng
        } : null);
      });
      mapRef.current = map;
      // The modal animates in; give layout a beat then fix tile sizing.
      setTimeout(() => {
        try {
          map.invalidateSize();
        } catch (e) {}
      }, 90);
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {}
        mapRef.current = null;
      }
    };
  }, []); // init once

  // GPS (or any external coords change) re-centres the map. Guarded against the
  // feedback from our own dragend → onPick so we don't fight the user's pan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !coords) return;
    const c = map.getCenter();
    if (Math.abs(c.lat - coords.lat) < 5e-5 && Math.abs(c.lng - coords.lng) < 5e-5) return;
    map.setView([coords.lat, coords.lng], Math.max(map.getZoom(), 16));
    setPinOffer(null);
  }, [coords]);

  // Area chosen but no pin yet → glide the map to that district so the pin is
  // one small nudge away instead of a hunt across Dubai. View-only: never
  // commits coordinates.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusPoint || coordsRef.current) return;
    try {
      map.setView([focusPoint.lat, focusPoint.lng], 14);
    } catch (e) {}
  }, [focusPoint]);
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  if (loadError) {
    return /*#__PURE__*/React.createElement("div", {
      className: "map-fallback"
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'map.fallback')), /*#__PURE__*/React.createElement("span", {
      className: "fa " + A
    }, tra(L, 'map.fallback')));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "map-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    ref: elRef,
    className: "map-canvas",
    "data-clarity-mask": "True"
  }), /*#__PURE__*/React.createElement("div", {
    className: "map-centerpin",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("span", {
    className: "mcp-head"
  }), /*#__PURE__*/React.createElement("span", {
    className: "mcp-stem"
  })), liveDist != null && /*#__PURE__*/React.createElement("div", {
    className: "map-dist"
  }, L === 'fa' ? numLocal(L, liveDist.toFixed(1)) + ' کیلومتر' : L === 'ar' ? liveDist.toFixed(1) + ' كم' : liveDist.toFixed(1) + ' km', " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + P
  }, tr(L, 'map.toRestaurant'))), pinOffer && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "map-setpin",
    onClick: () => {
      onPickRef.current(pinOffer);
      setPinOffer(null);
    }
  }, "\uD83D\uDCCD ", /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'map.setPin')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, "\xB7 ", tra(L, 'map.setPin'))), /*#__PURE__*/React.createElement("div", {
    className: "map-hint"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, IS_TOUCH ? 'map.twoFinger' : 'map.hint')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, IS_TOUCH ? 'map.twoFinger' : 'map.hint'))));
}

// Searchable area picker — replaces the native <select> that dumped all 46
// Dubai areas into one endless scroll. The customer types to filter; results are
// grouped by price so it reads like a tidy menu. Click or Enter to choose.
function AreaPicker({
  value,
  onChange,
  hasTestItem,
  openRef
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const selected = findArea(value);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // Clarity 2026-10-16: lets a hint outside the picker ("Choose your area to
  // see the delivery fee") open it exactly like a tap on the field — focus
  // (onFocus opens + lifts it in checkout) and open, in case it already had focus.
  if (openRef) openRef.current = () => {
    const el = inputRef.current;
    if (!el) return;
    setOpen(true);
    try {
      el.focus();
    } catch (e) {}
  };

  // Close when clicking outside the widget.
  useEffect(() => {
    if (!open) return;
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    // pointerdown fires for mouse AND touch — needed so an outside tap closes the
    // menu on mobile (a plain 'mousedown' listener is unreliable for touch).
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [open]);

  // Reset the keyboard highlight whenever the filter changes.
  useEffect(() => {
    setHi(0);
  }, [query]);
  // On phones the open list is a full-screen sheet (paper.css ≤720px) — the
  // Back button closes it instead of leaving checkout. Desktop dropdown: no.
  const sheetMode = open && (() => {
    try {
      return window.matchMedia('(max-width:720px)').matches;
    } catch (e) {
      return false;
    }
  })();
  useBackLayer(sheetMode, 'area', () => setOpen(false));
  // C8: the phone sheet is a dialog layer (Escape + Tab stay inside it); its
  // own input already holds focus, so no focus move/restore.
  useDialog(sheetMode, () => setOpen(false), {
    noFocus: true,
    ref: wrapRef
  });
  const qRaw = query.trim();
  const q = foldTr(qRaw); // Latin fold (tr-TR lower-case, ı ş ğ ü ö ç → ASCII): a Turkish keyboard's ı/İ still finds "DIFC"
  const qF = foldArabic(qRaw);
  const matches = q ? AREA_OPTIONS.filter(a => foldTr(a.en).indexOf(q) !== -1 || a.fa && foldArabic(a.fa).indexOf(qF) !== -1 || a.ar && foldArabic(a.ar).indexOf(qF) !== -1 || a.key.indexOf(q) !== -1) : AREA_OPTIONS;
  const pick = a => {
    if (!a) return;
    onChange(a.key);
    setQuery("");
    setOpen(false);
  };
  const onKey = e => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHi(h => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && matches[hi]) {
        e.preventDefault();
        pick(matches[hi]);
      }
    }
    // preventDefault: this Escape is spent — the dialog layer below (checkout) must not close too.
    else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  // Group matches by fee for tidy price headers, keeping a flat index so the
  // keyboard highlight lines up across groups.
  const groups = [];
  let curFee = null;
  matches.forEach((a, i) => {
    if (a.fee !== curFee) {
      groups.push({
        fee: a.fee,
        items: []
      });
      curFee = a.fee;
    }
    groups[groups.length - 1].items.push({
      a,
      i
    });
  });

  // Show the chosen area in the field when closed; an empty search box when open.
  const inputVal = !open && selected ? areaName(L, selected) + " — AED " + feeFmt(areaFeeNow(selected.fee)) : query;
  return /*#__PURE__*/React.createElement("div", {
    className: "area-combo" + (open ? " open" : ""),
    ref: wrapRef,
    role: sheetMode ? "dialog" : undefined,
    "aria-modal": sheetMode ? "true" : undefined,
    "aria-labelledby": sheetMode ? "area-sheet-title" : undefined
  }, sheetMode && /*#__PURE__*/React.createElement("span", {
    id: "area-sheet-title",
    className: "sf-vh"
  }, tr(L, 'area.label')), /*#__PURE__*/React.createElement("div", {
    className: "area-field"
  }, /*#__PURE__*/React.createElement("span", {
    className: "area-search-ic",
    "aria-hidden": "true"
  }, "\u2315"), /*#__PURE__*/React.createElement("input", {
    ref: inputRef,
    className: "area-input",
    type: "text",
    autoComplete: "off",
    placeholder: tr(L, 'area.ph'),
    value: inputVal,
    onChange: e => {
      if (selected) onChange("");
      setQuery(e.target.value);
      setOpen(true);
    },
    onFocus: e => {
      setOpen(true);
      // Lift the field into the top third of the checkout sheet so the
      // dropdown isn't buried under the phone keyboard. Container scroll
      // only — deliberately not a window scroll.
      const m = e.target.closest('.modal');
      if (m) {
        const r = e.target.getBoundingClientRect();
        const mr = m.getBoundingClientRect();
        const delta = r.top - mr.top;
        if (delta > mr.height * 0.35) m.scrollTop += delta - 72;
      }
    },
    onKeyDown: onKey,
    "aria-label": tr(L, 'area.label')
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "area-sheet-close",
    "aria-label": tr(L, 'ok.close'),
    onMouseDown: e => {
      e.preventDefault();
      setOpen(false);
    }
  }, "\u2715"), selected && !open ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "area-clear",
    "aria-label": tr(L, 'area.change'),
    onMouseDown: e => {
      e.preventDefault();
      onChange("");
      setQuery("");
      setOpen(true);
      if (inputRef.current) inputRef.current.focus();
    }
  }, "\u2715") : /*#__PURE__*/React.createElement("span", {
    className: "area-caret",
    "aria-hidden": "true"
  }, "\u25BE")), open && /*#__PURE__*/React.createElement("div", {
    className: "area-menu",
    role: "listbox"
  }, matches.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "area-empty"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'area.noMatch')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'area.noMatch'))) : groups.map(g => /*#__PURE__*/React.createElement("div", {
    key: g.fee,
    className: "area-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: "area-group-head " + P
  }, tr(L, 'area.groupHead'), " \xB7 ", DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
    style: {
      opacity: .55
    }
  }, "AED ", g.fee), " ", /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, "AED ", feeFmt(areaFeeNow(g.fee))), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: "area-group-band"
  }, bandUsually(L, bandForFee(g.fee)))), g.items.map(({
    a,
    i
  }) => /*#__PURE__*/React.createElement("div", {
    key: a.key,
    role: "option",
    "aria-selected": value === a.key,
    className: "area-opt" + (i === hi ? " on" : "") + (value === a.key ? " sel" : ""),
    onMouseEnter: () => setHi(i),
    onClick: () => pick(a)
  }, /*#__PURE__*/React.createElement("span", {
    className: "area-opt-name " + P
  }, areaName(L, a)), areaNameA(L, a) ? /*#__PURE__*/React.createElement("span", {
    className: "area-opt-fa " + A
  }, areaNameA(L, a)) : /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", {
    className: "area-opt-fee"
  }, DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
    style: {
      opacity: .5,
      marginInlineEnd: '.3em'
    }
  }, a.fee), "AED ", feeFmt(areaFeeNow(a.fee)))))))));
}

// ── Phase 2 checkout helpers (K1 address · K2 drop-off/extras · K3 saved
// addresses · K4 phone read-back). The server still receives ONE address
// string and ONE notes string — everything here is composed client-side in
// ENGLISH (plus a Farsi echo on the notes lines) because the rider and the
// kitchen read it.
const ADDR_TYPES = [['apt', 'co.aptT'], ['villa', 'co.villaT'], ['office', 'co.officeT']];
const ADDR_FIELDS = ['building', 'floor', 'flat', 'villa', 'street', 'company', 'landmark', 'makani'];
const EMPTY_ADDR = {
  building: '',
  floor: '',
  flat: '',
  villa: '',
  street: '',
  company: '',
  landmark: '',
  makani: ''
};
const DROPOFFS = [['hand', 'co.dropHand'], ['call', 'co.dropCall'], ['door', 'co.dropDoor'], ['nobell', 'co.dropNoBell']];
// Kitchen/rider lines, bilingual EN · FA. "Hand it to me" is the default and prints nothing.
const DROPOFF_NOTE = {
  call: '📞 Call when downstairs · پایین که رسیدید تماس بگیرید',
  door: '🚪 Leave at the door · پشت در بگذارید',
  nobell: "🔕 Don't ring the bell · زنگ نزنید"
};
const EXTRA_NOTE = {
  lemon: '🍋 Lemon · لیمو',
  spoons: '🥄 Spoons & napkins · قاشق و دستمال'
};
// Soup/broth dishes, and the platters that include soups (soupsIn) → spoons &
// napkins pre-ticked (until the customer touches it).
const SOUP_SLUGS = new Set(['soup', 'brain-soup']);
const ADDR_LABELS = [['home', 'co.lblHome'], ['work', 'co.lblWork'], ['family', 'co.lblFamily'], ['other', 'co.lblOther']];
// create.ts accepts any length ≥ 4; the ERP ingest caps address/notes at 2000
// and create.ts prefixes the area name — these caps stay far inside both.
const ADDR_MAX = 400;
const NOTES_MAX = 1000;
const ADDR_STORE_KEY = 'sf-addr-v1';
const ADDR_STORE_MAX = 5;
function westernDigits(s) {
  return String(s == null ? '' : s).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
}
function makaniNorm(s) {
  return westernDigits(s).replace(/[\s ‌‏‎]/g, '');
}
function makaniOk(s) {
  const n = makaniNorm(s);
  return n === '' || /^\d{10}$/.test(n);
}
function cleanAddrParts(p) {
  const out = {
    ...EMPTY_ADDR
  };
  if (p && typeof p === 'object') ADDR_FIELDS.forEach(k => {
    if (typeof p[k] === 'string') out[k] = p[k].slice(0, 200);
  });
  return out;
}
function composeAddress(type, p) {
  const v = k => westernDigits(p[k] || '').replace(/\s+/g, ' ').trim().slice(0, 200); // = the inputs' maxLength
  // "Flat Flat 12" guard: drop a label word the customer typed themselves.
  const num = (k, re) => v(k).replace(re, '').trim();
  const flat = num('flat', /^(flat|apt\.?|apartment|unit|no\.?|#)\s*/i);
  const floor = num('floor', /^(floor|fl\.?|level)\s*/i);
  const villa = num('villa', /^(villa|no\.?|#)\s*/i);
  let main;
  if (type === 'villa') main = [villa && 'Villa ' + villa, v('street')].filter(Boolean).join(', ');else if (type === 'office') main = [v('company') && 'Office: ' + v('company'), floor && 'Floor ' + floor].filter(Boolean).join(', ');else main = [flat && 'Flat ' + flat, floor && 'Floor ' + floor, v('building')].filter(Boolean).join(', ');
  const out = [main];
  if (v('landmark')) out.push('Landmark: ' + v('landmark'));
  const mk = makaniNorm(p.makani || '');
  if (/^\d{10}$/.test(mk)) out.push('Makani ' + mk);
  return out.filter(Boolean).join(' · ').slice(0, ADDR_MAX);
}
// Device-local saved addresses (never sent anywhere). Shape per entry:
// { label:'home'|'work'|'family'|'other', area, addressParts:{type,...fields},
//   dropoff, coords:{lat,lng}|null, at }. Newest first, max 5.
function loadSavedAddrs() {
  try {
    const a = JSON.parse(localStorage.getItem(ADDR_STORE_KEY) || '[]');
    return Array.isArray(a) ? a.filter(x => x && typeof x.area === 'string' && x.addressParts && typeof x.addressParts === 'object').slice(0, ADDR_STORE_MAX) : [];
  } catch (e) {
    return [];
  }
}
function writeSavedAddrs(list) {
  try {
    localStorage.setItem(ADDR_STORE_KEY, JSON.stringify(list.slice(0, ADDR_STORE_MAX)));
  } catch (e) {/* private mode */}
}
function savedAddrKey(x) {
  const p = x && x.addressParts || {};
  const main = p.type === 'villa' ? p.villa : p.type === 'office' ? p.company : p.building;
  return (x.area || '') + '|' + westernDigits(main || '').replace(/\s+/g, ' ').trim().toLowerCase();
}
// +9715XXXXXXXX → "05X XXX XXXX" (the way people say their number here).
function uaeLocalFmt(e164) {
  const d = String(e164 || '').replace(/^\+971/, '');
  return '0' + d.slice(0, 2) + ' ' + d.slice(2, 5) + ' ' + d.slice(5);
}
// ── Phase 3 checkout helpers: L3 gift delivery · L4 company tax invoice ──
const GIFT_NAME_MAX = 60;
const GIFT_MSG_MAX = 140;
const INV_COMPANY_MAX = 120;
const INV_ADDR_MAX = 200;
// One clean line (no newlines/tabs — the notes are split into lines by \n).
function oneLine(v, max) {
  return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, max);
}
// L3: the FIRST kitchen/rider notes lines of a gift order, bilingual EN · FA.
function giftNoteLines(name, e164, msg) {
  // The phone is ONE unspaced digit run (0551234567): a spaced "055 123 4567"
  // after an Arabic/Persian name gets its groups reversed by the bidi
  // algorithm in the owner email / POS view — a single run cannot be reordered.
  const n = oneLine(name, GIFT_NAME_MAX),
    ph = uaeLocalFmt(e164).replace(/\s+/g, ''),
    m = oneLine(msg, GIFT_MSG_MAX);
  const out = ['🎁 GIFT — deliver to ' + n + ' ' + ph + ' — call the recipient · هدیه — تحویل به ' + n + ' ' + ph + ' — با گیرنده تماس بگیرید'];
  if (m) out.push('✉️ Card: ' + m + ' · کارت: ' + m);
  return out;
}
// L4: a TRN typed with Arabic/Persian digits, spaces or dashes → 15 Western digits.
function trnNorm(v) {
  return westernDigits(v).replace(/[\s\-‐-―‌‏‎]/g, '');
}
function dubaiHourNow() {
  try {
    return Number(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      hourCycle: 'h23'
    }).format(new Date()));
  } catch (e) {
    return (new Date().getUTCHours() + 4) % 24;
  }
}
function CheckoutModal({
  open,
  onClose,
  cart,
  items,
  mode,
  setMode,
  deliveryArea,
  setDeliveryArea,
  onPlace,
  onAddById,
  erpSuggestions,
  onDismissSuggestion,
  introPct,
  whenReq,
  onWhenSync,
  paused,
  onServerAvail,
  gather,
  onGatherDone,
  hostDeclined
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  // C8: Escape closes checkout like ✕ — but never while a submit is in flight.
  const dialogRef = useDialog(open, onClose, {
    hold: () => CO_SUBMITTING
  });
  // Geolocation state for the Address pin-on-map control.
  // geoError/submitError hold DICTIONARY KEYS when the message is ours (so a
  // language switch re-renders them translated); unknown strings (e.g. raw
  // server error text) pass through tr() unchanged.
  // WhatsApp ordering: if the customer shared a location pin in chat, start
  // with it pinned.
  // Stray-tap / crash insurance: the half-filled form lives in sessionStorage
  // (per-tab, so nothing bleeds across customers on a shared device) and is
  // rehydrated below, so closing and reopening checkout never loses work.
  const _draft = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('sf-co-draft') || '{}') || {};
    } catch (e) {
      return {};
    }
  })();
  const [coords, setCoords] = useState(WA_CTX && WA_CTX.lastPin && typeof WA_CTX.lastPin.lat === "number" ? {
    lat: WA_CTX.lastPin.lat,
    lng: WA_CTX.lastPin.lng
  } : _draft.coords && typeof _draft.coords.lat === "number" ? _draft.coords : null);
  // ── Last-chance upsell line (placement 'checkout') ────────────────────────
  // One low-friction add-on (≤ AED 20), picked ONCE when the modal opens and
  // frozen (no flicker as the cart changes). Dismissible; never blocks or
  // delays payment — it is a plain receipt row above the pay button.
  const [coUpsell, setCoUpsell] = useState(null);
  const [coUpsellGone, setCoUpsellGone] = useState(false);
  // The line is frozen at open, but a dish can go off live meanwhile — read
  // today's state from `items` and drop the line if it is sold out now.
  const coUpsellOut = !!(coUpsell && (items.find(i => i.id === coUpsell.id) || coUpsell).soldOut);

  // ── Featured special (owner-set, src/lib/special.ts) ──────────────────────
  // Shown to EVERY order, above the algorithmic upsell line. Deliberately kept
  // OUTSIDE the upsell system: it does not consume a touch, does not call
  // notePush/noteSeen, and is not flagged is_upsell — otherwise a permanent
  // menu feature would drain the per-order upsell budget (starving checkout,
  // the best-converting placement at 6.3%) and pollute attach-rate reporting.
  // Copy arrives pre-localized from PaperLayout; hidden once it is in the cart.
  const [spGone, setSpGone] = useState(false);
  const special = typeof window !== 'undefined' && window.__SPECIAL__ || null;
  const specialInCart = !!(special && cart.some(l => l.id === special.slug));
  const specialItem = special ? items.find(i => i.id === special.slug) : null;
  // Never offer a special that is sold out / switched off live today.
  // KHAN: checkoutLine === false (src/lib/special.ts) = top bar only, no one-tap line here.
  const showSpecial = !!(special && special.checkoutLine !== false && specialItem && !specialItem.soldOut && !specialInCart && !spGone);
  useEffect(() => {
    if (!open) {
      setCoUpsell(null);
      setCoUpsellGone(false);
      return;
    }
    try {
      if (!(window.SFUpsell && window.SFUpsell.enabled() && window.SFUpsell.placementOn('checkout'))) return;
      const maxAed = window.SFUpsell.checkoutMaxAed || 20;
      // C10: slugs the table step offered and the customer declined are out
      // too (same union in the threshold helper below).
      const dis = new Set([...window.SFUpsell.dismissedList(), ...(hostDeclined ? Array.from(hostDeclined) : [])]);
      // Ask for the full pool (8, the ERP's own cap) rather than 6: the ribbon
      // has already claimed the top items and the cart strip the next few, so a
      // short list can leave this line with nothing eligible.
      const erp = resolveErpSuggestions(erpSuggestions, cart, items, dis, 8);
      const base = erp.length ? erp : getSuggestions(cart, null, items, dis, 8);
      const eligible = s => s.price <= maxAed && window.SFUpsell.canPush(s.id) && !cart.some(l => l.id === s.id) && !(mode !== "dinein" && DINE_IN_ONLY.has(s.id));
      // Prefer something the cart strip has NOT already put in front of them —
      // 237 session/item pairs were shown at both placements, which is the
      // "asking again for no reason" the owner objected to. canPush already
      // excludes what the ribbon pushed; `wasSeen` covers the ambient strip.
      // FALL BACK to any eligible item: on a small pool (e.g. a platter cart,
      // where the mains are stripped out) everything may already be seen, and a
      // familiar suggestion at the best-converting placement still beats none.
      const unseen = s => !(window.SFUpsell.wasSeen && window.SFUpsell.wasSeen(s.id));
      const pick = base.find(s => eligible(s) && unseen(s)) || base.find(eligible);
      if (pick) {
        setCoUpsell(pick);
        window.SFUpsell.notePush(pick.id);
        window.SFUpsell.track('shown', 'checkout', pick.id, {
          value: pick.price
        });
      }
    } catch (e) {}
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState("");
  // Clarity dead-click fixes (2026-10-16): the area hint opens the AreaPicker
  // (areaOpenRef), the "next: pin" nudge and a pin-blocked Pay bring the eye to
  // the map + GPS button (geoRef, gpsBtnRef), the Mode label points at the
  // chosen mode (modeOnRef). attnRef = the running sfAttn pulse.
  const areaOpenRef = useRef(null),
    geoRef = useRef(null),
    gpsBtnRef = useRef(null),
    modeOnRef = useRef(null),
    attnRef = useRef(0);
  useEffect(() => () => clearTimeout(attnRef.current), []);

  // Phase 8 — capture form values so we can submit a real order to the backend.
  // We also restore the customer's last-known details from localStorage so
  // repeat customers don't have to retype name/phone/address every time.
  const _savedCustomer = (() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('shaunfood-customer') : null;
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  })();
  // WhatsApp ordering: the CRM profile wins over localStorage. The phone is
  // the number the customer is chatting from — locked (channel-verified).
  const _waCustomer = WA_CTX && WA_CTX.customer ? WA_CTX.customer : null;
  const _waName = (() => {
    if (!_waCustomer) return "";
    const composed = [_waCustomer.firstName, _waCustomer.lastName].filter(Boolean).join(" ").trim();
    const n = composed || (_waCustomer.name || "").trim();
    // Auto-created contacts can be named by their phone — don't prefill that.
    return /^\+?[\d\s-]+$/.test(n) ? "" : n;
  })();
  const _waPhoneLocked = !!(_waCustomer && _waCustomer.phone);
  const _savedPhone = splitE164(_waCustomer && _waCustomer.phone || _savedCustomer.phone || "");
  const [name, setName] = useState(_waName || _draft.name || _savedCustomer.name || "");
  const [phoneDial, setPhoneDial] = useState(_waPhoneLocked ? _savedPhone.dialCode : _draft.phoneDial || _savedPhone.dialCode);
  const [phone, setPhone] = useState(_waPhoneLocked ? _savedPhone.local : _draft.phone || _savedPhone.local);
  // K1 — structured delivery address (Apartment / Villa / Office). An old
  // single-field value (a pre-Phase-2 draft, a saved customer, a WhatsApp
  // street) loads as Apartment → Building; while that imported text is left
  // as it was, Flat no. is not demanded (the old line usually carries it).
  const _addrInit = (() => {
    const fromParts = src => src && src.addrParts && typeof src.addrParts === 'object' ? {
      type: ADDR_TYPES.some(([k]) => k === src.addrType) ? src.addrType : 'apt',
      parts: cleanAddrParts(src.addrParts),
      legacy: typeof src.addrLegacy === 'string' ? src.addrLegacy : ''
    } : null;
    const fromLegacy = s => typeof s === 'string' && s.trim() ? {
      type: 'apt',
      parts: {
        ...EMPTY_ADDR,
        building: s.trim().slice(0, 200)
      },
      legacy: s.trim().slice(0, 200)
    } : null;
    return fromParts(_draft) || fromLegacy(_draft.address) || fromParts(_savedCustomer) || fromLegacy(_savedCustomer.address) || {
      type: 'apt',
      parts: {
        ...EMPTY_ADDR
      },
      legacy: ''
    };
  })();
  const [addrType, setAddrType] = useState(_addrInit.type);
  const [addrParts, setAddrParts] = useState(_addrInit.parts);
  const [addrLegacy, setAddrLegacy] = useState(_addrInit.legacy);
  const setPart = (k, v) => setAddrParts(p => ({
    ...p,
    [k]: v
  }));
  const address = composeAddress(addrType, addrParts);
  const _flatWaived = addrType === 'apt' && !!addrLegacy && addrParts.building.trim() === addrLegacy.trim();
  // K2 — drop-off (delivery) + free extras. `*Touched` = the customer chose it
  // themselves, so the automatic defaults never override them again.
  const [dropoff, setDropoff] = useState(DROPOFFS.some(([k]) => k === _draft.dropoff) ? _draft.dropoff : 'hand');
  const [dropTouched, setDropTouched] = useState(!!_draft.dropTouched);
  const [lemon, setLemon] = useState(!!_draft.lemon);
  const [spoons, setSpoons] = useState(!!_draft.spoons);
  const [spoonsTouched, setSpoonsTouched] = useState(!!_draft.spoonsTouched);
  const _cartHasSoup = cart.some(l => SOUP_SLUGS.has(l.id)) || cartSoupsIn(cart, items) > 0;
  useEffect(() => {
    if (!spoonsTouched) setSpoons(_cartHasSoup);
  }, [_cartHasSoup, spoonsTouched]);
  // K3 — device-saved addresses. A saved pin is shown but must be confirmed
  // with one tap before Pay (the deliberate-pin rule).
  const [savedAddrs, setSavedAddrs] = useState(() => WA_CTX ? [] : loadSavedAddrs());
  const [savedPick, setSavedPick] = useState(-1);
  const [pinNeedsConfirm, setPinNeedsConfirm] = useState(!!_draft.pinNeedsConfirm);
  const [saveLabel, setSaveLabel] = useState(ADDR_LABELS.some(([k]) => k === _draft.saveLabel) ? _draft.saveLabel : savedAddrs.length ? 'other' : 'home');
  const [saveLabelTouched, setSaveLabelTouched] = useState(!!_draft.saveLabelTouched);
  // Until the customer picks a label, it follows the address: the label it is
  // already saved under, else Home for the first address and Other after that.
  useEffect(() => {
    if (saveLabelTouched) return;
    const _k = savedAddrKey({
      area: deliveryArea || '',
      addressParts: {
        type: addrType,
        ...addrParts
      }
    });
    const hit = savedAddrs.find(x => savedAddrKey(x) === _k);
    setSaveLabel(hit && ADDR_LABELS.some(([k]) => k === hit.label) ? hit.label : savedAddrs.length ? 'other' : 'home');
  }, [savedAddrs, deliveryArea, addrType, addrParts.building, addrParts.villa, addrParts.company, saveLabelTouched]); // eslint-disable-line react-hooks/exhaustive-deps
  const phoneRef = useRef(null);
  // One-tap saved addresses (CRM address book + recent website orders).
  const _waAddresses = (() => {
    if (!WA_CTX) return [];
    const out = [];
    (WA_CTX.addresses || []).forEach(a => out.push({
      label: a.label || a.areaLabel || "Saved address",
      areaKey: a.areaKey || null,
      areaLabel: a.areaLabel || null,
      street: a.streetAddress || "",
      lat: a.lat,
      lng: a.lng
    }));
    (WA_CTX.derivedAddresses || []).forEach(a => out.push({
      label: a.areaLabel || "Previous order",
      areaKey: null,
      areaLabel: a.areaLabel || null,
      street: a.streetAddress || "",
      lat: a.lat,
      lng: a.lng
    }));
    return out.slice(0, 5);
  })();
  const _waPickAddress = a => {
    // Resolve the fee-driving area key: stored key first, else match the
    // label against the areas table (derived addresses carry only a label).
    let key = a.areaKey;
    if (!key && a.areaLabel) {
      const norm = a.areaLabel.trim().toLowerCase();
      const hit = DELIVERY_AREAS.find(d => d.en.toLowerCase() === norm || d.fa === a.areaLabel.trim());
      if (hit) key = hit.key;
    }
    if (key && findArea(key)) setDeliveryArea(key);
    if (a.street) {
      const s = String(a.street).trim().slice(0, 200);
      setAddrType('apt');
      setAddrParts({
        ...EMPTY_ADDR,
        building: s
      });
      setAddrLegacy(s);
    }
    if (typeof a.lat === "number" && typeof a.lng === "number") {
      setCoords({
        lat: a.lat,
        lng: a.lng
      });
      setGeoError("");
      setPinNeedsConfirm(false);
    }
    setMode("delivery");
  };
  // Dine-in ASAP: when will the guest arrive? Rides into the kitchen inside
  // the notes so no server change is needed. "now" | "20" | "40" | "60".
  const [arrivalTime, setArrivalTime] = useState("now");
  // Marketing consent. Starts FALSE and is never pre-ticked — a pre-ticked box
  // is not consent. Purely optional: it never blocks the order, and order
  // updates are sent regardless because those concern an order they placed.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Discount code (validated against the ERP via /api/orders/validate-discount).
  const [discInput, setDiscInput] = useState(() => {
    // ?code=SANGAK20 from an Instagram ad prefills the code entry.
    try {
      return (new URLSearchParams(window.location.search).get('code') || '').toUpperCase().slice(0, 24);
    } catch (e) {
      return "";
    }
  });
  const [disc, setDisc] = useState(null);
  const [discErr, setDiscErr] = useState("");
  const [discBusy, setDiscBusy] = useState(false);
  // Intro-loyalty preview: the % this phone earns (display-only; the server
  // re-derives the authoritative discount at create). Fetched when the phone
  // becomes a valid UAE mobile. Debounced; never blocks checkout.
  const [loyaltyPct, setLoyaltyPct] = useState(introPct || 0);
  useEffect(() => {
    if (_waPhoneLocked) {
      setLoyaltyPct(0);
      return;
    } // intro is web-only
    const e164 = buildE164(phone, phoneDial);
    // Until a valid phone is typed, keep showing the cart's optimistic tier
    // rather than dropping to 0 (the discount must not flicker away mid-typing).
    if (!isValidUaePhone(e164)) {
      setLoyaltyPct(introPct || 0);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      fetch("/api/orders/loyalty-preview", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          phone: e164,
          subtotal_aed: 0
        })
      }).then(r => r.ok ? r.json() : null).then(j => {
        if (alive && j) setLoyaltyPct(Number(j.percent) || 0);
      }).catch(() => {});
    }, 450);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [phone, phoneDial, _waPhoneLocked]);
  const [notes, setNotes] = useState(_draft.notes || "");
  // L3 — "Sending to someone else?" (delivery only). The payer stays the
  // order's name/phone (receipt, intro ladder, WhatsApp); the recipient rides
  // as the first kitchen/rider notes lines.
  const _ds = k => typeof _draft[k] === 'string' ? _draft[k] : "";
  const [giftOn, setGiftOn] = useState(!!_draft.giftOn);
  const [giftName, setGiftName] = useState(_ds('giftName'));
  const [giftPhone, setGiftPhone] = useState(_ds('giftPhone'));
  const [giftMsg, setGiftMsg] = useState(_ds('giftMsg'));
  const _gift = giftOn && mode === "delivery";
  // L4 — company tax invoice (collapsed unless the draft already holds one).
  const [invCompany, setInvCompany] = useState(_ds('invCompany'));
  const [invTrn, setInvTrn] = useState(_ds('invTrn'));
  const [invAddr, setInvAddr] = useState(_ds('invAddr'));
  const [invOpen, setInvOpen] = useState(!!(_ds('invCompany') || _ds('invTrn') || _ds('invAddr')));
  // Debounced draft writer — every keystroke survives a stray close or crash.
  // placedRef stops a trailing debounce from resurrecting the draft after the
  // order is placed and the draft deliberately cleared.
  const placedRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (placedRef.current) return;
      try {
        sessionStorage.setItem('sf-co-draft', JSON.stringify({
          name,
          phone,
          phoneDial,
          address,
          addrType,
          addrParts,
          addrLegacy,
          notes,
          coords,
          dropoff,
          dropTouched,
          lemon,
          spoons,
          spoonsTouched,
          pinNeedsConfirm,
          saveLabel,
          saveLabelTouched,
          giftOn,
          giftName,
          giftPhone,
          giftMsg,
          invCompany,
          invTrn,
          invAddr
        }));
      } catch (e) {/* private mode */}
    }, 400);
    return () => clearTimeout(t);
  }, [name, phone, phoneDial, address, addrType, addrParts, addrLegacy, notes, coords, dropoff, dropTouched, lemon, spoons, spoonsTouched, pinNeedsConfirm, saveLabel, saveLabelTouched, giftOn, giftName, giftPhone, giftMsg, invCompany, invTrn, invAddr]);
  // K3: re-read the device's saved addresses each time checkout opens (the
  // modal stays mounted between opens).
  useEffect(() => {
    if (open && !WA_CTX) {
      setSavedAddrs(loadSavedAddrs());
      setSavedPick(-1);
    }
  }, [open]);
  const _pickSaved = (x, i) => {
    const p = x.addressParts || {};
    if (x.area && findArea(x.area)) {
      setDeliveryArea(x.area);
      clearErr('area');
    }
    setAddrType(ADDR_TYPES.some(([k]) => k === p.type) ? p.type : 'apt');
    setAddrParts(cleanAddrParts(p));
    // A one-line pre-Phase-2 address saved with the flat waived keeps its
    // waiver (it only holds while the building text stays unchanged).
    setAddrLegacy(typeof x.addrLegacy === 'string' ? x.addrLegacy.slice(0, 200) : '');
    ['address', 'flat', 'makani'].forEach(clearErr);
    // A remembered special drop-off carries over as the customer's own choice
    // (only a drop-off they picked themselves is ever saved); a plain "hand
    // it to me" leaves the early-hours default free to apply.
    if (x.dropoff && x.dropoff !== 'hand' && DROPOFFS.some(([k]) => k === x.dropoff)) {
      setDropoff(x.dropoff);
      setDropTouched(true);
    } else {
      setDropTouched(false);
    }
    if (x.coords && typeof x.coords.lat === 'number' && typeof x.coords.lng === 'number') {
      setCoords({
        lat: x.coords.lat,
        lng: x.coords.lng
      });
      setGeoError("");
      setPinNeedsConfirm(true); // shown, but one tap must confirm it
    } else {
      setCoords(null);
      setPinNeedsConfirm(false);
    }
    setSaveLabelTouched(false); // the label follows the picked entry
    setSavedPick(i);
  };
  const _newAddr = () => {
    setAddrType('apt');
    setAddrParts({
      ...EMPTY_ADDR
    });
    setAddrLegacy('');
    setCoords(null);
    setPinNeedsConfirm(false);
    setDeliveryArea("");
    setDropTouched(false);
    setSavedPick(-1);
    setSaveLabelTouched(false);
  };
  const _forgetSaved = i => {
    const list = savedAddrs.filter((_, j) => j !== i);
    writeSavedAddrs(list);
    setSavedAddrs(list);
    if (savedPick === i) setSavedPick(-1);else if (savedPick > i) setSavedPick(savedPick - 1);
  };
  // Pre-order (scheduled) state. 'asap' = today's behavior; 'later' shows a picker.
  const [whenMode, setWhenMode] = useState("asap");
  const [scheduledFor, setScheduledFor] = useState("");
  // Pre-order front door: the home-page card opens checkout with Schedule
  // pre-selected (the slot-picker effect below then auto-fills the first slot).
  // The modal stays mounted between opens, so undo that pre-selection when a
  // card-armed session closes — otherwise a later "order now" from the cart bar
  // would silently place a scheduled order for the auto-picked slot.
  // An explicit request from App (cart Now | Schedule switch, the pre-order
  // door, reorder, a placed order) is applied once, on the next open; only a
  // door request arms the disarm-on-close.
  const whenArmedRef = useRef(false);
  const whenReqSeenRef = useRef(0);
  // L5: an occasion card's slot preference ({kind:'suhoor',from,to} |
  // {kind:'friday'}) — _defaultSlot honours it before its own night rule.
  const [slotPref, setSlotPref] = useState(null);
  const _whenReqN = whenReq ? whenReq.n : 0;
  useEffect(() => {
    if (open && whenReq && whenReq.n !== whenReqSeenRef.current) {
      whenReqSeenRef.current = whenReq.n;
      if (whenReq.v === 'later') {
        setWhenMode('later');
        whenArmedRef.current = !!whenReq.armed;
        setSlotPref(whenReq.pref || null);
      } else {
        setWhenMode('asap');
        whenArmedRef.current = false;
        setSlotPref(null);
      }
    }
    // A gathering has no ASAP to fall back to, so closing keeps the customer's
    // picked day/time (the disarm only exists to stop a silent scheduled order
    // when "now" is possible). The arm stays set for when the cart drops below 10.
    else if (!open && whenArmedRef.current && !gatherActive) {
      whenArmedRef.current = false;
      setWhenMode('asap');
      setScheduledFor('');
      setSlotPref(null);
    }
  }, [open, _whenReqN]); // eslint-disable-line react-hooks/exhaustive-deps
  // Report the real mode up so the cart switch always shows it.
  useEffect(() => {
    if (onWhenSync) onWhenSync(whenMode);
  }, [whenMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // datetime-local is zoneless — we interpret its value as Asia/Dubai on submit.
  // Picker bounds in Dubai wall-clock; lead is delivery 90 / dine-in 30 min, horizon 2 days.
  // Lead times mirror the server's PREP_LEAD_MIN (create.ts): delivery 90,
  // pickup 45, dine-in 30. On the WhatsApp page the non-delivery mode IS
  // pickup — using dine-in's 30 here would let customers pick a time the
  // server then rejects with "at least 45 minutes".
  const SCHED_LEAD_MIN = SCHED_LEAD_BY_MODE[mode] ?? 45;
  const dubaiLocalInput = d => {
    const p = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Dubai",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(d);
    const g = t => (p.find(x => x.type === t) || {}).value;
    return g("year") + "-" + g("month") + "-" + g("day") + "T" + g("hour") + ":" + g("minute");
  };
  // Kitchen paused (ERP): the kitchen receives a pre-order at its FIRE time
  // (scheduled_for − the mode's prep lead), so the fire time must not fall
  // inside the pause — nothing earlier than pause end + lead for this mode,
  // the same floor create.ts enforces — rounded UP to the minute so a slot on
  // the boundary can never land a few seconds short of it.
  const pauseMinMs = paused ? Math.ceil((Date.parse(paused.until) + SCHED_LEAD_MIN * 60000) / 60000) * 60000 : 0;
  // Phase 3 (L2): a gathering (came via /gatherings AND the cart feeds 10+)
  // schedules from tomorrow — the day after once Dubai passes 20:00 — through
  // Dubai today + 7, every 30-min slot of those days; ASAP is off. Below 10
  // servings the normal rules apply (the flag itself is kept).
  const gatherServings = gather && !WA_CTX ? cartServings(cart, items) : 0;
  const gatherActive = !!gather && !WA_CTX && gatherServings >= GATHER_MIN_SERVINGS;
  // KHAN (owner 2026-10-04): a cart holding the Khan's Platter books up to
  // Dubai today + 7 (create.ts / src/lib/khan.ts), no gathering rules. A
  // gathering keeps its own window. Removing the Khan → the re-pick effect
  // below falls back to a slot inside the normal 2-day horizon.
  const khanCart = !gatherActive && cartHasKhan(cart);
  // (Declared AFTER gatherActive: its deps array is read during render.)
  // The gathering ended while checkout was CLOSED (cart dropped below 10
  // servings, or emptied): the disarm the close skipped is due now, or the next
  // open would still carry a gathering day beyond the normal 2-day window.
  useEffect(() => {
    if (!open && !gatherActive && whenArmedRef.current) {
      whenArmedRef.current = false;
      setWhenMode('asap');
      setScheduledFor('');
      setSlotPref(null);
    }
  }, [open, gatherActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const _nowDubai = dubaiLocalInput(new Date());
  const _dayPlus = (d, n) => dubaiLocalInput(new Date(new Date(d + "T12:00:00+04:00").getTime() + n * 86400000)).slice(0, 10);
  const gatherFirstDay = _dayPlus(_nowDubai.slice(0, 10), Number(_nowDubai.slice(11, 13)) < GATHER_CUTOFF_HOUR ? 1 : 2);
  const gatherLastDay = _dayPlus(_nowDubai.slice(0, 10), GATHER_MAX_DAYS);
  const khanLastDay = _dayPlus(_nowDubai.slice(0, 10), KHAN_MAX_DAYS);
  const _schedMinBase = dubaiLocalInput(new Date(Math.max(Date.now() + SCHED_LEAD_MIN * 60000, pauseMinMs || 0)));
  const schedMin = gatherActive && gatherFirstDay + "T00:00" > _schedMinBase ? gatherFirstDay + "T00:00" : _schedMinBase;
  const schedMax = gatherActive ? gatherLastDay + "T23:30" : khanCart ? khanLastDay + "T23:30" : dubaiLocalInput(new Date(Date.now() + 2 * 86400000));
  // Pre-order picker options: days + 30-min slots derived from the same
  // bounds, so an invalid time simply cannot be chosen (the submit-time
  // errTime checks stay as a server-matching backstop).
  const _schedToday = dubaiLocalInput(new Date()).slice(0, 10);
  const _schedTomorrow = dubaiLocalInput(new Date(Date.now() + 86400000)).slice(0, 10);
  const _slotsFor = day => {
    const out = [];
    for (let h = 0; h < 24; h++) for (const m of ["00", "30"]) {
      const v = day + "T" + String(h).padStart(2, "0") + ":" + m;
      if (v >= schedMin && v <= schedMax) out.push(v.slice(11));
    }
    return out;
  };
  const _schedDays = (() => {
    const days = [];
    let cur = schedMin.slice(0, 10);
    const dMax = schedMax.slice(0, 10);
    let guard = 0;
    while (cur <= dMax && guard++ < 10) {
      days.push(cur);
      cur = dubaiLocalInput(new Date(new Date(cur + "T12:00:00+04:00").getTime() + 86400000)).slice(0, 10);
    }
    // Midnight edge: when schedMin lands after 23:30, "today" has zero 30-min
    // slots left — offer only days that actually have a pickable time.
    return days.filter(d => _slotsFor(d).length > 0);
  })();
  // Labels shared by the day tabs / slot chips (K5) and the list fallback.
  const schedDayLabel = (d, wdStyle) => {
    if (d === _schedToday) return tr(L, 'co.today');
    if (d === _schedTomorrow) return tr(L, 'co.tomorrow');
    try {
      return new Intl.DateTimeFormat(L === 'ar' ? 'ar-AE-u-nu-latn' : L === 'fa' ? 'fa-IR' : L === 'tr' ? 'tr-TR' : 'en-GB', wdStyle === 'long' ? {
        weekday: 'long',
        timeZone: 'Asia/Dubai'
      } : {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'Asia/Dubai'
      }).format(new Date(d + 'T12:00:00+04:00'));
    } catch (e) {
      return d;
    }
  };
  const schedTimeLabel = t => {
    if (L !== 'ar') return numLocal(L, t);
    const [h, m] = t.split(':').map(Number);
    return (h % 12 || 12) + ':' + String(m).padStart(2, '0') + (h < 12 ? ' ص' : ' م');
  };
  // Area answers the map: choosing a district pans the map there (view only —
  // the pin itself stays a deliberate tap).
  const [areaFocus, setAreaFocus] = useState(null);
  useEffect(() => {
    if (mode !== "delivery" || !deliveryArea || coords) return;
    let alive = true;
    geocodeAreaCenter(deliveryArea).then(pt => {
      if (alive && pt) setAreaFocus(pt);
    });
    return () => {
      alive = false;
    };
  }, [deliveryArea, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The slot Schedule pre-selects. Normally the earliest one — but when that
  // falls in the night (00:00-05:59) and the kitchen is not paused, a breakfast
  // house's customer almost surely means the MORNING: pre-select the first
  // breakfast slot (07:00-11:00) instead, so nobody books 00:00 by not looking.
  // L5: the occasion card's preference — Ramadan: the first slot inside the
  // suhoor window (tonight's, as days run in order); Thursday: Friday's first
  // breakfast slot. Bounded by the same _schedDays/_slotsFor, so never invalid.
  const _prefSlot = pref => {
    if (!pref) return "";
    for (const d of _schedDays) {
      if (pref.kind === 'friday' && new Date(d + 'T12:00:00+04:00').getUTCDay() !== 5) continue;
      const t = _slotsFor(d).find(x => pref.kind === 'suhoor' ? inTimeWindow(x, pref.from, pref.to) : x >= "07:00" && x <= "11:00");
      if (t) return d + "T" + t;
    }
    return "";
  };
  const _defaultSlot = () => {
    const pv = _prefSlot(slotPref);
    if (pv) return pv; // an explicit occasion preference beats the night rule
    const d0 = _schedDays[0];
    const s0 = d0 ? _slotsFor(d0) : [];
    if (!d0 || !s0[0]) return "";
    if (paused || s0[0] >= "06:00") return d0 + "T" + s0[0];
    for (const d of _schedDays) {
      const b = _slotsFor(d).find(t => t >= "07:00" && t <= "11:00");
      if (b) return d + "T" + b;
    }
    return d0 + "T" + s0[0];
  };
  useEffect(() => {
    if (whenMode !== "later" || scheduledFor) return;
    const v = _defaultSlot();
    if (v) setScheduledFor(v);
  }, [whenMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // Kitchen paused: ASAP is off, so force Schedule and pull a slot that is now
  // too early up to the first selectable one. Armed like the pre-order door so
  // closing checkout drops the forced schedule (a later "order now" after the
  // pause ends must not silently become a scheduled order).
  const pausedUntil = paused ? paused.until : "";
  useEffect(() => {
    if (!open || !pausedUntil) return;
    if (whenMode !== "later") {
      setWhenMode("later");
      whenArmedRef.current = true;
      return;
    }
    if (!scheduledFor || scheduledFor < schedMin) {
      const d = _schedDays[0];
      const slots = d ? _slotsFor(d) : [];
      if (d && slots[0]) setScheduledFor(d + "T" + slots[0]);
    }
  }, [open, pausedUntil, whenMode, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // The floor moves with the mode (delivery's lead is longer than dine-in's):
  // a slot picked under one mode can fall below the new floor — the <select>
  // would then show its first option while state keeps the stale slot and Pay
  // is refused. Re-clamp to the first selectable slot whenever the mode changes,
  // checkout (re)opens or Schedule is (re)chosen — a mode switched while the
  // modal was closed or on ASAP, or a slot gone stale over time, is caught too.
  useEffect(() => {
    if (!open || whenMode !== "later" || !scheduledFor || scheduledFor >= schedMin) return;
    const d = _schedDays[0];
    const slots = d ? _slotsFor(d) : [];
    if (d && slots[0]) setScheduledFor(d + "T" + slots[0]);
  }, [mode, open, whenMode]); // eslint-disable-line react-hooks/exhaustive-deps
  // L2: a gathering forces Schedule (armed; a close keeps the pick while the
  // gathering is active — see the whenReq effect) and keeps the slot inside
  // its window. Only two cases re-pick here: a gathering slot below the
  // gathering window (e.g. today's slot when the cart climbs to 10), and a
  // slot past the current max (the cart fell below 10 → back to 2 days).
  // Every other stale-low slot keeps the Phase-1 re-clamp above (first
  // selectable slot, no night→breakfast jump). Declared after the re-clamp so
  // its pick wins in the same commit.
  useEffect(() => {
    if (!open) return;
    if (gatherActive && whenMode !== "later") {
      setWhenMode("later");
      whenArmedRef.current = true;
      return;
    }
    if (whenMode === "later" && scheduledFor && gatherActive && scheduledFor < schedMin) {
      const v = _defaultSlot();
      if (v) setScheduledFor(v);
    } else if (whenMode === "later" && scheduledFor && scheduledFor > schedMax) {
      // Past the max (the Khan or the gathering left the cart): keep the
      // customer's day as far as possible — clamp DOWN to the latest valid
      // slot not after the chosen one — and say so under the picker (the
      // next pick clears it; submit recomputes its own errors).
      let v = "";
      for (let i = _schedDays.length - 1; i >= 0 && !v; i--) {
        const d = _schedDays[i];
        if (d > scheduledFor.slice(0, 10)) continue;
        const s = _slotsFor(d).filter(t => d + "T" + t <= scheduledFor);
        if (s.length) v = d + "T" + s[s.length - 1];
      }
      if (!v) v = _defaultSlot();
      if (v) {
        setScheduledFor(v);
        setFieldErrs(f => ({
          ...f,
          time: khanCart ? "co.errTimeMax7" : "co.errTimeMax"
        }));
      }
    }
  }, [open, gatherActive, khanCart, whenMode, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // L5: apply an occasion preference once per door request, when Schedule
  // shows — re-choosing Schedule later must not overwrite a hand-picked slot
  // (a stale one is still caught by the re-clamp above).
  const prefAppliedRef = useRef(null);
  useEffect(() => {
    if (!open || whenMode !== "later" || !slotPref || prefAppliedRef.current === whenReqSeenRef.current) return;
    prefAppliedRef.current = whenReqSeenRef.current;
    const v = _prefSlot(slotPref);
    if (v) setScheduledFor(v);
  }, [open, whenMode, slotPref]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ramadan: slots inside the suhoor window are grouped first under "Suhoor".
  const suhoorWin = slotPref && slotPref.kind === 'suhoor' ? slotPref : (occasionData() || {}).active === 'ramadan' ? suhoorWindow() : null;
  // K2: early-hours default — ASAP between 00:00 and 08:59 Dubai, or a
  // scheduled slot before 09:00, preselects "Don't ring the bell". Only while
  // the customer has not chosen a drop-off themselves.
  const _earlyHours = whenMode === "later" ? !!scheduledFor && Number(scheduledFor.slice(11, 13)) < 9 : dubaiHourNow() < 9;
  useEffect(() => {
    if (!open || dropTouched) return;
    setDropoff(_earlyHours ? 'nobell' : 'hand');
  }, [open, _earlyHours, dropTouched]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // One-pass validation marks (field key → dict error key) + phone blur tracking.
  const [fieldErrs, setFieldErrs] = useState({});
  const clearErr = k => setFieldErrs(f => {
    if (!f[k]) return f;
    const n = {
      ...f
    };
    delete n[k];
    return n;
  });
  const [phoneTouched, setPhoneTouched] = useState(false);
  // True once the "Connecting to payment…" overlay has been up unusually long,
  // so we can offer a manual escape instead of an endless spinner.
  const [stalled, setStalled] = useState(false);

  // ── Mobile "stuck on Connecting to payment…" rescue ──────────────────────
  // Tapping Stamp & Send shows the pay overlay, then redirects to the N-Genius
  // hosted page. On mobile, returning from the gateway (browser back, edge-
  // swipe, or the app switcher) restores THIS page from the back-forward cache
  // with its JS heap frozen — so `submitting` stays true and the overlay hangs
  // on "در حال اتصال به درگاه پرداخت…" forever. The robust cure is NOT to toggle
  // React state on the frozen tree (which can refuse to re-paint) but to LEAVE:
  // the moment the page is shown again we navigate to the order's /success page,
  // which reconciles with the gateway and shows the true state (paid timeline,
  // or "payment not completed → Pay now", or cancelled).
  const redirectingRef = useRef(false); // we sent the customer to the gateway
  const leftRef = useRef(false); // ...and the page actually went away
  const lastOrderRef = useRef(null);
  const payUrlRef = useRef(""); // the gateway URL we are sending them to
  const goToTracking = () => {
    const o = lastOrderRef.current;
    redirectingRef.current = false;
    leftRef.current = false;
    if (o && o.order_number) {
      window.location.replace("/success?order=" + encodeURIComponent(o.order_number));
    } else {
      // "Taking too long? Cancel" before create answered: drop that submit, so
      // a late answer can never still send the customer to the bank.
      submitGenRef.current++;
      CO_SUBMITTING = false;
      setSubmitting(false);
      setStalled(false);
    }
  };
  useEffect(() => {
    // pageshow fires ONLY when the page is (re)shown — never during the outbound
    // navigation to the gateway — so `redirectingRef.current` being true at
    // pageshow is BY ITSELF proof of a bfcache restore after we sent the user to
    // pay. Gate it on redirectingRef ALONE: no dependency on pagehide/leftRef
    // (which, if it ever failed to fire, would leave the overlay stuck — exactly
    // the bug we're killing). We also do NOT require event.persisted (many mobile
    // browsers restore from bfcache with it false).
    const onPageShow = () => {
      if (redirectingRef.current) goToTracking();
    };
    // focus / visibilitychange CAN fire spuriously (keyboard, app-switch glances),
    // so gate THOSE behind "the page actually left" (pagehide) to avoid bouncing
    // the user to /success before they ever reach the gateway.
    const onPageHide = () => {
      if (redirectingRef.current) leftRef.current = true;
    };
    const onMaybeReturn = () => {
      if (redirectingRef.current && leftRef.current) goToTracking();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onMaybeReturn();
    };
    // Back pressed while the bank page is still loading: the same-document
    // traversal cancels that navigation (checkout itself stays open — the
    // layer holds). The page never left (no pagehide), so start it again
    // rather than leave the customer on a dead spinner.
    const onPopState = () => {
      if (redirectingRef.current && !leftRef.current && payUrlRef.current) window.location.href = payUrlRef.current;
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("focus", onMaybeReturn);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("focus", onMaybeReturn);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  // Safety net: if the overlay is still up after 8s (the redirect silently
  // failed, or an exotic browser fired none of the events above), reveal a
  // manual escape so the customer is never trapped on the spinner.
  useEffect(() => {
    if (!submitting) {
      setStalled(false);
      return;
    }
    const t = setTimeout(() => setStalled(true), 8000);
    return () => clearTimeout(t);
  }, [submitting]);
  // Back is held while a submit is in flight (App's checkout layer reads this).
  useEffect(() => {
    CO_SUBMITTING = submitting;
    return () => {
      CO_SUBMITTING = false;
    };
  }, [submitting]);
  // If checkout closes mid-submit anyway, drop the submit: clear the overlay
  // state and bump the generation, so the in-flight handler never sends the
  // customer to the bank for an order they walked away from.
  const submitGenRef = useRef(0);
  useEffect(() => {
    if (open || !(submitting || redirectingRef.current)) return;
    submitGenRef.current++;
    redirectingRef.current = false;
    leftRef.current = false;
    CO_SUBMITTING = false;
    setSubmitting(false);
    setStalled(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pinLocation = () => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("geo.noSupport");
      return;
    }
    setGeoBusy(true);

    // Use low-accuracy (IP/Wi-Fi positioning) by default. High-accuracy GPS
    // hangs on desktop Chrome — it can sit forever waiting for a satellite
    // that doesn't exist on a laptop. Low-accuracy returns in ~1 second and
    // is plenty precise for delivery rider hand-off (within ~50–200m).
    navigator.geolocation.getCurrentPosition(pos => {
      setCoords({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      });
      setPinNeedsConfirm(false);
      clearErr('pin');
      setGeoBusy(false);
      setGeoError("");
    }, err => {
      const msg = err.code === 1 ? "geo.denied" : err.code === 2 ? "geo.unavailable" : err.code === 3 ? "geo.timeout" : "geo.failed";
      setGeoError(msg);
      setGeoBusy(false);
    }, {
      enableHighAccuracy: false,
      timeout: 12000,
      maximumAge: 60000
    });

    // Hard fail-safe: if the browser silently hangs without firing either
    // callback (some desktops never return for low-accuracy either), force
    // the UI back to a usable state after 15 seconds so the user can retry.
    setTimeout(() => {
      setGeoBusy(busy => {
        if (busy) {
          setGeoError("geo.slow");
          return false;
        }
        return busy;
      });
    }, 15000);
  };
  // Clarity 2026-10-16: "Next: set your pin on the map ↓" — bring the modal to
  // the map (the validation reveal's own instant scrollIntoView) and pulse GPS.
  // Never triggers GPS itself: the customer still taps it (or drags the map).
  const goToPin = () => {
    clarityEvent('pin_nudge_tap');
    const g = geoRef.current,
      b = gpsBtnRef.current;
    if (g) {
      try {
        g.scrollIntoView({
          block: 'center'
        });
      } catch (e) {}
    }
    if (b) {
      try {
        b.focus({
          preventScroll: true
        });
      } catch (e) {}
    }
    sfAttn(b, attnRef);
  };
  if (!open) return null;
  const lines = cart.map(l => ({
    l,
    item: items.find(i => i.id === l.id)
  })).filter(x => x.item);
  const subtotal = lines.reduce((s, x) => s + x.item.price * x.l.qty, 0);
  // Discount off the FOOD SUBTOTAL (mirrors the ERP formula; re-validated
  // server-side at create). VAT is then charged on the reduced amount.
  const round2d = n => Math.round(n * 100) / 100;
  const codeDiscountAed = disc ? Math.max(0, Math.min(disc.type === 'percent' ? round2d(subtotal * disc.value / 100) : disc.value, subtotal)) : 0;
  // Flash promo (World Cup Night) auto-applies during its window; take the
  // larger of any manual code and the promo (server enforces the same).
  const promoAed = wcPromoDiscount(subtotal);
  // Intro-loyalty preview (display-only; server re-derives at create). Whole-AED
  // rounding to MATCH the server (intro-tiers introDiscountAed) + the promo path,
  // so the shown grand total equals what the customer is charged.
  const loyaltyAed = loyaltyPct > 0 ? Math.round(subtotal * loyaltyPct / 100) : 0;
  const discountAed = Math.max(codeDiscountAed, promoAed, loyaltyAed);
  // Single summary line: mirror the server precedence — intro wins only if
  // strictly larger than both code and promo; else promo; else code.
  const loyaltyApplied = loyaltyAed > 0 && loyaltyAed > promoAed && loyaltyAed > codeDiscountAed;
  const promoApplied = !loyaltyApplied && promoAed > 0 && promoAed >= codeDiscountAed;
  const discountedSubtotal = round2d(subtotal - discountAed);
  const service = Math.round(discountedSubtotal * SERVICE_FEE_PCT / 100);
  const applyDiscount = async () => {
    const code = discInput.trim();
    if (!code) {
      setDiscErr("co.discEnter");
      return;
    }
    setDiscBusy(true);
    setDiscErr("");
    try {
      const r = await fetch("/api/orders/validate-discount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          code,
          subtotal_aed: subtotal
        })
      });
      const j = await r.json();
      if (j && j.valid) {
        setDisc({
          code: j.code,
          type: j.discount_type,
          value: Number(j.value),
          message: j.message
        });
        setDiscErr("");
      } else {
        setDisc(null);
        setDiscErr(L === 'en' && j && j.message || "co.discInvalid");
      }
    } catch (e) {
      setDisc(null);
      setDiscErr("co.discFailed");
    }
    setDiscBusy(false);
  };
  const removeDiscount = () => {
    setDisc(null);
    setDiscInput("");
    setDiscErr("");
  };
  // Test bypass: a cart with the payment-test item skips delivery fee (so total = 1 AED).
  const cartHasTestItem = lines.some(x => x.item.id === 'test-1aed');
  // Per-area delivery fee, driven by the Area selector below. Until an area is
  // picked the fee is unknown (areaFee == null) and the Place button stays
  // disabled, so `delivery` is only ever non-zero once a real area is chosen.
  const area = findArea(deliveryArea);
  // Discounted fee (what create.ts charges); area.fee stays the "was" price.
  const areaFee = area ? areaFeeNow(area.fee) : null;
  const needsArea = mode === "delivery" && !cartHasTestItem && areaFee == null;
  // Free delivery above the threshold (server enforces the same at create). The
  // area is still required — we still need to know WHERE to deliver.
  const freeDelivery = mode === "delivery" && !cartHasTestItem && subtotal >= FREE_DELIVERY_ABOVE_AED;
  const delivery = mode === "delivery" && !cartHasTestItem && areaFee != null && !freeDelivery ? areaFee : 0;
  const total = discountedSubtotal + service + delivery;
  // Min order threshold for delivery: lowest min_order_aed across active zones.
  // The server-side enforces per-zone after the address is geomatched; this is
  // the first-line UX guard so the customer can't even submit a too-small cart.
  const {
    min: minOrderForDelivery,
    shortfall: deliveryShortfall
  } = minOrderShortfall(mode, subtotal, cartHasTestItem);
  // Threshold nudge (placement 'threshold'): the min-order warning already
  // BLOCKS the sale — pair it with the one suggestion that unblocks it.
  // Prefer the cheapest item covering the whole shortfall; else the priciest
  // candidate (customer can bump qty). Pure render-time pick, no side effects.
  const shortfallPick = (() => {
    if (deliveryShortfall <= 0) return null;
    try {
      if (!(window.SFUpsell && window.SFUpsell.enabled() && window.SFUpsell.placementOn('threshold'))) return null;
      const dis = new Set([...window.SFUpsell.dismissedList(), ...(hostDeclined ? Array.from(hostDeclined) : [])]);
      const erp = resolveErpSuggestions(erpSuggestions, cart, items, dis, 8);
      const base = erp.length ? erp : getSuggestions(cart, null, items, dis, 8);
      const eligible = base.filter(s => !cart.some(l => l.id === s.id) && !(mode !== "dinein" && DINE_IN_ONLY.has(s.id)));
      if (!eligible.length) return null;
      const covering = eligible.filter(s => s.price >= deliveryShortfall).sort((a, b) => a.price - b.price);
      const pick = covering[0] || eligible.slice().sort((a, b) => b.price - a.price)[0];
      // 'shown' here is safe to fire from render: SFUpsell dedupes per
      // (placement|slug) per order, so re-renders produce exactly one beacon.
      if (pick) window.SFUpsell.track('shown', 'threshold', pick.id, {
        value: pick.price
      });
      return pick;
    } catch (e) {
      return null;
    }
  })();
  return /*#__PURE__*/React.createElement("div", {
    className: "modal-back open"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal",
    ref: dialogRef,
    tabIndex: -1,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": "co-title",
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("button", {
    className: "modal-x",
    onClick: onClose,
    "aria-label": tr(L, 'ok.close')
  }, "\u2715"), /*#__PURE__*/React.createElement("h3", {
    id: "co-title",
    className: P
  }, tr(L, 'co.title')), /*#__PURE__*/React.createElement("div", {
    className: "h-fa " + A
  }, tra(L, 'co.title')), WA_CTX && /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      margin: "8px 0 4px",
      padding: "8px 12px",
      borderRadius: 8,
      background: "rgba(37,211,102,.12)",
      border: "1px solid rgba(37,211,102,.45)",
      fontSize: 13,
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 700,
      color: "#128c4b"
    }
  }, tr(L, 'co.waBanner')), /*#__PURE__*/React.createElement("span", {
    className: A,
    style: {
      display: "block",
      fontSize: 12,
      color: "#33691e"
    }
  }, tra(L, 'co.waBanner'))), /*#__PURE__*/React.createElement("div", {
    className: "stamp"
  }, /*#__PURE__*/React.createElement("div", {
    className: "top"
  }, "ORDER"), /*#__PURE__*/React.createElement("div", {
    className: "mid"
  }, L === 'ar' ? 'طلب' : 'سفارش'), /*#__PURE__*/React.createElement("div", {
    className: "bot"
  }, "DUBAI")), /*#__PURE__*/React.createElement("form", {
    className: "form",
    onSubmit: e => e.preventDefault()
  }, /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: P + " co-mode-lbl",
    onClick: () => {
      const b = modeOnRef.current;
      if (!b) return;
      try {
        b.focus({
          preventScroll: true
        });
      } catch (e) {}
      sfAttn(b, attnRef);
    }
  }, tr(L, 'co.mode'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.mode'))), /*#__PURE__*/React.createElement("div", {
    className: "co-modes"
  }, (WA_CTX ? [["delivery", 'co.deliver'], ["pickup", 'co.pickup']] : [["delivery", 'co.deliver'], ["pickup", 'co.pickup'], ["dinein", 'co.dinein']]).map(([m, k]) => /*#__PURE__*/React.createElement("button", {
    key: m,
    type: "button",
    ref: mode === m ? modeOnRef : undefined,
    className: "co-mode-btn " + P + (mode === m ? " on" : ""),
    onClick: () => setMode(m)
  }, /*#__PURE__*/React.createElement("span", null, tr(L, k)), /*#__PURE__*/React.createElement("span", {
    className: "co-mode-acc " + A
  }, tra(L, k)), /*#__PURE__*/React.createElement(ModeSub, {
    L: L,
    m: m,
    areaKey: deliveryArea,
    free: subtotal >= FREE_DELIVERY_ABOVE_AED && !cartHasTestItem
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.when'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.when'))), /*#__PURE__*/React.createElement(PausedNote, {
    paused: paused
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      border: "1px solid var(--ink)"
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    onClick: () => setWhenMode("asap"),
    disabled: !!paused || gatherActive,
    "aria-disabled": !!paused || gatherActive,
    style: {
      flex: 1,
      padding: 10,
      border: "none",
      cursor: paused || gatherActive ? "not-allowed" : "pointer",
      opacity: paused || gatherActive ? .45 : 1,
      background: whenMode === "asap" ? "var(--ink)" : "transparent",
      color: whenMode === "asap" ? "var(--paper-1)" : "var(--ink)",
      fontFamily: isLatin(L) ? '"IM Fell DW Pica SC",serif' : '"Vazirmatn",sans-serif',
      fontSize: 13,
      letterSpacing: isLatin(L) ? ".12em" : "0",
      textTransform: isLatin(L) ? "uppercase" : "none"
    }
  }, tr(L, 'co.asap'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: SCR(ACCENT[L])
  }, tra(L, 'co.asap'))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    onClick: () => setWhenMode("later"),
    style: {
      flex: 1,
      padding: 10,
      border: "none",
      borderLeft: "1px solid var(--ink)",
      cursor: "pointer",
      background: whenMode === "later" ? "var(--ink)" : "transparent",
      color: whenMode === "later" ? "var(--paper-1)" : "var(--ink)",
      fontFamily: isLatin(L) ? '"IM Fell DW Pica SC",serif' : '"Vazirmatn",sans-serif',
      fontSize: 13,
      letterSpacing: isLatin(L) ? ".12em" : "0",
      textTransform: isLatin(L) ? "uppercase" : "none"
    }
  }, tr(L, 'co.schedule'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: SCR(ACCENT[L])
  }, tra(L, 'co.schedule')))), whenMode === "later" && /*#__PURE__*/React.createElement("div", {
    className: "f-time" + (fieldErrs.time ? " field-err" : ""),
    style: {
      marginTop: 8
    }
  }, (() => {
    const selDay = (scheduledFor || (_schedDays[0] || "") + "T").slice(0, 10);
    const selTime = (scheduledFor || "").slice(11);
    const pickDay = d => {
      const slots = _slotsFor(d);
      const sPick = slots.indexOf(selTime) >= 0 ? selTime : slots[0];
      if (sPick) setScheduledFor(d + "T" + sPick);
      clearErr('time');
    };
    const slots = _slotsFor(selDay);
    const isBf = t => t >= "07:00" && t <= "11:00";
    const inSuhoor = t => !!suhoorWin && inTimeWindow(t, suhoorWin.from, suhoorWin.to);
    const groups = [...(suhoorWin ? [['sched.suhoor', slots.filter(inSuhoor)]] : []), ['sched.breakfast', slots.filter(t => isBf(t) && !inSuhoor(t))], ['sched.later', slots.filter(t => !isBf(t) && !inSuhoor(t))]].filter(g => g[1].length > 0);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "sched-tabs",
      role: "group",
      "aria-label": tr(L, 'sched.day')
    }, _schedDays.map(d => {
      const wd = new Date(d + 'T12:00:00+04:00').getUTCDay();
      // Tag only a weekend day that still has breakfast slots.
      const weekend = (wd === 5 || wd === 6 || wd === 0) && _slotsFor(d).some(isBf);
      return /*#__PURE__*/React.createElement("button", {
        key: d,
        type: "button",
        "aria-pressed": d === selDay,
        className: "sched-tab " + P + (d === selDay ? " on" : ""),
        onClick: () => pickDay(d)
      }, /*#__PURE__*/React.createElement("span", {
        className: "sched-tab-day"
      }, schedDayLabel(d, d > _dayPlus(_schedToday, 5) ? 'short' : 'long')), weekend && /*#__PURE__*/React.createElement("span", {
        className: "sched-tab-tag"
      }, tr(L, 'sched.weekend')));
    })), groups.map(([gk, list]) => /*#__PURE__*/React.createElement("div", {
      key: gk,
      className: "sched-group"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sched-group-lbl " + P,
      id: "sched-g-" + gk.slice(6)
    }, tr(L, gk)), /*#__PURE__*/React.createElement("div", {
      className: "sched-chips" + (list.length > 12 ? " sched-chips--long" : ""),
      role: "group",
      "aria-labelledby": "sched-g-" + gk.slice(6)
    }, list.map(t => /*#__PURE__*/React.createElement("button", {
      key: t,
      type: "button",
      "aria-pressed": t === selTime,
      className: "sched-chip" + (t === selTime ? " on" : ""),
      onClick: () => {
        setScheduledFor(selDay + "T" + t);
        clearErr('time');
      }
    }, /*#__PURE__*/React.createElement("bdi", {
      dir: L === 'ar' ? undefined : 'ltr'
    }, schedTimeLabel(t))))))));
  })(), /*#__PURE__*/React.createElement("details", {
    className: "sched-list"
  }, /*#__PURE__*/React.createElement("summary", {
    className: P
  }, tr(L, 'sched.list')), /*#__PURE__*/React.createElement("div", {
    className: "sched-row"
  }, /*#__PURE__*/React.createElement("select", {
    value: (scheduledFor || (_schedDays[0] || "") + "T").slice(0, 10),
    onChange: e => {
      const d = e.target.value;
      const slots = _slotsFor(d);
      const cur = (scheduledFor || "").slice(11);
      const sPick = slots.indexOf(cur) >= 0 ? cur : slots[0];
      if (sPick) setScheduledFor(d + "T" + sPick);
      clearErr('time');
    },
    "aria-label": tr(L, 'co.when')
  }, _schedDays.map(d => /*#__PURE__*/React.createElement("option", {
    key: d,
    value: d
  }, schedDayLabel(d, 'short')))), /*#__PURE__*/React.createElement("select", {
    value: (scheduledFor || "").slice(11),
    onChange: e => {
      const d = (scheduledFor || (_schedDays[0] || "") + "T").slice(0, 10);
      setScheduledFor(d + "T" + e.target.value);
      clearErr('time');
    },
    "aria-label": tr(L, 'co.when')
  }, _slotsFor((scheduledFor || (_schedDays[0] || "") + "T").slice(0, 10)).map(t => /*#__PURE__*/React.createElement("option", {
    key: t,
    value: t
  }, schedTimeLabel(t)))))), fieldErrs.time && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.time)), gatherActive && /*#__PURE__*/React.createElement("div", {
    className: "co-gather-note " + P
  }, tr(L, 'co.gatherNote')), khanCart && /*#__PURE__*/React.createElement("div", {
    className: "co-gather-note co-khan-note " + P
  }, tr(L, 'co.khanNote')), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: 12,
      color: "var(--ink-soft)",
      marginTop: 4,
      fontStyle: isLatin(L) ? "italic" : "normal"
    }
  }, tr(L, 'co.schedNote1'), " ", numLocal(L, SCHED_LEAD_MIN), " ", tr(L, 'co.schedNote2'), /*#__PURE__*/React.createElement("span", {
    className: A,
    style: {
      display: "block",
      fontStyle: "normal"
    }
  }, tra(L, 'co.schedNote1'), " ", numLocal(ACCENT[L], SCHED_LEAD_MIN), " ", tra(L, 'co.schedNote2'))))), /*#__PURE__*/React.createElement("div", {
    className: "f-name" + (fieldErrs.name ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.name'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.name'))), /*#__PURE__*/React.createElement("input", {
    type: "text",
    autoComplete: "name",
    enterKeyHint: "next",
    placeholder: tr(L, 'co.namePh'),
    value: name,
    onChange: e => {
      setName(e.target.value);
      clearErr('name');
    }
  }), fieldErrs.name && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.name))), /*#__PURE__*/React.createElement("div", {
    className: "f-phone" + (fieldErrs.phone ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.phone'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.phone')), _waPhoneLocked && /*#__PURE__*/React.createElement("span", {
    style: {
      marginLeft: 6,
      fontSize: 11,
      color: "#128c4b"
    }
  }, tr(L, 'co.waVerified'))), /*#__PURE__*/React.createElement("div", {
    className: "phone-row",
    style: _waPhoneLocked ? {
      opacity: 0.75
    } : undefined
  }, WA_CTX ? /*#__PURE__*/React.createElement("select", {
    className: "phone-cc",
    value: phoneDial,
    onChange: e => setPhoneDial(e.target.value),
    disabled: _waPhoneLocked,
    "aria-label": tr(L, 'co.ccode')
  }, PHONE_COUNTRIES.map(c => /*#__PURE__*/React.createElement("option", {
    key: c.iso2,
    value: c.dialCode
  }, c.flag, " +", c.dialCode))) : /*#__PURE__*/React.createElement("span", {
    className: "phone-cc phone-cc-static",
    "aria-label": tr(L, 'co.ccode')
  }, /*#__PURE__*/React.createElement("span", {
    dir: "ltr"
  }, "\uD83C\uDDE6\uD83C\uDDEA +971")), /*#__PURE__*/React.createElement("input", {
    className: "phone-num",
    type: "tel",
    inputMode: "numeric",
    autoComplete: "tel-national",
    enterKeyHint: "next",
    placeholder: "50 123 4567",
    ref: phoneRef,
    value: phone,
    readOnly: _waPhoneLocked,
    onChange: e => {
      setPhone(e.target.value);
      clearErr('phone');
    },
    onBlur: () => setPhoneTouched(true)
  })), fieldErrs.phone && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.phone)), !_waPhoneLocked && (phoneTouched || phone.replace(/[^0-9]/g, '').length >= 9) && phone.trim() !== "" && !isValidUaePhone(buildE164(phone, WA_CTX ? phoneDial : "971")) && /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      marginTop: 5,
      fontSize: 12,
      color: "var(--seal)",
      fontFamily: isLatin(L) ? '"IM Fell English",serif' : '"Vazirmatn",sans-serif',
      letterSpacing: isLatin(L) ? ".02em" : "0"
    }
  }, tr(L, 'co.errUaePhone')), !_waPhoneLocked && !fieldErrs.phone && isValidUaePhone(buildE164(phone, WA_CTX ? phoneDial : "971")) && (() => {
    const _cb = tr(L, 'co.callBack').split('{p}');
    return /*#__PURE__*/React.createElement("div", {
      className: "co-callback " + P
    }, _cb[0], /*#__PURE__*/React.createElement("bdi", {
      dir: "ltr",
      className: "ltr-num"
    }, uaeLocalFmt(buildE164(phone, WA_CTX ? phoneDial : "971"))), _cb[1], ' ', /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "co-callback-edit " + P,
      onClick: () => {
        const el = phoneRef.current;
        if (el) {
          try {
            el.focus();
            el.select();
          } catch (e) {}
        }
      }
    }, tr(L, 'co.edit')));
  })(), mode === "delivery" && /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      marginTop: 5,
      fontSize: 11.5,
      color: "var(--ink-soft, #8a7f70)",
      lineHeight: 1.35
    }
  }, tr(L, 'co.riderCall'), /*#__PURE__*/React.createElement("span", {
    className: A,
    style: {
      display: 'block'
    }
  }, tra(L, 'co.riderCall')))), mode === "delivery" && /*#__PURE__*/React.createElement("div", {
    className: "full co-gift"
  }, /*#__PURE__*/React.createElement("label", {
    className: "co-optin co-gift-toggle"
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: giftOn,
    onChange: e => {
      setGiftOn(e.target.checked);
      if (e.target.checked) {
        setSaveLabelTouched(false);
        setSavedPick(-1);
      } else {
        clearErr('giftName');
        clearErr('giftPhone');
      }
    }
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\uD83C\uDF81 "), tr(L, 'co.giftToggle')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'co.giftToggle')))), giftOn && /*#__PURE__*/React.createElement("div", {
    className: "co-gift-fields"
  }, /*#__PURE__*/React.createElement("div", {
    className: "co-drop-note " + P
  }, tr(L, 'co.giftNote')), /*#__PURE__*/React.createElement("div", {
    className: "f-gift-name" + (fieldErrs.giftName ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-gift-name"
  }, tr(L, 'co.giftName'), /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement("input", {
    id: "co-gift-name",
    type: "text",
    autoComplete: "off",
    enterKeyHint: "next",
    maxLength: GIFT_NAME_MAX,
    value: giftName,
    "aria-invalid": fieldErrs.giftName ? true : undefined,
    onChange: e => {
      setGiftName(e.target.value);
      clearErr('giftName');
    }
  }), fieldErrs.giftName && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.giftName))), /*#__PURE__*/React.createElement("div", {
    className: "f-gift-phone" + (fieldErrs.giftPhone ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-gift-phone"
  }, tr(L, 'co.giftPhone'), /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement("div", {
    className: "phone-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "phone-cc phone-cc-static",
    "aria-label": tr(L, 'co.ccode')
  }, /*#__PURE__*/React.createElement("span", {
    dir: "ltr"
  }, "\uD83C\uDDE6\uD83C\uDDEA +971")), /*#__PURE__*/React.createElement("input", {
    id: "co-gift-phone",
    className: "phone-num",
    type: "tel",
    inputMode: "numeric",
    autoComplete: "off",
    enterKeyHint: "next",
    placeholder: "50 123 4567",
    value: giftPhone,
    "aria-invalid": fieldErrs.giftPhone ? true : undefined,
    onChange: e => {
      setGiftPhone(e.target.value);
      clearErr('giftPhone');
    }
  })), fieldErrs.giftPhone && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.giftPhone)), /*#__PURE__*/React.createElement("div", {
    className: "co-drop-note " + P
  }, tr(L, 'co.giftPhoneNote'))), /*#__PURE__*/React.createElement("div", {
    className: "f-gift-msg"
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-gift-msg"
  }, tr(L, 'co.giftMsg'), /*#__PURE__*/React.createElement("span", {
    className: "addr-opt"
  }, " \xB7 ", tr(L, 'co.optional'))), /*#__PURE__*/React.createElement("input", {
    id: "co-gift-msg",
    type: "text",
    autoComplete: "off",
    enterKeyHint: "next",
    maxLength: GIFT_MSG_MAX,
    placeholder: tr(L, 'co.giftMsgPh'),
    value: giftMsg,
    onChange: e => setGiftMsg(e.target.value)
  }), /*#__PURE__*/React.createElement("div", {
    className: "co-gift-count",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, numLocal(L, giftMsg.length), "/", numLocal(L, GIFT_MSG_MAX)))))), mode === "delivery" ? /*#__PURE__*/React.createElement(React.Fragment, null, _waAddresses.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.savedAddr'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.savedAddr'))), /*#__PURE__*/React.createElement("div", {
    "data-clarity-mask": "True",
    style: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 4
    }
  }, _waAddresses.map((a, i) => /*#__PURE__*/React.createElement("button", {
    key: i,
    type: "button",
    onClick: () => _waPickAddress(a),
    style: {
      padding: "7px 12px",
      borderRadius: 999,
      border: "1px solid rgba(37,211,102,.55)",
      background: "rgba(37,211,102,.08)",
      cursor: "pointer",
      fontSize: 12.5,
      lineHeight: 1.3,
      textAlign: "start",
      maxWidth: "100%"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 700
    }
  }, "\uD83D\uDCCD ", a.label), a.street ? /*#__PURE__*/React.createElement("span", {
    style: {
      display: "block",
      color: "#555",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 220
    }
  }, a.street) : null)))), savedAddrs.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "full co-saved"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.myAddr'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.myAddr'))), /*#__PURE__*/React.createElement("div", {
    className: "co-saved-chips"
  }, savedAddrs.map((x, i) => {
    const ar = findArea(x.area);
    const lbl = (ADDR_LABELS.find(([k]) => k === x.label) || ADDR_LABELS[3])[1];
    return /*#__PURE__*/React.createElement("span", {
      key: savedAddrKey(x) + i,
      className: "co-saved-chip" + (savedPick === i ? " on" : "")
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "co-saved-pick " + P,
      "aria-pressed": savedPick === i,
      onClick: () => _pickSaved(x, i)
    }, /*#__PURE__*/React.createElement("b", null, tr(L, lbl)), ar ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", areaName(L, ar)) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "co-saved-x",
      "aria-label": tr(L, 'co.forget'),
      title: tr(L, 'co.forget'),
      onClick: () => _forgetSaved(i)
    }, "\u2715"));
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "co-saved-new " + P,
    onClick: _newAddr
  }, "+ ", tr(L, 'co.newAddr')))), /*#__PURE__*/React.createElement("div", {
    className: "full area-select" + (needsArea ? " area-required" : "") + (fieldErrs.area ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.area'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.area')), !cartHasTestItem && /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement(AreaPicker, {
    value: deliveryArea,
    onChange: k => {
      setDeliveryArea(k);
      clearErr('area');
    },
    hasTestItem: cartHasTestItem,
    openRef: areaOpenRef
  }), area ? /*#__PURE__*/React.createElement("div", {
    className: "area-fee-note " + P
  }, tr(L, 'co.deliveryTo'), L === 'tr' ? ': ' : ' ', areaName(L, area), areaNameA(L, area) ? /*#__PURE__*/React.createElement("span", {
    className: A
  }, " \xB7 ", areaNameA(L, area)) : null, L === 'tr' ? ' · ' : ': ', freeDelivery ? /*#__PURE__*/React.createElement("b", {
    style: {
      color: '#1a7a3a'
    }
  }, tr(L, 'co.freeDelivery')) : /*#__PURE__*/React.createElement(React.Fragment, null, DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
    style: {
      opacity: .55
    }
  }, "AED ", area.fee), " ", /*#__PURE__*/React.createElement("b", null, "AED ", feeFmt(areaFeeNow(area.fee)))), DELIVERY_PROMO_ON && !freeDelivery && /*#__PURE__*/React.createElement("div", {
    className: "promo-d50",
    style: {
      color: 'var(--gold)',
      marginTop: '.25em'
    }
  }, tr(L, 'promo.delivery50').replace('{p}', numLocal(L, DELIVERY_FEE_DISCOUNT_PCT)))) : !cartHasTestItem &&
  /*#__PURE__*/
  // Clarity 2026-10-16: was a plain note tapped 8-9× in a row —
  // now it opens the area list (focus + open, like the field).
  React.createElement("button", {
    type: "button",
    className: "area-fee-note area-fee-hint area-hint-btn",
    onClick: () => {
      clarityEvent('area_hint_tap');
      if (areaOpenRef.current) areaOpenRef.current();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "area-hint-main " + P
  }, tr(L, 'co.areaHint'), /*#__PURE__*/React.createElement("span", {
    className: "area-hint-chev",
    "aria-hidden": "true"
  }, "\u25BE")), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'co.areaHint'))), area && !coords && !cartHasTestItem && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "pin-nudge " + P,
    onClick: goToPin
  }, tr(L, 'co.pinNext'))), /*#__PURE__*/React.createElement("div", {
    className: "full f-address"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.addrType'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.addrType'))), /*#__PURE__*/React.createElement("div", {
    className: "addr-types",
    role: "radiogroup",
    "aria-label": tr(L, 'co.addrType')
  }, ADDR_TYPES.map(([k, key]) => /*#__PURE__*/React.createElement("button", {
    key: k,
    type: "button",
    role: "radio",
    "aria-checked": addrType === k,
    className: "arrive-chip " + P + (addrType === k ? " on" : ""),
    onClick: () => {
      setAddrType(k);
      ['address', 'flat'].forEach(clearErr);
    }
  }, tr(L, key)))), (() => {
    const fld = (k, labelKey, opts) => {
      const o = opts || {};
      const err = o.errKey && fieldErrs[o.errKey];
      return /*#__PURE__*/React.createElement("div", {
        className: "addr-f addr-" + k + (o.cls ? " " + o.cls : "") + (o.half ? "" : " addr-full") + (err ? " field-err" : "")
      }, /*#__PURE__*/React.createElement("label", {
        className: P
      }, tr(L, labelKey), o.req ? /*#__PURE__*/React.createElement("span", {
        className: "req-mark",
        "aria-hidden": "true"
      }, "*") : /*#__PURE__*/React.createElement("span", {
        className: "addr-opt"
      }, " \xB7 ", tr(L, 'co.optional'))), /*#__PURE__*/React.createElement("input", {
        type: "text",
        enterKeyHint: "next",
        autoComplete: o.ac || "off",
        inputMode: o.im,
        maxLength: o.max || 120,
        placeholder: o.ph ? tr(L, o.ph) : undefined,
        value: addrParts[k],
        "aria-invalid": err ? true : undefined,
        onChange: e => {
          setPart(k, e.target.value);
          if (o.errKey) clearErr(o.errKey);
        }
      }), err && /*#__PURE__*/React.createElement("div", {
        className: "field-err-msg " + P
      }, tr(L, err)));
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "addr-grid"
    }, addrType === 'villa' ? /*#__PURE__*/React.createElement(React.Fragment, null, fld('villa', 'co.villaNo', {
      req: true,
      half: true,
      errKey: 'address',
      cls: 'addr-main',
      ac: 'address-line1'
    }), fld('street', 'co.street', {
      half: true,
      ac: 'address-line2'
    })) : addrType === 'office' ? /*#__PURE__*/React.createElement(React.Fragment, null, fld('company', 'co.company', {
      req: true,
      errKey: 'address',
      cls: 'addr-main',
      ac: 'organization'
    }), fld('floor', 'co.floor', {
      half: true,
      max: 20
    })) : /*#__PURE__*/React.createElement(React.Fragment, null, fld('building', 'co.building', {
      req: true,
      errKey: 'address',
      cls: 'addr-main',
      ac: 'address-line1',
      max: 200
    }), fld('floor', 'co.floor', {
      half: true,
      max: 20
    }), _flatWaived ? fld('flat', 'co.flat', {
      half: true,
      max: 20,
      errKey: 'flat'
    }) : fld('flat', 'co.flat', {
      req: true,
      half: true,
      max: 20,
      errKey: 'flat'
    })), fld('landmark', 'co.landmark', {
      ph: 'co.landmarkPh',
      max: 160
    }), fld('makani', 'co.makani', {
      ph: 'co.makaniPh',
      im: 'numeric',
      max: 16,
      errKey: 'makani',
      cls: 'addr-makani-f'
    }));
  })()), /*#__PURE__*/React.createElement("div", {
    ref: geoRef,
    className: "full geo" + (coords && !pinNeedsConfirm ? " geo-pinned-ok" : cartHasTestItem ? "" : " geo-required") + (fieldErrs.pin ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.pin'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.pin')), !cartHasTestItem && /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement(MapPicker, {
    restaurant: RESTAURANT_COORDS,
    coords: coords,
    focusPoint: areaFocus,
    onPick: c => {
      setCoords(c);
      setGeoError("");
      clearErr('pin');
      setPinNeedsConfirm(false);
      // The mandatory pin is the most-suspected drop-off point in
      // the whole funnel. Coordinates are NEVER sent — only the
      // fact that a pin was set.
      clarityEvent('pin_set');
      // Pin answers the area: one deliberate pin pre-selects the
      // matching district (customer can still override; both
      // fields stay mandatory).
      if (!deliveryArea) reverseGeocodeArea(c.lat, c.lng).then(k => {
        if (k) setDeliveryArea(k);
      });
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "geo-row"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    ref: gpsBtnRef,
    className: "geo-btn" + (coords || cartHasTestItem || geoError === "geo.denied" ? "" : " geo-btn-required"),
    onClick: pinLocation,
    disabled: geoBusy
  }, geoBusy ? /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'geo.locating')) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "geo-pin"
  }, "\u2316"), " ", /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'geo.useGps')), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'geo.useGps')))), coords && /*#__PURE__*/React.createElement("span", {
    className: "geo-pinned-inline"
  }, /*#__PURE__*/React.createElement("span", {
    className: "geo-coord"
  }, coords.lat.toFixed(5), ", ", coords.lng.toFixed(5)), /*#__PURE__*/React.createElement("span", {
    className: "geo-fa " + P
  }, tr(L, 'geo.pinned')))), coords && pinNeedsConfirm && /*#__PURE__*/React.createElement("div", {
    className: "geo-confirm"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.pinCheck')), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "geo-confirm-btn " + P,
    onClick: () => {
      setPinNeedsConfirm(false);
      clearErr('pin');
      clarityEvent('pin_set');
    }
  }, "\u2713 ", tr(L, 'co.pinConfirm'))), fieldErrs.pin && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.pin)), !coords && !cartHasTestItem && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "geo-hint geo-hint-btn",
    disabled: geoBusy,
    onClick: () => {
      clarityEvent('pin_hint_gps');
      pinLocation();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'geo.hint')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'geo.hint'))), geoError && /*#__PURE__*/React.createElement("div", {
    className: "geo-error " + P
  }, tr(L, geoError))), /*#__PURE__*/React.createElement("div", {
    className: "full co-dropoff"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.dropoff'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.dropoff'))), /*#__PURE__*/React.createElement("div", {
    className: "arrive-chips co-drop-chips",
    role: "radiogroup",
    "aria-label": tr(L, 'co.dropoff')
  }, DROPOFFS.map(([k, key]) => /*#__PURE__*/React.createElement("button", {
    key: k,
    type: "button",
    role: "radio",
    "aria-checked": dropoff === k,
    className: "arrive-chip " + P + (dropoff === k ? " on" : ""),
    onClick: () => {
      setDropoff(k);
      setDropTouched(true);
    }
  }, tr(L, key)))), !dropTouched && dropoff === 'nobell' && /*#__PURE__*/React.createElement("div", {
    className: "co-drop-note " + P
  }, tr(L, 'co.dropEarly'))), !WA_CTX && /*#__PURE__*/React.createElement("div", {
    className: "full co-savelbl"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.saveAs'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.saveAs'))), /*#__PURE__*/React.createElement("div", {
    className: "arrive-chips",
    role: "radiogroup",
    "aria-label": tr(L, 'co.saveAs')
  }, ADDR_LABELS.map(([k, key]) => {
    const _on = saveLabel === k && (!_gift || saveLabelTouched);
    return /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      role: "radio",
      "aria-checked": _on,
      className: "arrive-chip " + P + (_on ? " on" : ""),
      onClick: () => {
        setSaveLabel(k);
        setSaveLabelTouched(true);
      }
    }, tr(L, key));
  })), /*#__PURE__*/React.createElement("div", {
    className: "co-drop-note " + P
  }, tr(L, _gift && !saveLabelTouched ? 'co.giftNoSave' : 'co.saveNote')))) : mode === "pickup" ? /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pickup-note"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\uD83C\uDFE0"), /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.pickupNote')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'co.pickupNote')))) : null, mode !== "delivery" && whenMode === "asap" && !WA_CTX && /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, mode === "pickup" ? 'co.collect' : 'co.arrive'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, mode === "pickup" ? 'co.collect' : 'co.arrive'))), /*#__PURE__*/React.createElement("div", {
    className: "arrive-chips"
  }, ["now", "20", "40", "60"].map(v => /*#__PURE__*/React.createElement("button", {
    key: v,
    type: "button",
    className: "arrive-chip" + (arrivalTime === v ? " on" : "") + " " + P,
    onClick: () => setArrivalTime(v)
  }, v === "now" ? tr(L, 'co.arriveNow') : (L === 'ar' ? 'بعد ' : L === 'fa' ? 'حدود ' : '~') + numLocal(L, v) + " " + tr(L, 'co.min'))))), mode !== "dinein" && /*#__PURE__*/React.createElement("div", {
    className: "full co-extras"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.extras'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.extras'))), /*#__PURE__*/React.createElement("div", {
    className: "arrive-chips"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-pressed": lemon,
    className: "arrive-chip " + P + (lemon ? " on" : ""),
    onClick: () => setLemon(!lemon)
  }, lemon ? '✓ ' : '', "\uD83C\uDF4B ", tr(L, 'co.lemon')), /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-pressed": spoons,
    className: "arrive-chip " + P + (spoons ? " on" : ""),
    onClick: () => {
      setSpoons(!spoons);
      setSpoonsTouched(true);
    }
  }, spoons ? '✓ ' : '', "\uD83E\uDD44 ", tr(L, 'co.spoons')))), /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: P
  }, tr(L, 'co.notes'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.notes'))), /*#__PURE__*/React.createElement("input", {
    type: "text",
    enterKeyHint: "done",
    maxLength: 600,
    placeholder: tr(L, 'co.notesPh'),
    value: notes,
    onChange: e => setNotes(e.target.value)
  })), !WA_CTX && /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("label", {
    className: "co-optin"
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: marketingOptIn,
    onChange: e => setMarketingOptIn(e.target.checked)
  }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.optIn')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'co.optIn')), /*#__PURE__*/React.createElement("span", {
    className: "co-optin-note " + P
  }, tr(L, 'co.optInNote'))))), TAX_INVOICE_ENABLED && /*#__PURE__*/React.createElement("div", {
    className: "full"
  }, /*#__PURE__*/React.createElement("details", {
    className: "co-invoice",
    open: invOpen,
    onToggle: e => setInvOpen(e.currentTarget.open)
  }, /*#__PURE__*/React.createElement("summary", {
    className: P
  }, tr(L, 'co.invToggle')), /*#__PURE__*/React.createElement("div", {
    className: "co-inv-grid"
  }, /*#__PURE__*/React.createElement("div", {
    className: "f-inv-company" + (fieldErrs.invCompany ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-inv-company"
  }, tr(L, 'co.invCompany')), /*#__PURE__*/React.createElement("input", {
    id: "co-inv-company",
    type: "text",
    autoComplete: "organization",
    enterKeyHint: "next",
    maxLength: INV_COMPANY_MAX,
    value: invCompany,
    "aria-invalid": fieldErrs.invCompany ? true : undefined,
    onChange: e => {
      setInvCompany(e.target.value);
      clearErr('invCompany');
    }
  }), fieldErrs.invCompany && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.invCompany))), /*#__PURE__*/React.createElement("div", {
    className: "f-inv-trn" + (fieldErrs.invTrn ? " field-err" : "")
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-inv-trn"
  }, tr(L, 'co.invTrn'), /*#__PURE__*/React.createElement("span", {
    className: "addr-opt"
  }, " \xB7 ", tr(L, 'co.optional'))), /*#__PURE__*/React.createElement("input", {
    id: "co-inv-trn",
    type: "text",
    inputMode: "numeric",
    autoComplete: "off",
    enterKeyHint: "next",
    maxLength: 24,
    placeholder: tr(L, 'co.invTrnPh'),
    value: invTrn,
    "aria-invalid": fieldErrs.invTrn ? true : undefined,
    onChange: e => {
      setInvTrn(e.target.value);
      clearErr('invTrn');
    }
  }), fieldErrs.invTrn && /*#__PURE__*/React.createElement("div", {
    className: "field-err-msg " + P
  }, tr(L, fieldErrs.invTrn))), /*#__PURE__*/React.createElement("div", {
    className: "f-inv-addr"
  }, /*#__PURE__*/React.createElement("label", {
    className: P,
    htmlFor: "co-inv-addr"
  }, tr(L, 'co.invAddr'), /*#__PURE__*/React.createElement("span", {
    className: "addr-opt"
  }, " \xB7 ", tr(L, 'co.optional'))), /*#__PURE__*/React.createElement("input", {
    id: "co-inv-addr",
    type: "text",
    autoComplete: "off",
    enterKeyHint: "done",
    maxLength: INV_ADDR_MAX,
    value: invAddr,
    onChange: e => setInvAddr(e.target.value)
  })), /*#__PURE__*/React.createElement("div", {
    className: "co-drop-note " + P
  }, tr(L, 'co.invNote')))))), coUpsell && !coUpsellGone && !coUpsellOut ? /*#__PURE__*/React.createElement("div", {
    className: "up-checkline",
    role: "button",
    tabIndex: 0,
    onClick: () => {
      if (onAddById) onAddById(coUpsell.id, {
        upsell: true,
        placement: 'checkout'
      });
      setCoUpsellGone(true);
    },
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (onAddById) onAddById(coUpsell.id, {
          upsell: true,
          placement: 'checkout'
        });
        setCoUpsellGone(true);
      }
    },
    "aria-label": tr(L, 'a11y.add').replace('{s}', nm(L, coUpsell))
  }, /*#__PURE__*/React.createElement("span", {
    className: "up-cl-thumb" + (coUpsell.image ? " up-cl-thumb--photo" : ""),
    "aria-hidden": "true"
  }, coUpsell.image ? /*#__PURE__*/React.createElement("img", {
    src: coUpsell.image,
    alt: "",
    loading: "lazy"
  }) : ITEM_ICONS[ITEM_ICON_MAP[coUpsell.id] || 'bowl']), /*#__PURE__*/React.createElement("span", {
    className: "up-cl-text"
  }, /*#__PURE__*/React.createElement("span", {
    className: "up-cl-label " + P
  }, tr(L, 'up.lastChance')), /*#__PURE__*/React.createElement("span", {
    className: "up-cl-item " + P
  }, nm(L, coUpsell), nmA(L, coUpsell) ? /*#__PURE__*/React.createElement("span", {
    className: "up-cl-fa " + A
  }, " \xB7 ", nmA(L, coUpsell)) : null)), /*#__PURE__*/React.createElement("span", {
    className: "up-cl-add",
    "aria-hidden": "true"
  }, "+ ", coUpsell.price, " ", /*#__PURE__*/React.createElement("span", {
    className: "up-cl-cur"
  }, "AED")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "up-cl-x",
    onClick: e => {
      e.stopPropagation();
      // Route through App's dismissSuggestion so React state and the
      // SFUpsell session store stay in sync (the rail hides it too).
      if (onDismissSuggestion) onDismissSuggestion(coUpsell.id, 'checkout');else {
        try {
          if (window.SFUpsell) window.SFUpsell.dismiss('checkout', coUpsell.id);
        } catch (err) {}
      }
      setCoUpsellGone(true);
    },
    "aria-label": tr(L, 'a11y.hideOne')
  }, "\u2715")) : showSpecial ? /*#__PURE__*/React.createElement("div", {
    className: "sf-sp-line",
    role: "button",
    tabIndex: 0,
    onClick: () => {
      if (onAddById) onAddById(special.slug);
      setSpGone(true);
    },
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (onAddById) onAddById(special.slug);
        setSpGone(true);
      }
    },
    "aria-label": tr(L, 'a11y.add').replace('{s}', special.name)
  }, /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-badge"
  }, special.badge), /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-text"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-name " + P
  }, special.name), /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-blurb " + P
  }, special.blurb)), /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-add",
    "aria-hidden": "true"
  }, "+ ", specialItem.price, " ", /*#__PURE__*/React.createElement("span", {
    className: "sf-sp-cur"
  }, "AED")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "sf-sp-x",
    "aria-label": tr(L, 'a11y.hideOne'),
    onClick: e => {
      e.stopPropagation();
      setSpGone(true);
    }
  }, "\xD7")) : null, (() => {
    const _q = lines.reduce((t, x) => t + x.l.qty, 0);
    const _w = L === 'en' ? _q !== 1 ? 'items' : 'item' : tr(L, _q !== 1 ? 'cart.items' : 'cart.item');
    return /*#__PURE__*/React.createElement("details", {
      className: "co-receipt"
    }, /*#__PURE__*/React.createElement("summary", {
      className: P
    }, /*#__PURE__*/React.createElement("span", null, tr(L, 'rail.yourOrder'), " \xB7 ", L === 'en' ? _q + ' ' + _w : itemsText(L, _q)), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", {
      className: "ltr-num"
    }, feeFmt(total), " AED"))), /*#__PURE__*/React.createElement("div", {
      className: "totals"
    }, hostGroup(lines).map(({
      l,
      item,
      sub
    }) => /*#__PURE__*/React.createElement("div", {
      key: item.id,
      className: "ln" + (sub ? " host-grp" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, l.qty, "\xD7 ", nm(L, item), nmA(L, item) ? /*#__PURE__*/React.createElement("span", {
      className: A
    }, " \xB7 ", nmA(L, item)) : null), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, feeFmt(item.price * l.qty), " AED"))), /*#__PURE__*/React.createElement("div", {
      className: "ln",
      style: {
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.subtotal'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
      className: A
    }, tra(L, 'co.subtotal'))), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, feeFmt(subtotal), " AED")), loyaltyApplied ? /*#__PURE__*/React.createElement("div", {
      className: "ln",
      style: {
        color: '#1a7a3a'
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.introDiscount').replace('{n}', numLocal(L, loyaltyPct))), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, "\u2212", loyaltyAed, " AED")) : promoApplied ? /*#__PURE__*/React.createElement("div", {
      className: "ln",
      style: {
        color: '#1a7a3a'
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.promo')), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, "\u2212", promoAed, " AED")) : disc && codeDiscountAed > 0 && /*#__PURE__*/React.createElement("div", {
      className: "ln",
      style: {
        color: '#1a7a3a'
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.discount'), " (", disc.code, ")"), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, "\u2212", codeDiscountAed, " AED")), /*#__PURE__*/React.createElement("div", {
      className: "ln"
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.vat'), " ", L === 'tr' ? '%' : null, numLocal(L, SERVICE_FEE_PCT), L === 'tr' ? null : L === 'fa' ? '٪' : '%'), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, service, " AED")), mode === "delivery" && !cartHasTestItem && /*#__PURE__*/React.createElement("div", {
      className: "ln"
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.delivery'), area ? " — " + areaName(L, area) : ""), /*#__PURE__*/React.createElement("span", {
      className: areaFee == null && !freeDelivery ? P : undefined,
      style: freeDelivery ? {
        color: '#1a7a3a'
      } : undefined
    }, freeDelivery ? tr(L, 'co.freeDelivery') : areaFee != null ? /*#__PURE__*/React.createElement(React.Fragment, null, DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
      style: {
        opacity: .55,
        marginInlineEnd: '.35em'
      }
    }, area.fee), feeFmt(delivery) + " AED") : tr(L, 'co.selectArea'))), mode === "delivery" && !cartHasTestItem && DELIVERY_PROMO_ON && !freeDelivery && /*#__PURE__*/React.createElement("div", {
      className: "ln promo-d50-ln",
      style: {
        color: 'var(--gold)'
      }
    }, needsArea ?
    /*#__PURE__*/
    // Clarity 2026-10-16: no area yet → the promo line opens the area list.
    React.createElement("button", {
      type: "button",
      className: "promo-d50 promo-d50-tap " + P,
      onClick: () => {
        clarityEvent('promo_area_tap');
        if (areaOpenRef.current) areaOpenRef.current();
      }
    }, tr(L, 'promo.delivery50').replace('{p}', numLocal(L, DELIVERY_FEE_DISCOUNT_PCT)), /*#__PURE__*/React.createElement("span", {
      className: "promo-d50-act"
    }, " \xB7 ", tr(L, 'mode.chooseArea'), /*#__PURE__*/React.createElement("span", {
      className: "area-hint-chev",
      "aria-hidden": "true"
    }, "\u25BE"))) : /*#__PURE__*/React.createElement("span", {
      className: "promo-d50 " + P
    }, tr(L, 'promo.delivery50').replace('{p}', numLocal(L, DELIVERY_FEE_DISCOUNT_PCT)))), /*#__PURE__*/React.createElement("div", {
      className: "ln grand"
    }, /*#__PURE__*/React.createElement("span", {
      className: P
    }, tr(L, 'co.grand'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
      className: A
    }, tra(L, 'co.grand'))), /*#__PURE__*/React.createElement("span", {
      className: "amt"
    }, feeFmt(total), " AED"))));
  })(), /*#__PURE__*/React.createElement("div", {
    className: "co-disc"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 10,
      paddingTop: 10,
      borderTop: '1px dashed #c9b79a'
    }
  }, disc ? /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: P,
    style: {
      color: loyaltyApplied || promoApplied ? 'var(--ink-faint)' : '#1a7a3a',
      fontSize: '.9em'
    }
  }, loyaltyApplied || promoApplied ? tr(L, 'co.betterApplied') : /*#__PURE__*/React.createElement(React.Fragment, null, "\u2713 ", disc.code, " ", tr(L, 'co.applied'), disc.message && L === 'en' ? " — " + disc.message : "")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    onClick: removeDiscount,
    style: {
      background: 'none',
      border: 'none',
      color: '#8a1c1c',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontSize: '.85em'
    }
  }, tr(L, 'co.remove'))) : /*#__PURE__*/React.createElement("details", {
    className: "co-havecode",
    open: discInput ? true : undefined
  }, /*#__PURE__*/React.createElement("summary", {
    className: P
  }, tr(L, 'co.haveCode')), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: 6,
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    placeholder: tr(L, 'co.discPh'),
    value: discInput,
    onChange: e => {
      setDiscInput(e.target.value.toUpperCase());
      if (discErr) setDiscErr("");
    },
    onKeyDown: e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyDiscount();
      }
    },
    style: {
      flex: 1,
      padding: '8px 10px',
      border: '1px solid #c9b79a',
      borderRadius: 6,
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      background: '#fffaf0',
      fontSize: 16,
      minWidth: 0
    }
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    onClick: applyDiscount,
    disabled: discBusy || !discInput.trim(),
    style: {
      padding: '8px 14px',
      background: '#221408',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      cursor: 'pointer',
      opacity: discBusy || !discInput.trim() ? 0.6 : 1,
      whiteSpace: 'nowrap'
    }
  }, discBusy ? "…" : tr(L, 'co.apply'))), discErr && /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      color: '#8a1c1c',
      fontSize: '.82em',
      marginTop: 6
    }
  }, tr(L, discErr))))), deliveryShortfall > 0 && /*#__PURE__*/React.createElement("div", {
    className: "co-shortfall " + (isLatin(L) ? '' : 'scr-arab'),
    style: {
      margin: "10px 0",
      padding: "8px 12px",
      border: "1px solid var(--seal)",
      background: "rgba(138,28,28,.06)",
      color: "var(--seal-deep)",
      fontFamily: isLatin(L) ? '"IM Fell DW Pica SC",serif' : '"Vazirmatn",sans-serif',
      fontSize: 12,
      letterSpacing: isLatin(L) ? ".15em" : "0",
      textTransform: isLatin(L) ? "uppercase" : "none",
      textAlign: "center"
    }
  }, tr(L, 'co.minWarn1') + " " + numLocal(L, deliveryShortfall) + " " + tr(L, 'co.minWarn2') + " " + numLocal(L, minOrderForDelivery) + tr(L, 'co.minWarn3'), isLatin(L) && /*#__PURE__*/React.createElement("div", {
    dir: "rtl",
    style: {
      fontFamily: '"Vazirmatn",serif',
      fontSize: 12,
      letterSpacing: 0,
      textTransform: "none",
      color: "var(--ink-soft)",
      marginTop: 4
    }
  }, tra(L, 'co.minWarn1') + " " + numLocal(ACCENT[L], deliveryShortfall) + " " + tra(L, 'co.minWarn2') + " " + numLocal(ACCENT[L], minOrderForDelivery) + tra(L, 'co.minWarn3')), shortfallPick && /*#__PURE__*/React.createElement("div", {
    className: "up-shortfall"
  }, /*#__PURE__*/React.createElement("span", {
    className: "up-sf-label " + P
  }, tr(L, 'up.reachMin')), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "up-sf-add",
    onClick: () => {
      if (onAddById) onAddById(shortfallPick.id, {
        upsell: true,
        placement: 'threshold'
      });
    },
    "aria-label": tr(L, 'a11y.add').replace('{s}', nm(L, shortfallPick))
  }, "+ ", nm(L, shortfallPick), " \u2014 ", shortfallPick.price, " AED"))), submitError && /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      margin: "8px 0",
      color: "var(--seal)",
      fontFamily: isLatin(L) ? '"IM Fell DW Pica SC",serif' : '"Vazirmatn",sans-serif',
      fontSize: 13,
      letterSpacing: isLatin(L) ? ".08em" : "0",
      textTransform: isLatin(L) ? "uppercase" : "none"
    }
  }, submitError === "co.errTimeMin" ? tr(L, 'co.errTimeMin') + " " + SCHED_LEAD_MIN + " " + tr(L, 'co.errTimeMin2') : submitError === "co.errCount" ? tr(L, 'co.errCount').replace('{n}', numLocal(L, Object.keys(fieldErrs).length)) : submitError === "co.btnShort" ? tr(L, 'co.btnShort').replace('{n}', numLocal(L, deliveryShortfall)) : tr(L, submitError)), submitting && /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay",
    role: "dialog",
    "aria-label": tr(L, 'co.payConnect'),
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay-spinner",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay-en " + P
  }, tr(L, 'co.payConnect')), /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay-fa " + A
  }, tra(L, 'co.payConnect')), /*#__PURE__*/React.createElement("div", {
    className: "pay-overlay-sub " + P
  }, tr(L, 'co.dontClose'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'co.dontClose'))), stalled && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    onClick: goToTracking,
    style: {
      marginTop: 18,
      padding: "9px 18px",
      background: "transparent",
      color: "var(--seal)",
      border: "1px solid var(--seal)",
      cursor: "pointer",
      fontFamily: isLatin(L) ? '"IM Fell DW Pica SC",serif' : '"Vazirmatn",sans-serif',
      fontSize: 11,
      letterSpacing: isLatin(L) ? ".18em" : "0",
      textTransform: isLatin(L) ? "uppercase" : "none"
    }
  }, tr(L, 'co.tooLong')))), /*#__PURE__*/React.createElement("div", {
    className: "co-paynotes"
  }, /*#__PURE__*/React.createElement("div", {
    className: "co-trust " + P
  }, REVIEWS && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, "\u2605 ", numLocal(L, REVIEWS.rating)), " \xB7 ", tr(L, 'co.trustReviews').split('{n}')[0], /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, numLocal(L, REVIEWS.count)), tr(L, 'co.trustReviews').split('{n}')[1], " \xB7 "), /*#__PURE__*/React.createElement("a", {
    href: `tel:${PHONE_TEL}`
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, PHONE_DISPLAY))), /*#__PURE__*/React.createElement("div", {
    className: P + " co-refund"
  }, tr(L, 'co.refundNote')), L === 'fa' && /*#__PURE__*/React.createElement("details", {
    className: "co-payguide " + P
  }, /*#__PURE__*/React.createElement("summary", null, tr(L, 'co.payGuide')), /*#__PURE__*/React.createElement("ol", null, PAY_GUIDE_FA.map((t, i) => /*#__PURE__*/React.createElement("li", {
    key: i
  }, t))))), /*#__PURE__*/React.createElement("div", {
    className: "co-footer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "co-payrow"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, payRowLine(L)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "co-close-link " + P,
    onClick: onClose
  }, "\u2715 ", tr(L, 'ok.close'))), INAPP_HINTS_ENABLED && isInAppBrowser() && /*#__PURE__*/React.createElement("div", {
    className: "co-inapp " + P,
    style: {
      fontSize: 12.5,
      margin: '4px 0 6px',
      padding: '6px 10px',
      border: '1px dashed var(--seal)',
      borderRadius: 6,
      display: 'flex',
      gap: 8,
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, tr(L, 'inapp.tip')), /*#__PURE__*/React.createElement("a", {
    href: openInBrowserTarget(cart, L),
    onClick: () => clarityEvent('inapp_open_browser'),
    style: {
      color: 'var(--seal)',
      fontWeight: 700,
      whiteSpace: 'nowrap'
    }
  }, tr(L, 'inapp.open'), " \u2192")), /*#__PURE__*/React.createElement("button", {
    className: "place",
    disabled: lines.length === 0 || submitting,
    onClick: async () => {
      setSubmitError("");
      // ONE-PASS validation: mark every missing detail at once (seal-red at
      // the field itself), then take the customer to the first one — no
      // more one-error-per-tap whack-a-mole.
      const errs = {};
      if (deliveryShortfall > 0) errs.min = "co.btnShort";
      if (!name.trim() || name.trim().length < 2) errs.name = "co.errName";
      // Compose the canonical +E.164 number. WhatsApp ordering: the token's
      // phone is ALREADY canonical E.164 and the field is locked — submit it
      // verbatim (the split/rebuild round-trip corrupts non-listed codes).
      // Web checkout is UAE-only, so the dial code is pinned to +971.
      const fullPhone = _waPhoneLocked && _waCustomer && _waCustomer.phone ? _waCustomer.phone : buildE164(phone, WA_CTX ? phoneDial : "971");
      if (_waPhoneLocked) {
        if (!fullPhone || fullPhone.replace(/[^0-9]/g, '').length < 8) errs.phone = "co.errPhone";
      } else if (!isValidUaePhone(fullPhone)) {
        errs.phone = "co.errUaePhone";
      }
      if (mode === "delivery") {
        // K1: the required field of the chosen address type, then the
        // composed line must still pass create.ts's ≥ 4-character rule.
        const _ap = addrParts;
        if (addrType === 'villa') {
          if (!_ap.villa.trim()) errs.address = "co.errVilla";
        } else if (addrType === 'office') {
          if (_ap.company.trim().length < 2) errs.address = "co.errCompany";
        } else {
          if (_ap.building.trim().length < 2) errs.address = "co.errBuilding";
          if (!_ap.flat.trim() && !_flatWaived) errs.flat = "co.errFlat";
        }
        if (!errs.address && address.trim().length < 4) errs.address = "co.errAddress";
        if (!makaniOk(_ap.makani)) errs.makani = "co.errMakani";
      }
      if (needsArea) errs.area = "co.errArea";
      if (mode === "delivery" && !cartHasTestItem) {
        if (!coords) errs.pin = "co.errPin";else if (pinNeedsConfirm) errs.pin = "co.errPinConfirm"; // K3: saved pin not yet confirmed
      }
      // L3: recipient name + UAE mobile (same normalisation as the payer's).
      const giftE164 = _gift ? buildE164(giftPhone, "971") : null;
      if (_gift) {
        if (oneLine(giftName, GIFT_NAME_MAX).length < 2) errs.giftName = "co.errGiftName";
        if (!isValidUaePhone(giftE164)) errs.giftPhone = "co.errGiftPhone";
      }
      // L4: company invoice — only when something was typed; TRN optional
      // but, if given, exactly 15 digits (Arabic/Persian digits accepted).
      const _invC = oneLine(invCompany, INV_COMPANY_MAX),
        _invT = trnNorm(invTrn),
        _invA = oneLine(invAddr, INV_ADDR_MAX);
      let invoiceBody;
      if (_invC || _invT || _invA) {
        if (_invC.length < 2) errs.invCompany = "co.errInvCompany";
        if (_invT && !/^\d{15}$/.test(_invT)) errs.invTrn = "co.errTrn";
        if (!errs.invCompany && !errs.invTrn) invoiceBody = {
          company: _invC,
          trn: _invT || undefined,
          address: _invA || undefined
        };
      }
      // Pre-order: interpret the picked Dubai wall-clock as +04:00 → unambiguous UTC.
      let scheduledForUTC = null;
      if (whenMode === "later") {
        if (!scheduledFor) errs.time = "co.errTime";else {
          const t = new Date(scheduledFor + ":00+04:00").getTime();
          if (Number.isNaN(t)) errs.time = "co.errTimeBad";else if (t < Date.now() + SCHED_LEAD_MIN * 60000) errs.time = "co.errTimeMin";
          // L2: a gathering's own window replaces the 2-day horizon.
          else if (gatherActive && scheduledFor.slice(0, 10) < gatherFirstDay) errs.time = "co.errGatherCutoff";else if (gatherActive && scheduledFor.slice(0, 10) > gatherLastDay) errs.time = "co.errGatherMax";else if (khanCart && scheduledFor.slice(0, 10) > khanLastDay) errs.time = "co.errTimeMax7";else if (!gatherActive && !khanCart && t > Date.now() + 2 * 86400000) errs.time = "co.errTimeMax";else if (pauseMinMs && t < pauseMinMs) errs.time = "co.errPaused";else scheduledForUTC = new Date(t).toISOString();
        }
      }
      const errKeys = Object.keys(errs);
      if (errKeys.length) {
        setFieldErrs(errs);
        setSubmitError(errKeys.length === 1 ? errs[errKeys[0]] : "co.errCount");
        // Focus + reveal the FIRST problem — a scroll of the modal's own
        // container, deliberately immune to the page-scroll bug family.
        const _order = ["min", "name", "phone", "giftName", "giftPhone", "time", "area", "address", "flat", "makani", "pin", "invCompany", "invTrn"];
        const _first = _order.find(k => errs[k]);
        const _sel = {
          min: '.co-shortfall',
          name: '.f-name input',
          phone: '.f-phone .phone-num',
          giftName: '.f-gift-name input',
          giftPhone: '.f-gift-phone input',
          time: '.f-time select',
          area: '.area-input',
          address: '.f-address .addr-main input',
          flat: '.f-address .addr-flat input',
          makani: '.f-address .addr-makani input',
          pin: '.geo',
          invCompany: '.f-inv-company input',
          invTrn: '.f-inv-trn input'
        }[_first];
        // The invoice fields live in a collapsible — open it first.
        if (errs.invCompany || errs.invTrn) setInvOpen(true);
        // The time <select>s sit in a collapsed <details> (not rendered,
        // so neither scroll nor focus would work) — reveal the visible
        // picker instead: the chosen chip, else the active day tab.
        const _q = x => document.querySelector('.modal ' + x);
        const _reveal = () => {
          const _el = _first === 'time' ? _q('.f-time .sched-tab.on') || _q('.f-time .sched-chip.on') || _q('.f-time') : _sel && _q(_sel);
          if (_el) {
            try {
              _el.scrollIntoView({
                block: 'center'
              });
            } catch (e) {}
            if (_el.focus) {
              try {
                _el.focus({
                  preventScroll: true
                });
              } catch (e) {
                try {
                  _el.focus();
                } catch (e2) {}
              }
            }
          }
        };
        // A just-opened <details> renders its fields on the next paint.
        if (_first === 'invCompany' || _first === 'invTrn') setTimeout(_reveal, 30);else _reveal();
        // Clarity 2026-10-16: blocked by the pin → also pulse the fix
        // (GPS, or the saved-pin confirm button) so the eye lands on it.
        if (_first === 'pin') sfAttn(errs.pin === 'co.errPinConfirm' ? _q('.geo-confirm-btn') : gpsBtnRef.current, attnRef);
        // The FIELD NAME only — never its value. Tells us which field
        // stops people without recording anything they typed.
        try {
          clarityEvent('checkout_blocked_' + String(Object.keys(errs)[0] || 'unknown'));
        } catch (e) {}
        return;
      }
      setFieldErrs({});
      clarityEvent('checkout_submit');
      setSubmitting(true);
      CO_SUBMITTING = true;
      const gen = ++submitGenRef.current;
      try {
        const res = await fetch("/api/orders/create", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            cart,
            customer: {
              name: name.trim(),
              phone: fullPhone,
              address: mode === "delivery" ? address.trim() : undefined,
              // K2: one kitchen line per non-default drop-off / extra
              // choice, prepended; then the customer's own note.
              notes: [
              // L3: the gift lines come FIRST (the slice below trims from the end).
              ...(_gift ? giftNoteLines(giftName, giftE164, giftMsg) : []), ...(mode === "delivery" && DROPOFF_NOTE[dropoff] ? [DROPOFF_NOTE[dropoff]] : []), ...(mode !== "dinein" && lemon ? [EXTRA_NOTE.lemon] : []), ...(mode !== "dinein" && spoons ? [EXTRA_NOTE.spoons] : []), mode !== "delivery" && whenMode === "asap" && !WA_CTX && arrivalTime !== "now" ? ((mode === "pickup" ? "Collecting in ~" : "Guest arrives in ~") + arrivalTime + " min. " + notes.trim()).trim() : notes.trim()].filter(Boolean).join("\n").slice(0, NOTES_MAX) || undefined,
              scheduled_for: scheduledForUTC,
              coords: coords || undefined,
              area: mode === "delivery" ? deliveryArea || undefined : undefined
            },
            // mode IS the fulfilment method now that pickup is a real
            // third choice. The server re-validates against
            // ['delivery','dinein','pickup'] and re-derives every fee.
            fulfillment_method: mode,
            // Ad click-IDs + Meta cookies, so the SERVER-SIDE Purchase
            // sent after payment capture can be matched back to the ad.
            // lang rides along (2026-10), so attribution is now ALWAYS an
            // object — classify traffic by utm/fbclid, never by IS NULL.
            attribution: Object.assign({}, captureAttribution() || {}, hostAttr(L)),
            // Marketing consent. Only ever true when the customer ticked
            // the box themselves on this order.
            marketing_opt_in: marketingOptIn === true,
            discount_code: disc ? disc.code : undefined,
            // L4: { company, trn?, address? } — create.ts stores it in orders.invoice_details.
            invoice: invoiceBody,
            // L2: only while the gathering rules are really in force.
            gathering: gatherActive ? true : undefined,
            channel: WA_CTX ? "whatsapp" : undefined,
            wa_token: WA_CTX ? WA_CTX.token : undefined
          })
        });
        const json = await res.json();
        // Checkout was closed (or "Taking too long? Cancel" pressed) while
        // this request was in flight: its answer — success OR error — is
        // stale. Drop it before it can touch the state of a newer submit
        // (no error text, no "placed" bookkeeping, no bank redirect).
        if (submitGenRef.current !== gen) return;
        // Live-availability refusals (create.ts guard) map by CODE in every
        // language, EN included: the raw server text is for logs, not guests.
        if (json && json.code === 'kitchen_paused') {
          if (onServerAvail) onServerAvail(json); // App: note the pause + refresh status
          setWhenMode("later");
          whenArmedRef.current = true;
          setFieldErrs({
            time: "co.errPaused"
          });
          setSubmitError("co.errPaused");
          setSubmitting(false);
          return;
        }
        // L2: gathering refusals (create.ts / src/lib/gathering.ts).
        if (json && (json.code === 'gathering_cutoff' || json.code === 'gathering_max' || json.code === 'gathering_min')) {
          const gk = json.code === 'gathering_min' ? "co.errGatherMin" : json.code === 'gathering_max' ? "co.errGatherMax" : "co.errGatherCutoff";
          if (json.code !== 'gathering_min') setFieldErrs({
            time: gk
          });
          setSubmitError(gk);
          setSubmitting(false);
          return;
        }
        if (json && json.code === 'item_unavailable') {
          if (onServerAvail) onServerAvail(json); // App: mark slugs sold out → cart sanitiser drops them
          setSubmitError("co.errUnavailable");
          setSubmitting(false);
          return;
        }
        if (!res.ok || !json.ok) {
          setSubmitError(L === 'en' ? json.error || "co.errOrder" : json.discount_invalid ? "co.discInvalid" : (() => {
            const e = String(json.error || '');
            return /UAE mobile/i.test(e) ? "co.errUaePhone" : /Pinned location/i.test(e) ? "co.errPin" : /delivery area/i.test(e) ? "co.errArea" : /do not deliver/i.test(e) ? "co.errNoZone" : /Minimum order/i.test(e) ? "co.errMinSrv" : /schedule at least/i.test(e) ? "co.errTimeMin" : /days ahead/i.test(e) ? khanCart ? "co.errTimeMax7" : "co.errTimeMax" : /dine-in only/i.test(e) ? "co.errDineinOnly" : /previous payment/i.test(e) ? "co.errPrevPay" : /address is required/i.test(e) ? "co.errAddress" : /Name is required/i.test(e) ? "co.errName" : /Phone is required/i.test(e) ? "co.errPhone" : "co.errOrder";
          })());
          setSubmitting(false);
          return;
        }
        // Remember this customer so they don't have to retype on next visit
        try {
          // L3: a gift order's address is the RECIPIENT's — it must not
          // become this customer's prefilled/saved address unless they
          // explicitly picked a label for it.
          const _keepAddr = _gift && !saveLabelTouched;
          let _prevCust = {};
          if (_keepAddr) {
            try {
              _prevCust = JSON.parse(localStorage.getItem('shaunfood-customer') || '{}') || {};
            } catch (e) {
              _prevCust = {};
            }
          }
          localStorage.setItem('shaunfood-customer', JSON.stringify(_keepAddr ? {
            ..._prevCust,
            name: name.trim(),
            phone: fullPhone
          } : {
            name: name.trim(),
            phone: fullPhone,
            address: mode === "delivery" ? address.trim() : '',
            ...(mode === "delivery" ? {
              addrType,
              addrParts: cleanAddrParts(addrParts),
              addrLegacy: _flatWaived ? addrLegacy : ''
            } : {})
          }));
          // K3: remember this delivery address on THIS device (newest
          // first, max 5, one entry per area + building/villa/company).
          // Never sent to the server.
          if (mode === "delivery" && !WA_CTX && deliveryArea && !_keepAddr) {
            const _entry = {
              label: saveLabel,
              area: deliveryArea,
              addressParts: {
                type: addrType,
                ...cleanAddrParts(addrParts)
              },
              // Only a drop-off the customer chose — never the automatic
              // early-hours "Don't ring the bell" default.
              dropoff: dropTouched ? dropoff : 'hand',
              ...(addrType === 'apt' && _flatWaived ? {
                addrLegacy
              } : {}),
              coords: coords ? {
                lat: coords.lat,
                lng: coords.lng
              } : null,
              at: Date.now()
            };
            const _k = savedAddrKey(_entry);
            writeSavedAddrs([_entry, ...loadSavedAddrs().filter(x => savedAddrKey(x) !== _k)]);
          }
          // Also stash the phone for /orders auto-lookup
          localStorage.setItem('shaunfood-customer-phone', fullPhone);
          placedRef.current = true;
          sessionStorage.removeItem('sf-co-draft'); // order placed — draft done
          // Save the active order for the floating tracking pill so the
          // customer can find their order from any page on the site.
          // Remember WHAT they ordered (ids + qty only) for the one-tap "order again" card.
          try {
            localStorage.setItem('sf-last-cart', JSON.stringify({
              at: Date.now(),
              lines: cart.map(l => ({
                id: l.id,
                qty: l.qty
              }))
            }));
          } catch (e) {/* ignore */}
          localStorage.setItem('shaunfood-active-order', JSON.stringify({
            order_number: json.order_number,
            saved_at: Date.now()
          }));
        } catch (e) {/* localStorage unavailable — fine */}
        if (onGatherDone) onGatherDone(); // L2: the hand-off ends a gathering visit

        // Upsell learning loop. 'purchased' events only when the order
        // is CONFIRMED here (COD/dine-in — no gateway step left). On the
        // pay-online path the customer may still abandon the gateway, so
        // we only reset the per-order discipline; real conversion is
        // measured server-side from items_json.is_upsell on paid orders.
        try {
          hostClear(); // table-step order state (sf-host-v1) ends with the order
          if (window.SFUpsell) {
            if (json.payment_url) {
              window.SFUpsell.resetOrder();
            } else {
              window.SFUpsell.orderPlaced(json.order_number, cart.filter(l => l.is_upsell === true).map(l => l.id));
            }
          }
        } catch (e) {}

        // If a payment URL was returned (NI session), redirect customer to the
        // hosted payment page. They'll come back to /success or /cancel.
        if (json.payment_url) {
          // Remember we kicked off a gateway redirect, so that if the
          // customer backs out on mobile (bfcache restore) the pageshow/
          // visibility effect can clear the overlay and offer to resume
          // THIS order instead of hanging on the spinner forever.
          redirectingRef.current = true;
          // The last thing that happens on OUR site. A session with
          // checkout_submit but no payment_redirect died in our code;
          // one with payment_redirect and no purchase died on the
          // gateway. That distinction is the whole 36% question.
          clarityEvent('payment_redirect');
          lastOrderRef.current = {
            order_number: json.order_number,
            total_aed: json.total_aed
          };
          if (submitGenRef.current !== gen) {
            redirectingRef.current = false;
            return;
          } // closed meanwhile
          payUrlRef.current = json.payment_url;
          payBeacon('pay_redirect', json.order_number); // R5 card-page counter (first-party, order number only)
          window.location.href = json.payment_url;
          return;
        }
        // Otherwise (no payment provider configured) — show in-page confirmation.
        setSubmitting(false);
        onPlace({
          orderNo: json.order_number,
          total: json.total_aed
        });
      } catch (err) {
        if (submitGenRef.current !== gen) return; // checkout closed meanwhile
        setSubmitError("co.errNetwork");
        setSubmitting(false);
      }
    }
  }, submitting ? /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.sending')) : deliveryShortfall > 0 ? /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.btnShort').replace('{n}', numLocal(L, deliveryShortfall))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'co.stamp'), " \xB7 ", /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, feeFmt(total), " AED")), L === 'en' && /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, "\xB7 ", tra(L, 'co.stamp'))))), /*#__PURE__*/React.createElement("div", {
    className: "co-nextstep " + P
  }, tr(L, 'co.nextStep')))));
}
function Confirm({
  open,
  onClose,
  orderNo
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  if (!open) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "confirm-back open",
    onClick: onClose
  }, /*#__PURE__*/React.createElement("div", {
    className: "confirm",
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement("div", {
    className: "seal"
  }, /*#__PURE__*/React.createElement("div", {
    className: "top"
  }, "SEALED"), /*#__PURE__*/React.createElement("div", {
    className: "mid"
  }, L === 'ar' ? 'طلب' : 'سفارش'), /*#__PURE__*/React.createElement("div", {
    className: "bot"
  }, L === 'ar' ? new Date().toLocaleDateString('ar-AE-u-nu-latn', {
    timeZone: 'Asia/Dubai'
  }) : L === 'tr' ? new Date().toLocaleDateString('tr-TR', {
    timeZone: 'Asia/Dubai'
  }) : new Date().toLocaleDateString("fa-IR"))), /*#__PURE__*/React.createElement("h2", {
    className: P
  }, tr(L, 'ok.onFire')), /*#__PURE__*/React.createElement("div", {
    className: "h-fa " + A
  }, tra(L, 'ok.onFire')), /*#__PURE__*/React.createElement("div", {
    className: "num " + P
  }, tr(L, 'ok.orderNo'), " ", orderNo), /*#__PURE__*/React.createElement("p", {
    className: "msg " + P
  }, tr(L, 'ok.msg')), /*#__PURE__*/React.createElement("button", {
    onClick: onClose
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'ok.close')), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'ok.close')))));
}
function Toast({
  message,
  fa,
  show
}) {
  const L = useLang();
  return /*#__PURE__*/React.createElement("div", {
    className: "toast " + (show ? "show" : ""),
    role: "status",
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement("span", {
    className: SCR(L)
  }, message), fa && /*#__PURE__*/React.createElement("span", {
    className: "fa " + SCR(ACCENT[L])
  }, fa));
}
function Footer() {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const link = (href, key) => /*#__PURE__*/React.createElement("a", {
    href: href
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, key)), " ", /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, key)));
  return /*#__PURE__*/React.createElement("footer", {
    className: "footer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "left"
  }, /*#__PURE__*/React.createElement("div", null, L === 'ar' ? /*#__PURE__*/React.createElement(React.Fragment, null, "\u0634\u0648\u0646 \u0630\u0627 \u0634\u064A\u0628 \xB7 \u062C\u0645\u064A\u0631\u0627\u060C \u062F\u0628\u064A \xB7 \xA9 ", /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr"
  }, new Date().getFullYear())) : L === 'tr' ? '© ' + new Date().getFullYear() + ' · Shaun the Sheep · Jumeirah, Dubai' : '© 1404 · Shaun the Sheep · Jumeirah, Dubai'), /*#__PURE__*/React.createElement("div", {
    className: "footer-links"
  }, link('/privacy', 'foot.privacy'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/terms', 'foot.terms'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/reservation', 'nav.reserve'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/gatherings', 'foot.gather'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/bread', 'foot.bread'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/blog', 'nav.blog'), /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), link('/orders', 'nav.orders'), WHATSAPP_URL && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xB7"), /*#__PURE__*/React.createElement("a", {
    href: WHATSAPP_URL,
    target: "_blank",
    rel: "noopener noreferrer"
  }, "WhatsApp")))), /*#__PURE__*/React.createElement("div", {
    className: "center",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("img", {
    src: "food-img/logo.jpg",
    alt: "",
    onError: e => {
      e.currentTarget.parentElement.style.display = 'none';
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "right"
  }, L === 'ar' ? 'نطبخ بحبّ · بالهنا والعافية' : 'با عشق پخته می‌شود', " \xB7 ", /*#__PURE__*/React.createElement("bdi", null, "Made by hand, served in a brass bowl.")));
}

// Cart persists across reloads + return visits — the shopkeeper remembers.
const CART_KEY = "kp-cart-v1";
const loadStoredCart = () => {
  let stored = [];
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    stored = Array.isArray(parsed) ? parsed : [];
  } catch {
    stored = [];
  }
  // ?cart=slug:qty,slug:qty — carries a basket ACROSS browsers. Written by the
  // in-app-browser "Open in browser" link: Instagram/Facebook webviews keep their
  // own storage, so without this the customer would land in Safari/Chrome with an
  // empty cart. Stripped from the URL after reading (like ?lang=). URL lines win.
  try {
    const u = new URL(window.location.href);
    const q = u.searchParams.get('cart');
    if (q) {
      // Only slugs that are orderable RIGHT NOW (on the menu, not sold out) —
      // a receipt's "Order again" may name a dish that has since been retired.
      const orderable = new Set(MENU.flatMap(s => s.items).filter(i => !i.soldOut).map(i => i.id));
      // A /gatherings hand-off may carry bread/doogh "for everyone" (up to 30).
      const qMax = u.searchParams.get('gather') === '1' ? 30 : 20;
      const fromUrl = q.split(',').map(s => {
        const [id, n] = s.split(':');
        const qty = Math.min(qMax, Math.max(1, parseInt(n, 10) || 1));
        return id && /^[a-z0-9-]{1,40}$/.test(id) && orderable.has(id) ? {
          id,
          qty
        } : null;
      }).filter(Boolean);
      if (fromUrl.length) {
        const ids = new Set(fromUrl.map(l => l.id));
        // A /gatherings basket REPLACES the stored cart: an old order still
        // sitting in kp-cart-v1 (it is not cleared after a card payment) would
        // otherwise be charged on top of the total /gatherings showed.
        stored = qMax === 30 ? fromUrl : [...fromUrl, ...stored.filter(l => l && !ids.has(l.id))];
      }
      u.searchParams.delete('cart');
      window.history.replaceState(null, '', u);
    }
  } catch (e) {/* no URL access — keep stored */}
  return stored;
};

// ── In-app-browser escape hatch (Instagram / Facebook / Snapchat / TikTok) ─────
// Those webviews cannot show Apple Pay / Google Pay and often break the 3DS
// redirect, so the payment page degrades to bare card entry — exactly the step
// where mobile customers drop (35% of web orders never pay). We nudge them into
// the real browser and carry the cart along. `?inapp=1` forces detection on.
// 2026-09-17: the in-app-browser bar + checkout tip are OFF. They went live
// 2026-09-16 12:40 UTC and the following 11 peak hours produced zero orders on
// ~300 ad clicks (80% of ad visitors arrive in the Instagram/Facebook webview
// and saw a warning-style bar first). No error was ever found — but the
// messages have no proven upside, and Apple Pay is unavailable inside those
// webviews anyway. The ?cart= hand-off below stays (harmless, used by /success).
const INAPP_HINTS_ENABLED = false;
const isInAppBrowser = () => {
  try {
    if (new URLSearchParams(window.location.search).get('inapp') === '1') return true;
    const ua = navigator.userAgent || '';
    return /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Snapchat|TikTok|musical_ly|BytedanceWebview|Line\/|; wv\)/i.test(ua);
  } catch (e) {
    return false;
  }
};
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
const isAndroid = () => /Android/i.test(navigator.userAgent || '');
const openInBrowserTarget = (cart, lang) => {
  const u = new URL(window.location.origin + '/');
  const c = (cart || []).filter(l => l && l.qty > 0).map(l => l.id + ':' + l.qty).join(',');
  if (c) u.searchParams.set('cart', c);
  if (lang) u.searchParams.set('lang', lang);
  const https = u.toString();
  if (isAndroid()) {
    // Chrome intent, with an https fallback for phones without Chrome.
    return 'intent://' + https.replace(/^https?:\/\//, '') + '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' + encodeURIComponent(https) + ';end';
  }
  if (isIOS()) return 'x-safari-' + https; // opens Safari from most in-app webviews
  return https;
};

// ── Server-injected merchandising data (index.astro) ─────────────────────────
// __POPULAR__: slugs ranked by real paid-order quantity (src/lib/popular.ts).
// __REVIEWS__: live Google rating + count from the ERP's daily snapshot.
const POPULAR = typeof window !== 'undefined' && Array.isArray(window.__POPULAR__) ? window.__POPULAR__.filter(s => typeof s === 'string') : [];
const POPULAR_TOP3 = new Set(POPULAR.slice(0, 3));
const REVIEWS = typeof window !== 'undefined' && window.__REVIEWS__ && Number(window.__REVIEWS__.count) > 0 && Number(window.__REVIEWS__.rating) > 0 ? window.__REVIEWS__ : null;
// Farsi-only crib sheet for the bank's card page (Network International shows
// English/Arabic only). Rendered in CheckoutModal under the refund note.
const PAY_GUIDE_FA = ['شماره‌ی ۱۶ رقمی کارت را در کادر شماره‌ی کارت (Card number) وارد کنید.', 'تاریخ انقضای روی کارت (ماه و سال) را در کادر Expiry وارد کنید.', 'کد سه‌رقمی پشت کارت (CVV یا CVV2) را در کادر CVV بنویسید.', 'اگر بانک کد پیامکی فرستاد، همان‌جا واردش کنید تا خودکار به همین صفحه برگردید.'];
// R1 (2026-10-25): the checkout pay row names the wallets — 82% of the people who pay use one (Apple Pay
// 73%, Google Pay 7%), and the row promised typed cards only. Brand names stay Latin in every language; in
// Arabic and Farsi each one is its own LTR isolate (the .ltr-num rule that keeps numbers whole), so the RTL
// line keeps its list order; a Latin-script line (isLatin: en, tr) stays a plain string. A function declaration
// (hoisted: no TDZ), called only at render (CheckoutModal).
function payRowLine(L) {
  const t = tr(L, 'co.payRow');
  if (isLatin(L)) return t;
  return t.split(/(Apple Pay|Google Pay|Network International)/).map((s, i) => i % 2 ? /*#__PURE__*/React.createElement("bdi", {
    key: i,
    dir: "ltr",
    className: "ltr-num"
  }, s) : s);
}
// How many a platter feeds. Directus products carry no `serves` field, but the
// Farsi names do ("دیس ۶ نفره" = 6-person dish), so derive it from there; the
// built-in fallback menu's `serves: "6 persons"` still wins when present.
const persianToInt = s => parseInt(String(s).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)), 10);
const servesOf = item => {
  try {
    const s = fld('en', item, 'serves') || '';
    let m = /(\d+)/.exec(s);
    if (m) return +m[1] || null;
    m = /([۰-۹٠-٩\d]+)\s*نفره/.exec(item.fa || '');
    if (m) {
      const n = persianToInt(m[1]);
      return n > 0 ? n : null;
    }
  } catch (e) {}
  return null;
};
// "Feeds N" — only for sharing platters (N ≥ 2; a single portion needs no
// headcount). Arabic counts decline: شخصين for 2, أشخاص for 3-10, شخصاً above.
// "{n} items" with the right number form per language (Arabic declines:
// one, dual, 3-10 plural, 11+ singular-accusative).
const itemsText = (L, n) => {
  if (L === 'ar') return n === 1 ? 'صنف واحد' : n === 2 ? 'صنفان' : n <= 10 ? n + ' أصناف' : n + ' صنفاً';
  if (L === 'fa') return numLocal(L, n) + ' قلم';
  if (L === 'tr') return n + ' ürün'; // singular after a numeral
  return n === 1 ? '1 item' : n + ' items';
};
const feedsText = (L, n) => {
  if (!(n >= 2)) return '';
  if (L === 'ar') return n === 2 ? 'تكفي شخصين' : n <= 10 ? 'تكفي ' + n + ' أشخاص' : 'تكفي ' + n + ' شخصاً';
  return tr(L, 'menu.feeds').replace('{n}', numLocal(L, n));
};

// ── Phase 3 (L2) gathering orders ───────────────────────────────────────────
// /gatherings hands off with ?gather=1. The flag lives in sessionStorage
// 'sf-gather' (this tab only) until the order hand-off succeeds or the cart
// empties. It only ACTS while the cart feeds 10+ (Σ qty × servesOf; items with
// no head-count count 0): then checkout forces Schedule, the day tabs run from
// tomorrow (the day after once Dubai passes 20:00) to today + 7, and the
// create request carries gathering:true. create.ts (src/lib/gathering.ts)
// enforces the same window and the 10+ rule.
const GATHER_KEY = 'sf-gather';
const GATHER_MIN_SERVINGS = 10;
const GATHER_MAX_DAYS = 7;
const GATHER_CUTOFF_HOUR = 20;
const readGatherFlag = () => {
  let url = false;
  try {
    url = new URL(window.location.href).searchParams.get('gather') === '1';
  } catch (e) {}
  try {
    if (url) sessionStorage.setItem(GATHER_KEY, '1');
    return url || sessionStorage.getItem(GATHER_KEY) === '1';
  } catch (e) {
    return url;
  }
};
const clearGatherFlag = () => {
  try {
    sessionStorage.removeItem(GATHER_KEY);
  } catch (e) {}
};
const cartServings = (cart, items) => (cart || []).reduce((s, l) => {
  const it = (items || []).find(i => i.id === l.id);
  return s + (it ? Math.max(0, Math.floor(Number(l.qty) || 0)) * (servesOf(it) || 0) : 0);
}, 0);

// ═══ HOST AT THE DISH: logic ═══
// The table step (2026-10): once a platter is on the order, ONE in-flow step
// (under the PlatterCard, or inside the platter's sheet) offers what the dish
// does NOT contain. Platter contents, hand-copied from the live descriptions:
//   dish-1 Single  cheek · 1 trotter · half a brain · half a bread
//   dish-2 Double  cheek · 2 trotters · half a brain · tongue · 1 eye · a whole bread
//   dish-3 Full    2 cheeks · meat · 4 trotters · whole brain · tongue · 2 eyes · 2 breads
//   dish-6 Khan's  4 cheeks · 2 meats · 8 trotters · 2 brains · 3 tongues · 4 eyes · 5 breads · 6 soups
//   combos (2026-10-24) = dish-1 + 1 soup + 1 eye, dish-2 + 2 soups, dish-3 + 3 soups
// Owner answers: D1 (2026-10-02) no platter included broth — since 2026-10-24
// the Khan's Platter and the combos include their soups (soupsIn), so the
// broth row asks only for the shortfall; D2 every delivery bag already
// holds lemon + a small mixed torshi (never offered as missing); D3 normal
// prices only (no set price, no discount). No JSX in this logic part — the
// node test (pairings kit check/hostplan.test.cjs) evals it as plain JS.
const HOST_SHEET_STAY = true; // G1 stop rule: false → the sheet closes on Add again
const HOST_REVEAL = true; // the one bounded reveal glide (card path)
const HOST_REVEAL_MAX = 240; // v2: the head (title + sub + set button) is measured at run time
const HOST_HOLD_IDLE_MS = 15000;
const HOST_MAX_N = 9; // 10+ is gathering territory (GATHER_MIN_SERVINGS)
const HOST_STORE = 'sf-host-v1';
// Slugs per role. Drink: doogh AND lemonade (C1 two-choice row); bread (v3):
// sangak AND sangak-x, the same two-choice row. Never water/tea/soft drinks,
// brain-soup or bread-add.
const HOST_SLUG = {
  broth: ['soup'],
  drink: ['doogh', 'lemonade'],
  bread: ['sangak', 'sangak-x'],
  omelette: ['omelette']
};
const HOST_EACH = {
  broth: true,
  drink: true
}; // target N (one per person); others (bread too) 1
// Bread target for 3+ people. V3 said 2, but the only signed-off copy for it
// ("Extra bread each" + host.why.breadN "one more") contradicts a 2-bread tap,
// and no 2-bread title/reason is approved in EN/FA/AR (fixer r2). 1 keeps
// title, reason and the "10 AED" pill in agreement; the stepper adds more.
// Raise to 2 only together with owner-approved 2-bread title + reason copy.
const HOST_BREAD_T3 = 1;
// v3: base rows in order — bread is a base row from the start (no chain), the
// eye row is retired (the eye pair is an Extra-cuts card). `cuts` = the
// Extra-cuts scroller, data-ranked per anchor (paid web orders, all history).
// Each combo mirrors its base dish (no order history of its own yet); the
// Single combo already carries its eye, so eye is not an extra cut there.
const HOST_CFG = {
  'dish-1': {
    rows: ['broth', 'drink', 'bread'],
    bundle: true,
    cuts: ['tongue', 'meat', 'brain', 'eye', 'trotters', 'mojoon']
  },
  'single-dish-soup-eye': {
    rows: ['broth', 'drink', 'bread'],
    bundle: true,
    cuts: ['tongue', 'meat', 'brain', 'trotters', 'mojoon']
  },
  'dish-2': {
    rows: ['broth', 'drink', 'bread'],
    bundle: true,
    cuts: ['brain', 'eye', 'tongue', 'meat', 'cheek', 'mojoon']
  },
  'double-dish-2-soups': {
    rows: ['broth', 'drink', 'bread'],
    bundle: true,
    cuts: ['brain', 'eye', 'tongue', 'meat', 'cheek', 'mojoon']
  },
  'dish-3': {
    rows: ['broth', 'drink', 'bread'],
    bundle: false,
    cuts: ['eye', 'tongue', 'trotters', 'cheek', 'brain', 'meat']
  },
  'full-dish-3-soups': {
    rows: ['broth', 'drink', 'bread'],
    bundle: false,
    cuts: ['eye', 'tongue', 'trotters', 'cheek', 'brain', 'meat']
  },
  'dish-6': {
    rows: ['broth', 'drink', 'bread', 'omelette'],
    bundle: false,
    cuts: ['tongue', 'brain', 'cheek', 'eye', 'meat', 'trotters']
  },
  mixed: {
    rows: ['broth', 'drink', 'bread'],
    bundle: false,
    cuts: ['tongue', 'brain', 'eye', 'meat', 'cheek', 'mojoon']
  }
};
// The half-bread Singles: their own key at any qty, and the bread1 / bread1n reasons.
const HOST_SINGLE = new Set(['dish-1', 'single-dish-soup-eye']);
// Kill switch: upsell.ts placements.companion (serialized into __UPSELL__).
function hostEnabled() {
  try {
    const u = window.__UPSELL__;
    return !(u && u.placements && u.placements.companion === false);
  } catch (e) {
    return true;
  }
}
const hostDocLang = () => {
  try {
    return document.documentElement.getAttribute('data-lang') || 'en';
  } catch (e) {
    return 'en';
  }
};

// Per-order state, sessionStorage 'sf-host-v1': done {anchor: 'set'|'none'},
// declined [slug], shown ['placement|slug'], opened ['variant|anchor'],
// revealed [anchor] (C9: once per anchor per order, survives remounts), hx/hs
// (order attribution). Parsed ONCE into the in-memory mirror _hostMem (C13 —
// no JSON re-parse per scroll frame); every write goes through hostSave, so
// the mirror is the truth and also the fallback when storage throws.
const hostBlank = () => ({
  done: {},
  declined: [],
  shown: [],
  opened: [],
  revealed: [],
  hx: '',
  hs: ''
});
let _hostMem = null;
function hostLoad() {
  if (!_hostMem) {
    const s = hostBlank();
    // Each field is taken only when its type matches the blank (arrays as arrays).
    try {
      const o = JSON.parse(sessionStorage.getItem(HOST_STORE)) || {};
      for (const k in s) if (o[k] && typeof o[k] === typeof s[k] && Array.isArray(o[k]) === Array.isArray(s[k])) s[k] = o[k];
    } catch (e) {}
    _hostMem = s;
  }
  return {
    ..._hostMem
  };
}
function hostSave(patch) {
  _hostMem = {
    ...hostLoad(),
    ...patch
  };
  try {
    sessionStorage.setItem(HOST_STORE, JSON.stringify(_hostMem));
  } catch (e) {}
}
function hostClear() {
  _hostMem = hostBlank();
  try {
    sessionStorage.removeItem(HOST_STORE);
  } catch (e) {}
}
// true the FIRST time `key` is recorded in list `field` this order.
function hostOnce(field, key) {
  const s = hostLoad();
  if (s[field].indexOf(key) >= 0) return false;
  hostSave({
    [field]: [...s[field], key]
  });
  return true;
}
// Order attribution keys (create.ts ATTR_KEYS whitelists lang/hx/hs).
function hostAttr(L) {
  const s = hostLoad(),
    a = {
      lang: L,
      hx: s.hx || 'none'
    };
  if (s.hs) a.hs = s.hs;
  return a;
}

// Self-contained telemetry → /api/upsell/track (placements companion-card /
// companion-sheet). Deliberately NOT SFUpsell.track: no upsell_* pixel events,
// no touch budget. Same session id format as suggestions.js so rows join the
// old funnel.
// (_hostSid is the in-memory id when sessionStorage is blocked.)
const _hostSid = 'u' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
function hostBeacon(action, placement, slug) {
  try {
    let sid = _hostSid,
      snap = [];
    try {
      sid = sessionStorage.getItem('sf-up-sid') || (sessionStorage.setItem('sf-up-sid', _hostSid), _hostSid);
    } catch (e) {}
    try {
      snap = JSON.parse(localStorage.getItem(CART_KEY)).map(l => l.id).filter(x => typeof x === 'string').slice(0, 30);
    } catch (e) {}
    const body = JSON.stringify({
      session_id: sid,
      placement,
      action,
      item_slug: slug,
      cart_snapshot: snap,
      lang: hostDocLang()
    });
    const url = window.__UPSELL__ && window.__UPSELL__.track || '/api/upsell/track';
    let ok = false;
    try {
      ok = navigator.sendBeacon(url, new Blob([body], {
        type: 'application/json'
      }));
    } catch (e) {}
    if (!ok) fetch(url, {
      method: 'POST',
      body,
      keepalive: true
    }).catch(() => {});
  } catch (e) {}
}

// The plan: pure, null = silent. N = Σ qty × servesOf over platter lines.
// Rows: {role, id, item, opts:[{id,item,cur}], target, cur, max} — `opts`
// holds the usable slugs for the role (two for the drink row when doogh and
// lemonade are both on, C1); id/item are the primary option (most in the
// cart, ties → first) and `cur` is the role's total in the cart.
// bundle (Single/Double, N ≤ 2): soup + one drink each in ONE write; the
// drink follows the language (fa → doogh, else lemonade; C1).
// v3: bread is a base row (two choices, target 1 for N ≤ 2, else 2; none at
// dine-in — bread is on the house at our tables); no eye row in either
// variant. cuts = [{id, item, cur}] in the anchor's data order, usable ones
// only, [] when fewer than 2 remain. Each cut tap adds ONE (never per person).
// 2026-10-24: broth target = N − the soups the cart's platters already include
// (soupsIn); at 0 there is no broth row (so no bundle, no host.sub).
function hostPlan(cart, items, mode, anchorId, L) {
  items = items || [];
  const find = id => items.find(i => i.id === id);
  const lines = (cart || []).filter(l => l && l.qty > 0);
  const plats = lines.filter(l => PLATTER_IDS.has(l.id));
  if (!plats.some(l => l.id === anchorId)) return null;
  let n = 0,
    soups = 0;
  for (const l of plats) {
    const it = find(l.id),
      s = it ? servesOf(it) : null;
    if (!s) return null;
    n += s * l.qty;
    soups += soupsIn(it) * l.qty;
  }
  if (n < 1 || n > HOST_MAX_N) return null;
  const one = plats.length === 1 ? plats[0] : null;
  const key = one && HOST_CFG[one.id] && (one.qty === 1 || HOST_SINGLE.has(one.id)) ? one.id : 'mixed';
  const cfg = HOST_CFG[key];
  const qtyIn = id => {
    const l = lines.find(x => x.id === id);
    return l ? l.qty : 0;
  };
  const usable = id => {
    const it = find(id);
    return it && !it.soldOut && !(mode !== 'dinein' && DINE_IN_ONLY.has(id)) ? it : null;
  };
  const mk = role => {
    if (role === 'bread' && mode === 'dinein') return null; // bread is on the house at our tables
    const opts = HOST_SLUG[role].map(usable).filter(Boolean).slice(0, 2).map(it => ({
      id: it.id,
      item: it,
      cur: qtyIn(it.id)
    }));
    if (!opts.length) return null;
    const top = opts.reduce((a, o) => o.cur > a.cur ? o : a, opts[0]),
      t = role === 'broth' ? Math.max(0, n - soups) : HOST_EACH[role] ? n : role === 'bread' && n > 2 ? HOST_BREAD_T3 : 1;
    if (!t) return null; // every guest already has a bowl from the platter
    return {
      role,
      id: top.id,
      item: top.item,
      opts,
      target: t,
      cur: opts.reduce((s, o) => s + o.cur, 0),
      max: Math.min(20, 2 * t + 2)
    };
  };
  const rows = cfg.rows.map(mk).filter(Boolean);
  if (rows.length < 2) return null;
  const broth = rows.find(r => r.role === 'broth'),
    drink = rows.find(r => r.role === 'drink');
  let cuts = cfg.cuts.map(usable).filter(Boolean).map(it => ({
    id: it.id,
    item: it,
    cur: qtyIn(it.id)
  }));
  if (cuts.length < 2) cuts = [];
  let bundle = null;
  // The set is "a bowl EACH": never while platter soups cover part of the table.
  if (cfg.bundle && n <= 2 && broth && drink && broth.target === n) {
    const want = (L || hostDocLang()) === 'fa' ? 'doogh' : 'lemonade';
    // A drink already on the order wins, so the sub-line names what gets topped up.
    const d = drink.cur && drink.opts.find(o => o.cur > 0) || drink.opts.find(o => o.id === want) || drink.opts[0];
    const bl = [{
      id: broth.id,
      to: Math.max(broth.cur, n),
      from: broth.cur
    }, {
      id: d.id,
      to: d.cur + Math.max(0, n - drink.cur),
      from: d.cur
    }];
    bundle = {
      lines: bl,
      drink: d.item,
      aed: bl.reduce((s, x) => s + (x.to - x.from) * Number((find(x.id) || {}).price || 0), 0),
      done: bl.every(x => x.to === x.from)
    };
  }
  return {
    key,
    n,
    anchorId,
    rows,
    cuts,
    bundle,
    sig: rows.map(r => r.opts.map(o => o.id + ':' + o.cur).join('+')).join(',') + '/' + cuts.map(o => o.id).join(',') + (bundle ? '|' + bundle.drink.id : '')
  };
}
const hostPlanSafe = (c, it, m, a, L) => {
  try {
    return hostPlan(c, it, m, a, L);
  } catch (e) {
    return null;
  }
};
// Every slug the step offers (rows' options + the cut cards).
const hostIds = p => p ? p.rows.reduce((a, r) => a.concat(r.opts), []).concat(p.cuts) : [];
// The set's price, per-unit rule: "16 AED per person" while N > 1 and the whole
// set is still to add; the bare unit sum at N = 1; what remains otherwise.
const hostSetPx = p => {
  const b = p.bundle,
    k = p.n > 1 && b.lines.every(x => !x.from && x.to === p.n) ? 'host.pp' : null;
  return [k ? b.aed / p.n : b.aed, k];
};
// v2 #4 "your table": DISPLAY-ONLY order for any list of cart lines ({l, item}).
// src 'companion' lines sit directly under the FIRST platter line, tagged sub;
// no platter (or no companion) → the list unchanged. Never reorders the cart.
function hostGroup(lines) {
  const isC = x => x.l.src === 'companion' && !PLATTER_IDS.has(x.l.id),
    f = lines.find(x => PLATTER_IDS.has(x.l.id));
  const c = f ? lines.filter(isC) : [];
  if (!c.length) return lines;
  const r = lines.filter(x => !isC(x)),
    j = r.indexOf(f) + 1;
  return r.slice(0, j).concat(c.map(x => ({
    ...x,
    sub: 1
  })), r.slice(j));
}
// Offered slugs still at 0 — what a resolve without adding counts as declined
// (rows whose role total is 0; the caller keeps only seen ones). Fixer r1: the
// Extra-cuts cards are NOT declines — a passively seen carousel card must not
// leave the ribbon / checkout for the order (V3 tracks cuts as shown/accepted only).
const hostZero = p => p ? p.rows.filter(r => !r.cur).reduce((a, r) => a.concat(r.opts), []).map(o => o.id) : [];

// ONE cart write for any set of step lines ({id, to, from}): re-reads MENU so a
// stale row can never add a sold-out / dine-in-only item; `to` is absolute and
// clamped 0..30; 0 removes the line; an existing line keeps its own fields
// (organic stays organic, is_upsell stays is_upsell); a NEW line is
// {id, qty, src:'companion'}. Returns {next, adds:[{it, d}]} with deltas
// measured against the live cart — call it INSIDE the setCart updater (C16).
function hostMerge(cart, lines, mode) {
  const all = MENU.flatMap(s => s.items),
    adds = [];
  let next = (cart || []).slice();
  (lines || []).forEach(x => {
    const it = x && all.find(i => i.id === x.id);
    if (!it) return;
    const ex = next.find(l => l.id === x.id),
      from = ex ? ex.qty : 0;
    const to = Math.max(0, Math.min(30, Math.floor(Number(x.to)) || 0));
    if (to === from || to > from && (it.soldOut || mode !== 'dinein' && DINE_IN_ONLY.has(it.id))) return;
    next = !to ? next.filter(l => l.id !== x.id) : ex ? next.map(l => l.id === x.id ? {
      ...l,
      qty: to
    } : l) : [...next, {
      id: x.id,
      qty: to,
      src: 'companion'
    }];
    if (to > from) adds.push({
      it,
      d: to - from
    });
  });
  return {
    next,
    adds
  };
}
// add_to_cart with the TRUE quantity (value = unit × qty, items[] at the unit
// price) + the 'accepted' beacon, per positive delta.
function hostTrack(adds, placement) {
  (adds || []).forEach(({
    it,
    d
  }) => {
    try {
      if (window.sfTrack) window.sfTrack('add_to_cart', {
        id: it.id,
        name: it.en,
        value: it.price * d,
        qty: d,
        items: [{
          item_id: it.id,
          item_name: it.en,
          price: it.price,
          quantity: d
        }]
      });
    } catch (e) {}
    hostBeacon('accepted', placement, it.id);
  });
}

// Copy helpers. Product names always come from the live menu (nm), so the
// step, the cart and the receipt say the same thing.
const hostFor = (L, n) => tr(L, 'host.for' + Math.max(1, Math.min(9, n)));
const hostName = (L, it) => {
  const x = it.id === 'eye' ? fld(L, it, 'unit') || fld(L, it, 'hint') : '';
  return nm(L, it) + (x ? ' · ' + x : '');
};
// why: broth/omelette by role; drink by the primary slug (doogh | lemonade);
// bread1/bread1n on the Single(s), breadN otherwise.
const hostWhyKey = (r, p) => 'host.why.' + (r.role === 'drink' ? r.id : r.role === 'bread' ? !HOST_SINGLE.has(p.key) ? 'breadN' : p.n > 1 ? 'bread1n' : 'bread1' : r.role);
// Two-choice row title (V3): "…each" (RowN) when a tap adds more than one,
// else Row1. Fixer r2: the unapproved host.breadRow2 is gone; with
// HOST_BREAD_T3 = 1 the bread row always reads host.breadRow1.
const hostRowTitleKey = (r, p) => 'host.' + r.role + 'Row' + (r.target > 1 ? 'N' : '1');
// btn: bowl / one (+Each when N > 1 and the row is one per person — not a
// broth top-up next to a soup-including platter) / add.
const hostBtnKey = (r, p) => 'host.btn.' + ({
  broth: 'bowl',
  drink: 'one'
}[r.role] || 'add') + (HOST_EACH[r.role] && p.n > 1 && r.target === p.n ? 'Each' : '');
// A tap adds the whole gap only under an '…Each' label; a singular label ('Add a bowl') adds exactly one.
// (2026-10-25: a broth top-up of 2-3 bowls next to a soup-including platter keeps the
// pill, one bowl per tap, until the shortfall is met; then the stepper. hostRowEl's pill.)
const hostPillTo = (r, p) => {
  const o = r.opts.find(x => x.id === r.id),
    gap = r.target - r.cur;
  return o.cur + (HOST_EACH[r.role] && !(p.n > 1 && r.target === p.n) ? Math.min(1, gap) : gap);
};
// Brand / product names that keep their written casing inside a Turkish sentence (STYLE-GUIDE b "Inline item names").
const TR_BRAND_NAME = /^(doogh|pepsi|coca-cola|7 up|sprite|fanta|mirinda|mountain dew)/i;
// "a bowl of soup and a lemonade (each)"; doogh carries its hint in EN/AR/TR.
const hostBundleSub = (L, p) => {
  // tr: common nouns go lowercase mid-sentence with Turkish casing (İ/ı); brand names stay as written.
  const lc = s => L === 'en' ? s.toLowerCase() : L === 'tr' ? TR_BRAND_NAME.test(s) ? s : s.toLocaleLowerCase('tr-TR') : s,
    d = p.bundle.drink,
    soup = p.rows.find(r => r.role === 'broth');
  const hint = d.id === 'doogh' && L !== 'fa' ? L === 'ar' ? ' (لبن مملّح)' : L === 'tr' ? ' (tuzlu yoğurt içeceği)' : ' (salted yogurt drink)' : '';
  return tr(L, p.n > 1 ? 'host.bundleSubN' : 'host.bundleSub1').replace('{soup}', lc(nm(L, soup.item))).replace('{drink}', lc(nm(L, d)) + hint);
};

// DOM helpers (call-time only; every caller null-checks its root).
const hostReduced = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};
const hostRect = sel => {
  const el = document.querySelector(sel);
  return el && el.getClientRects().length ? el.getBoundingClientRect() : null;
};
// Where the cart bar's top WILL be: it slides in (cartBarIn .35s) on the first
// add, so its live rect still sits at the screen edge when the step mounts.
const hostBarTop = () => {
  const el = document.querySelector('.mobile-cart-bar.has-items');
  return el && el.getClientRects().length ? window.innerHeight - el.offsetHeight : window.innerHeight;
};
// Passive capture-phase window listeners; returns the detach (same capture flag).
const hostListen = (types, fn) => {
  types.forEach(t => window.addEventListener(t, fn, {
    passive: true,
    capture: true
  }));
  return () => types.forEach(t => window.removeEventListener(t, fn, true));
};
// The band a row must sit in to count as seen: sheet = inside the card above
// its sticky foot; card = below the sticky nav/category strip, above the cart bar.
function hostBand(variant, root) {
  if (variant === 'sheet') {
    const card = root.closest('.item-sheet-card');
    if (!card) return null;
    const c = card.getBoundingClientRect(),
      f = card.querySelector('.is-foot');
    return {
      top: c.top,
      bottom: f ? Math.min(c.bottom, f.getBoundingClientRect().top) : c.bottom
    };
  }
  const s = hostRect('.cat-tabs-strip'),
    n = hostRect('.nav');
  return {
    top: Math.max(0, s ? s.bottom : 0, n ? n.bottom : 0),
    bottom: hostBarTop()
  };
}
// 'shown' beacon once per placement+slug per order; the clarity event once per
// anchor (C9); the first sighting tags the order hx=seen.
function hostMarkShown(placement, slug, variant, anchorId) {
  const s = hostLoad(),
    k = placement + '|' + slug;
  if (s.shown.indexOf(k) >= 0) return;
  hostSave({
    shown: [...s.shown, k],
    hx: s.hx || 'seen',
    hs: s.hx ? s.hs : variant
  });
  hostBeacon('shown', placement, slug);
  if (hostOnce('opened', 'shown|' + variant + '|' + anchorId)) clarityEvent('host_shown_' + variant);
  if (!s.hx) clarityTag('host', 'seen');
}

// ═══ HOST AT THE DISH: components ═══
// Each host surface sits in its own boundary: a bug renders nothing, never
// the root "Something went wrong" screen. (Class declarations are not hoisted,
// but rendering only starts at createRoot at the end of the file.)
class HostBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = {
      err: false
    };
  }
  static getDerivedStateFromError() {
    return {
      err: true
    };
  }
  // onError lets App drop a dead card step so the C8 ribbon comes back.
  componentDidCatch() {
    try {
      if (this.props.onError) this.props.onError();
    } catch (e) {}
    clarityEvent('host_error');
  }
  render() {
    return this.state.err ? null : this.props.children;
  }
}
const hostAed = (v, c) => /*#__PURE__*/React.createElement("bdi", {
  dir: "ltr",
  className: 'ltr-num ' + c
}, v, ' AED');
// Prices read per unit (owner 2026-10-03: "+20 AED" on Doogh read as a 20 AED
// doogh). A tap still adds one per person; when it adds more than one the price
// says "each" / "per person". k = null → the bare unit price.
const hostPx = (L, aed, k, c) => {
  const s = k ? tr(L, k).split('{p}') : ['', ''];
  return /*#__PURE__*/React.createElement("span", {
    className: c
  }, s[0], hostAed(feeFmt(aed), ''), s[1]);
};
const hostPxT = (L, aed, k) => k ? tr(L, k).replace('{p}', feeFmt(aed) + ' AED') : feeFmt(aed) + ' AED';
// Plain-string aria-label (AMENDMENTS B: FA/AR use the Arabic comma).
const hostA11y = (L, btn, name, p) => tr(L, 'host.a11yPill').replace('{btn}', btn).replace('{name}', name).replace('{p}', p);
// The cart-style − n + stepper (step rows + the sheet's platter line).
const hostStepper = (L, k, label, n, dec, inc, max) => /*#__PURE__*/React.createElement("span", {
  key: k,
  className: "stepper",
  role: "group",
  "aria-label": label
}, /*#__PURE__*/React.createElement("button", {
  onClick: dec,
  "aria-label": tr(L, 'a11y.dec')
}, "\u2212"), /*#__PURE__*/React.createElement("span", {
  className: "qty"
}, n), /*#__PURE__*/React.createElement("button", {
  disabled: max,
  onClick: inc,
  "aria-label": tr(L, 'a11y.inc')
}, "+"));

// A dish's round thumb: its photo, else the menu icon.
const hostThumb = it => it.image ? /*#__PURE__*/React.createElement("img", {
  src: it.image,
  alt: "",
  loading: "lazy"
}) : ITEM_ICONS[ITEM_ICON_MAP[it.id] || 'bowl'];

// One row: [thumb | name | action] / [reason]. Pill while below target (a tap
// SETS the line — to target, or one more under a singular label: hostPillTo —
// never additive); stepper once at target, mirroring
// the real cart line. Drink row (C1): nothing in the cart → two pills [Doogh]
// [Lemonade] under the row title, each pill with its own thumb (so the
// row has none), one-per-person; at target → a stepper per drink in the
// cart + a small "+ Lemonade" for the other. v3: the bread row works the same
// way ([Sangak Bread][Special Sangak], target 1 / 2, "+ Special Sangak").
function hostRowEl(r, p, L, P, set) {
  const btn = tr(L, hostBtnKey(r, p)),
    gap = r.target - r.cur,
    two = !r.cur && r.opts.length > 1,
    how = 'row_' + r.role;
  const to = (o, n, h) => () => set([{
    id: o.id,
    to: n,
    from: o.cur
  }], h);
  // th: the two-choice drink pills carry that drink's own thumb (v2 #3).
  const pill = (o, label, n, th) => {
    const k = n - o.cur > 1 ? 'host.each' : null,
      u = o.item.price;
    return /*#__PURE__*/React.createElement("button", {
      key: o.id,
      className: 'host-pill ' + P + (th ? ' host-pill--th' : ''),
      onClick: to(o, n, how),
      "aria-label": hostA11y(L, btn, nm(L, o.item), hostPxT(L, u, k))
    }, th ? /*#__PURE__*/React.createElement("span", {
      className: "hp-th",
      "aria-hidden": "true"
    }, hostThumb(o.item)) : null, /*#__PURE__*/React.createElement("span", {
      className: "hp-x"
    }, /*#__PURE__*/React.createElement("span", {
      className: "hp-l"
    }, label), hostPx(L, u, k, 'hp-p')));
  };
  const inCart = r.opts.filter(o => o.cur > 0),
    pri = r.opts.find(o => o.id === r.id);
  // st: stepper (+ alt). V3: the bread row turns into a stepper as soon as ONE
  // bread is in the cart, even below its target of 2 (fixer r1) — so it can be
  // removed and the other bread is still offered.
  const st = gap <= 0 || r.role === 'bread' && r.cur > 0;
  const act = two ? r.opts.map(o => pill(o, nm(L, o.item), r.target, 1)) : !st ? pill(pri, btn, hostPillTo(r, p)) : inCart.map(o => hostStepper(L, o.id, nm(L, o.item), o.cur, to(o, o.cur - 1, 'minus'), to(o, o.cur + 1, 'plus'), r.cur >= r.max)).concat(r.opts.filter(o => !o.cur).map(o => /*#__PURE__*/React.createElement("button", {
    key: 'a' + o.id,
    className: 'host-alt ' + P,
    onClick: to(o, 1, how)
  }, '+ ' + nm(L, o.item) + ' ', hostAed(feeFmt(o.item.price), 'hp-p'))));
  return /*#__PURE__*/React.createElement("li", {
    key: r.role,
    className: 'host-row' + (two ? ' host-row--two' : ''),
    "data-host-row": r.opts.map(o => o.id).join(' ')
  }, two ? null : /*#__PURE__*/React.createElement("span", {
    className: 'hr-thumb' + (r.cur > 0 ? ' is-on' : ''),
    "aria-hidden": "true"
  }, hostThumb(r.item), r.cur > 0 ? /*#__PURE__*/React.createElement("i", {
    className: "hr-stamp",
    key: 's' + r.cur
  }, "\u2713") : null), /*#__PURE__*/React.createElement("span", {
    className: 'hr-name ' + P
  }, two ? tr(L, hostRowTitleKey(r, p)) : (inCart.length ? inCart : [r]).map(o => hostName(L, o.item)).join(' · ')), /*#__PURE__*/React.createElement("span", {
    className: 'hr-why ' + P
  }, tr(L, hostWhyKey(r, p))), /*#__PURE__*/React.createElement("span", {
    className: 'hr-act' + (two || st && r.opts.length > 1 ? ' hr-act--two' : '')
  }, act));
}

// v3 Extra cuts: one photo card per single (data order; the first two carry
// "Often added"). "+" adds ONE; once in the cart the card shows the cart-style
// − n + stepper (0 removes) and the ✓ stamp on its photo.
// Fixer r1: a tap on a card that is only PARTLY inside the scroller (the ~30px
// peek) reveals it instead of adding (its stamp / stepper sit off-screen, and
// 'shown' needs it in view); the "+" itself always adds. scrollBy takes a
// visual delta, so LTR and RTL both work. No event (tests) → add.
const hostCutTap = add => e => {
  const li = e && e.currentTarget,
    sc = li && li.parentElement;
  if (sc && !(e.target && e.target.closest && e.target.closest('.hc-add'))) {
    const r = li.getBoundingClientRect(),
      x = sc.getBoundingClientRect();
    if (r.left < x.left - 2 || r.right > x.right + 2) {
      try {
        sc.scrollBy({
          left: r.left < x.left ? r.left - x.left - 12 : r.right - x.right + 12,
          behavior: hostReduced() ? 'auto' : 'smooth'
        });
      } catch (err) {}
      return;
    }
  }
  add();
};
// Same-finger double-tap guard: the stepper's "+" replaces the add "+" on the
// same spot, so a fast second tap meant as ONE add is swallowed (450 ms per slug).
const _hostCutAt = {};
function hostCutEl(o, i, L, P, set) {
  // The eye card names its pair in brackets: " · ۲" read as "۲۰" in FA.
  const u = o.id === 'eye' ? fld(L, o.item, 'unit') || fld(L, o.item, 'hint') : '',
    name = nm(L, o.item) + (u ? ' (' + u + ')' : ''),
    px = feeFmt(o.item.price) + ' AED',
    to = (n, h) => () => set([{
      id: o.id,
      to: n,
      from: o.cur
    }], h);
  const add = () => {
    _hostCutAt[o.id] = Date.now();
    to(1, 'cut')();
  };
  const inc = () => {
    if (Date.now() - (_hostCutAt[o.id] || 0) < 450) return;
    to(o.cur + 1, 'plus')();
  };
  return (
    /*#__PURE__*/
    // Polish: while at 0 the WHOLE card adds (people tap the photo); the "+" stays
    // the keyboard / screen-reader control and its click bubbles to this handler.
    // Once in the cart only the stepper takes taps (a stray tap never adds again).
    React.createElement("li", {
      key: o.id,
      className: 'host-cut' + (o.cur ? ' is-on' : ''),
      "data-host-cut": o.id,
      onClick: o.cur ? undefined : hostCutTap(add)
    }, /*#__PURE__*/React.createElement("span", {
      className: "hc-ph",
      "aria-hidden": "true"
    }, o.item.image ? /*#__PURE__*/React.createElement("img", {
      src: o.item.image,
      srcSet: o.item.imageMd ? o.item.image + ' 200w, ' + o.item.imageMd + ' 400w' : undefined,
      sizes: o.item.imageMd ? '148px' : undefined,
      alt: "",
      loading: "lazy",
      decoding: "async"
    }) : hostThumb(o.item), o.cur ? /*#__PURE__*/React.createElement("i", {
      className: "hr-stamp",
      key: 's' + o.cur
    }, "\u2713") : null), i < 2 ? /*#__PURE__*/React.createElement("bdi", {
      className: 'hc-often ' + P
    }, tr(L, 'host.often')) : null, /*#__PURE__*/React.createElement("span", {
      className: 'hc-nm ' + P
    }, name), hostAed(feeFmt(o.item.price), 'hc-p'), /*#__PURE__*/React.createElement("span", {
      className: "hc-act"
    }, o.cur ? hostStepper(L, 'st', name, o.cur, to(o.cur - 1, 'minus'), inc, o.cur >= 10) : /*#__PURE__*/React.createElement("button", {
      className: "hc-add",
      "aria-label": tr(L, 'host.addItem').replace('{name}', name).replace('{p}', px)
    }, "+")))
  );
}

// The step itself (both paths). ALL hooks above `if (!plan) return null`;
// hook deps only name consts declared above them (TDZ, 2026-09-20).
function TableStep({
  variant,
  anchorId,
  plan,
  mode,
  covered,
  engaged,
  onSetLines,
  onResolve,
  onMount,
  onPing
}) {
  const L = useLang();
  const P = SCR(L);
  const rootRef = useRef(null);
  const coveredRef = useRef(covered);
  coveredRef.current = covered;
  const placement = variant === 'sheet' ? 'companion-sheet' : 'companion-card';
  const live = plan ? 1 : 0;
  const sig = plan ? plan.sig : '';
  // Open: the clarity event once per anchor per order (C9); onMount on every
  // mount (the sheet re-registers itself) with "near the fold" for the
  // welcome-card hold (C6).
  useEffect(() => {
    if (!live) return;
    if (hostOnce('opened', variant + '|' + anchorId)) clarityEvent('host_open_' + variant);
    if (onMount) {
      const el = rootRef.current,
        h = window.innerHeight,
        t = el ? el.getBoundingClientRect().top : 0;
      onMount(anchorId, t > -h && t < 2 * h);
    }
    // Card add swapped the Add button for the stepper (focus fell to <body>):
    // land on the step so a screen reader announces the region.
    const r = rootRef.current,
      a = document.activeElement;
    if (variant === 'card' && r && (!a || a === document.body)) try {
      r.tabIndex = -1;
      r.focus({
        preventScroll: true
      });
    } catch (e) {}
  }, [live]); // eslint-disable-line react-hooks/exhaustive-deps
  // 'shown': a row counts once its centre is inside the visible band and no
  // layer covers it. Capture-phase scroll catches the sheet's inner scroller.
  // No IntersectionObserver (unreliable here, see PairingChips). Listeners
  // detach as soon as every offered row is marked (C13).
  // v3: an Extra-cuts card [data-host-cut] also needs its horizontal centre
  // inside the scroller's visible box (the capture-phase scroll listener sees
  // the scroller's own scroll events, so a swipe re-checks).
  useEffect(() => {
    const root = rootRef.current;
    if (!sig || covered || !root) return;
    const ids = [],
      els = () => root.querySelectorAll('[data-host-row],[data-host-cut]');
    els().forEach(el => ids.push(...(el.getAttribute('data-host-row') || el.getAttribute('data-host-cut')).split(' ')));
    const all = () => {
      const sh = hostLoad().shown;
      return ids.every(id => sh.indexOf(placement + '|' + id) >= 0);
    };
    if (all()) return;
    let raf = 0,
      off = null;
    const check = () => {
      raf = 0;
      if (coveredRef.current || !root.getClientRects().length) return;
      const b = hostBand(variant, root);
      if (!b) return;
      // Fixer r1: the step is out of the band → none of its rows/cards can be;
      // skip the per-element rect reads (the listener can live long in v3).
      const rr = root.getBoundingClientRect();
      if (rr.bottom <= b.top || rr.top >= b.bottom) return;
      const sc = root.querySelector('.host-cuts'),
        x = sc ? sc.getBoundingClientRect() : null;
      els().forEach(el => {
        const r = el.getBoundingClientRect(),
          c = (r.top + r.bottom) / 2,
          cut = el.getAttribute('data-host-cut'),
          cx = (r.left + r.right) / 2;
        if (c > b.top && c < b.bottom && (!cut || x && cx > x.left && cx < x.right)) (cut || el.getAttribute('data-host-row')).split(' ').forEach(id => hostMarkShown(placement, id, variant, anchorId));
      });
      if (all() && off) {
        off();
        off = null;
      }
    };
    off = hostListen(['scroll', 'resize'], () => {
      if (!raf) raf = requestAnimationFrame(check);
    });
    const t = setTimeout(check, 480);
    return () => {
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
      if (off) off();
    };
  }, [variant, covered, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  // The one bounded reveal (C7, card only): evaluated once per anchor per
  // order; glides ≤240px DOWN only when the step's head is hidden under the
  // cart bar / below the fold but within a viewport of it, the customer has
  // not touched or scrolled, no layer is open and motion is allowed.
  useEffect(() => {
    if (!live || variant !== 'card' || !HOST_REVEAL || !hostOnce('revealed', anchorId)) return;
    const y0 = window.scrollY;
    let moved = false;
    const off = hostListen(['touchstart', 'wheel'], () => {
      moved = true;
    });
    const t = setTimeout(() => {
      off();
      const el = rootRef.current;
      if (!el || moved || coveredRef.current || hostReduced() || Math.abs(window.scrollY - y0) > 4 || document.body.style.overflow === 'hidden') return;
      // The head = down to the set button's real bottom (else the first row).
      const lim = hostBarTop() - 12,
        hb = el.querySelector('.host-bundle,.host-row');
      const top = el.getBoundingClientRect().top,
        need = (hb ? hb.getBoundingClientRect().bottom : top + 96) - lim;
      if (need > 0 && top < lim + window.innerHeight) {
        glideTo(window.scrollY + Math.min(need, HOST_REVEAL_MAX));
        clarityEvent('host_reveal');
      }
    }, 420);
    return () => {
      clearTimeout(t);
      off();
    };
  }, [live]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!plan) return null;
  // Focus: a tap swaps pill ⇄ stepper (and the card step unmounts on resolve),
  // so focus left on <body> goes back to the touched row / the PlatterCard.
  const resolve = () => {
    const r = rootRef.current,
      pc = r && r.contains(document.activeElement) && r.previousElementSibling;
    if (onResolve) onResolve(engaged ? 'set' : 'none');
    if (pc) try {
      pc.tabIndex = -1;
      pc.focus({
        preventScroll: true
      });
    } catch (e) {}
  };
  const set = (lines, how) => {
    const id = lines[0] && lines[0].id;
    // Fixer r1 backstop: a cut card the customer just tapped was on screen, so
    // its 'shown' always precedes its 'accepted' (idempotent per order).
    if (how === 'cut' && id) hostMarkShown(placement, id, variant, anchorId);
    if (onSetLines) onSetLines(lines, how);
    requestAnimationFrame(() => {
      const r = rootRef.current,
        a = document.activeElement,
        row = r && (!a || a === document.body) && r.querySelector('[data-host-row~="' + id + '"],[data-host-cut="' + id + '"]');
      // A cut card's add → its stepper's "+" (the next press adds again, never removes).
      const bt = row && (row.hasAttribute('data-host-cut') && row.querySelector('.stepper button:last-child:not(:disabled)') || row.querySelector('button:not(:disabled)'));
      if (bt) bt.focus({
        preventScroll: true
      });
    });
  };
  // Clarity host_cuts_scroll once per step (per anchor + variant, this order).
  // Fixer r2: only after a customer gesture ON the scroller (pointer / wheel),
  // so Tab-focus scroll-into-view and snap re-settling on resize never count.
  // A peek-card tap's reveal scrollBy does count, on purpose: the customer
  // tapped the scroller to see the next card.
  const cutsGest = e => {
    const t = e.currentTarget;
    if (t && t.dataset) t.dataset.g = '1';
  };
  const cutsScroll = e => {
    const t = e.currentTarget;
    if (!t || !t.dataset || t.dataset.s || !t.dataset.g) return;
    t.dataset.s = '1';
    if (hostOnce('opened', 'cuts|' + variant + '|' + anchorId)) clarityEvent('host_cuts_scroll');
  };
  // v2 order: title / sub / the SET (card only — the sheet's sticky foot
  // carries it) / "or choose yourself" / compact rows / trust + footer.
  // v3: essentials (soup / drink / bread | omelette) then the Extra-cuts scroller.
  const b = plan.bundle,
    card = variant === 'card',
    open = b && !b.done;
  const bm = open ? tr(L, plan.n > 1 ? 'host.bundleN' : 'host.bundle1') : '';
  const bs = bm ? hostBundleSub(L, plan) : '';
  const [bAed, bK] = open ? hostSetPx(plan) : [0, null];
  const trust = mode === 'delivery' ? /*#__PURE__*/React.createElement("div", {
    className: 'host-trust ' + P
  }, tr(L, 'host.trust')) : null;
  const cuts = plan.cuts.length ? 'hc-' + variant + '-' + anchorId : ''; // the scroller's label id
  return /*#__PURE__*/React.createElement("section", {
    ref: rootRef,
    className: 'host-step host-step--' + variant,
    role: "region",
    "aria-label": tr(L, 'host.a11y'),
    onPointerDown: onPing
  }, card ? /*#__PURE__*/React.createElement("button", {
    className: "host-x",
    "aria-label": tr(L, 'host.none'),
    onClick: resolve
  }, "\u2715") : null, /*#__PURE__*/React.createElement("div", {
    className: 'host-title ' + P
  }, tr(L, 'host.title').replace('{for}', hostFor(L, plan.n))), plan.rows.some(r => r.role === 'broth') ? /*#__PURE__*/React.createElement("div", {
    className: 'host-sub ' + P
  }, tr(L, 'host.sub')) : null, !b ? null : b.done ? /*#__PURE__*/React.createElement("div", {
    className: 'host-bundle is-done ' + P
  }, tr(L, 'host.bundleDone')) : !card ? null : /*#__PURE__*/React.createElement("button", {
    className: 'host-bundle ' + P,
    onClick: () => set(b.lines, 'bundle'),
    "aria-label": hostA11y(L, bm, bs, hostPxT(L, bAed, bK))
  }, /*#__PURE__*/React.createElement("span", {
    className: "hb-l"
  }, /*#__PURE__*/React.createElement("b", null, bm), /*#__PURE__*/React.createElement("span", {
    className: "hb-sub"
  }, bs)), hostPx(L, bAed, bK, 'hb-p')), open ? /*#__PURE__*/React.createElement("div", {
    className: 'host-or ' + P
  }, tr(L, 'host.orChoose')) : null, /*#__PURE__*/React.createElement("ul", {
    className: "host-rows"
  }, plan.rows.map(r => hostRowEl(r, plan, L, P, set))), cuts ? /*#__PURE__*/React.createElement("div", {
    className: 'host-lbl ' + P,
    id: cuts
  }, tr(L, 'host.cutsTitle')) : null, cuts ? /*#__PURE__*/React.createElement("ul", {
    className: "host-cuts",
    "aria-labelledby": cuts,
    onPointerDown: cutsGest,
    onWheel: cutsGest,
    onScroll: cutsScroll
  }, plan.cuts.map((o, i) => hostCutEl(o, i, L, P, set))) : null, card || trust ? /*#__PURE__*/React.createElement("div", {
    className: "host-end"
  }, trust, card ? /*#__PURE__*/React.createElement("button", {
    className: 'host-btn ' + P + (engaged ? ' is-done' : ''),
    onClick: resolve
  }, tr(L, engaged ? 'host.done' : 'host.none')) : null) : null);
}

// S4: the settled line under the card once the anchor resolved 'set' and a
// step row item is still in the cart (C5: no head-count). Static — no beacons.
function TableSummary({
  anchorId,
  cart,
  items,
  mode,
  onChange
}) {
  const L = useLang();
  const P = SCR(L);
  const plan = hostPlanSafe(cart, items, mode, anchorId, L);
  // A line the step added (src 'companion', C12) must still be on the order.
  if (!plan || !hostIds(plan).some(o => cart.some(l => l.id === o.id && l.qty > 0 && l.src === 'companion'))) return null;
  const it = (items || []).find(i => i.id === anchorId);
  return /*#__PURE__*/React.createElement("div", {
    className: 'host-summary ' + P,
    role: "status"
  }, /*#__PURE__*/React.createElement("span", {
    className: "hs-seal",
    "aria-hidden": "true"
  }, "\u2713"), /*#__PURE__*/React.createElement("span", null, tr(L, 'host.set').replace(/^✓\s*/, '')), /*#__PURE__*/React.createElement("button", {
    className: 'hs-change ' + P,
    onClick: onChange,
    "aria-label": it ? tr(L, 'host.change') + ' — ' + nm(L, it) : undefined
  }, tr(L, 'host.change')));
}
// ═══ END HOST AT THE DISH ═══

// ── Phase 3 (L5) occasions ──────────────────────────────────────────────────
// index.astro injects window.__OCCASIONS__ = {config, active} (src/lib/
// occasions.ts decides `active` in Dubai time). ?occasion_preview=<kind>
// previews the card (display only; the URL param is tidied away on mount).
const OCC_KINDS = ['ramadan', 'thursday', 'nowruz', 'eid'];
const OCC_PREVIEW = (() => {
  try {
    const v = new URL(window.location.href).searchParams.get('occasion_preview');
    return OCC_KINDS.indexOf(v) >= 0 ? v : null;
  } catch (e) {
    return null;
  }
})();
const occasionData = () => {
  try {
    return window.__OCCASIONS__ || null;
  } catch (e) {
    return null;
  }
};
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
// The suhoor window from config (placeholder hours until the owner confirms).
const suhoorWindow = () => {
  const r = ((occasionData() || {}).config || {}).ramadan || {};
  return {
    kind: 'suhoor',
    from: HHMM_RE.test(r.suhoorFrom) ? r.suhoorFrom : '01:30',
    to: HHMM_RE.test(r.suhoorTo) ? r.suhoorTo : '04:00'
  };
};
// A "HH:MM" slot inside [from, to] (a window may cross midnight).
const inTimeWindow = (t, from, to) => from <= to ? t >= from && t <= to : t >= from || t <= to;
// Fill {key} placeholders with React nodes (LTR-isolated numbers etc.).
const fillNodes = (s, map) => String(s).split(/(\{\w+\})/).map((p, i) => {
  const m = /^\{(\w+)\}$/.exec(p);
  return m && map[m[1]] != null ? /*#__PURE__*/React.createElement(React.Fragment, {
    key: i
  }, map[m[1]]) : p;
});

// Remember the customer's delivery area between visits — but only keep it if it
// still maps to a known area (the fee table may change between deploys).
const AREA_KEY = "shaunfood-delivery-area";
const loadStoredArea = () => {
  try {
    const raw = localStorage.getItem(AREA_KEY);
    return raw && findArea(raw) ? raw : "";
  } catch {
    return "";
  }
};

// Welcome popup — a letterpress "invitation" announcing the intro loyalty
// ladder as three ascending stamps (1st 10% → 2nd 15% → 3rd 20%). Brand tokens
// only (paper/ink/seal/gold + IM Fell / Cormorant); shown after a 15s dwell.
// ── "Eating for" platter helper (K7) ────────────────────────────────────────
// One slim strip above the platter cards. A head-count chip outlines the
// platter(s) that fit (App scrolls the first into view); 7+ proposes the
// fewest platters (then the cheapest) covering the head-count, with an "Add
// all" that goes through the normal addItem path. "New to kalle pache?" opens
// the Single Dish sheet with a one-line note. NOT an upsell touch: it never
// calls notePush/noteSeen and adds nothing flagged is_upsell. Not a pop-up.
const EAT_CHIPS = [['1', 1, 1], ['2', 2, 2], ['3-4', 3, 4], ['5-6', 5, 6], ['7+', 7, 99]];
const NEWCOMER_SLUG = 'dish-1';
// Platters covering >= n people. Default (7+): the EXACT fit first (fewest spare
// servings), then fewest platters, then lowest total price — 10 people get
// Khan's + Full + Single (feeds 10), not 2x Khan's (feeds 12, AED 150 more). cheapest=true (the 1-6 fallback when the matching platter is
// sold out): lowest total price, ties -> fewest — never a 6-person platter
// for three people. Unbounded knapsack over head-count; `avail` = orderable
// platters with a known size.
function platterCombo(avail, n, cheapest) {
  if (!avail.length || !(n > 0)) return null;
  const better = cheapest ? (a, b) => !b || a.price < b.price || a.price === b.price && a.count < b.count : (a, b) => !b || a.cover < b.cover || a.cover === b.cover && (a.count < b.count || a.count === b.count && a.price < b.price);
  const dp = [{
    cover: 0,
    count: 0,
    price: 0,
    ids: []
  }];
  for (let k = 1; k <= n; k++) {
    let best = null;
    for (const x of avail) {
      const prev = dp[Math.max(0, k - x.serves)];
      if (!prev) continue;
      const cand = {
        cover: prev.cover + x.serves,
        count: prev.count + 1,
        price: prev.price + x.item.price,
        ids: prev.ids.concat(x.item.id)
      };
      if (better(cand, best)) best = cand;
    }
    dp[k] = best;
  }
  return dp[n];
}
const peopleText = (L, n) => {
  if (L === 'ar') return n === 1 ? 'شخص واحد' : n === 2 ? 'شخصين' : n <= 10 ? n + ' أشخاص' : n + ' شخصاً';
  if (L === 'fa') return numLocal(L, n) + ' نفر';
  if (L === 'tr') return n + ' kişi'; // singular after a numeral
  return n === 1 ? '1 person' : n + ' people';
};
const eatChipText = (L, c) => {
  const d = n => numLocal(L, n);
  if (c[0] === '7+') return d(7) + '+';
  return c[1] === c[2] ? d(c[1]) : d(c[1]) + '–' + d(c[2]);
};
function EatingFor({
  platters,
  onFit,
  onAddAll,
  onNewcomer,
  hasNewcomer
}) {
  const L = useLang();
  const P = SCR(L);
  const [pick, setPick] = useState(null);
  const [many, setMany] = useState(7);
  const avail = (platters || []).filter(i => !i.soldOut).map(i => ({
    item: i,
    serves: servesOf(i) || 0
  })).filter(x => x.serves > 0);
  const chip = EAT_CHIPS.find(c => c[0] === pick) || null;
  const fitsOf = c => c[0] === '7+' ? [] : avail.filter(x => x.serves >= c[1] && x.serves <= c[2]);
  const fits = chip ? fitsOf(chip) : [];
  // 7+, or a range nothing on today's menu matches exactly -> a combination.
  const comboN = (c, m) => c[0] === '7+' ? m : fitsOf(c).length ? 0 : c[1];
  const comboFor = chip ? comboN(chip, many) : 0;
  const combo = comboFor ? platterCombo(avail, comboFor, chip[0] !== '7+') : null;
  const report = (c, m, scroll) => {
    const f = fitsOf(c);
    if (f.length) {
      onFit(f.map(x => x.item.id), scroll);
      return;
    }
    const cb = platterCombo(avail, comboN(c, m), c[0] !== '7+');
    onFit(cb ? Array.from(new Set(cb.ids)) : null, false);
  };
  // A platter going off (or back on) live while a chip is picked: re-outline.
  const availKey = avail.map(x => x.item.id).join(',');
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (chip) report(chip, many, false);
  }, [availKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = c => {
    if (pick === c[0]) {
      setPick(null);
      onFit(null, false);
      return;
    }
    setPick(c[0]);
    clarityEvent('eat_for_' + c[0]);
    report(c, many, true);
  };
  const bump = dlt => {
    const n = Math.max(7, Math.min(30, many + dlt));
    setMany(n);
    report(EAT_CHIPS[4], n, false);
  };
  const comboLine = combo && combo.ids.length ? (() => {
    const counts = [];
    combo.ids.forEach(id => {
      const e = counts.find(c => c.id === id);
      if (e) e.q++;else counts.push({
        id,
        q: 1
      });
    });
    const byId = id => avail.find(x => x.item.id === id).item;
    return {
      list: counts.map(c => numLocal(L, c.q) + '× ' + nm(L, byId(c.id))).join(' + '),
      items: combo.ids.map(byId)
    };
  })() : null;
  return /*#__PURE__*/React.createElement("div", {
    className: "eat-for"
  }, /*#__PURE__*/React.createElement("div", {
    className: "eat-for-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "eat-for-lbl " + P
  }, tr(L, 'eat.for')), EAT_CHIPS.map(c => /*#__PURE__*/React.createElement("button", {
    key: c[0],
    type: "button",
    "aria-pressed": pick === c[0],
    className: "eat-chip" + (pick === c[0] ? " on" : ""),
    onClick: () => choose(c)
  }, /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr"
  }, eatChipText(L, c)))), hasNewcomer && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "eat-chip eat-chip--new " + P,
    onClick: onNewcomer
  }, tr(L, 'eat.new'))), /*#__PURE__*/React.createElement("div", {
    className: "eat-for-out " + P,
    role: "status",
    "aria-live": "polite"
  }, chip && fits.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "eat-fit"
  }, tr(L, 'eat.fit').replace('{x}', fits.map(x => nm(L, x.item)).join(' · '))), chip && comboLine && /*#__PURE__*/React.createElement("div", {
    className: "eat-combo"
  }, chip[0] === '7+' && /*#__PURE__*/React.createElement("span", {
    className: "eat-step"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => bump(-1),
    disabled: many <= 7,
    "aria-label": tr(L, 'eat.fewer')
  }, "\u2212"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => bump(+1),
    disabled: many >= 30,
    "aria-label": tr(L, 'eat.more')
  }, "+")), /*#__PURE__*/React.createElement("span", {
    className: "eat-combo-txt"
  }, L === 'ar' && comboFor <= 2 ? (comboFor === 1 ? 'لشخص واحد' : 'لشخصين') + ': ' + comboLine.list // Arabic joins لـ to the word: لشخص / لشخصين, not «لعدد شخص»
  : tr(L, 'eat.combo').replace('{n}', peopleText(L, comboFor)).replace('{list}', comboLine.list), ' · ', /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, "AED ", combo.price)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "eat-add " + P,
    onClick: () => onAddAll(comboLine.items)
  }, tr(L, 'eat.addAll'))), chip && chip[0] === '7+' && /*#__PURE__*/React.createElement("a", {
    className: "eat-gather " + P,
    href: "/gatherings",
    onClick: () => clarityEvent('eat_for_gatherings')
  }, tr(L, 'eat.gather'))));
}
function WelcomePopup({
  open,
  onClose,
  onLang
}) {
  const L = useLang();
  const P = SCR(L),
    A = SCR(ACCENT[L]);
  const cardRef = useDialog(!!open, onClose);
  useEffect(() => {
    if (open) clarityEvent('welcome_open');
  }, [open]);
  if (!open) return null;
  const ladder = typeof window !== 'undefined' && window.__LOYALTY__ && window.__LOYALTY__.introLadder || [10, 15, 20];
  const fill = s => s.replace('{a}', numLocal(L, ladder[0])).replace('{b}', numLocal(L, ladder[1])).replace('{c}', numLocal(L, ladder[2]));
  return /*#__PURE__*/React.createElement("div", {
    className: "modal-back open",
    onClick: onClose,
    style: {
      zIndex: 1200
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal",
    ref: cardRef,
    tabIndex: -1,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": "welcome-title",
    onClick: e => e.stopPropagation(),
    style: {
      maxWidth: 430,
      padding: 0,
      background: 'var(--paper-1)',
      border: '1px solid var(--paper-edge)',
      borderRadius: 'var(--r-2)',
      boxShadow: 'var(--shadow-4)'
    }
  }, onLang && /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: 14
    }
  }, /*#__PURE__*/React.createElement(LangSwitcher, {
    lang: L,
    onPick: onLang,
    compact: true
  })), /*#__PURE__*/React.createElement("button", {
    className: "modal-x",
    onClick: onClose,
    "aria-label": tr(L, 'ok.close')
  }, "\u2715"), /*#__PURE__*/React.createElement("div", {
    style: {
      margin: 14,
      border: '1px solid var(--rule-faint)',
      borderRadius: 'var(--r-1)',
      padding: '24px 20px 20px',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      color: 'var(--gold)',
      letterSpacing: '.35em',
      fontSize: 13,
      marginBottom: 12
    }
  }, "\u2726 \uD83D\uDC11 \u2726"), /*#__PURE__*/React.createElement("h3", {
    id: "welcome-title",
    className: P,
    style: {
      margin: '0 0 3px',
      fontFamily: '"IM Fell DW Pica SC","Cormorant Garamond",serif',
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.2,
      color: 'var(--ink)',
      letterSpacing: '.02em'
    }
  }, tr(L, 'loy.welcomeTitle')), /*#__PURE__*/React.createElement("div", {
    className: A,
    dir: "auto",
    style: {
      fontSize: '.9rem',
      color: 'var(--ink-faint)',
      marginBottom: 20
    }
  }, tra(L, 'loy.welcomeTitle')), /*#__PURE__*/React.createElement("div", {
    style: {
      width: 126,
      height: 126,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: '2px solid var(--gold)',
      background: 'rgba(160,123,47,.10)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: '"Cormorant Garamond",serif',
      fontWeight: 600,
      fontSize: 46,
      lineHeight: 1,
      color: 'var(--seal)'
    }
  }, L === 'tr' ? '%' : null, ladder[0], L === 'tr' ? null : '%'), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: 11,
      letterSpacing: '.2em',
      textTransform: 'uppercase',
      color: 'var(--ink-soft)',
      marginTop: 2
    }
  }, tr(L, 'loy.offWord'))), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      marginTop: 11,
      fontSize: 11,
      letterSpacing: '.16em',
      textTransform: 'uppercase',
      color: 'var(--ink-soft)'
    }
  }, tr(L, 'loy.firstOrderCap')), /*#__PURE__*/React.createElement("div", {
    className: A,
    dir: "auto",
    style: {
      fontSize: '.84rem',
      color: 'var(--ink-faint)',
      marginTop: 3
    }
  }, tra(L, 'loy.firstOrderCap')), /*#__PURE__*/React.createElement("div", {
    style: {
      margin: '17px 0 20px',
      paddingTop: 14,
      borderTop: '1px dashed var(--rule-faint)'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: '.87rem',
      color: 'var(--ink-faint)',
      lineHeight: 1.5
    }
  }, fill(tr(L, 'loy.growsTo'))), /*#__PURE__*/React.createElement("div", {
    className: A,
    dir: "auto",
    style: {
      fontSize: '.81rem',
      color: 'var(--ink-faint)',
      opacity: .85,
      lineHeight: 1.7,
      marginTop: 3
    }
  }, fill(tra(L, 'loy.growsTo'))), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: '.78rem',
      color: 'var(--ink-faint)',
      marginTop: 8
    }
  }, tr(L, 'co.noStack'))), /*#__PURE__*/React.createElement("button", {
    className: P,
    onClick: () => {
      clarityEvent('welcome_claim');
      onClose();
      const el = document.getElementById('order');
      if (el) glideTo(el.getBoundingClientRect().top + window.scrollY - 70);
    },
    style: {
      width: '100%',
      padding: '13px',
      background: 'var(--ink)',
      color: 'var(--paper-1)',
      border: 'none',
      borderRadius: 'var(--r-2)',
      fontFamily: '"IM Fell DW Pica SC",serif',
      fontSize: '.98rem',
      letterSpacing: '.14em',
      textTransform: 'uppercase',
      cursor: 'pointer'
    }
  }, fill(tr(L, 'loy.welcomeCta'))))));
}

// Slim top strip shown only inside in-app browsers. Dismissable per session;
// the checkout pay-row repeats the nudge at the moment it matters most.
function InAppBar({
  cart,
  lang
}) {
  const L = lang;
  const [show, setShow] = useState(() => {
    if (!isInAppBrowser()) return false;
    try {
      return sessionStorage.getItem('sf-inapp-hide') !== '1';
    } catch (e) {
      return true;
    }
  });
  const [hint, setHint] = useState(false);
  useEffect(() => {
    if (isInAppBrowser()) clarityEvent('inapp_detected');
  }, []);
  if (!show) return null;
  const go = () => {
    clarityEvent('inapp_open_browser');
    try {
      window.location.href = openInBrowserTarget(cart, L);
    } catch (e) {/* ignore */}
    // Still here after 1.5s = the webview refused the scheme → show the manual route.
    setTimeout(() => {
      if (document.visibilityState === 'visible') setHint(true);
    }, 1500);
  };
  const hide = () => {
    setShow(false);
    try {
      sessionStorage.setItem('sf-inapp-hide', '1');
    } catch (e) {/* ignore */}
  };
  return /*#__PURE__*/React.createElement("div", {
    dir: isLatin(L) ? 'ltr' : 'rtl',
    role: "region",
    "aria-label": tr(L, 'inapp.bar'),
    style: {
      background: 'var(--ink)',
      color: 'var(--paper-1)',
      fontSize: 13,
      lineHeight: 1.35,
      padding: '8px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      position: 'relative',
      zIndex: 60
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      flex: 1
    }
  }, hint ? tr(L, 'inapp.hint') : tr(L, 'inapp.bar')), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: go,
    style: {
      background: 'var(--seal)',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      padding: '7px 12px',
      fontWeight: 700,
      fontSize: 13,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      minHeight: 36
    }
  }, tr(L, 'inapp.open')), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: hide,
    "aria-label": tr(L, 'ok.close'),
    style: {
      background: 'none',
      border: 'none',
      color: 'var(--paper-1)',
      opacity: .7,
      fontSize: 18,
      cursor: 'pointer',
      padding: '4px 6px',
      minWidth: 32
    }
  }, "\u2715"));
}

// Two one-tap front doors under the hero: repeat your last order (the intro
// ladder's 15%/20% rung is automatic per phone — we only remind), and pre-order
// for a time (scheduled orders average 2× the ASAP basket; kalle pache is a
// morning meal and demand peaks Fri–Sun, so the copy leans into both).
// Welcome-reward stamp card (C11). paidCount = this phone's PAID web orders,
// derived from the ladder rung loyalty-preview returned (null = unknown → no
// stamps). Used rungs are stamped ✓, the NEXT one is the big stamp with its
// label, later ones stay small. After all three: one quiet thank-you line.
const INTRO_LADDER_UI = (() => {
  try {
    const l = window.__LOYALTY__ && window.__LOYALTY__.introLadder;
    return Array.isArray(l) && l.length >= 3 ? l.map(Number) : [10, 15, 20];
  } catch (e) {
    return [10, 15, 20];
  }
})();
function StampRow({
  L,
  paidCount
}) {
  if (paidCount == null) return null;
  const P = SCR(L);
  const pct = n => L === 'tr' ? '%' + numLocal(L, n) : numLocal(L, n) + (L === 'en' ? '%' : '٪'); // Arabic uses ٪ too, matching /success, /orders, /me; Turkish is sign-first (%10)
  if (paidCount >= 3) return /*#__PURE__*/React.createElement("div", {
    className: "stamp-done " + P
  }, "\u2713 ", tr(L, 'stamp.done'));
  return /*#__PURE__*/React.createElement("div", {
    className: "stamp-row",
    role: "img",
    "aria-label": tr(L, 'stamp.aria').replace('{k}', numLocal(L, paidCount))
  }, INTRO_LADDER_UI.slice(0, 3).map((p, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    className: "stamp-dot" + (i < paidCount ? " is-used" : i === paidCount ? " is-next" : ""),
    "aria-hidden": "true"
  }, i < paidCount ? '✓' : /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, pct(p)))), /*#__PURE__*/React.createElement("span", {
    className: "stamp-next " + P,
    "aria-hidden": "true"
  }, tr(L, 'stamp.next').replace('{n}', numLocal(L, INTRO_LADDER_UI[paidCount]))));
}

// Phase 3 (L5): ONE slim card for the active occasion (never a pop-up).
// Ramadan / Thursday open the Schedule door with a slot preference; Nowruz /
// Eid link to /gatherings. Nothing renders when no occasion is active.
function OccasionCard({
  L,
  onPreorder
}) {
  const occ = occasionData();
  const kind = OCC_PREVIEW || (occ && OCC_KINDS.indexOf(occ.active) >= 0 ? occ.active : null);
  if (!kind) return null;
  // OPEN (STYLE-GUIDE i.13 / j.14): the Nevruz card is hidden for Turkish until the owner decides; Sahur + Bayram stay.
  if (L === 'tr' && kind === 'nowruz') return null;
  const P = SCR(L);
  const num = s => /*#__PURE__*/React.createElement("bdi", {
    dir: "ltr",
    className: "ltr-num"
  }, numLocal(L, s));
  let text, action;
  if (kind === 'ramadan') {
    const w = suhoorWindow();
    text = fillNodes(tr(L, 'occ.ramadanT'), {
      from: num(w.from),
      to: num(w.to)
    });
    action = /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "occ-btn " + P,
      onClick: () => {
        clarityEvent('occasion_ramadan');
        onPreorder(w);
      }
    }, tr(L, 'occ.ramadanB'));
  } else if (kind === 'thursday') {
    text = tr(L, 'occ.thuT');
    action = /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "occ-btn " + P,
      onClick: () => {
        clarityEvent('occasion_thursday');
        onPreorder({
          kind: 'friday'
        });
      }
    }, tr(L, 'occ.thuB'));
  } else {
    text = fillNodes(tr(L, kind === 'nowruz' ? 'occ.nowruzT' : 'occ.eidT'), {
      range: num('10–30')
    });
    action = /*#__PURE__*/React.createElement("a", {
      className: "occ-btn " + P,
      href: "/gatherings",
      onClick: () => clarityEvent('occasion_' + kind)
    }, tr(L, 'occ.gatherB'));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "occ-card occ-card--" + kind
  }, /*#__PURE__*/React.createElement("span", {
    className: "occ-txt " + P
  }, text), action);
}
function QuickCards({
  lang,
  introPct,
  paidCount,
  onReorder,
  onPreorder,
  allItems,
  mode
}) {
  const L = lang;
  const P = SCR(L);
  // Only advertise a reorder that can actually happen: drop lines that are off
  // the menu, sold out today, or dine-in-only outside dine-in mode (the same
  // filter reorderLast applies), and hide the card when nothing survives.
  const last = (() => {
    try {
      const j = JSON.parse(localStorage.getItem('sf-last-cart') || 'null');
      const lines = (j && Array.isArray(j.lines) ? j.lines : []).filter(l => l && typeof l.id === 'string' && (allItems || []).some(i => i.id === l.id && !i.soldOut) && !(mode !== 'dinein' && DINE_IN_ONLY.has(l.id)));
      return lines.length ? {
        ...j,
        lines
      } : null;
    } catch (e) {
      return null;
    }
  })();
  const n = last ? last.lines.reduce((s, l) => s + (Number(l.qty) || 0), 0) : 0;
  const card = {
    border: '1px solid var(--paper-edge, #c9b79a)',
    background: 'var(--paper-1, #fffaf0)',
    borderRadius: 10,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: '1 1 300px',
    minWidth: 0
  };
  const btn = {
    background: 'var(--ink, #221408)',
    color: 'var(--paper-1, #fffaf0)',
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    minHeight: 44
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "quick-cards",
    dir: isLatin(L) ? 'ltr' : 'rtl',
    style: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 12,
      margin: '14px 0 4px'
    }
  }, /*#__PURE__*/React.createElement(OccasionCard, {
    L: L,
    onPreorder: onPreorder
  }), last && /*#__PURE__*/React.createElement("div", {
    style: card
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontWeight: 700
    }
  }, tr(L, 'quick.reorderT')), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: 13,
      opacity: .82
    }
  }, tr(L, 'quick.reorderS').replace('{items}', itemsText(L, n))), /*#__PURE__*/React.createElement(StampRow, {
    L: L,
    paidCount: paidCount
  })), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    style: btn,
    onClick: onReorder
  }, tr(L, 'quick.reorderB'))), /*#__PURE__*/React.createElement("div", {
    style: card
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontWeight: 700
    }
  }, tr(L, 'quick.preT')), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: 13,
      opacity: .82
    }
  }, tr(L, 'quick.preS'))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    style: btn,
    onClick: onPreorder
  }, tr(L, 'quick.preB'))), /*#__PURE__*/React.createElement("div", {
    style: card
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontWeight: 700
    }
  }, tr(L, 'quick.breadT')), /*#__PURE__*/React.createElement("div", {
    className: P,
    style: {
      fontSize: 13,
      opacity: .82
    }
  }, tr(L, 'quick.breadS'))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: P,
    style: btn,
    onClick: () => {
      clarityEvent('quick_bread');
      window.location.assign('/bread?entry=quick_card');
    }
  }, tr(L, 'quick.breadB'))));
}
function App() {
  // ── Language ─────────────────────────────────────────────────────────────
  const [lang, setLangState] = useState(detectLang);
  const setLang = l => {
    if (KNOWN_LANGS.indexOf(l) === -1) return;
    setLangState(l);
    persistLang(l);
    applyLangToDocument(l);
  };
  // Freeze ad attribution on ARRIVAL, not at checkout. The click-ID is in the
  // URL of the first page view and nowhere else: a visitor who lands from an
  // ad, leaves, and comes back tomorrow from a bookmark would otherwise submit
  // an order with no trace of the ad that won them. Runs once, costs nothing.
  useEffect(() => {
    try {
      captureAttribution();
    } catch (e) {}
  }, []);

  // Apply direction/lang to <html> on first mount (SSR rendered it LTR/en).
  useEffect(() => {
    applyLangToDocument(lang);
    // Tidy the ?lang= ad parameter out of the address bar once honored.
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('lang')) {
        u.searchParams.delete('lang');
        window.history.replaceState(null, '', u);
      }
    } catch (e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const L = lang;
  const P = SCR(L),
    A = SCR(ACCENT[L]);

  // C12 (Host at the dish): src:'companion' tags written by an EARLIER tab's
  // table step are dropped when this tab holds no step state for the order,
  // so a stale tag can never be credited to a step this order never showed.
  const [cart, setCart] = useState(() => {
    const c = loadStoredCart(),
      s = hostLoad();
    return s.hx || Object.keys(s.done).length ? c : c.map(l => {
      if (!l || !l.src) return l;
      const {
        src,
        ...r
      } = l;
      return r;
    });
  });
  const [mode, setMode] = useState("delivery");
  const [deliveryArea, setDeliveryArea] = useState(loadStoredArea);
  const [activeCat, setActiveCat] = useState(MENU[0].id);
  const [toast, setToast] = useState({
    msg: "",
    fa: "",
    show: false
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // ONE source of truth for Now | Schedule. checkoutWhen mirrors checkout's
  // real whenMode (the modal reports every change up via onWhenSync), so the
  // cart switch never shows "Now" while checkout holds a scheduled slot.
  // Explicit requests (cart switch, pre-order door, reorder, a placed order)
  // go through requestWhen: a nonce the modal applies on its next open.
  // armed=true (pre-order door only) keeps the Phase-1 disarm: closing such a
  // session drops the auto-picked slot so a later "order now" can never
  // silently become a scheduled order. A cart-chosen Schedule is the
  // customer's own choice and survives a close.
  const [checkoutWhen, setCheckoutWhen] = useState("asap");
  const [whenReq, setWhenReq] = useState({
    v: "asap",
    armed: false,
    n: 0,
    pref: null
  });
  // pref (Phase 3 L5, occasion cards only): {kind:'suhoor',from,to} or
  // {kind:'friday'} — the slot Schedule should pre-select instead of the
  // normal default. Anything else (e.g. a click event) is ignored.
  const requestWhen = useCallback((v, armed, pref) => {
    const okPref = pref && (pref.kind === 'suhoor' || pref.kind === 'friday') ? pref : null;
    setCheckoutWhen(v);
    setWhenReq(r => ({
      v,
      armed: !!armed,
      n: r.n + 1,
      pref: okPref
    }));
  }, []);
  // Phase 3 (L2): the /gatherings hand-off flag (see readGatherFlag).
  const [gather, setGather] = useState(readGatherFlag);
  const endGather = useCallback(() => {
    clearGatherFlag();
    setGather(false);
  }, []);
  const cartWhen = useCallback(v => requestWhen(v, false), [requestWhen]);
  // Same rules as the cart sanitisers below: only items on today's menu, not
  // sold out, and no dine-in-only item unless we are in dine-in mode.
  const reorderableLines = lines => (Array.isArray(lines) ? lines : []).filter(l => l && typeof l.id === 'string' && allItems.some(i => i.id === l.id && !i.soldOut) && !(mode !== 'dinein' && DINE_IN_ONLY.has(l.id))).map(l => ({
    id: l.id,
    qty: Math.max(1, Math.min(30, l.qty | 0))
  }));
  const reorderLast = () => {
    try {
      const j = JSON.parse(localStorage.getItem('sf-last-cart') || 'null');
      const lines = reorderableLines(j && j.lines);
      if (!lines.length) return;
      // Merge into whatever is already in the cart (never silently drop a
      // half-built order), then go straight to checkout as the card promises.
      setCart(c => {
        const m = new Map(c.map(l => [l.id, {
          ...l
        }]));
        for (const l of lines) {
          const cur = m.get(l.id);
          m.set(l.id, {
            id: l.id,
            qty: Math.min(30, (cur ? cur.qty : 0) + l.qty)
          });
        }
        return [...m.values()];
      });
      clarityEvent('quick_reorder');
      setToast({
        msg: tr(lang, 'toast.reorder'),
        fa: tra(lang, 'toast.reorder'),
        show: true
      });
      setTimeout(() => setToast(s => ({
        ...s,
        show: false
      })), 3400);
      requestWhen("asap", false);
      setCheckoutOpen(true);
    } catch (e) {/* ignore */}
  };
  const preorderStart = pref => {
    requestWhen("later", true, pref);
    clarityEvent('quick_preorder');
    if (cart.length > 0) {
      setCheckoutOpen(true);
      return;
    }
    // Empty cart: glide to the menu and say why — the time is chosen at checkout.
    scrollTo("order");
    setToast({
      msg: tr(lang, 'toast.schedArmed'),
      fa: tra(lang, 'toast.schedArmed'),
      show: true
    });
    setTimeout(() => setToast(s => ({
      ...s,
      show: false
    })), 3400);
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Recipe-card sheet: the item being looked at closely (null = closed).
  const [sheetItem, setSheetItem] = useState(null);
  // K7: dictionary key of a one-line note for the item sheet (the "New to
  // kalle pache?" door), and the platter ids the "Eating for" strip outlines.
  const [sheetNote, setSheetNote] = useState(null);
  const [platterFit, setPlatterFit] = useState(null);
  // Every close path (✕, backdrop, Back button) drops the note with the sheet.
  useEffect(() => {
    if (!sheetItem) setSheetNote(null);
  }, [sheetItem]);

  // ── Host at the dish: App state (2026-10) ───────────────────────────────
  // hostInline: the platter whose fresh CARD step is open. hostHold: holds the
  // welcome card (C6) — set by the card step's onMount only when the step is
  // near the fold; released by a REAL 700 ms timer after Done/That's all/✕,
  // the 15 s idle cap, the anchor leaving the cart, or no card plan (effect
  // further down, after hostCardOpen exists). hostDone {anchor:'set'|'none'}
  // and hostDeclined (slugs) persist per order in sf-host-v1.
  const [hostInline, setHostInline] = useState(null);
  const [hostHold, setHostHold] = useState(false);
  const [hostPing, setHostPing] = useState(0);
  const [hostEngaged, setHostEngaged] = useState({});
  const [hostDone, setHostDone] = useState(() => hostLoad().done);
  const [hostDeclined, setHostDeclined] = useState(() => new Set(hostLoad().declined));
  // hostRelRef: the pending 700 ms release; hostHeldRef: the anchor that
  // already held once (a remount never re-holds); hostCtxRef: what addItem
  // (stable [mode, lang] callback) needs from the latest render (C8).
  const hostSheetRef = useRef(null),
    hostPrevInline = useRef(null),
    hostRelRef = useRef(0),
    hostHeldRef = useRef(null),
    hostCtxRef = useRef({});
  // (Empty → nothing written: the cart-empty reset's hostClear stays a clear.)
  useEffect(() => {
    if (Object.keys(hostDone).length || hostDeclined.size) hostSave({
      done: hostDone,
      declined: Array.from(hostDeclined)
    });
  }, [hostDone, hostDeclined]);
  useEffect(() => {
    if (!hostHold) return;
    // C6 (spec): released after 15 s with no TAP inside the step (onPing re-arms).
    // Fixer r1: the polish scroll/touch extension was reverted — it was not in
    // V3 and could hold the welcome card indefinitely while the menu scrolled.
    const t = setTimeout(() => setHostHold(false), HOST_HOLD_IDLE_MS);
    return () => clearTimeout(t);
  }, [hostHold, hostPing]);
  useEffect(() => () => clearTimeout(hostRelRef.current), []);
  useEffect(() => {
    if (hostInline && !cart.some(l => l.id === hostInline && l.qty > 0)) {
      setHostInline(null);
      setHostHold(false);
    }
  }, [cart, hostInline]);
  // A card step replaced by another platter's step counts as 'set' when the
  // customer had added from it (and the platter is still on the order).
  useEffect(() => {
    const prev = hostPrevInline.current;
    hostPrevInline.current = hostInline;
    if (prev && prev !== hostInline && hostEngaged[prev] && cart.some(l => l.id === prev)) setHostDone(d => d[prev] ? d : {
      ...d,
      [prev]: 'set'
    });
  }, [hostInline]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sheet close by ✕ / backdrop / Escape / phone Back (useBackLayer nulls
  // sheetItem directly). hostResolve is a const further down — only CALLED
  // here at effect time, after this render has declared it.
  useEffect(() => {
    if (sheetItem) return;
    const s = hostSheetRef.current;
    // (hostCtxRef.on: a step that vanished with the switch/pause is no decline.)
    if (s && s.anchor && s.anchor !== hostInline && hostCtxRef.current.on) hostResolve(s.anchor, null, 'sheet');else hostSheetRef.current = null;
  }, [sheetItem]); // eslint-disable-line react-hooks/exhaustive-deps
  const orderNoRef = useRef(String(Math.floor(Math.random() * 9000) + 1000));

  // Intro-loyalty % used by the CART preview (the cart has no phone yet, so we
  // can't know the exact tier there). A returning visitor's SAVED phone gives
  // their real tier; a first-time visitor is assumed order #1 (first rung).
  // Display-only — /api/orders/create always re-derives the real discount.
  // null = unknown (no saved phone / lookup not back yet) → no stamps shown.
  const [paidCount, setPaidCount] = useState(null);
  const [introPct, setIntroPct] = useState(() => {
    try {
      const l = window.__LOYALTY__ && window.__LOYALTY__.introLadder || [10, 15, 20];
      return l[0] || 0;
    } catch (e) {
      return 0;
    }
  });
  useEffect(() => {
    let saved = "";
    try {
      saved = JSON.parse(localStorage.getItem('shaunfood-customer') || '{}').phone || "";
    } catch (e) {}
    if (!saved) return; // brand-new visitor keeps the first-rung default
    let alive = true;
    fetch("/api/orders/loyalty-preview", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        phone: saved,
        subtotal_aed: 0
      })
    }).then(r => r.ok ? r.json() : null).then(j => {
      if (!alive || !j) return;
      const pct = Number(j.percent) || 0;
      setIntroPct(pct);
      // Stamp card: which rung this phone is on = how many paid web orders
      // it has. Only when the server says the lookup really ran (`known`):
      // percent 0 is ALSO what a rate-limit, a lookup error/timeout or a
      // non-UAE phone returns, so a bare 0 is unknown → no stamps (C11).
      if (j.known !== true) {
        setPaidCount(null);
        return;
      }
      const i = INTRO_LADDER_UI.slice(0, 3).indexOf(pct);
      setPaidCount(i >= 0 ? i : pct === 0 ? 3 : null);
    }).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Welcome popup — first visit only, and only after the visitor has actually
  // spent 15s on the page (never ambush someone the moment they land).
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('sf_welcome_seen')) return;
    } catch (e) {
      return;
    }
    const t = setTimeout(() => setShowWelcome(true), 15000);
    return () => clearTimeout(t);
  }, []);
  // …or on the first add-to-cart, whichever comes first: the 10% persuades
  // best once a price is in view. Slight delay so the cart-bar slide-in that
  // confirms the add is seen before the card appears.
  const hasCart = cart.length > 0;
  useEffect(() => {
    if (!hasCart) return;
    try {
      if (localStorage.getItem('sf_welcome_seen')) return;
    } catch (e) {
      return;
    }
    const t = setTimeout(() => setShowWelcome(true), 1200);
    return () => clearTimeout(t);
  }, [hasCart]);
  const closeWelcome = () => {
    setShowWelcome(false);
    try {
      localStorage.setItem('sf_welcome_seen', '1');
    } catch (e) {}
  };

  // ── Suggestion engine state ─────────────────────────────────────────────
  // lastAddedId: drives the transient InlineSuggestion ribbon. Auto-clears
  //   after INLINE_SUGGEST_MS so the ribbon doesn't linger.
  // dismissedSuggestions: session-only set of slugs the customer has waved
  //   off. NOT persisted to localStorage (they may change their mind tomorrow).
  const INLINE_SUGGEST_MS = 7000;
  const [lastAddedId, setLastAddedId] = useState(null);
  const lastAddedTimerRef = useRef(null);
  // Seed from SFUpsell's sessionStorage list so dismissals survive reloads
  // within the same tab session (mission rule: dismissed stays dismissed).
  const [dismissedSuggestions, setDismissedSuggestions] = useState(() => {
    try {
      return new Set(window.SFUpsell ? window.SFUpsell.dismissedList() : []);
    } catch (e) {
      return new Set();
    }
  });
  // ── ERP-driven suggestions (SFUpsell) ───────────────────────────────────
  // null → no data (endpoint absent/down/empty) → static rules take over.
  // Debounced 300ms behind cart changes; SFUpsell caches ~5 min per cart
  // signature and NEVER rejects, so this can't disturb browsing or checkout.
  // Held as {sig, data} — the cart signature the answer was fetched FOR, next
  // to the answer. That lets `erpReady` be derived during render instead of
  // tracked by a flag: the add-ribbon freezes its chips at mount, so a flag
  // set from an effect is always one commit too late (addItem sets
  // lastAddedId and React re-renders, mounting the ribbon, before any effect
  // runs — measured: a first attempt using a pending flag still logged
  // add|sangak + add|torshi-mix). Mounting early locked the ribbon onto the
  // STATIC fallback, billed a 'shown' beacon and one of the per-order touches
  // to it, and then the real answer landed ~300ms later and the cart strip
  // showed something else — which is why 68% of ribbon impressions were
  // torshi/doogh while the ERP's real picks only reached the cart strip.
  const [erpState, setErpState] = useState({
    sig: null,
    data: null
  });
  const cartSig = cart.map(l => l.id).sort().join(',');
  const erpReady = erpState.sig === cartSig;
  const erpSugg = erpReady ? erpState.data : null;
  useEffect(() => {
    if (!(window.SFUpsell && window.SFUpsell.enabled()) || cart.length === 0) {
      setErpState({
        sig: cartSig,
        data: null
      });
      return;
    }
    let alive = true;
    const settle = v => {
      if (alive) setErpState({
        sig: cartSig,
        data: v
      });
    };
    const t = setTimeout(() => {
      try {
        window.SFUpsell.get(cart.map(l => l.id)).then(s => settle(Array.isArray(s) && s.length ? s : null)).catch(() => settle(null));
      } catch (e) {
        settle(null);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [cartSig]); // eslint-disable-line react-hooks/exhaustive-deps
  // New order lifecycle: when the cart empties, reset the per-order upsell
  // discipline (touch budget, pushed set, shown dedupe).
  useEffect(() => {
    if (cart.length === 0) {
      try {
        if (window.SFUpsell) window.SFUpsell.resetOrder();
      } catch (e) {}
    }
    // …and the table step's per-order state (memory + sf-host-v1).
    if (cart.length === 0) {
      setHostInline(null);
      setHostHold(false);
      setHostEngaged({});
      setHostDone(d => Object.keys(d).length ? {} : d);
      setHostDeclined(s => s.size ? new Set() : s);
      hostHeldRef.current = null;
      hostClear();
    }
    // L2: an emptied cart ends a gathering visit.
    if (cart.length === 0 && gather) endGather();
  }, [cart.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Continuous-menu navigation state ────────────────────────────────────
  // tabsRef: the horizontal sticky tab strip (for centring the active chip).
  // spyPausedUntilRef: timestamp until which the scroll-spy must stay quiet —
  //   set when a tab tap starts a smooth scroll, so the tapped tab stays lit
  //   instead of flickering through every section passed along the way.
  const tabsRef = useRef(null);
  const spyPausedUntilRef = useRef(0);

  // Persist cart on every change
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {}
  }, [cart]);

  // Remember the chosen delivery area for next time.
  useEffect(() => {
    try {
      localStorage.setItem(AREA_KEY, deliveryArea || "");
    } catch {}
  }, [deliveryArea]);

  // One-time cleanup of stale cart lines that are no longer orderable — an item
  // pulled from the menu, or now sold out. Crucially this evicts a lingering
  // `test-1aed` payment-test item: once it's in the cart it deliberately zeroes
  // the delivery fee (and skips the pin) so a 1-AED card test can run — but left
  // behind in a real cart it silently suppresses the delivery charge, which
  // reads as "the delivery fee isn't being added at checkout". MENU is the
  // source of truth for what can be ordered right now.
  useEffect(() => {
    const orderable = new Set(MENU.flatMap(s => s.items).filter(i => !i.soldOut).map(i => i.id));
    setCart(c => {
      const cleaned = c.filter(l => orderable.has(l.id));
      return cleaned.length === c.length ? c : cleaned;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep links. ?item=<slug> (the special bar, shared dish links) glides to that
  // dish and opens its recipe card; ?when=later (the receipt's "order again for
  // later") arms the Schedule door exactly like the QuickCards pre-order card.
  // ?cart= was already folded into the cart by loadStoredCart, so with a cart
  // checkout opens straight on Schedule. Both params are stripped like ?lang=.
  // Unknown or sold-out slug → just land on the menu.
  useEffect(() => {
    let u;
    try {
      u = new URL(window.location.href);
    } catch (e) {
      return;
    }
    const slug = String(u.searchParams.get('item') || '').toLowerCase();
    const later = u.searchParams.get('when') === 'later';
    // L2/L5: ?gather=1 was read into sessionStorage and ?occasion_preview at
    // load — tidy both out of the address bar like ?lang=.
    if (u.searchParams.has('gather') || u.searchParams.has('occasion_preview')) {
      u.searchParams.delete('gather');
      u.searchParams.delete('occasion_preview');
      try {
        window.history.replaceState(null, '', u);
      } catch (e) {}
    }
    if (!u.searchParams.has('item') && !u.searchParams.has('when')) return;
    u.searchParams.delete('item');
    u.searchParams.delete('when');
    try {
      window.history.replaceState(null, '', u);
    } catch (e) {}
    const orderable = MENU.flatMap(s => s.items).filter(i => !i.soldOut);
    if (later) {
      // Judge the SANITISED cart (this closure's `cart` is the first render's,
      // before the mount-time cleanup dropped sold-out lines).
      const ids = new Set(orderable.map(i => i.id));
      if (cart.some(l => ids.has(l.id))) {
        requestWhen("later", true);
        setCheckoutOpen(true);
        clarityEvent('quick_preorder');
        return;
      }
      // Nothing orderable left: the card's empty-cart branch, inline (the
      // stale preorderStart would see the unsanitised cart and open an empty
      // checkout) — armed, glide to the menu, say why.
      requestWhen("later", true);
      clarityEvent('quick_preorder');
      setTimeout(() => {
        scrollTo("order");
        setToast({
          msg: tr(lang, 'toast.schedArmed'),
          fa: tra(lang, 'toast.schedArmed'),
          show: true
        });
        setTimeout(() => setToast(s => ({
          ...s,
          show: false
        })), 3400);
      }, 300);
      if (!slug) return;
    }
    const item = /^[a-z0-9-]{1,40}$/.test(slug) ? orderable.find(i => i.id === slug) : null;
    // Let the menu lay out first, then put the dish under the sticky strip.
    // INSTANT, and BEFORE the sheet opens: the sheet's history entry is pushed
    // a tick later and the depth-0 entry keeps the scroll position of that
    // moment — so every close (✕, backdrop, Add, Back) returns to the dish,
    // not to the hero a smooth glide would still be leaving.
    setTimeout(() => {
      const el = item ? document.querySelector('[data-item="' + item.id + '"]') : null;
      if (el) window.scrollTo(0, Math.max(0, el.getBoundingClientRect().top + window.scrollY - 140));else if (!later) scrollTo("order");
      if (item) setSheetItem(item);
    }, 350);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reorder hand-off: when the customer arrives from /orders "Order again", the
  // cart was already loaded from localStorage (and sanitised above). Confirm it
  // with a toast and pop the cart open so they can see the items + check out.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('reorder') !== '1') return;
      setToast({
        msg: tr(lang, 'toast.reorder'),
        fa: tra(lang, 'toast.reorder'),
        show: true
      });
      setTimeout(() => setToast(s => ({
        ...s,
        show: false
      })), 3400);
      setMobileCartOpen(true);
      if (window.history && window.history.replaceState) window.history.replaceState({}, '', '/');
    } catch (e) {/* no-op */}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching to delivery drops any dine-in-only items already in the cart
  // (e.g. customer added the kettle of tea in dine-in mode, then switched to
  // delivery). Keeps the cart honest with what a rider can actually carry.
  useEffect(() => {
    if (mode !== "delivery") return;
    const blocked = cart.filter(l => DINE_IN_ONLY.has(l.id) && l.qty > 0);
    if (blocked.length === 0) return;
    setCart(c => c.filter(l => !DINE_IN_ONLY.has(l.id)));
    const names = blocked.map(l => {
      const it = allItems.find(i => i.id === l.id);
      return it ? nm(lang, it) : l.id;
    }).join(isLatin(lang) ? ", " : "، ");
    setToast({
      msg: `${names} — ${tr(lang, 'toast.removedDinein')}`,
      fa: tra(lang, 'toast.removedDinein'),
      show: true
    });
    setTimeout(() => setToast(s => ({
      ...s,
      show: false
    })), 2200);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // toggle bottom-bar spacing on body so the fixed bar doesn't cover the footer
  useEffect(() => {
    const count = cart.reduce((s, l) => s + l.qty, 0);
    document.body.classList.toggle("has-cart", count > 0);
  }, [cart]);

  // ── Clarity session tags ──────────────────────────────────────────────
  // Filter dimensions for replays and heatmaps. Without `lang` the heatmap is
  // unreadable: EN/AR/FA share one URL and the RTL layouts are mirrored, so
  // clicks from three different layouts land on one snapshot.
  useEffect(() => {
    clarityTag('lang', lang);
  }, [lang]);
  useEffect(() => {
    clarityTag('mode', mode);
  }, [mode]);
  useEffect(() => {
    clarityTag('area', deliveryArea || 'none');
  }, [deliveryArea]);

  // Marketing funnel: fire begin_checkout each time the checkout modal opens.
  useEffect(() => {
    if (!checkoutOpen) return;
    try {
      const subtotal = cart.reduce((s, l) => {
        const it = allItems.find(i => i.id === l.id);
        return s + (it ? it.price * l.qty : 0);
      }, 0);
      if (window.sfTrack) window.sfTrack('begin_checkout', {
        value: subtotal
      });
      clarityEvent('checkout_open');
    } catch (e) {}
  }, [checkoutOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // lock body scroll when a drawer/sheet is open on mobile
  useEffect(() => {
    const anyOpen = menuOpen || mobileCartOpen || checkoutOpen || confirmOpen;
    document.body.style.overflow = anyOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, mobileCartOpen, checkoutOpen, confirmOpen]);

  // Sticky-nav shadow: deepen the nav shadow once the user scrolls down a bit.
  useEffect(() => {
    const nav = document.querySelector('.nav');
    if (!nav) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (window.scrollY > 8) nav.classList.add('is-scrolled');else nav.classList.remove('is-scrolled');
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cart badge bump animation: re-trigger the keyframe each time cart count changes.
  useEffect(() => {
    const badge = document.querySelector('.nav-cart .badge');
    if (!badge) return;
    badge.classList.remove('bump');
    // Force reflow so the animation re-fires
    void badge.offsetWidth;
    badge.classList.add('bump');
  }, [cart]);

  // Keep the sticky tab strip glued to the nav's real bottom edge. The nav's
  // height varies by breakpoint (its links wrap to two rows on mid-width
  // screens) and it sits 52–60px lower when the order-tracking bar is active,
  // so measure instead of hardcoding — written to a CSS var the strip reads.
  useEffect(() => {
    const nav = document.querySelector('.nav');
    if (!nav) return;
    const apply = () => {
      const stickyTop = parseFloat(getComputedStyle(nav).top) || 0;
      document.documentElement.style.setProperty('--cat-strip-top', stickyTop + nav.offsetHeight - 1 + 'px');
    };
    apply();
    window.addEventListener('resize', apply);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(apply) : null;
    if (mo) mo.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => {
      window.removeEventListener('resize', apply);
      if (mo) mo.disconnect();
    };
  }, []);

  // Scroll-spy — the menu is one continuous scroll (Talabat-style), so as the
  // customer reads down the page we light up whichever category section is
  // currently under the reading line (just below the sticky nav + tab strip).
  useEffect(() => {
    if (search.trim()) return; // category sections are unmounted during search
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (Date.now() < spyPausedUntilRef.current) return;
        const strip = tabsRef.current ? tabsRef.current.parentElement : null;
        const readingLine = (strip ? strip.getBoundingClientRect().bottom : 130) + 24;
        let current = MENU[0].id;
        for (const s of MENU) {
          const el = document.getElementById("cat-" + s.id);
          if (el && el.getBoundingClientRect().top <= readingLine) current = s.id;
        }
        setActiveCat(prev => prev === current ? prev : current);
      });
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [search]); // re-bind when search hides/shows the sections

  // Keep the lit tab visible inside the horizontal strip as the spy moves it.
  // Manual horizontal scrollTo (not scrollIntoView) so the page never jumps.
  useEffect(() => {
    const strip = tabsRef.current;
    if (!strip) return;
    const btn = strip.querySelector(`.cat-tab[data-cat="${activeCat}"]`);
    if (!btn) return;
    const target = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
    glideX(strip, Math.max(0, target));
  }, [activeCat, search]);

  // Edge fades on the tab strip — only show a fade when there really is
  // hidden content past that edge, so the resting strip stays crisp.
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const strip = el.parentElement;
    const update = () => {
      strip.classList.toggle('can-left', el.scrollLeft > 4);
      strip.classList.toggle('can-right', el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [search]);

  // Clean up the lastAddedId timer on unmount.
  useEffect(() => () => {
    if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
  }, []);

  // ── Live availability (ERP) ─────────────────────────────────────────────
  // availVer bumps whenever applyLiveAvail flipped a soldOut flag, so allItems
  // (and every row fed from MENU) picks up the new item objects.
  const [availVer, setAvailVer] = useState(0);
  const [paused, setPaused] = useState(() => {
    try {
      return livePaused(window.__AVAIL__);
    } catch (e) {
      return null;
    }
  });
  // A pause create.ts just reported outranks a status read that may predate it
  // (the site caches the ERP answer ~15 s) — held 20 s, never past its end.
  const pauseHintRef = useRef(null);
  // When the earliest timed 86 ends (ms, 0 = none) — re-applied then (below).
  const [liveWake, setLiveWake] = useState(() => nextLiveUntil());
  const applyAvail = useCallback(list => {
    if (applyLiveAvail(list)) setAvailVer(v => v + 1);
    setLiveWake(nextLiveUntil());
  }, []);
  // Same for dishes create.ts just refused (item_unavailable): a status read
  // cached from before the 86 must not put them straight back on the menu.
  const unavailHintRef = useRef(null);
  const takeAvail = useCallback(st => {
    if (!st) return;
    if (Array.isArray(st.unavailable)) {
      const u = unavailHintRef.current;
      const held = u && Date.now() - u.at < 20000 ? u.slugs.filter(s => !st.unavailable.some(x => x && x.slug === s)) : [];
      applyAvail(held.length ? [...st.unavailable, ...held.map(slug => ({
        slug,
        until: null
      }))] : st.unavailable);
    }
    let p = livePaused(st);
    const h = pauseHintRef.current;
    if (!p && h && Date.now() - h.at < 20000) p = livePaused({
      paused: h
    });
    setPaused(prev => prev && p && prev.until === p.until || !prev && !p ? prev : p);
  }, [applyAvail]);
  const refreshAvail = useCallback(() => {
    try {
      fetch('/api/online-status', {
        cache: 'no-store'
      }).then(r => r.ok ? r.json() : null).then(j => {
        if (j && typeof j === 'object') takeAvail(j);
      }).catch(() => {}); // fail-open: keep what we have, ordering never blocks on this
    } catch (e) {}
  }, [takeAvail]);
  // Poll on mount and every 60 s while the tab is visible; a tab coming back
  // to the foreground refreshes at once. Hidden tabs cost the ERP nothing.
  useEffect(() => {
    let t = 0;
    const start = () => {
      clearInterval(t);
      t = setInterval(refreshAvail, 60000);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        refreshAvail();
        start();
      } else clearInterval(t);
    };
    refreshAvail();
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshAvail]);
  // …and every time checkout opens (the moment it matters most).
  useEffect(() => {
    if (checkoutOpen) refreshAvail();
  }, [checkoutOpen, refreshAvail]);
  // The pause ends by the clock — drop the notice then, without waiting a poll.
  useEffect(() => {
    if (!paused) return;
    const ms = Date.parse(paused.until) - Date.now();
    const t = setTimeout(() => {
      setPaused(null);
      refreshAvail();
    }, Math.max(1000, Math.min(ms + 1000, 2147483000)));
    return () => clearTimeout(t);
  }, [paused, refreshAvail]);
  // A timed 86 ends by the clock: re-apply the list at the earliest `until`
  // (+1 s), so the dish is orderable again on time, not up to a poll later.
  useEffect(() => {
    if (!liveWake) return;
    const t = setTimeout(() => applyAvail(LIVE_UNAVAIL), Math.max(1000, Math.min(liveWake - Date.now() + 1000, 2147483000)));
    return () => clearTimeout(t);
  }, [liveWake, applyAvail]);
  // "{items} — finished for today, so we removed it…" for the given slugs.
  const toastRemovedOut = ids => {
    const find = id => MENU.flatMap(s => s.items).find(i => i.id === id);
    const list = (LL, name) => ids.map(id => {
      const it = find(id);
      return it && name(it) || id;
    }).join(isLatin(LL) ? ", " : "، ");
    setToast({
      msg: tr(lang, 'toast.removedOut').replace('{items}', list(lang, it => nm(lang, it))),
      fa: tra(lang, 'toast.removedOut').replace('{items}', list(ACCENT[lang], it => nmA(lang, it) || nm(lang, it))),
      show: true
    });
    setTimeout(() => setToast(s => ({
      ...s,
      show: false
    })), 3400);
  };
  // create.ts refused the order (409): trust its answer right away, then
  // re-read the full status. item_unavailable adds the named slugs to the live
  // list AND always takes them out of the cart here, with the toast — even when
  // the client already had them as sold out (no soldOut flip → no availVer
  // bump → the sanitiser below would never run, and Pay would 409 forever).
  const onServerAvail = useCallback(j => {
    if (!j) return;
    if (j.code === 'kitchen_paused' && typeof j.paused_until === 'string') {
      pauseHintRef.current = {
        until: j.paused_until,
        reason: '',
        at: Date.now()
      };
      takeAvail({
        paused: {
          until: j.paused_until,
          reason: ''
        }
      });
    } else if (j.code === 'item_unavailable' && Array.isArray(j.slugs)) {
      const slugs = j.slugs.filter(s => typeof s === 'string');
      // Cart first: with React 18 batching, the availVer sanitiser then sees a
      // cart without them and stays quiet (one toast, not two).
      setCart(c => {
        const n = c.filter(l => !slugs.includes(l.id));
        return n.length === c.length ? c : n;
      });
      if (slugs.length) toastRemovedOut(slugs);
      const prevHint = unavailHintRef.current;
      const keep = prevHint && Date.now() - prevHint.at < 20000 ? prevHint.slugs.filter(s => !slugs.includes(s)) : [];
      unavailHintRef.current = {
        slugs: [...keep, ...slugs],
        at: Date.now()
      };
      applyAvail([...LIVE_UNAVAIL, ...slugs.map(slug => ({
        slug,
        until: null
      }))]);
    }
    refreshAvail();
  }, [takeAvail, refreshAvail, applyAvail, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = useMemo(() => MENU.flatMap(s => s.items), [availVer]); // eslint-disable-line react-hooks/exhaustive-deps
  const minPlatter = useMemo(() => minPlatterPrice(allItems), [allItems]);

  // A dish in the cart just went off (live list): take it out, exactly like the
  // mount-time sanitiser, and say so once. An open recipe card re-reads its item.
  useEffect(() => {
    if (!availVer) return;
    const out = new Set(allItems.filter(i => i.soldOut).map(i => i.id));
    setSheetItem(s => s ? allItems.find(i => i.id === s.id) || s : s);
    const gone = cart.filter(l => out.has(l.id));
    if (!gone.length) return;
    setCart(c => c.filter(l => !out.has(l.id)));
    toastRemovedOut(gone.map(l => l.id));
  }, [availVer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phone Back button: each open layer owns a history entry (see useBackLayer).
  // Entries stack in the order layers open, so the newest layer closes first.
  useBackLayer(menuOpen, 'menu', () => setMenuOpen(false));
  useBackLayer(mobileCartOpen, 'cart', () => setMobileCartOpen(false));
  useBackLayer(checkoutOpen, 'checkout', () => {
    setCheckoutOpen(false);
  }, () => CO_SUBMITTING);
  useBackLayer(!!sheetItem, 'item', () => setSheetItem(null));
  const qtyOf = id => {
    const l = cart.find(x => x.id === id);
    return l ? l.qty : 0;
  };
  const cartCount = cart.reduce((s, l) => s + l.qty, 0);

  // useCallback (2026-08-05 perf pass): these two are props of every memo'd
  // MenuRow/PlatterCard — a fresh function identity each render would defeat
  // the memo entirely. Deps: [mode, lang] for addItem (the dine-in guard reads
  // mode, the toast strings read lang); setters and refs are stable by
  // contract. changeQty touches only setCart → empty deps.
  const addItem = useCallback((item, opts) => {
    // Guard: dine-in-only items can't be added while ordering for delivery.
    // (The menu button is already disabled in delivery mode; this also covers
    // suggestion chips and any other programmatic add path.)
    if (mode !== "dinein" && DINE_IN_ONLY.has(item.id)) {
      setToast({
        msg: `${nm(lang, item)} — ${tr(lang, 'toast.dineinOnly')}`,
        fa: tra(lang, 'toast.dineinOnly'),
        show: true
      });
      setTimeout(() => setToast(s => ({
        ...s,
        show: false
      })), 1800);
      return;
    }
    // Guard: never add a dish that is sold out / switched off live. Re-read
    // MENU — a frozen chip or checkout line may hold a stale item object.
    const cur = MENU.flatMap(s => s.items).find(i => i.id === item.id) || item;
    if (cur.soldOut) {
      setToast({
        msg: `${nm(lang, item)} — ${tr(lang, 'menu.outToday')}`,
        fa: tra(lang, 'menu.outToday'),
        show: true
      });
      setTimeout(() => setToast(s => ({
        ...s,
        show: false
      })), 1800);
      return;
    }
    const viaUpsell = !!(opts && opts.upsell);
    setCart(c => {
      const ex = c.find(l => l.id === item.id);
      if (ex) return c.map(l => l.id === item.id ? {
        ...l,
        qty: l.qty + 1
      } : l);
      // is_upsell: line-level attribution when the add came from a suggestion
      // (rides the cart verbatim into /api/orders/create → items_json → ERP).
      // A qty bump on an existing organic line stays organic (safe direction).
      return [...c, viaUpsell ? {
        id: item.id,
        qty: 1,
        is_upsell: true
      } : {
        id: item.id,
        qty: 1
      }];
    });
    // Marketing funnel event (GA4/Meta Pixel/Plausible via the layout shim).
    try {
      if (window.sfTrack) window.sfTrack('add_to_cart', {
        id: item.id,
        name: item.en,
        value: item.price,
        qty: 1
      });
    } catch (e) {}
    // Upsell learning loop: record the acceptance with its placement.
    if (viaUpsell) {
      try {
        if (window.SFUpsell) window.SFUpsell.track('accepted', opts && opts.placement || 'cart', item.id, {
          value: item.price
        });
      } catch (e) {}
    }
    setToast({
      msg: `${tr(lang, 'toast.added')} — ${nm(lang, item)}`,
      fa: nmA(lang, item),
      show: true
    });
    setTimeout(() => setToast(s => ({
      ...s,
      show: false
    })), 1600);
    // Drive the transient InlineSuggestion ribbon. Auto-clears after 12s.
    // SKIPPED for suggestion-driven adds: accepting an upsell must never
    // trigger another upsell push (no cascades, no ribbon behind the modal).
    // Host at the dish: a platter added from its CARD opens the table step
    // under it INSTEAD of the ribbon — only when a step will really render
    // (C8: kill switch, WA, paused, gather, an anchor already resolved, any
    // decline this order, no servings / <2 rows / N>9 all keep the old ribbon
    // exactly). A sheet add (host:'sheet') is hosted in the sheet; EatingFor's
    // add-all (noHost) gets neither. hostCtxRef = the latest render's state.
    const hc = hostCtxRef.current || {},
      o = opts || {};
    // A sheet add of another platter replaces the open card step (S3: one step at a time).
    if (hc.on && o.host === 'sheet' && PLATTER_IDS.has(item.id)) setHostInline(x => x && x !== item.id ? null : x);
    if (!viaUpsell && !(hc.on && (o.host === 'sheet' || o.noHost))) {
      const cc = hc.cart || [];
      // (opened: each anchor's card step opens at most once per order.)
      const card = hc.on && PLATTER_IDS.has(item.id) && !hc.done[item.id] && !hc.declinedAny && hostLoad().opened.indexOf('card|' + item.id) < 0 && !!hostPlanSafe(cc.some(l => l.id === item.id) ? cc.map(l => l.id === item.id ? {
        ...l,
        qty: l.qty + 1
      } : l) : [...cc, {
        id: item.id,
        qty: 1
      }], MENU.flatMap(s => s.items), mode, item.id, lang);
      if (card) {
        setHostInline(item.id);
        setHostPing(p => p + 1);
      }
      // A single added while a fresh step is open gets no ribbon (one ask at a time).
      else if (!hc.open || PLATTER_IDS.has(item.id)) {
        setLastAddedId(item.id);
        if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
        lastAddedTimerRef.current = setTimeout(() => setLastAddedId(null), INLINE_SUGGEST_MS);
      }
    }
  }, [mode, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeQty = useCallback((id, delta) => {
    setCart(c => c.map(l => l.id === id ? {
      ...l,
      qty: Math.max(0, l.qty + delta)
    } : l).filter(l => l.qty > 0));
  }, []);
  const removeItem = id => setCart(c => c.filter(l => l.id !== id));
  const addById = (id, opts) => {
    const item = allItems.find(i => i.id === id);
    if (item) addItem(item, opts);
  };
  // Table-step writes: ONE setCart for any set of lines (hostMerge re-reads
  // MENU, clamps 0..30, 0 removes, new lines carry src:'companion'); the
  // add_to_cart deltas are taken INSIDE the updater (C16). "Added" is decided
  // from the request, never from a variable the updater sets. No toast, no
  // ribbon, no SFUpsell.
  const hostSetLines = useCallback((lines, how, surface, anchor) => {
    setHostPing(p => p + 1);
    if (!lines || !lines.length) return;
    if (lines.some(x => x.to > x.from)) {
      hostSave({
        hx: 'added',
        hs: surface
      });
      clarityTag('host', 'added');
      setHostEngaged(e => e[anchor] ? e : {
        ...e,
        [anchor]: true
      });
      // v2 #6: the SET (card button / sheet foot) also sends ONE 'set' beacon,
      // item_slug = the set's drink; row/pill adds keep only 'accepted'.
      if (how === 'bundle') {
        const d = lines.find(x => HOST_SLUG.drink.indexOf(x.id) >= 0);
        hostBeacon('set', surface === 'sheet' ? 'companion-sheet' : 'companion-card', d ? d.id : null);
        clarityEvent('host_set_' + surface);
      }
    }
    setCart(c => {
      const r = hostMerge(c, lines, mode);
      hostTrack(r.adds, surface === 'sheet' ? 'companion-sheet' : 'companion-card');
      return r.next;
    });
    clarityEvent('host_' + how);
  }, [mode]);
  // Done / That's all / ✕ / sheet close. Shown rows left at 0 become declined
  // for every old surface this order (C10); a sheet closed before any row was
  // seen is a peek, not a decline. The welcome-card hold ends 700 ms later.
  const hostResolve = (anchor, state, surface) => {
    if (hostSheetRef.current && hostSheetRef.current.anchor === anchor) hostSheetRef.current = null;
    // Only resolving the open CARD step starts the release (C6).
    if (hostInline === anchor) {
      clearTimeout(hostRelRef.current);
      hostRelRef.current = setTimeout(() => {
        hostRelRef.current = 0;
        setHostHold(false);
      }, 700);
      setHostInline(null);
    }
    if (!cart.some(l => l.id === anchor && l.qty > 0)) return;
    const st = state || (hostEngaged[anchor] || hostDone[anchor] === 'set' ? 'set' : 'none');
    // An explicit button (state given) counts a row seen on EITHER surface (the
    // sheet's "That's all" can resolve an anchor whose rows were seen on its
    // card step); a plain sheet close counts only rows seen in the sheet (peek
    // rule). Dismissed under the placement it was seen on, this surface first.
    const pl = surface === 'sheet' ? 'companion-sheet' : 'companion-card',
      sh = hostLoad().shown;
    const seenPl = id => (state ? [pl, 'companion-card', 'companion-sheet'] : [pl]).find(p => sh.indexOf(p + '|' + id) >= 0);
    // Fixer r2: an EXPLICIT "That's all" / ✕ with nothing added (state 'none')
    // also declines the Extra-cuts cards actually seen at 0 — the customer passed
    // on them, so no other surface asks again (C10/C11). A 'set' resolve or a
    // plain sheet close never declines a cut (hostZero stays rows-only).
    const hp = hostPlanSafe(cart, allItems, mode, anchor, L);
    const zero = hostZero(hp).concat(state === 'none' && hp ? hp.cuts.filter(o => !o.cur).map(o => o.id) : []).filter(seenPl);
    if (st === 'none' && surface === 'sheet' && !zero.length) return;
    // 'dismissed' once per placement+slug per order, like 'shown' ('x|' never
    // collides with the 'companion-…|' shown keys).
    zero.forEach(id => {
      const p = seenPl(id);
      if (hostOnce('shown', 'x|' + p + '|' + id)) hostBeacon('dismissed', p, id);
    });
    // A decline silences every step: another anchor's open card step closes
    // through the prev-inline path (engaged → 'set', S3), never orphaned.
    if (st === 'none' && hostInline && hostInline !== anchor) setHostInline(null);
    setHostDone(d => d[anchor] === st ? d : {
      ...d,
      [anchor]: st
    });
    if (zero.length) setHostDeclined(s => {
      const n = new Set(s);
      zero.forEach(id => n.add(id));
      return n;
    });
    if (st === 'none' && hostLoad().hx !== 'added') {
      hostSave({
        hx: 'declined',
        hs: surface
      });
      clarityTag('host', 'declined');
    }
    clarityEvent(st === 'set' ? 'host_set' : 'host_none');
  };
  // Permanently hide a suggested slug for this session (until tab close).
  const dismissSuggestion = (slug, placement) => {
    // Mirror into SFUpsell so the dismissal persists across reloads this
    // session AND is recorded for the learning loop (attach-rate pruning).
    try {
      if (window.SFUpsell) window.SFUpsell.dismiss(placement || 'cart', slug);
    } catch (e) {}
    setDismissedSuggestions(prev => {
      const next = new Set(prev);
      next.add(slug);
      return next;
    });
    // If the dismissed slug equals lastAddedId's anchor, clear the inline timer
    // so the ribbon disappears immediately rather than after 12s.
    if (lastAddedId && lastAddedId === slug) {
      if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
      setLastAddedId(null);
    }
  };
  // Close the inline ribbon WITHOUT recording anything — the ✕ on the ribbon
  // dismisses the suggested chips (via dismissSuggestion) but must never mark
  // the just-added ANCHOR item as "dismissed" (it was bought, not rejected).
  const clearInlineRibbon = () => {
    if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
    setLastAddedId(null);
  };
  // ── Host at the dish: render-time state (plain consts + the hold effect) ──
  const hostOn = hostEnabled() && !WA_CTX && !paused && !gather;
  const hostDeclinedAny = Object.keys(hostDone).some(k => hostDone[k] === 'none');
  const hostCardPlan = hostOn && hostInline && !hostDone[hostInline] && !hostDeclinedAny ? hostPlanSafe(cart, allItems, mode, hostInline, L) : null;
  const hostCardOpen = !!hostCardPlan;
  hostCtxRef.current = {
    on: hostOn,
    done: hostDone,
    declinedAny: hostDeclinedAny,
    cart,
    open: hostCardOpen
  };
  // C11: while a card step is open its row items are kept off the rail /
  // cart-sheet strip (desktop always shows the rail — never offer twice).
  const hostRailEx = hostIds(hostCardPlan).map(o => o.id).join(' ');
  // C10: the ribbon feed excludes the step's declines too.
  const ribbonDis = useMemo(() => hostDeclined.size ? new Set([...dismissedSuggestions, ...hostDeclined]) : dismissedSuggestions, [dismissedSuggestions, hostDeclined]);
  // C6: no card plan → no hold, unless the 700 ms release is still pending.
  useEffect(() => {
    if (hostHold && !hostCardOpen && !hostRelRef.current) setHostHold(false);
  }, [hostHold, hostCardOpen]);
  const welcomeOpen = showWelcome && !checkoutOpen && !mobileCartOpen && !menuOpen && !confirmOpen && !sheetItem && !hostHold;
  const hostCovered = welcomeOpen || checkoutOpen || mobileCartOpen || menuOpen || confirmOpen || !!sheetItem;
  // The card step's mount: hold the welcome card only when the step is near
  // the fold (C6), once per anchor (a search-toggle remount never re-holds).
  const hostOnMount = (a, near) => {
    if (!near || hostHeldRef.current === a) return;
    hostHeldRef.current = a;
    clearTimeout(hostRelRef.current);
    hostRelRef.current = 0;
    setHostHold(true);
    setHostPing(p => p + 1);
  };
  const hostPingNow = () => setHostPing(p => p + 1);
  // The rail/sheet sees the full cart-based suggestion list. ERP data (gap
  // detection + real pairings) wins when present; static rules otherwise.
  const railSuggestions = useMemo(() => {
    try {
      if (window.SFUpsell && window.SFUpsell.enabled()) {
        if (!window.SFUpsell.placementOn('cart')) return [];
        // Same reasoning as the ribbon: say nothing until the ERP answer for
        // THIS cart has landed, rather than flashing the static rules for the
        // ~300ms of the debounce. On the strip that flash was worse than
        // cosmetic — it fired real 'cart' beacons for torshi-mix/doogh AND
        // (since noteSeen was added) marked them as already-shown, which then
        // wrongly barred them from the checkout line. erpReady still turns
        // true when the endpoint is down/empty (data just resolves to null),
        // so a dead ERP still degrades to the static rules as before.
        if (!erpReady) return [];
      }
    } catch (e) {}
    // Choose the slate for THIS cart while IGNORING dismissals, then subtract
    // what the customer has dismissed. Dismissing therefore SHRINKS the strip
    // (3 → 2 → 1 → gone) instead of instantly backfilling the empty slot from
    // the 8-deep candidate pool. The old behaviour made the ✕ a game of
    // whack-a-mole: two thirds of dismissals were answered by a fresh chip and
    // one session had to swat the strip 13 times in five seconds — exactly the
    // "asking again and again for no reason" the ✕ is supposed to stop.
    // Dismissals are still recorded (they gate every other surface); this only
    // stops the refill.
    //
    // Anything the ribbon already pushed this order is excluded WHEN THE SLATE
    // IS CHOSEN (not filtered out afterwards) so the strip still fills with
    // three useful chips instead of shrinking to one. The strip used to simply
    // repeat the ribbon: 245 session/item pairs were shown at both, 62% of
    // sessions saw some item at two or more placements, and lemonade alone ran
    // up 495 impressions across three surfaces for 15 accepts. Reading the
    // pushed set is deliberately READ-ONLY — see SFUpsell.pushedList; the strip
    // must never call notePush or it would spend the whole per-order budget.
    let pushedSet = new Set();
    try {
      if (window.SFUpsell && window.SFUpsell.pushedList) pushedSet = new Set(window.SFUpsell.pushedList());
    } catch (e) {}
    // Host at the dish: the step's declines (C10) and, while a card step is
    // open, its own row items (C11) are kept out when the slate is chosen.
    hostDeclined.forEach(s => pushedSet.add(s));
    if (hostRailEx) hostRailEx.split(' ').forEach(s => pushedSet.add(s));
    const slate = resolveErpSuggestions(erpSugg, cart, allItems, pushedSet, 3);
    const base = slate.length ? slate : getSuggestions(cart, lastAddedId, allItems, pushedSet, 3);
    return base.filter(s => !dismissedSuggestions.has(s.id));
  },
  // lastAddedId matters twice over: it feeds the static fallback, and it flips
  // to null when the ribbon expires — which is the moment the strip should
  // re-evaluate and drop whatever the ribbon just pushed.
  // erpReady must be listed separately from erpSugg: when the endpoint answers
  // with NOTHING, erpSugg stays null while erpReady flips false→true, and
  // without this dep the memo would never re-run and the strip would stay
  // permanently empty instead of falling back to the static rules.
  [cart, lastAddedId, allItems, dismissedSuggestions, erpSugg, erpReady, hostDeclined, hostRailEx]);
  // ERP-aware feed for the per-row InlineSuggestion ribbon (falls back to the
  // per-anchor static rules at the render site when null). ribbonDis =
  // dismissals + the table step's declines (C10).
  const inlineErpSuggestions = useMemo(() => {
    const erp = resolveErpSuggestions(erpSugg, cart, allItems, ribbonDis, 2);
    return erp.length ? erp : null;
  }, [cart, allItems, ribbonDis, erpSugg]);
  // The slot under every PlatterCard (menu AND search results): the fresh
  // card step, else the old ribbon (C8 fallback / kill switch), else the S4
  // settled line once the anchor resolved 'set'.
  const hostSlot = item => hostCardOpen && hostInline === item.id ? /*#__PURE__*/React.createElement(HostBoundary, {
    key: "step",
    onError: () => {
      setHostInline(null);
      setHostHold(false);
    }
  }, /*#__PURE__*/React.createElement(TableStep, {
    variant: "card",
    anchorId: item.id,
    plan: hostCardPlan,
    mode: mode,
    covered: hostCovered,
    engaged: !!hostEngaged[item.id],
    onSetLines: (lines, how) => hostSetLines(lines, how, 'card', item.id),
    onResolve: st => hostResolve(item.id, st, 'card'),
    onMount: hostOnMount,
    onPing: hostHold ? hostPingNow : undefined
  })) : lastAddedId === item.id && erpReady ? /*#__PURE__*/React.createElement(InlineSuggestion, {
    forItem: item,
    suggestions: inlineErpSuggestions || getSuggestions(cart, item.id, allItems, ribbonDis, 2),
    onAdd: addById,
    onDismiss: dismissSuggestion,
    onClose: clearInlineRibbon
  }) : hostOn && hostDone[item.id] === 'set' && qtyOf(item.id) > 0 ? /*#__PURE__*/React.createElement(HostBoundary, {
    key: "sum"
  }, /*#__PURE__*/React.createElement(TableSummary, {
    anchorId: item.id,
    cart: cart,
    items: allItems,
    mode: mode,
    onChange: () => {
      clarityEvent('host_change');
      setSheetItem(item);
    }
  })) : null;
  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 70;
      glideTo(top);
    }
  };
  const ordinals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

  // Tap a tab → glide the page to that section's heading, landing it just
  // below the sticky nav + tab strip. All sections are always mounted, so
  // this scrolls in BOTH directions. The scroll-spy is paused while the
  // glide is in flight (it re-syncs from the landing position afterwards).
  const switchCategory = id => {
    setActiveCat(id);
    spyPausedUntilRef.current = Date.now() + 900;
    const section = document.getElementById("cat-" + id);
    if (!section) return;
    // Measure the pinned nav + strip instead of hardcoding — both vary by
    // breakpoint, and the nav drops lower when the order-tracking bar shows.
    const nav = document.querySelector('.nav');
    const navStickyTop = nav ? parseFloat(getComputedStyle(nav).top) || 0 : 0;
    const navH = nav ? nav.offsetHeight : 70;
    const strip = tabsRef.current ? tabsRef.current.parentElement : null;
    const stripH = strip ? strip.offsetHeight : 56;
    const headerOffset = navStickyTop + navH + stripH + 8;
    const target = section.getBoundingClientRect().top + window.scrollY - headerOffset;
    glideTo(target);
  };

  // On narrow screens the nav cart icon should open the slide-up cart sheet,
  // not jump straight to checkout. Detect via matchMedia (no resize listener
  // needed — the user can't resize a phone).
  const handleCartClick = () => {
    if (cartCount === 0) {
      // Empty cart: the pill is a shortcut to the food, never a dead tap.
      const el = document.getElementById('order');
      if (el) glideTo(el.getBoundingClientRect().top + window.scrollY - 70);
      return;
    }
    if (window.matchMedia("(max-width: 1080px)").matches) {
      setMobileCartOpen(true);
    } else {
      setCheckoutOpen(true);
    }
  };

  // Hero teaser = the REAL most-ordered dishes (server-ranked), falling back to
  // the first platters when there is no order history yet.
  const teaserItems = (() => {
    const ranked = POPULAR.map(id => allItems.find(i => i.id === id)).filter(Boolean);
    const seen = new Set();
    // Never tease a dish that can't be ordered right now.
    return [...ranked, ...(MENU[0] && MENU[0].items || [])].filter(it => it && !it.soldOut && !seen.has(it.id) && seen.add(it.id)).slice(0, 3);
  })();
  return /*#__PURE__*/React.createElement(LangCtx.Provider, {
    value: lang
  }, INAPP_HINTS_ENABLED && /*#__PURE__*/React.createElement(InAppBar, {
    cart: cart,
    lang: lang
  }), /*#__PURE__*/React.createElement(Nav, {
    cartCount: cartCount,
    onCartClick: handleCartClick,
    onNav: scrollTo,
    onMenuOpen: () => setMenuOpen(true),
    lang: lang,
    onLang: setLang
  }), /*#__PURE__*/React.createElement(MobileMenu, {
    open: menuOpen,
    onClose: () => setMenuOpen(false),
    onNav: scrollTo,
    lang: lang,
    onLang: setLang
  }), /*#__PURE__*/React.createElement("div", {
    className: "shell"
  }, /*#__PURE__*/React.createElement(Hero, {
    onOrderClick: () => scrollTo("order"),
    minPlatter: minPlatter,
    deliveryArea: deliveryArea
  }), /*#__PURE__*/React.createElement(QuickCards, {
    lang: lang,
    introPct: introPct,
    paidCount: paidCount,
    onReorder: reorderLast,
    onPreorder: preorderStart,
    allItems: allItems,
    mode: mode
  })), MENU[0] && MENU[0].items.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "shell"
  }, /*#__PURE__*/React.createElement("div", {
    className: "hero-teaser"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ht-label " + P
  }, tr(L, 'menu.mostOrdered'), " \xB7 ", /*#__PURE__*/React.createElement("span", {
    className: A
  }, tra(L, 'menu.mostOrdered'))), /*#__PURE__*/React.createElement("div", {
    className: "ht-cards"
  }, teaserItems.map(it => /*#__PURE__*/React.createElement("button", {
    key: it.id,
    type: "button",
    className: "ht-card",
    onClick: () => setSheetItem(it)
  }, it.imageLg || it.image ? /*#__PURE__*/React.createElement("img", {
    src: it.imageLg || it.image,
    srcSet: [it.image && it.image + " 200w", it.imageMd && it.imageMd + " 400w", it.imageLg && it.imageLg + " 800w"].filter(Boolean).join(", ") || undefined,
    sizes: "(max-width: 719px) 46vw, 300px",
    alt: "",
    loading: "lazy",
    decoding: "async"
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: "ht-name " + P
  }, nm(L, it)), /*#__PURE__*/React.createElement("span", {
    className: "ht-price num"
  }, it.price, " AED")))))), /*#__PURE__*/React.createElement("div", {
    className: "shell"
  }, /*#__PURE__*/React.createElement("div", {
    className: "order-section",
    id: "order"
  }, /*#__PURE__*/React.createElement("div", {
    className: "order-main"
  }, /*#__PURE__*/React.createElement(PausedNote, {
    paused: paused
  }), /*#__PURE__*/React.createElement("div", {
    className: "halal-note",
    role: "note"
  }, /*#__PURE__*/React.createElement("span", {
    className: "halal-mark",
    "aria-hidden": "true"
  }, "\u2713"), " ", /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'menu.halalNote'))), /*#__PURE__*/React.createElement("div", {
    className: "vat-note",
    role: "note"
  }, /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, 'menu.vatNote')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, 'menu.vatNote'))), /*#__PURE__*/React.createElement("div", {
    className: "first-timer",
    role: "note"
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\uD83D\uDC11"), /*#__PURE__*/React.createElement("span", {
    className: P
  }, tr(L, hostEnabled() && !WA_CTX ? 'menu.firstTimer' : 'menu.firstTimer0')), /*#__PURE__*/React.createElement("span", {
    className: "fa " + A
  }, tra(L, hostEnabled() && !WA_CTX ? 'menu.firstTimer' : 'menu.firstTimer0'))), /*#__PURE__*/React.createElement("div", {
    className: "search-wrap" + (search ? " has-value" : "")
  }, /*#__PURE__*/React.createElement("span", {
    className: "search-icon",
    "aria-hidden": "true"
  }, "\u2315"), /*#__PURE__*/React.createElement("input", {
    className: "search-input",
    type: "text",
    placeholder: tr(L, 'menu.searchPh'),
    value: search,
    onChange: e => setSearch(e.target.value),
    "aria-label": tr(L, 'a11y.search')
  }), search && /*#__PURE__*/React.createElement("button", {
    className: "search-clear",
    onClick: () => setSearch(""),
    "aria-label": tr(L, 'a11y.clearSearch')
  }, "\u2715")), (() => {
    const q = search.trim();
    if (q) {
      // Latin fields fold through foldTr (tr-TR lower-case + ı ş ğ ü ö ç → ASCII) on BOTH
      // sides, so "iskembe" finds İşkembe in every language; the English name is always searched.
      const qT = foldTr(q);
      const qF = foldArabic(q);
      const matches = MENU.flatMap(s => s.items.filter(i => foldTr(i.en).includes(qT) || i.tr && foldTr(i.tr).includes(qT) || i.fa && foldArabic(i.fa).includes(qF) || i.ar && foldArabic(i.ar).includes(qF) || i.hint && foldTr(i.hint).includes(qT) || i.hintTr && foldTr(i.hintTr).includes(qT) || (i.hintAr || i.descAr) && foldArabic(i.hintAr || i.descAr).includes(qF) || foldTr(SEARCH_ALIASES[i.id] || '').includes(qT) || foldArabic(SEARCH_ALIASES_AR[i.id] || '').includes(qF)).map(item => ({
        item,
        sec: s
      })));
      // tr: ONE whole template (“{q}” için {n} sonuç) — the English prefix order does not work in Turkish.
      const trCountT = L === 'tr' ? tr(L, 'menu.matches') : '';
      const trCount = trCountT.indexOf('{q}') !== -1 ? trCountT.replace('{n}', () => numLocal(L, matches.length)).replace('{q}', () => q) : null;
      return /*#__PURE__*/React.createElement("div", {
        role: "tabpanel",
        "aria-label": tr(L, 'a11y.results')
      }, /*#__PURE__*/React.createElement("div", {
        className: "search-meta"
      }, /*#__PURE__*/React.createElement("span", {
        className: P
      }, L === 'ar' ? /*#__PURE__*/React.createElement(React.Fragment, null, "\u0646\u062A\u0627\u0626\u062C \u0627\u0644\u0628\u062D\u062B \u0639\u0646 \xAB", q, "\xBB: ", matches.length) : trCount != null ? trCount : /*#__PURE__*/React.createElement(React.Fragment, null, numLocal(L, matches.length), " ", L === 'en' ? (matches.length === 1 ? "match" : "matches") + " for" : tr(L, 'menu.matches'), " \"", q, "\"")), /*#__PURE__*/React.createElement("span", {
        className: "fa " + A
      }, tra(L, 'menu.matches'), " \xAB", q, "\xBB")), matches.length === 0 ? /*#__PURE__*/React.createElement("div", {
        className: "search-empty"
      }, /*#__PURE__*/React.createElement("div", {
        className: "se-en " + P
      }, tr(L, 'menu.noMatch')), /*#__PURE__*/React.createElement("div", {
        className: "se-fa " + A
      }, tra(L, 'menu.noMatch'))) : /*#__PURE__*/React.createElement("div", {
        className: "menu-board"
      }, matches.map(({
        item,
        sec
      }) => sec.id === "platters" ? /*#__PURE__*/React.createElement(React.Fragment, {
        key: item.id
      }, /*#__PURE__*/React.createElement(PlatterCard, {
        item: item,
        qty: qtyOf(item.id),
        onAdd: addItem,
        onChange: changeQty,
        mode: mode,
        onOpen: setSheetItem
      }), hostSlot(item)) : /*#__PURE__*/React.createElement(MenuRow, {
        key: item.id,
        item: item,
        qty: qtyOf(item.id),
        onAdd: addItem,
        onChange: changeQty,
        mode: mode,
        onOpen: setSheetItem
      }))));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "cat-tabs-strip"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cat-search-btn",
      "aria-label": tr(L, 'menu.searchPh'),
      onClick: () => {
        const el = document.querySelector('.search-input');
        if (el) {
          glideTo(el.getBoundingClientRect().top + window.scrollY - 150);
          setTimeout(() => {
            try {
              el.focus({
                preventScroll: true
              });
            } catch (e) {
              try {
                el.focus();
              } catch (e2) {}
            }
          }, 240);
        }
      }
    }, "\u2315"), /*#__PURE__*/React.createElement("div", {
      ref: tabsRef,
      className: "cat-tabs",
      role: "tablist",
      "aria-label": tr(L, 'a11y.menuCats')
    }, MENU.map(s => {
      const shortPri = nm(L, s).split(/[,&،]/)[0].trim();
      const shortAcc = nmA(L, s).split(/[,&،]/)[0].trim();
      return /*#__PURE__*/React.createElement("button", {
        key: s.id,
        "data-cat": s.id,
        role: "tab",
        "aria-selected": activeCat === s.id,
        className: "cat-tab" + (activeCat === s.id ? " active" : ""),
        onClick: () => switchCategory(s.id)
      }, /*#__PURE__*/React.createElement("span", {
        className: "t-en " + P
      }, shortPri), /*#__PURE__*/React.createElement("span", {
        className: "t-fa " + A
      }, shortAcc));
    }))), MENU.map((sec, si) => /*#__PURE__*/React.createElement("section", {
      key: sec.id,
      id: "cat-" + sec.id,
      className: "menu-cat",
      "aria-label": nm(L, sec)
    }, si > 0 && /*#__PURE__*/React.createElement("div", {
      className: "cat-divider",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cat-divider-orn"
    }, "\u2726")), /*#__PURE__*/React.createElement(SecHead, {
      num: ordinals[si],
      section: sec,
      ornament: sec.ornament
    }), sec.id === "platters" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EatingFor, {
      platters: sec.items,
      hasNewcomer: allItems.some(i => i.id === NEWCOMER_SLUG && !i.soldOut),
      onFit: (ids, scroll) => {
        setPlatterFit(ids && ids.length ? ids : null);
        if (!scroll || !ids || !ids.length) return;
        setTimeout(() => {
          const el = document.querySelector('.platters .platter[data-item="' + ids[0] + '"]');
          if (!el) return;
          // Land it below the sticky nav + category strip (measured, as switchCategory does).
          const nav = document.querySelector('.nav');
          const navTop = nav ? parseFloat(getComputedStyle(nav).top) || 0 : 0;
          const strip = tabsRef.current ? tabsRef.current.parentElement : null;
          const off = navTop + (nav ? nav.offsetHeight : 70) + (strip ? strip.offsetHeight : 56) + 12;
          glideTo(el.getBoundingClientRect().top + window.scrollY - off);
        }, 30);
      },
      onAddAll: list => {
        clarityEvent('eat_for_add_all');
        list.forEach(it => addItem(it, {
          noHost: true
        }));
      },
      onNewcomer: () => {
        const it = allItems.find(i => i.id === NEWCOMER_SLUG);
        if (!it) return;
        clarityEvent('eat_for_newcomer');
        setSheetNote('eat.newNote');
        setSheetItem(it);
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "platters"
    }, sec.items.map(item => /*#__PURE__*/React.createElement(React.Fragment, {
      key: item.id
    }, /*#__PURE__*/React.createElement(PlatterCard, {
      item: item,
      qty: qtyOf(item.id),
      onAdd: addItem,
      onChange: changeQty,
      mode: mode,
      onOpen: setSheetItem,
      fit: !!platterFit && platterFit.indexOf(item.id) !== -1
    }), hostSlot(item))))) : /*#__PURE__*/React.createElement("div", {
      className: "menu-board"
    }, sec.items.map(item => /*#__PURE__*/React.createElement(React.Fragment, {
      key: item.id
    }, /*#__PURE__*/React.createElement(MenuRow, {
      item: item,
      qty: qtyOf(item.id),
      onAdd: addItem,
      onChange: changeQty,
      mode: mode,
      onOpen: setSheetItem
    }), lastAddedId === item.id && erpReady && !hostCardOpen && /*#__PURE__*/React.createElement(InlineSuggestion, {
      forItem: item,
      suggestions: inlineErpSuggestions || getSuggestions(cart, item.id, allItems, ribbonDis, 2),
      onAdd: addById,
      onDismiss: dismissSuggestion,
      onClose: clearInlineRibbon
    })))))));
  })()), /*#__PURE__*/React.createElement("div", {
    className: "order-rail"
  }, /*#__PURE__*/React.createElement(OrderRail, {
    cart: cart,
    items: allItems,
    mode: mode,
    setMode: setMode,
    deliveryArea: deliveryArea,
    onAreaChange: setDeliveryArea,
    onAddById: addById,
    suggestions: railSuggestions,
    onDismissSuggestion: dismissSuggestion,
    introPct: introPct,
    when: checkoutWhen,
    onWhen: cartWhen,
    paused: paused,
    onCheckout: {
      changeQty,
      remove: removeItem,
      open: () => setCheckoutOpen(true)
    }
  })))), /*#__PURE__*/React.createElement(StatusStrip, null), /*#__PURE__*/React.createElement(HowMade, null), /*#__PURE__*/React.createElement("div", {
    className: "shell"
  }, /*#__PURE__*/React.createElement(FindUs, null), /*#__PURE__*/React.createElement(Footer, null)), /*#__PURE__*/React.createElement(CheckoutModal, {
    open: checkoutOpen,
    whenReq: whenReq,
    onWhenSync: setCheckoutWhen,
    onClose: () => {
      setCheckoutOpen(false);
    },
    cart: cart,
    items: allItems,
    mode: mode,
    setMode: setMode,
    deliveryArea: deliveryArea,
    setDeliveryArea: setDeliveryArea,
    onAddById: addById,
    erpSuggestions: erpSugg,
    onDismissSuggestion: dismissSuggestion,
    introPct: introPct,
    paused: paused,
    onServerAvail: onServerAvail,
    gather: gather,
    onGatherDone: endGather,
    hostDeclined: hostDeclined,
    onPlace: ({
      orderNo
    }) => {
      orderNoRef.current = orderNo;
      requestWhen("asap", false); // the next order starts on Now
      setCheckoutOpen(false);
      setConfirmOpen(true);
    }
  }), /*#__PURE__*/React.createElement(Confirm, {
    open: confirmOpen,
    onClose: () => {
      setConfirmOpen(false);
      setCart([]);
      orderNoRef.current = String(Math.floor(Math.random() * 9000) + 1000);
    },
    orderNo: orderNoRef.current
  }), /*#__PURE__*/React.createElement(Toast, {
    message: toast.msg,
    fa: toast.fa,
    show: toast.show
  }), /*#__PURE__*/React.createElement(MobileCartBar, {
    cart: cart,
    items: allItems,
    mode: mode,
    deliveryArea: deliveryArea,
    introPct: introPct,
    onOpen: () => {
      clarityEvent('cart_sheet_open');
      setMobileCartOpen(true);
    },
    onCheckout: () => {
      setMobileCartOpen(false);
      setCheckoutOpen(true);
    }
  }), /*#__PURE__*/React.createElement(MobileCartSheet, {
    open: mobileCartOpen,
    onClose: () => setMobileCartOpen(false),
    cart: cart,
    items: allItems,
    mode: mode,
    setMode: setMode,
    deliveryArea: deliveryArea,
    onAreaChange: setDeliveryArea,
    onAddById: addById,
    suggestions: railSuggestions,
    onDismissSuggestion: dismissSuggestion,
    introPct: introPct,
    when: checkoutWhen,
    onWhen: cartWhen,
    paused: paused,
    onCheckout: {
      changeQty,
      remove: removeItem,
      open: () => {
        setMobileCartOpen(false);
        setCheckoutOpen(true);
      }
    }
  }), /*#__PURE__*/React.createElement(ItemSheet, {
    item: sheetItem,
    onClose: () => {
      setSheetItem(null);
      setSheetNote(null);
    },
    onAdd: addItem,
    mode: mode,
    note: sheetNote,
    cart: cart,
    items: allItems,
    onOpenItem: it => {
      setSheetNote(null);
      setSheetItem(it);
    },
    host: hostOn ? {
      done: hostDone,
      declinedAny: hostDeclinedAny,
      engaged: hostEngaged,
      setLines: hostSetLines,
      resolve: hostResolve,
      onStep: a => {
        hostSheetRef.current = {
          anchor: a
        };
      },
      changeQty
    } : null
  }), /*#__PURE__*/React.createElement(WelcomePopup, {
    open: welcomeOpen,
    onClose: closeWelcome,
    onLang: setLang
  }));
}
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      message: ''
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error && error.message || 'Unexpected error'
    };
  }
  componentDidCatch(error, info) {
    // Log to console and to Sentry if loaded
    console.error('[ErrorBoundary]', error, info);
    if (typeof window !== 'undefined' && window.Sentry && typeof window.Sentry.captureException === 'function') {
      try {
        window.Sentry.captureException(error, {
          extra: info
        });
      } catch (e) {/* swallow */}
    }
  }
  render() {
    if (this.state.hasError) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          maxWidth: 560,
          margin: '80px auto',
          padding: '32px 28px',
          textAlign: 'center',
          background: '#fffaf0',
          border: '1px solid #b89e6e',
          fontFamily: '"IM Fell English", Georgia, serif',
          color: '#221408'
        }
      }, /*#__PURE__*/React.createElement("h1", {
        style: {
          fontSize: 32,
          margin: '0 0 12px'
        }
      }, "Something went wrong."), /*#__PURE__*/React.createElement("div", {
        dir: "rtl",
        style: {
          fontFamily: '"Vazirmatn", sans-serif',
          fontSize: 18,
          color: '#553418',
          margin: '0 0 20px'
        }
      }, "\u0645\u0634\u06A9\u0644\u06CC \u067E\u06CC\u0634 \u0622\u0645\u062F \xB7 \u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627"), /*#__PURE__*/React.createElement("p", {
        style: {
          fontSize: 16,
          color: '#553418',
          lineHeight: 1.6,
          margin: '0 0 20px'
        }
      }, "The kitchen is fine \u2014 but the page got confused. Try reloading.", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
        style: {
          fontFamily: '"Vazirmatn", sans-serif',
          fontSize: 14
        }
      }, "\u0622\u0634\u067E\u0632\u062E\u0627\u0646\u0647 \u0633\u0631\u062C\u0627\u06CC\u0634 \u0627\u0633\u062A \u2014 \u0644\u0637\u0641\u0627\u064B \u0635\u0641\u062D\u0647 \u0631\u0627 \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u06A9\u0646\u06CC\u062F.")), /*#__PURE__*/React.createElement("button", {
        onClick: () => {
          try {
            window.location.reload();
          } catch (e) {}
        },
        style: {
          padding: '12px 24px',
          background: '#8a1c1c',
          color: '#fff',
          border: 'none',
          fontFamily: '"IM Fell DW Pica SC", serif',
          fontSize: 14,
          letterSpacing: '.25em',
          textTransform: 'uppercase',
          cursor: 'pointer'
        }
      }, "Reload \xB7 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC"));
    }
    return this.props.children;
  }
}

/**
 * WaLocationSetup — the focused "set your delivery location" screen for the
 * WhatsApp ordering flow (/wa-loc/<token>). No menu, no cart — just area +
 * map pin + street, then straight back to the chat where the bot continues
 * the order automatically. Reuses AreaPicker/MapPicker so the experience
 * matches the checkout exactly.
 */
function WaLocationSetup() {
  const loc = window.__WA_LOC_MODE__ || {};
  const waCtx = window.__WA_CONTEXT__ || null;
  const initialPin = waCtx && waCtx.lastPin && typeof waCtx.lastPin.lat === "number" ? {
    lat: waCtx.lastPin.lat,
    lng: waCtx.lastPin.lng
  } : null;
  const [area, setArea] = useState("");
  const [street, setStreet] = useState("");
  const [label, setLabel] = useState("Home");
  const [coords, setCoords] = useState(initialPin);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const pinLocation = () => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Your browser does not support location.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(pos => {
      setCoords({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      });
      setGeoBusy(false);
    }, err => {
      setGeoError(err.code === 1 ? "Permission denied — enable location and try again." : "Couldn't get your position — drag the pin instead.");
      setGeoBusy(false);
    }, {
      enableHighAccuracy: false,
      timeout: 12000,
      maximumAge: 60000
    });
    setTimeout(() => setGeoBusy(b => b ? (setGeoError("Taking too long — drag the pin on the map instead."), false) : b), 15000);
  };
  const selectedArea = findArea(area);
  const canSave = !!selectedArea && !!coords && street.trim().length >= 4 && !saving;
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/api/wa/save-address", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token: loc.token,
          area_key: area,
          area_label: selectedArea.en,
          street: street.trim(),
          lat: coords.lat,
          lng: coords.lng,
          label
        })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) {
        setError(j.error || "Couldn't save — please try again");
        setSaving(false);
        return;
      }
      setSaved(true);
    } catch (e) {
      setError("Network problem — try again");
      setSaving(false);
    }
  };
  const card = {
    maxWidth: 480,
    margin: "24px auto",
    padding: "22px 18px",
    background: "var(--paper-1, #fffaf0)",
    border: "1px solid #d8c9a8",
    borderRadius: 14,
    boxShadow: "0 8px 30px rgba(0,0,0,.08)"
  };
  if (saved) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        padding: 16
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        ...card,
        textAlign: "center"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 46
      }
    }, "\u2705"), /*#__PURE__*/React.createElement("h2", {
      style: {
        margin: "10px 0 4px"
      }
    }, "Location saved!"), /*#__PURE__*/React.createElement("div", {
      style: {
        direction: "rtl",
        color: "#555",
        marginBottom: 6
      }
    }, "\u0622\u062F\u0631\u0633 \u0634\u0645\u0627 \u0630\u062E\u06CC\u0631\u0647 \u0634\u062F"), /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#555",
        fontSize: 14,
        margin: "0 0 16px"
      }
    }, "Go back to WhatsApp \u2014 your order continues right there.", /*#__PURE__*/React.createElement("span", {
      style: {
        display: "block",
        direction: "rtl"
      }
    }, "\u0628\u0647 \u0648\u0627\u062A\u0633\u200C\u0627\u067E \u0628\u0631\u06AF\u0631\u062F\u06CC\u062F \u2014 \u0633\u0641\u0627\u0631\u0634 \u0634\u0645\u0627 \u0647\u0645\u0627\u0646\u200C\u062C\u0627 \u0627\u062F\u0627\u0645\u0647 \u062F\u0627\u0631\u062F.")), /*#__PURE__*/React.createElement("a", {
      href: loc.waMeHref || "#",
      style: {
        display: "inline-block",
        background: "#25d366",
        color: "#fff",
        fontWeight: 700,
        padding: "12px 26px",
        borderRadius: 999,
        textDecoration: "none"
      }
    }, "\u21A9\uFE0F Back to WhatsApp \xB7 \u0628\u0627\u0632\u06AF\u0634\u062A \u0628\u0647 \u0648\u0627\u062A\u0633\u200C\u0627\u067E")));
  }
  return /*#__PURE__*/React.createElement("div", {
    style: {
      padding: 16
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: card
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      textAlign: "center",
      marginBottom: 14
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 36
    }
  }, "\uD83D\uDCCD"), /*#__PURE__*/React.createElement("h2", {
    style: {
      margin: "6px 0 2px"
    }
  }, "Set your delivery location"), /*#__PURE__*/React.createElement("div", {
    style: {
      direction: "rtl",
      color: "#555"
    }
  }, "\u0645\u0648\u0642\u0639\u06CC\u062A \u0627\u0631\u0633\u0627\u0644 \u062E\u0648\u062F \u0631\u0627 \u062B\u0628\u062A \u06A9\u0646\u06CC\u062F")), /*#__PURE__*/React.createElement("div", {
    className: "form",
    style: {
      display: "block"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "full",
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("label", null, "Area \xB7 \u0645\u0646\u0637\u0642\u0647 ", /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement(AreaPicker, {
    value: area,
    onChange: setArea,
    hasTestItem: false
  }), selectedArea && /*#__PURE__*/React.createElement("div", {
    className: "area-fee-note"
  }, "Delivery to ", selectedArea.en, ": ", DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("s", {
    style: {
      opacity: .55
    }
  }, "AED ", selectedArea.fee), " ", /*#__PURE__*/React.createElement("b", null, "AED ", feeFmt(areaFeeNow(selectedArea.fee))), DELIVERY_PROMO_ON && /*#__PURE__*/React.createElement("div", {
    style: {
      color: '#a4791f'
    }
  }, "\uD83C\uDF89 All delivery fees include a ", DELIVERY_FEE_DISCOUNT_PCT, "% discount \u2014 limited time"))), /*#__PURE__*/React.createElement("div", {
    className: "full",
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("label", null, "Street / building \xB7 \u0622\u062F\u0631\u0633 ", /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement("input", {
    type: "text",
    placeholder: "Apartment, street, neighbourhood",
    value: street,
    onChange: e => setStreet(e.target.value),
    style: {
      width: "100%",
      boxSizing: "border-box"
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "full",
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("label", null, "Pin on map \xB7 \u0646\u0642\u0634\u0647 ", /*#__PURE__*/React.createElement("span", {
    className: "req-mark",
    "aria-hidden": "true"
  }, "*")), /*#__PURE__*/React.createElement(MapPicker, {
    restaurant: RESTAURANT_COORDS,
    coords: coords,
    onPick: c => {
      setCoords(c);
      setGeoError("");
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "geo-row",
    style: {
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "geo-btn",
    onClick: pinLocation,
    disabled: geoBusy
  }, geoBusy ? "Locating…" : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "geo-pin"
  }, "\u2316"), " Use my GPS ", /*#__PURE__*/React.createElement("span", {
    className: "fa"
  }, "\u0645\u0648\u0642\u0639\u06CC\u062A \u0645\u0646"))), coords && /*#__PURE__*/React.createElement("span", {
    className: "geo-pinned-inline"
  }, /*#__PURE__*/React.createElement("span", {
    className: "geo-coord"
  }, coords.lat.toFixed(5), ", ", coords.lng.toFixed(5)), /*#__PURE__*/React.createElement("span", {
    className: "geo-fa"
  }, "\u067E\u06CC\u0646 \u0634\u062F \u2713"))), geoError && /*#__PURE__*/React.createElement("div", {
    className: "geo-error"
  }, geoError)), /*#__PURE__*/React.createElement("div", {
    className: "full",
    style: {
      marginBottom: 14
    }
  }, /*#__PURE__*/React.createElement("label", null, "Save as \xB7 \u0630\u062E\u06CC\u0631\u0647 \u0628\u0647\u200C\u0639\u0646\u0648\u0627\u0646"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8
    }
  }, [["Home", "🏠 Home · خانه"], ["Work", "🏢 Work · محل کار"], ["Other", "📌 Other · دیگر"]].map(([val, text]) => /*#__PURE__*/React.createElement("button", {
    key: val,
    type: "button",
    onClick: () => setLabel(val),
    style: {
      flex: 1,
      padding: "9px 6px",
      borderRadius: 8,
      cursor: "pointer",
      fontSize: 13,
      border: label === val ? "2px solid #128c4b" : "1px solid #c9b79a",
      background: label === val ? "rgba(37,211,102,.12)" : "transparent"
    }
  }, text)))), error && /*#__PURE__*/React.createElement("div", {
    style: {
      color: "#8a1c1c",
      marginBottom: 10,
      fontSize: 14
    }
  }, error), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: save,
    disabled: !canSave,
    style: {
      width: "100%",
      padding: 14,
      borderRadius: 10,
      border: "none",
      cursor: canSave ? "pointer" : "not-allowed",
      fontWeight: 800,
      fontSize: 16,
      color: "#fff",
      background: canSave ? "#128c4b" : "#9fbfae"
    }
  }, saving ? "Saving… · در حال ذخیره…" : "✅ Save & return to WhatsApp · ذخیره و بازگشت"), !canSave && !saving && /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8,
      fontSize: 12.5,
      color: "#8a6d3b",
      textAlign: "center"
    }
  }, !selectedArea ? "Choose your area · منطقه را انتخاب کنید" : !coords ? "Pin your location on the map · موقعیت را پین کنید" : street.trim().length < 4 ? "Enter your street / building · آدرس را بنویسید" : ""))));
}

// ── Mount: IMMEDIATE (final owner decision, 2026-08-06) ────────────────────
// The interaction-gated mount is retired: it protected the lab LCP (Chrome
// re-opens the LCP race when the SSR view unmounts, so a delayed mount kept
// the score at ~100) but it made hands-off visitors sit on the splash — and
// the owner is right that a restaurant site must simply OPEN. Mounting here,
// as soon as the deferred bundle executes, puts the real app on screen
// ~0.5-1.5s after first paint; the splash is only the bridge for that gap.
// Known, accepted cost: the app hero becomes the LCP at mount time (roughly
// -8-12 mobile lab points vs the gated design). Do not "optimise" this back
// into a gate without an explicit owner decision.
ReactDOM.createRoot(document.getElementById("root")).render(/*#__PURE__*/React.createElement(ErrorBoundary, null, window.__WA_LOC_MODE__ ? /*#__PURE__*/React.createElement(WaLocationSetup, null) : /*#__PURE__*/React.createElement(App, null)));